import {
  DIFFICULTIES,
  ENEMIES,
  MODULES,
  PLAYER_BASE_MOVE_SPEED,
  SYNERGIES,
  TEAM_TECH,
  applyStatBonuses,
  computePlayerStats,
  evaluateSynergies,
  getAbilityTuning,
  getEnemy,
  getModule,
  getSynergy,
} from "./phase-three-content";
import {
  HIDDEN_SYNERGIES,
  evaluateHiddenSynergies,
  getActiveHiddenEffectIds,
  getHiddenEffect,
  getHiddenSynergy,
} from "./hidden-effects";
import { SeededRandom, hashSeed } from "./rng";
import {
  SURFACE_MINING_CONFIG,
  generateSurfaceVeins,
  type SurfaceVeinState,
} from "./surface-mining";
import {
  getUndeadUnit,
  hasUndeadAbility,
} from "./undead-units";
import {
  LUCKY_STAR_HIDDEN_EFFECT_ID,
  getFinanceStakeTarget,
  resolvePeriodicSpendAccumulatorCycle,
} from "./finance-state";
import {
  ROUND_CLOCK_LABOR_SYNERGY_ID,
  cancelCircleTraversal,
  updateCircleTraversal,
  type CircleTraversalTracker,
} from "./labor";
import {
  CITY_ALLY_RALLY_RADIUS,
  CITY_ALLY_GLOBAL_CAPS,
  CITY_BUILDING_DEFINITIONS,
  CITY_ENEMY_AGGRO_CONFIG,
  CITY_GATE_LINK_RADIUS,
  MONSTER_CITY_LAYOUT,
  getCityAllyDefinition,
} from "./monster-city-buildings";
import {
  CONTENT_VERSION,
  DEFAULT_GAME_DURATION_MS,
  PLAYER_ABILITY_SLOTS,
  PLAYER_LEVEL_CAP,
  PLAYER_TALENT_POINT_CAP,
  PROTOCOL_VERSION,
  WORLD_HEIGHT,
  WORLD_WIDTH,
  type AbilityEntityKind,
  type AbilityEntityWireState,
  type AbilityMechanicDefinition,
  type ArchaeologyNodeState,
  type CompanionDefinition,
  type CityAllyBehavior,
  type CityAllyUnitState,
  type CityBuildingState,
  type DamageSourceEntry,
  type DamageSourceKind,
  type DifficultyDefinition,
  type DifficultyId,
  type DefenseSegmentState,
  type EarthSpiritBehavior,
  type EarthSpiritStar,
  type EnemyRank,
  type EnemyWireState,
  type FinanceResourceId,
  type FinanceStakeTargetId,
  type GrowthEvent,
  type GuardFormId,
  type HiddenRuntimeEffect,
  type LaborDefinition,
  type LaneId,
  type LaneState,
  type MineCaveId,
  type ModuleDefinition,
  type ModuleTier,
  type PerformanceMetrics,
  type PlayerContribution,
  type PlayerPublicState,
  type PlayerStatus,
  type PlayerStats,
  type PublicRoomState,
  type RoomPhase,
  type SideObjectiveState,
  type SimulationEvent,
  type StatBonuses,
  type SurfaceVeinViewState,
  type SynergyMechanicDefinition,
  type TalentId,
  type TalentState,
  type TeamSignalKind,
  type TeamSignalState,
  type TeamTechId,
  type TeamTechState,
  type UpgradeOffer,
  type UndeadUnitId,
  type Vec2,
  type WorldSpaceId,
} from "./phase-three-types";

interface SimPlayer {
  id: string;
  name: string;
  spaceId: WorldSpaceId;
  x: number;
  y: number;
  health: number;
  mana: number;
  experience: number;
  level: number;
  talentPoints: number;
  talents: Record<TalentId, number>;
  wage: number;
  archaeologyYield: number;
  ready: boolean;
  connected: boolean;
  aiControlled: boolean;
  input: Vec2;
  inputSequence: number;
  scrap: number;
  core: number;
  kills: number;
  status: PlayerStatus;
  reviveProgressMs: number;
  reviveRequiredMs: number;
  contribution: PlayerContribution;
  modules: Array<string | null>;
  moduleSlotStates: Map<
    number,
    {
      moduleId: string;
      experienceStored: number;
      killCharge: number;
      financeEnteredAtMs?: number;
      financeNextAtMs?: number;
      financeAccumulatedSpend: number;
      financeCharge: number;
      laborDistanceRemainder: number;
      laborStationaryRemainderMs: number;
      laborMovingRemainderMs: number;
      financeStake?: {
        targetId: FinanceStakeTargetId;
        amount: number;
      };
    }
  >;
  experienceVaultBranchOrdinals: number[];
  financeOutcomeOrdinals: number[];
  financeStakeOrdinals: number[];
  financeAccumulatorOrdinals: number[];
  financeStatAdjustments: Map<FinanceStakeTargetId, number>;
  lockedSlots: boolean[];
  synergies: string[];
  hiddenEffectIds: Set<string>;
  hiddenSynergies: string[];
  repeatableHiddenEffectGrants: Map<string, ModuleTier[]>;
  hiddenSynergyProgress: Map<string, number>;
  hiddenEffectCooldowns: Map<string, number>;
  castReleaseCounts: Map<string, number>;
  laborMovementDistance: number;
  laborCircleCount: number;
  laborStationaryAchievementMs: number;
  laborRouteLegs: number;
  laborBarbecueBonusGold: number;
  laborCircleTracker: CircleTraversalTracker;
  laborRouteArmed?: "core" | "outer_gate";
  laborRouteInsideKey?: string;
  laborPatrolLastGateKey?: string;
  laborTransportLastGateKey?: string;
  laborRouteQualifiedMovement: boolean;
  laborLastNormalPosition: Vec2;
  enemySlowUntilMs: number;
  enemySlowRatio: number;
  mechanicCooldowns: Map<string, number>;
  deathGuardCooldowns: Map<string, number>;
  activeDeathRecoveries: Map<
    string,
    {
      remainingHealing: number;
      healingPerMs: number;
      endsAtMs: number;
    }
  >;
  invulnerableUntilMs: number;
  synergyStackExpirations: Map<string, number[]>;
  synergyNextStackAtMs: Map<string, number>;
  turretMoveRatio: number;
  turretMovementAccumulatorMs: number;
  turretLastMoving: boolean | null;
  dayNightPhase: "day" | "night";
  dayNightSwitchAtMs: number;
  dayNightNextAttackAtMs: number;
  stats: PlayerStats;
  growthBonuses: Partial<
    Record<
      | "damage"
      | "maxHealth"
      | "armor"
      | "moveSpeed"
      | "manaMax",
      number
    >
  >;
  permanentOneShotBonuses: StatBonuses;
  pendingOneShotVitalClamp?: { health: number; mana: number };
  copiedBonuses: StatBonuses[];
  growthCounters: Map<string, number>;
  growthApplied: Map<string, number>;
  abilityDamageGrowth: Map<string, number>;
  abilityTargetOrdinals: Map<string, number>;
  synergyCounters: Map<string, number>;
  synergySubstitutions: Map<
    string,
    { moduleId: string; expiresAtMs: number; sourceAbilityId: string }
  >;
  lastPlayerSkillTargetId?: number;
  pendingDelayedHits: Array<{
    executeAtMs: number;
    originEnemyId: number;
    originX: number;
    originY: number;
    originSpaceId: WorldSpaceId;
    damage: number;
    abilityId: string;
    sourceSlot?: number;
    targetMode?: "nearest_other" | "same_target";
  }>;
  debtReductionBySlot: Map<number, number>;
  debtCrowDoubleUntilMs: number;
  abilityCooldowns: Map<string, number>;
  economyCooldowns: Map<string, number>;
  companionCooldowns: Map<string, number>;
  nextAttackAtMs: number;
  basicAttackJammedUntilMs: number;
  temporaryShield: number;
  temporaryShieldUntilMs: number;
  nextHealAtMs: number;
  respawnAtMs: number;
  nextSignalAtMs: number;
  pendingOffer?: UpgradeOffer;
  declinedUpgradeOffers: Map<
    number,
    {
      currentModuleId: string;
      targetTier: ModuleTier;
      candidates: string[];
    }
  >;
  upgradeRefreshSignatures: Map<number, string>;
  offerHistory: string[];
  offersSeen: number;
  rerolls: number;
  lastPosition: Vec2;
  lastDamageAtMs: number;
  surfaceMiningVeinId?: number;
  surfaceMiningAccumulatorMs: number;
  surfaceMiningGoldRemainder: number;
  surfaceMiningCrystalRemainder: number;
  surfaceDevourVeinId?: number;
  surfaceDevourSourceSlot?: number;
  surfaceDevourProgressMs: number;
  surfaceDevourCooldownUntilMs: number;
  surfaceDevourCount: number;
  surfaceDevourMovePenaltyRatio: number;
  gourmetContactCooldowns: Map<number, number>;
}

interface DamageAttribution {
  kind: DamageSourceKind;
  abilityId?: string;
  hiddenSourceId?: string;
  entityId?: number;
  sourceSlot?: number;
  variant?: string;
}

interface SimEnemy {
  id: number;
  definitionId: string;
  spaceId: WorldSpaceId;
  x: number;
  y: number;
  health: number;
  maxHealth: number;
  armor: number;
  laneId: LaneId;
  nextAttackAtMs: number;
  nextPlayerAttackAtMs: number;
  slowUntilMs: number;
  slowRatio: number;
  stunnedUntilMs: number;
  rootedUntilMs: number;
  revealedUntilMs: number;
  armorReductionStacks: Array<{
    sourceId: string;
    amount: number;
    expiresAtMs: number;
  }>;
  hitCounters: Map<string, number>;
  followerComboHits: Map<string, number>;
  recentAbilityHits: Map<string, number>;
  hotFloorEmpoweredUntilMs: number;
  forcedAggroPlayerId?: string;
  forcedAggroAbilityId?: string;
  forcedAggroRange?: number;
  forcedAggroSlowRatio?: number;
  velocityX: number;
  velocityY: number;
  recentAttacker?: {
    kind: "player" | "ability" | "city_ally" | "defense";
    id: string | number;
    expiresAtMs: number;
  };
  objectiveId?: SideObjectiveState["id"];
  objectiveOriginX?: number;
  objectiveOriginY?: number;
  objectiveEmpowered?: boolean;
  objectivePowerMultiplier?: number;
  siegeBuffUntilMs?: number;
  marshalNextOrderAtMs?: number;
  marshalNextChargeAtMs?: number;
  marshalChargeResolveAtMs?: number;
  marshalChargeTarget?: { kind: "building" | "defense"; id: string | number };
  marshalShieldTriggered?: boolean;
  marshalShieldUntilMs?: number;
}

interface SimAbilityEntity {
  id: number;
  ownerId: string;
  abilityId: string;
  sourceSlot?: number;
  kind: AbilityEntityKind;
  presentationId: string;
  spaceId: WorldSpaceId;
  x: number;
  y: number;
  health: number;
  maxHealth: number;
  armor: number;
  damage: number;
  intervalMs: number;
  range: number;
  moveSpeed: number;
  orbitRadius: number;
  orbitOffset: number;
  nextAttackAtMs: number;
  attackCount: number;
  deathProcessed: boolean;
  nextPeriodicHealAtMs: number;
  temporaryShield: number;
  temporaryShieldUntilMs: number;
  nextIncomeAtMs: number;
  incomeGold: number;
  respawnMs: number;
  respawnAtMs: number;
  auraMultiplier: number;
  ethereal: boolean;
  nextHuntAtMs: number;
  undeadUnitId?: UndeadUnitId;
      undeadAbilityCooldowns?: Map<number, number>;
  followerAbilityCooldowns?: Map<string, number>;
  guardForm?: GuardFormId;
  guardAttackCount?: number;
  guardHasFlame?: boolean;
  guardHasCritical?: boolean;
  guardHasDash?: boolean;
  guardAbilityDamageMultiplier?: number;
  guardAbilityCooldowns?: Map<string, number>;
  movement?: NonNullable<CompanionDefinition["movement"]> & {
    returning: boolean;
    target: Vec2;
  };
  field?: {
    radius: number;
    damage: number;
    tickIntervalMs: number;
    nextTickAtMs: number;
    expiresAtMs: number;
    pullNormals?: boolean;
  };
  guardCouncilMultiplier?: number;
  earthSpirit?: {
    star: EarthSpiritStar;
    behavior: EarthSpiritBehavior;
    activityCenter: Vec2;
    alertRange: number;
    returnDelayMs: number;
    targetEnemyId?: number;
    waitUntilMs: number;
    aggregationId?: number;
  };
}

interface EarthAggregationSavedState {
  behavior: Exclude<EarthSpiritBehavior, "aggregating">;
  targetEnemyId?: number;
  waitRemainingMs: number;
}

interface EarthAggregationGroup {
  id: number;
  ownerId: string;
  star: Exclude<EarthSpiritStar, 0 | 5>;
  target: Vec2;
  participantIds: number[];
  savedStates: Map<number, EarthAggregationSavedState>;
}

interface GuardRuntimeProfile {
  form: GuardFormId;
  presentationId: string;
  health: number;
  armor: number;
  damage: number;
  intervalMs: number;
  range: number;
  moveSpeed: number;
  respawnMs: number;
  hasFlame: boolean;
  hasCritical: boolean;
  hasDash: boolean;
  abilityDamageMultiplier: number;
}

interface SimDefenseSegment extends DefenseSegmentState {
  nextAttackAtMs: number;
}

interface SimCityBuilding extends CityBuildingState {
  nextSpawnAtMs: number;
}

interface SimCityAlly extends CityAllyUnitState {
  sourceBuildingId: string;
  armor: number;
  behavior: CityAllyBehavior;
  rallyGateId?: number;
  activityCenterX: number;
  activityCenterY: number;
  targetEnemyId?: number;
  damage: number;
  attackIntervalMs: number;
  attackRange: number;
  moveSpeed: number;
  alertRadius: number;
  activityRadius: number;
  nextAttackAtMs: number;
}

type EnemyAttackTarget =
  | { kind: "player"; value: SimPlayer }
  | { kind: "ability"; value: SimAbilityEntity }
  | { kind: "city_ally"; value: SimCityAlly }
  | { kind: "building"; value: SimCityBuilding }
  | { kind: "objective"; value: SideObjectiveState }
  | { kind: "defense"; value: SimDefenseSegment }
  | { kind: "core"; value: GameSimulation["core"] };

export interface SimulationOptions {
  seed: string;
  durationMs?: number;
}

export interface SnapshotOptions {
  includeWorldState?: boolean;
  includeDefenses?: boolean;
  includeMotionAnchors?: boolean;
  includeEnemyAnchors?: boolean;
  includeDefenseAnchors?: boolean;
  /** Used to crop hidden surface veins before serialization. */
  viewerPlayerId?: string;
}

export type ActionResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

export const UPGRADE_GOLD_COSTS: Readonly<Record<ModuleTier, number>> = {
  1: 50,
  2: 150,
  3: 600,
  4: 1_200,
  5: 2_500,
  6: 0,
};

export const REFORGE_CRYSTAL_COSTS: Readonly<Record<ModuleTier, number>> = {
  1: 20,
  2: 50,
  3: 50,
  4: 100,
  5: 100,
  6: 0,
};

export const ASCENSION_CRYSTAL_COST = 500;
export const CRYSTAL_WAGE_PER_MINUTE = 0;

const defaultMetrics: PerformanceMetrics = {
  tickP95Ms: 0,
  outgoingKbpsPerClient: 0,
  enemyCount: 0,
  abilityEntityCount: 0,
};

const BASE_RESPAWN_MS = 6_000;
const RESPAWN_MS_PER_LEVEL_AFTER_FIRST = 2_000;
const REVIVE_REQUIRED_MS = 3_000;
const REVIVE_RADIUS = 120;
const MAX_ENEMIES = 1_200;
const ENEMY_SEPARATION_CELL = 72;
const ENEMY_AGGRO_RADIUS = 720;
const ENEMY_WALL_AGGRO_RADIUS = 820;
const ENEMY_TOWER_AGGRO_RADIUS = 1_120;
const ENEMY_SIEGE_DAMAGE_MULTIPLIER = 0.38;
const TOWER_RANGE = 620;
const TOWER_BASE_DAMAGE = 34;
const TOWER_BASE_INTERVAL_MS = 900;
const MOONWELL_MAX_HEALTH = 12_000;
const MOONWELL_HEAL_RATIO_PER_SECOND = 0.005;
const SALVAGE_CART_MAX_HEALTH = 18_000;
const RIFT_REQUIRED_KILLS = 200;
const RIFT_SPAWN_INTERVAL_MS = 250;
const RIFT_WANDER_RADIUS = 2_000;
const FIRST_BOSS_AT_MS = 6 * 60_000;
const SECOND_BOSS_AT_MS = 12 * 60_000;
const FINAL_BOSS_AT_MS = 18 * 60_000;

interface EnemyTimeScaling {
  health: number;
  damage: number;
  armor: number;
  moveSpeed: number;
}

const ENEMY_TIME_SCALING_END: Record<
  DifficultyId,
  Record<Exclude<EnemyRank, "boss">, EnemyTimeScaling>
> = {
  apprentice: {
    normal: { health: 2.8, damage: 2.2, armor: 1.25, moveSpeed: 1.08 },
    elite: { health: 3.3, damage: 2.6, armor: 1.35, moveSpeed: 1.1 },
  },
  veteran: {
    normal: { health: 4, damage: 3, armor: 1.55, moveSpeed: 1.15 },
    elite: { health: 4.8, damage: 3.6, armor: 1.8, moveSpeed: 1.18 },
  },
  master: {
    normal: { health: 5.8, damage: 4.4, armor: 2, moveSpeed: 1.22 },
    elite: { health: 7, damage: 5.4, armor: 2.5, moveSpeed: 1.25 },
  },
};

/** Late-wave pressure curve. Bosses retain their separately authored timeline. */
export function getEnemyTimeScaling(
  difficultyId: DifficultyId,
  rank: EnemyRank,
  rawProgress: number,
): EnemyTimeScaling {
  const progress = clamp(rawProgress, 0, 1);
  if (rank === "boss") {
    return {
      health: 1.18 + progress * 0.52,
      damage: 1 + progress * 0.6,
      armor: 1,
      moveSpeed: 1,
    };
  }
  const curve = progress ** 1.6;
  const end = ENEMY_TIME_SCALING_END[difficultyId][rank];
  return {
    health: 1.18 + (end.health - 1.18) * curve,
    damage: 1 + (end.damage - 1) * curve,
    armor: 1 + (end.armor - 1) * curve,
    moveSpeed: 1 + (end.moveSpeed - 1) * curve,
  };
}

export function getEliteSpawnIntervalMs(
  difficultyId: DifficultyId,
  rawProgress: number,
): number {
  const progress = clamp(rawProgress, 0, 1);
  const baseInterval = 58_000 - 42_000 * progress ** 1.25;
  return Math.max(
    9_000,
    baseInterval / DIFFICULTIES[difficultyId].eliteMultiplier,
  );
}

export function getEliteSpawnBatchSize(
  difficultyId: DifficultyId,
  rawProgress: number,
): number {
  const progress = clamp(rawProgress, 0, 1);
  if (difficultyId === "master" && progress >= 0.65) return 2;
  if (difficultyId === "veteran" && progress >= 0.85) return 2;
  return 1;
}

const LANE_CONFIGS: Array<{
  id: LaneId;
  name: string;
  x: number;
  y: number;
}> = [
  { id: "north", name: "北境", x: WORLD_WIDTH / 2, y: 120 },
  { id: "east", name: "东林", x: WORLD_WIDTH - 120, y: WORLD_HEIGHT / 2 },
  { id: "south", name: "南沼", x: WORLD_WIDTH / 2, y: WORLD_HEIGHT - 120 },
  { id: "west", name: "西岭", x: 120, y: WORLD_HEIGHT / 2 },
];

const TALENT_DEFINITIONS: Array<{
  id: TalentId;
  name: string;
  description: string;
  maxLevel: number;
}> = [
  { id: "vital_essence", name: "生命本源", description: "每级生命上限 +60", maxLevel: 5 },
  { id: "mana_well", name: "魔力源泉", description: "每级能量上限 +30", maxLevel: 5 },
  { id: "life_font", name: "复苏泉眼", description: "每级生命恢复 +1.5/秒", maxLevel: 5 },
  { id: "mana_tide", name: "潮汐冥想", description: "每级能量恢复 +0.75/秒", maxLevel: 5 },
  { id: "war_essence", name: "战争本源", description: "每级基础攻击力 +8", maxLevel: 5 },
  { id: "haste_essence", name: "迅击本源", description: "每级攻击速度约 +6%", maxLevel: 5 },
  { id: "swift_essence", name: "疾行本源", description: "每级移动速度 +12", maxLevel: 5 },
  { id: "stone_skin", name: "岩甲本源", description: "每级护甲 +8", maxLevel: 5 },
  {
    id: "spirit_sight",
    name: "灵视",
    description: "每级扩大对隐形目标的感知范围",
    maxLevel: 5,
  },
  { id: "far_sight", name: "鹰眼侦查", description: "每级侦查范围 +120", maxLevel: 5 },
];

export class GameSimulation {
  readonly players = new Map<string, SimPlayer>();
  readonly enemies = new Map<number, SimEnemy>();
  readonly abilityEntities = new Map<number, SimAbilityEntity>();
  readonly surfaceVeins = new Map<number, SurfaceVeinState>();
  private readonly pendingContactKnockbacks: Array<{
    enemyId: number;
    origin: Vec2;
    distance: number;
  }> = [];
  readonly durationMs: number;
  readonly runSeed: string;

  phase: RoomPhase = "lobby";
  difficulty: DifficultyId = "apprentice";
  elapsedMs = 0;
  resultReason: string | undefined;

  readonly core = {
    x: WORLD_WIDTH / 2,
    y: WORLD_HEIGHT / 2,
    radius: 58,
    health: 16_000,
    maxHealth: 16_000,
    armor: 0,
  };

  readonly objectives: SideObjectiveState[] = [
    {
      id: "moonwell",
      name: "净化月泉",
      description: "在东北月泉完成引导，恢复营地并获得远征物资。",
      presentationId: "objective.moonwell",
      status: "waiting",
      x: WORLD_WIDTH / 2 + 3_050,
      y: WORLD_HEIGHT / 2 - 2_050,
      activateAtMs: 180_000,
      progressMs: 0,
      requiredMs: 60_000,
      expiresAtMs: DEFAULT_GAME_DURATION_MS,
      health: MOONWELL_MAX_HEALTH,
      maxHealth: MOONWELL_MAX_HEALTH,
      invulnerable: false,
      attractionRadius: 2_500,
    },
    {
      id: "salvage",
      name: "护送炼金车",
      description: "前往西南炼金车处接应；接应后炼金车会自行驶向怪物宝城。",
      presentationId: "objective.alchemy-cart",
      status: "waiting",
      x: WORLD_WIDTH / 2 - 3_350,
      y: WORLD_HEIGHT / 2 + 2_200,
      activateAtMs: 480_000,
      progressMs: 0,
      requiredMs: 1,
      expiresAtMs: DEFAULT_GAME_DURATION_MS,
      health: SALVAGE_CART_MAX_HEALTH,
      maxHealth: SALVAGE_CART_MAX_HEALTH,
      attractionRadius: 2_000,
      playerEngaged: false,
    },
    {
      id: "rift",
      name: "封印虚空裂口",
      description: "前往裂口区域，在60秒内由玩家归属击杀200名虚空单位。",
      presentationId: "objective.void-rift",
      status: "waiting",
      x: WORLD_WIDTH / 2 - 3_000,
      y: WORLD_HEIGHT / 2 - 2_200,
      activateAtMs: 780_000,
      progressMs: 0,
      requiredMs: 60_000,
      expiresAtMs: 840_000,
      kills: 0,
      requiredKills: RIFT_REQUIRED_KILLS,
      spawnedCount: 0,
      playerEngaged: false,
    },
  ];

  /** @deprecated Protocol-17 archive surface. New games never populate it. */
  readonly archaeologyNodes: ArchaeologyNodeState[] = [];
  readonly signals: TeamSignalState[] = [];
  readonly defenses: SimDefenseSegment[] = [];
  readonly cityBuildings = new Map<string, SimCityBuilding>();
  readonly cityAllies = new Map<number, SimCityAlly>();
  readonly teamTechLevels = Object.fromEntries(
    TEAM_TECH.map((definition) => [definition.id, 0]),
  ) as Record<TeamTechId, number>;

  private readonly random: SeededRandom;
  private readonly events: SimulationEvent[] = [];
  private nextEnemyId = 1;
  private nextAbilityEntityId = 1;
  private nextCityAllyId = 1;
  private nextEarthAggregationId = 1;
  private readonly earthAggregationGroups = new Map<
    number,
    EarthAggregationGroup
  >();
  private offerSequence = 1;
  private spawnAccumulator = 0;
  private nextEliteAtMs = 80_000;
  private firstBossSpawned = false;
  private secondBossSpawned = false;
  private finalBossSpawned = false;
  private finalBossDefeated = false;
  private riftSpawnAccumulatorMs = 0;
  private lastWageMinute = 0;
  private signalSequence = 1;
  private towerGoldPool = 0;
  private towerGoldPaid = 0;

  constructor(options: SimulationOptions) {
    this.runSeed = options.seed.trim().slice(0, 48) || "p3-default";
    this.durationMs = options.durationMs ?? DEFAULT_GAME_DURATION_MS;
    this.random = new SeededRandom(hashSeed(this.runSeed));
    this.createDefenses();
    this.createCityBuildings();
    const spawnPoints = Array.from({ length: 4 }, (_, index) => {
      const angle = (index / 4) * Math.PI * 2 + Math.PI / 2;
      return {
        x: this.core.x + Math.cos(angle) * 190,
        y: this.core.y + Math.sin(angle) * 190,
      };
    });
    for (const vein of generateSurfaceVeins({
      seed: this.runSeed,
      worldWidth: WORLD_WIDTH,
      worldHeight: WORLD_HEIGHT,
      core: this.core,
      spawns: spawnPoints,
      objectives: this.objectives,
      defenses: this.defenses,
    })) {
      this.surfaceVeins.set(vein.id, vein);
    }
  }

  addPlayer(id: string, name: string): ActionResult<PlayerPublicState> {
    if (this.phase !== "lobby") {
      return { ok: false, error: "对局已经开始，暂不支持中途加入。" };
    }
    if (this.players.has(id)) {
      return { ok: false, error: "玩家已经存在。" };
    }

    const index = this.players.size;
    const angle = (index / 4) * Math.PI * 2 + Math.PI / 2;
    const modules = Array<string | null>(PLAYER_ABILITY_SLOTS).fill(null);
    const stats = computePlayerStats(modules);
    const player: SimPlayer = {
      id,
      name: sanitizeName(name),
      spaceId: "surface",
      x: this.core.x + Math.cos(angle) * 190,
      y: this.core.y + Math.sin(angle) * 190,
      health: stats.maxHealth,
      mana: stats.manaMax,
      experience: 0,
      level: 1,
      talentPoints: 1,
      talents: {
        vital_essence: 0,
        mana_well: 0,
        life_font: 0,
        mana_tide: 0,
        war_essence: 0,
        haste_essence: 0,
        swift_essence: 0,
        stone_skin: 0,
        survey: 0,
        spirit_sight: 0,
        far_sight: 0,
      },
      wage: 22,
      archaeologyYield: 0,
      ready: false,
      connected: true,
      aiControlled: false,
      input: { x: 0, y: 0 },
      inputSequence: 0,
      scrap: 180,
      core: 6,
      kills: 0,
      status: "active",
      reviveProgressMs: 0,
      reviveRequiredMs: REVIVE_REQUIRED_MS,
      contribution: {
        damageDealt: 0,
        damageBySource: [],
        scrapEarned: 0,
        experienceEarned: 0,
        goldDonated: 0,
        goldReceived: 0,
        surfaceMiningGoldEarned: 0,
        archaeologyEarned: 0,
        coreDonated: 0,
        coreReceived: 0,
        teamTechInvested: 0,
        repairDone: 0,
        healingDone: 0,
        objectiveMs: 0,
        revives: 0,
        timesDowned: 0,
        signalsSent: 0,
        oneShotsTriggered: 0,
        companionsSummoned: 0,
        firstModuleAtMs: null,
        firstSynergyAtMs: null,
        firstTierFiveAtMs: null,
        firstAscensionAtMs: null,
      },
      modules,
      moduleSlotStates: new Map(),
      experienceVaultBranchOrdinals: Array.from(
        { length: PLAYER_ABILITY_SLOTS },
        () => 0,
      ),
      financeOutcomeOrdinals: Array.from(
        { length: PLAYER_ABILITY_SLOTS },
        () => 0,
      ),
      financeStakeOrdinals: Array.from(
        { length: PLAYER_ABILITY_SLOTS },
        () => 0,
      ),
      financeAccumulatorOrdinals: Array.from(
        { length: PLAYER_ABILITY_SLOTS },
        () => 0,
      ),
      financeStatAdjustments: new Map(),
      lockedSlots: Array<boolean>(PLAYER_ABILITY_SLOTS).fill(false),
      synergies: [],
      hiddenEffectIds: new Set(),
      hiddenSynergies: [],
      repeatableHiddenEffectGrants: new Map(),
      hiddenSynergyProgress: new Map(),
      hiddenEffectCooldowns: new Map(),
      castReleaseCounts: new Map(),
      laborMovementDistance: 0,
      laborCircleCount: 0,
      laborStationaryAchievementMs: 0,
      laborRouteLegs: 0,
      laborBarbecueBonusGold: 0,
      laborCircleTracker: { nextSampleAtMs: 0 },
      laborRouteQualifiedMovement: false,
      laborLastNormalPosition: { x: 0, y: 0 },
      enemySlowUntilMs: 0,
      enemySlowRatio: 0,
      mechanicCooldowns: new Map(),
      deathGuardCooldowns: new Map(),
      activeDeathRecoveries: new Map(),
      invulnerableUntilMs: 0,
      synergyStackExpirations: new Map(),
      synergyNextStackAtMs: new Map(),
      turretMoveRatio: 1,
      turretMovementAccumulatorMs: 0,
      turretLastMoving: null,
      dayNightPhase: "day",
      dayNightSwitchAtMs: 0,
      dayNightNextAttackAtMs: 0,
      stats,
      growthBonuses: {},
      permanentOneShotBonuses: {},
      copiedBonuses: [],
      growthCounters: new Map(),
      growthApplied: new Map(),
      abilityDamageGrowth: new Map(),
      abilityTargetOrdinals: new Map(),
      synergyCounters: new Map(),
      synergySubstitutions: new Map(),
      pendingDelayedHits: [],
      debtReductionBySlot: new Map(),
      debtCrowDoubleUntilMs: 0,
      abilityCooldowns: new Map(),
      economyCooldowns: new Map(),
      companionCooldowns: new Map(),
      nextAttackAtMs: 0,
      basicAttackJammedUntilMs: 0,
      temporaryShield: 0,
      temporaryShieldUntilMs: 0,
      nextHealAtMs: 0,
      respawnAtMs: 0,
      nextSignalAtMs: 0,
      declinedUpgradeOffers: new Map(),
      upgradeRefreshSignatures: new Map(),
      offerHistory: [],
      offersSeen: 0,
      rerolls: 0,
      lastPosition: { x: 0, y: 0 },
      lastDamageAtMs: -1,
      surfaceMiningAccumulatorMs: 0,
      surfaceMiningGoldRemainder: 0,
      surfaceMiningCrystalRemainder: 0,
      surfaceDevourProgressMs: 0,
      surfaceDevourCooldownUntilMs: 0,
      surfaceDevourCount: 0,
      surfaceDevourMovePenaltyRatio: 0,
      gourmetContactCooldowns: new Map(),
    };
    player.lastPosition = { x: player.x, y: player.y };
    player.laborLastNormalPosition = { x: player.x, y: player.y };
    this.players.set(id, player);
    return { ok: true, value: this.toPublicPlayer(player) };
  }

  setPlayerConnected(playerId: string, connected: boolean): void {
    const player = this.players.get(playerId);
    if (!player) {
      return;
    }
    player.connected = connected;
    player.aiControlled = !connected;
    if (connected) {
      player.input = { x: 0, y: 0 };
    }
  }

  removePlayer(playerId: string): boolean {
    if (this.phase !== "lobby") {
      return false;
    }
    return this.players.delete(playerId);
  }

  setReady(playerId: string, ready: boolean): ActionResult<boolean> {
    const player = this.players.get(playerId);
    if (!player || this.phase !== "lobby") {
      return { ok: false, error: "当前不能修改准备状态。" };
    }
    player.ready = ready;
    return { ok: true, value: ready };
  }

  start(difficulty: DifficultyId): ActionResult<true> {
    if (this.phase !== "lobby") {
      return { ok: false, error: "对局已经开始。" };
    }
    if (this.players.size === 0) {
      return { ok: false, error: "房间中没有玩家。" };
    }
    if ([...this.players.values()].some((player) => !player.ready)) {
      return { ok: false, error: "仍有玩家没有准备。" };
    }
    if (!DIFFICULTIES[difficulty]) {
      return { ok: false, error: "未知难度。" };
    }

    this.difficulty = difficulty;
    this.phase = "playing";
    const playerScale = 1 + (this.players.size - 1) * 0.3;
    this.core.maxHealth = Math.round(16_000 * playerScale);
    this.core.health = this.core.maxHealth;
    for (const segment of this.defenses) {
      if (segment.kind === "gate") {
        continue;
      }
      segment.maxHealth = Math.round(segment.maxHealth * playerScale);
      segment.health = segment.maxHealth;
    }
    this.events.push({
      type: "notice",
      message:
        `守卫营地 ${Math.ceil(this.durationMs / 60_000)} 分钟 · ` +
        `${DIFFICULTIES[difficulty].name}难度`,
    });
    return { ok: true, value: true };
  }

  setInput(playerId: string, sequence: number, x: number, y: number): void {
    const player = this.players.get(playerId);
    if (!player || !player.connected || sequence <= player.inputSequence) {
      return;
    }
    const length = Math.hypot(x, y);
    player.input = length > 1 ? { x: x / length, y: y / length } : { x, y };
    player.inputSequence = sequence;
  }

  tick(deltaMs: number): void {
    if (this.phase !== "playing") {
      return;
    }
    const safeDelta = Math.min(Math.max(deltaMs, 0), 100);
    this.elapsedMs += safeDelta;

    // Reaching zero is an immediate loss and cannot be undone by a repair
    // tick that happens later in the same server frame.
    if (this.core.health <= 0) {
      this.finish("defeat", "营地魔力核心被摧毁");
      return;
    }

    this.updateFinanceStates();
    this.updatePlayerRecovery(safeDelta);
    this.updatePlayers(safeDelta);
    this.updatePendingDelayedHits();
    this.updateSurfaceMining(safeDelta);
    this.updateAdvancedAbilities();
    this.updateHiddenEffects(safeDelta);
    this.updateAbilityEntities(safeDelta);
    this.updateObjectives(safeDelta);
    this.updateSpawning(safeDelta);
    this.updateDefenseTowers();
    this.updateCityBuildingsAndAllies(safeDelta);
    this.updateEnemies(safeDelta);
    this.applyPendingContactKnockbacks();
    this.resolveEnemySeparation();
    this.payWages();
    this.expireSignals();

    if (this.core.health <= 0) {
      this.finish("defeat", "营地魔力核心被摧毁");
    } else if (this.elapsedMs >= this.durationMs) {
      const completedObjectives = this.objectives.filter(
        (objective) => objective.status === "completed",
      ).length;
      if (this.finalBossDefeated && completedObjectives === this.objectives.length) {
        this.finish("victory", "守住怪物宝城、完成全部支线并击败远征王·奥雷恩");
      } else {
        const missing: string[] = [];
        if (!this.finalBossDefeated) missing.push("远征王·奥雷恩未被击败");
        if (completedObjectives < this.objectives.length) {
          missing.push(`胜利进度 ${completedObjectives}/${this.objectives.length}`);
        }
        this.finish("draw", `怪物宝城仍屹立，但${missing.join("、")}`);
      }
    }
  }

  requestOffer(playerId: string, slot: number): ActionResult<UpgradeOffer> {
    const player = this.players.get(playerId);
    const validation = this.validateSlotAction(player, slot);
    if (!validation.ok) {
      return validation;
    }
    if (player!.pendingOffer) {
      if (player!.pendingOffer!.slot === slot) {
        return { ok: true, value: player!.pendingOffer! };
      }
      return { ok: false, error: "请先处理当前候选。" };
    }

    const currentId = player!.modules[slot];
    const currentTier = currentId ? getModule(currentId).tier : 0;
    if (currentTier >= 5) {
      return {
        ok: false,
        error:
          currentTier === 5
            ? "五阶秘印需要进行固定升格。"
            : "该秘印已经升格并永久锁定。",
      };
    }
    const targetTier = (currentTier + 1) as ModuleTier;
    if (
      MODULES.filter(
        (module) =>
          module.tier === targetTier && module.weight > 0 && !module.hidden,
      ).length < 4
    ) {
      return {
        ok: false,
        error: `P4 的${targetTier}阶随机技能池尚未完成设计。`,
      };
    }
    const scrapCost = UPGRADE_GOLD_COSTS[targetTier];
    if (player!.scrap < scrapCost) {
      return { ok: false, error: `升阶需要 ${scrapCost} 金币。` };
    }
    const offer = this.generateOffer(
      player!,
      slot,
      targetTier,
      "upgrade",
      scrapCost,
      0,
      player!.upgradeRefreshSignatures.get(slot),
    );
    player!.upgradeRefreshSignatures.delete(slot);
    player!.scrap -= scrapCost;
    player!.pendingOffer = offer;
    return { ok: true, value: offer };
  }

  requestReforge(playerId: string, slot: number): ActionResult<UpgradeOffer> {
    const player = this.players.get(playerId);
    const validation = this.validateSlotAction(player, slot);
    if (!validation.ok) {
      return validation;
    }
    if (player!.pendingOffer) {
      return { ok: false, error: "请先处理当前候选。" };
    }
    const currentId = player!.modules[slot];
    if (!currentId) {
      return { ok: false, error: "空秘印槽不能重铸。" };
    }
    const tier = getModule(currentId).tier;
    if (tier === 6 || player!.lockedSlots[slot]) {
      return { ok: false, error: "升格秘印不能重铸。" };
    }
    if (
      MODULES.filter(
        (module) =>
          module.tier === tier && module.weight > 0 && !module.hidden,
      ).length < 4
    ) {
      return {
        ok: false,
        error: `P4 的${tier}阶随机技能池尚无可用重铸候选。`,
      };
    }
    const cost = REFORGE_CRYSTAL_COSTS[tier];
    if (player!.core < cost) {
      return { ok: false, error: `本阶重铸需要 ${cost} 枚奥术结晶。` };
    }
    player!.core -= cost;
    player!.rerolls += 1;
    const offer = this.generateOffer(player!, slot, tier, "reforge", 0, cost);
    player!.pendingOffer = offer;
    return { ok: true, value: offer };
  }

  rerollOffer(playerId: string, offerId: string): ActionResult<UpgradeOffer> {
    const player = this.players.get(playerId);
    const current = player?.pendingOffer;
    if (!player || !current || current.id !== offerId) {
      return { ok: false, error: "候选已失效。" };
    }
    if (player.core < current.rerollCost) {
      return {
        ok: false,
        error: `再次重铸需要 ${current.rerollCost} 枚奥术结晶。`,
      };
    }
    player.core -= current.rerollCost;
    player.rerolls += 1;
    const offer = this.generateOffer(
      player,
      current.slot,
      current.targetTier,
      current.mode,
      current.scrapCost,
      current.crystalCost,
    );
    player.pendingOffer = offer;
    return { ok: true, value: offer };
  }

  cancelOffer(playerId: string, offerId: string): ActionResult<true> {
    const player = this.players.get(playerId);
    if (!player?.pendingOffer || player.pendingOffer.id !== offerId) {
      return { ok: false, error: "候选已经失效。" };
    }
    const offer = player.pendingOffer;
    if (
      !offer.canAbandon ||
      offer.mode !== "upgrade" ||
      !offer.currentModuleId
    ) {
      return { ok: false, error: "本次候选不能放弃，只能选择或收起。" };
    }
    // Cancelling is a paid rejection of this exact three-choice set. Remember
    // its signature so the next upgrade request for the slot must roll a new
    // set immediately; it must not require an intermediate reforge.
    player.declinedUpgradeOffers.delete(offer.slot);
    player.upgradeRefreshSignatures.set(
      offer.slot,
      [...offer.candidates].sort().join(","),
    );
    player.pendingOffer = undefined;
    this.events.push({
      type: "notice",
      message:
        `${player.name} 放弃了本次升阶，${offer.scrapCost}金币不返还；` +
        "下一次升阶会重新随机候选。",
    });
    return { ok: true, value: true };
  }

  selectOffer(
    playerId: string,
    offerId: string,
    moduleId: string,
  ): ActionResult<PlayerPublicState> {
    const player = this.players.get(playerId);
    const offer = player?.pendingOffer;
    if (!player || !offer || offer.id !== offerId) {
      return { ok: false, error: "候选已失效。" };
    }
    if (!offer.candidates.includes(moduleId)) {
      return { ok: false, error: "该秘印不在本次候选中。" };
    }
    if (
      player.lockedSlots[offer.slot] ||
      player.modules[offer.slot] !== offer.currentModuleId
    ) {
      player.pendingOffer = undefined;
      return { ok: false, error: "候选对应的秘印槽已经变化，本次候选已失效。" };
    }
    const firstModule = player.modules.every((id) => id === null);
    if (offer.mode === "reforge") {
      const declined = player.declinedUpgradeOffers.get(offer.slot);
      if (declined) {
        player.upgradeRefreshSignatures.set(
          offer.slot,
          [...declined.candidates].sort().join(","),
        );
        player.declinedUpgradeOffers.delete(offer.slot);
      }
    } else {
      player.declinedUpgradeOffers.delete(offer.slot);
      player.upgradeRefreshSignatures.delete(offer.slot);
    }
    if (firstModule) {
      player.contribution.firstModuleAtMs = this.elapsedMs;
    }
    if (
      getModule(moduleId).tier === 5 &&
      player.contribution.firstTierFiveAtMs === null
    ) {
      player.contribution.firstTierFiveAtMs = this.elapsedMs;
    }
    player.pendingOffer = undefined;
    this.replaceModuleInSlot(player, offer.slot, moduleId, {
      suppressVaultPayout: false,
      lockSlot: false,
    });
    return { ok: true, value: this.toPublicPlayer(player) };
  }

  ascendAbility(playerId: string, slot: number): ActionResult<PlayerPublicState> {
    const player = this.players.get(playerId);
    const validation = this.validateSlotAction(player, slot);
    if (!validation.ok) {
      return validation;
    }
    if (player!.pendingOffer) {
      return { ok: false, error: "请先处理当前候选。" };
    }
    const currentId = player!.modules[slot];
    if (!currentId) {
      return { ok: false, error: "空秘印槽不能升格。" };
    }
    const current = getModule(currentId);
    const ascensionSource = current.evolutionTo
      ? current
      : current.stateFamily?.inheritsAscension
        ? getModule(current.stateFamily.canonicalModuleId)
        : current;
    const stagedAscension = current.stagedAscension;
    const ascensionTargetId =
      stagedAscension?.targetModuleId ?? ascensionSource.evolutionTo;
    if (
      !ascensionTargetId ||
      (!stagedAscension && current.tier !== 5)
    ) {
      return { ok: false, error: "只有五阶秘印可以进行固定升格。" };
    }
    if (
      player!.modules.some((ownedId) => {
        if (!ownedId) return false;
        const owned = getModule(ownedId);
        return (
          owned.id === ascensionTargetId ||
          owned.stateFamily?.canonicalModuleId === ascensionTargetId
        );
      })
    ) {
      return {
        ok: false,
        error: `每名玩家只能拥有一个「${getModule(ascensionTargetId).name}」。`,
      };
    }
    const ascensionCost = Math.round(
      ASCENSION_CRYSTAL_COST * (stagedAscension?.costMultiplier ?? 1),
    );
    if (player!.core < ascensionCost) {
      return {
        ok: false,
        error: `升格需要 ${ascensionCost} 枚奥术结晶。`,
      };
    }
    player!.core -= ascensionCost;
    player!.contribution.firstAscensionAtMs ??= this.elapsedMs;
    this.replaceModuleInSlot(player!, slot, ascensionTargetId, {
      suppressVaultPayout: true,
      lockSlot: true,
    });
    this.events.push({
      type: "notice",
      message: `${player!.name} 将「${current.name}」升格为「${getModule(ascensionTargetId).name}」`,
    });
    return { ok: true, value: this.toPublicPlayer(player!) };
  }

  investTalent(playerId: string, talentId: TalentId): ActionResult<PlayerPublicState> {
    if (talentId === "survey") {
      return { ok: false, error: "采矿勘测已封存（ARCHIVED_MINING）。" };
    }
    const player = this.players.get(playerId);
    const definition = TALENT_DEFINITIONS.find(
      (talent) => talent.id === talentId,
    );
    if (!player || !definition || this.phase !== "playing") {
      return { ok: false, error: "当前不能分配本源点。" };
    }
    const level = player.talents[talentId];
    if (level >= definition.maxLevel) {
      return { ok: false, error: `${definition.name}已经满级。` };
    }
    if (player.talentPoints <= 0) {
      return { ok: false, error: "没有可用的本源点。" };
    }
    player.talentPoints -= 1;
    player.talents[talentId] += 1;
    this.recomputePlayer(player);
    return { ok: true, value: this.toPublicPlayer(player) };
  }

  startMineTransit(
    playerId: string,
    caveId: MineCaveId,
    direction: "enter" | "exit",
  ): ActionResult<true> {
    void playerId;
    void caveId;
    void direction;
    return { ok: false, error: "采矿玩法已封存（ARCHIVED_MINING）。" };
  }

  setMineWork(
    playerId: string,
    targetKind: "rubble" | "deposit",
    targetId: number,
    active: boolean,
  ): ActionResult<true> {
    void playerId;
    void targetKind;
    void targetId;
    void active;
    return { ok: false, error: "采矿玩法已封存（ARCHIVED_MINING）。" };
  }

  pickupMineCargo(playerId: string, cargoId: number): ActionResult<true> {
    void playerId;
    void cargoId;
    return { ok: false, error: "采矿玩法已封存（ARCHIVED_MINING）。" };
  }

  surveyMine(
    playerId: string,
    caveId?: MineCaveId,
  ): ActionResult<boolean> {
    void playerId;
    void caveId;
    return { ok: false, error: "采矿玩法已封存（ARCHIVED_MINING）。" };
  }

  excavate(playerId: string, nodeId: number): ActionResult<ArchaeologyNodeState> {
    void playerId;
    void nodeId;
    return {
      ok: false,
      error: "旧考古玩法已封存（ARCHIVED_ARCHAEOLOGY）。",
    };
  }

  donateCore(sourceId: string, targetId: string, amount: number): ActionResult<true> {
    return this.aidResource(sourceId, targetId, "crystal", amount);
  }

  aidResource(
    sourceId: string,
    targetId: string,
    resource: "gold" | "crystal",
    amount: number,
  ): ActionResult<true> {
    const source = this.players.get(sourceId);
    const target = this.players.get(targetId);
    const safeAmount = Math.floor(amount);
    if (!source || !target || source === target) {
      return { ok: false, error: "无效的援助目标。" };
    }
    const available = resource === "gold" ? source?.scrap ?? 0 : source?.core ?? 0;
    if (safeAmount <= 0 || available < safeAmount) {
      return {
        ok: false,
        error: resource === "gold" ? "已入库金币不足。" : "已入库结晶不足。",
      };
    }
    if (resource === "gold") {
      source.scrap -= safeAmount;
      target.scrap += safeAmount;
      source.contribution.goldDonated += safeAmount;
      target.contribution.goldReceived += safeAmount;
    } else {
      source.core -= safeAmount;
      target.core += safeAmount;
      source.contribution.coreDonated += safeAmount;
      target.contribution.coreReceived += safeAmount;
    }
    this.events.push({
      type: "notice",
      message: `${source.name} 向 ${target.name} 援助了 ${safeAmount} ${resource === "gold" ? "金币" : "枚结晶"}`,
    });
    return { ok: true, value: true };
  }

  investTeamTech(playerId: string, techId: TeamTechId): ActionResult<TeamTechState> {
    const player = this.players.get(playerId);
    const definition = TEAM_TECH.find((tech) => tech.id === techId);
    if (!player || this.phase !== "playing") {
      return { ok: false, error: "当前不能激活远征祝福。" };
    }
    if (!definition) {
      return { ok: false, error: "未知远征祝福。" };
    }
    const currentLevel = this.teamTechLevels[definition.id];
    const nextLevel = definition.levels[currentLevel];
    if (!nextLevel) {
      return { ok: false, error: `${definition.name}已经满级。` };
    }
    if (player.scrap < nextLevel.cost) {
      return {
        ok: false,
        error: `${definition.name}升级需要 ${nextLevel.cost} 金币。`,
      };
    }

    player.scrap -= nextLevel.cost;
    player.contribution.teamTechInvested += nextLevel.cost;
    this.teamTechLevels[definition.id] = currentLevel + 1;
    this.applyTeamTechLevel(definition.id);
    this.events.push({
      type: "notice",
      message:
        `${player.name} 投入 ${nextLevel.cost} 金币，` +
        `${definition.name}升至 ${currentLevel + 1} 级`,
    });
    return {
      ok: true,
      value: this.getTeamTechStates().find((tech) => tech.id === definition.id)!,
    };
  }

  createTeamSignal(
    playerId: string,
    kind: TeamSignalKind,
    laneId: LaneId,
  ): ActionResult<TeamSignalState> {
    const player = this.players.get(playerId);
    const lane = LANE_CONFIGS.find((candidate) => candidate.id === laneId);
    if (!player || !lane || this.phase !== "playing") {
      return { ok: false, error: "当前不能发送该标记。" };
    }
    if (!["danger", "assist", "objective"].includes(kind)) {
      return { ok: false, error: "未知标记类型。" };
    }
    if (this.elapsedMs < player.nextSignalAtMs) {
      return { ok: false, error: "标记发送过于频繁。" };
    }
    player.nextSignalAtMs = this.elapsedMs + 2_000;
    player.contribution.signalsSent += 1;
    const signal: TeamSignalState = {
      id: this.signalSequence++,
      playerId,
      playerName: player.name,
      kind,
      laneId,
      x: lane.x,
      y: lane.y,
      createdAtMs: this.elapsedMs,
      expiresAtMs: this.elapsedMs + 8_000,
    };
    this.signals.push(signal);
    this.events.push({ type: "teamSignal", signal: { ...signal } });
    return { ok: true, value: signal };
  }

  getPendingOffer(playerId: string): UpgradeOffer | undefined {
    return this.players.get(playerId)?.pendingOffer;
  }

  getSnapshot(
    roomCode: string,
    hostId: string,
    serverTime: number,
    includeEnemies: boolean,
    metrics: PerformanceMetrics = defaultMetrics,
    options: SnapshotOptions = {},
  ): PublicRoomState {
    const includeWorldState = options.includeWorldState ?? true;
    const includeDefenses = options.includeDefenses ?? includeWorldState;
    return {
      protocolVersion: PROTOCOL_VERSION,
      contentVersion: CONTENT_VERSION,
      runSeed: this.runSeed,
      roomCode,
      phase: this.phase,
      hostId,
      difficulty: this.difficulty,
      serverTime,
      elapsedMs: this.elapsedMs,
      durationMs: this.durationMs,
      core: { ...this.core },
      players: includeWorldState
        ? [...this.players.values()].map((player) => this.toPublicPlayer(player))
        : [],
      playerMotions: options.includeMotionAnchors
        ? [...this.players.values()].map((player) => [
            player.id,
            Math.round(player.x * 10) / 10,
            Math.round(player.y * 10) / 10,
            Math.max(0, Math.round(player.health)),
            Math.max(0, Math.round(player.mana)),
            player.status,
            player.inputSequence,
            player.connected,
            player.aiControlled,
            Math.round(player.reviveProgressMs),
            Math.round(player.respawnAtMs),
            player.spaceId,
          ])
        : undefined,
      enemies: includeEnemies
        ? [...this.enemies.values()].map((enemy) => this.toWireEnemy(enemy))
        : undefined,
      enemyAnchors: options.includeEnemyAnchors
        ? [...this.enemies.values()].map((enemy) => [
            enemy.id,
            Math.round(enemy.x),
            Math.round(enemy.y),
            Math.max(0, Math.round(enemy.health)),
            Math.round(enemy.velocityX * 10) / 10,
            Math.round(enemy.velocityY * 10) / 10,
          ])
        : undefined,
      abilityEntities: includeWorldState
        ? [...this.abilityEntities.values()].map((entity) =>
            this.toWireAbilityEntity(entity),
          )
        : undefined,
      abilityEntityAnchors: options.includeMotionAnchors
        ? [...this.abilityEntities.values()].map((entity) => [
            entity.id,
            Math.round(entity.x),
            Math.round(entity.y),
            Number.isFinite(entity.health)
              ? Math.max(0, Math.round(entity.health))
              : 1,
            entity.spaceId,
          ])
        : undefined,
      defenses: includeDefenses
        ? this.defenses.map((segment) => ({
            id: segment.id,
            ring: segment.ring,
            kind: segment.kind,
            x: segment.x,
            y: segment.y,
            width: segment.width,
            height: segment.height,
            health: Math.max(0, Math.round(segment.health)),
            maxHealth: Math.round(segment.maxHealth),
            armor: segment.armor,
            hasTower: segment.hasTower,
            destroyed: segment.destroyed,
          }))
        : [],
      defenseAnchors: options.includeDefenseAnchors
        ? this.defenses.map((segment) => [
            segment.id,
            Math.max(0, Math.round(segment.health)),
            Math.round(segment.maxHealth),
            segment.armor,
            segment.destroyed,
          ])
        : undefined,
      cityBuildings: includeDefenses
        ? [...this.cityBuildings.values()].map((building) =>
            this.toWireCityBuilding(building),
          )
        : [],
      cityBuildingAnchors: options.includeDefenseAnchors
        ? [...this.cityBuildings.values()].map((building) => [
            building.id,
            Math.max(0, Math.round(building.health)),
            building.destroyed,
            building.activeUnitCount,
          ])
        : undefined,
      cityAllies: includeWorldState
        ? [...this.cityAllies.values()].map((ally) => this.toWireCityAlly(ally))
        : undefined,
      cityAllyAnchors: options.includeMotionAnchors
        ? [...this.cityAllies.values()].map((ally) => [
            ally.id,
            Math.round(ally.x),
            Math.round(ally.y),
            Math.max(0, Math.round(ally.health)),
          ])
        : undefined,
      defenseEconomy: {
        towerGoldPool: Math.round(this.towerGoldPool),
        towerGoldPaid: Math.round(this.towerGoldPaid),
      },
      surfaceVeins: this.getVisibleSurfaceVeins(options.viewerPlayerId),
      archaeologyNodes: [],
      lanes: this.getLaneStates(),
      objectives: this.objectives.map((objective) => ({ ...objective })),
      victoryProgress: {
        completedObjectives: this.objectives.filter(
          (objective) => objective.status === "completed",
        ).length,
        requiredObjectives: this.objectives.length,
        finalBossDefeated: this.finalBossDefeated,
      },
      teamTech: this.getTeamTechStates(),
      signals: this.signals.map((signal) => ({ ...signal })),
      metrics: {
        ...metrics,
        enemyCount: this.enemies.size,
        abilityEntityCount: this.abilityEntities.size + this.cityAllies.size,
      },
      resultReason: this.resultReason,
    };
  }

  private getVisibleSurfaceVeins(
    viewerPlayerId: string | undefined,
  ): SurfaceVeinViewState[] {
    if (!viewerPlayerId || !this.players.has(viewerPlayerId)) {
      return [];
    }
    const detectors = [...this.players.values()].filter(
      (player) =>
        player.connected &&
        player.status !== "downed" &&
        player.spaceId === "surface" &&
        player.stats.revealRadius > 0,
    );
    return [...this.surfaceVeins.values()]
      .filter((vein) =>
        detectors.some(
          (player) =>
            Math.hypot(player.x - vein.x, player.y - vein.y) <=
            player.stats.revealRadius + vein.radius,
        ) ||
        [...this.abilityEntities.values()].some((entity) => {
          if (
            baseAbilityId(entity.abilityId) !== "night_watch_imp" ||
            entity.health <= 0
          ) {
            return false;
          }
          const owner = this.players.get(entity.ownerId);
          const revealRadius = owner?.stats.revealRadius ?? 0;
          return (
            revealRadius > 0 &&
            Math.hypot(entity.x - vein.x, entity.y - vein.y) <=
              revealRadius + vein.radius
          );
        }),
      )
      .sort((left, right) => left.id - right.id)
      .map((vein) => ({ ...vein }));
  }

  private updateSurfaceMining(deltaMs: number): void {
    const players = [...this.players.values()].sort((left, right) =>
      left.id.localeCompare(right.id),
    );
    const ordinaryMining: Array<{
      player: SimPlayer;
      vein: SurfaceVeinState;
    }> = [];
    const completedDevours: Array<{
      player: SimPlayer;
      veinId: number;
      sourceSlot: number;
      source: ModuleDefinition;
    }> = [];

    for (const player of players) {
      if (player.status === "downed" || player.spaceId !== "surface") {
        this.resetSurfaceMiningTarget(player);
        continue;
      }
      const vein = [...this.surfaceVeins.values()]
        .sort((left, right) => left.id - right.id)
        .find(
          (candidate) =>
            Math.hypot(player.x - candidate.x, player.y - candidate.y) <=
            candidate.radius,
        );
      if (!vein) {
        this.resetSurfaceMiningTarget(player);
        continue;
      }
      if (player.surfaceMiningVeinId !== vein.id) {
        player.surfaceMiningVeinId = vein.id;
        player.surfaceMiningAccumulatorMs = 0;
        player.surfaceDevourVeinId = undefined;
        player.surfaceDevourSourceSlot = undefined;
        player.surfaceDevourProgressMs = 0;
      }

      const devourSource = this.getSurfaceDevourSource(player);
      if (
        devourSource &&
        this.elapsedMs >= player.surfaceDevourCooldownUntilMs
      ) {
        if (
          player.surfaceDevourVeinId !== vein.id ||
          player.surfaceDevourSourceSlot !== devourSource.slot
        ) {
          player.surfaceDevourVeinId = vein.id;
          player.surfaceDevourSourceSlot = devourSource.slot;
          player.surfaceDevourProgressMs = 0;
        }
        player.surfaceDevourProgressMs += deltaMs;
        if (
          player.surfaceDevourProgressMs >=
          devourSource.module.surfaceMining!.devour!.channelMs
        ) {
          completedDevours.push({
            player,
            veinId: vein.id,
            sourceSlot: devourSource.slot,
            source: devourSource.module,
          });
        }
        // Big Bag deliberately replaces ordinary auto-mining while channeling.
        // During its cooldown, and after evolving, ordinary mining resumes.
        continue;
      }
      player.surfaceDevourVeinId = undefined;
      player.surfaceDevourSourceSlot = undefined;
      player.surfaceDevourProgressMs = 0;
      ordinaryMining.push({ player, vein });
    }

    // The array inherits the stable player-id ordering above. If several players
    // finish in one tick, only the first still sees a non-empty vein.
    for (const candidate of completedDevours) {
      this.tryDevourSurfaceVein(candidate);
    }

    for (const { player, vein } of ordinaryMining) {
      if (!this.surfaceVeins.has(vein.id)) {
        this.resetSurfaceMiningTarget(player);
        continue;
      }
      player.surfaceMiningAccumulatorMs += deltaMs;
      while (this.surfaceVeins.has(vein.id)) {
        const poweredMining = this.getPoweredSurfaceMiningSpeed(player);
        const speedBonus =
          player.stats.surfaceMiningSpeedBonus +
          poweredMining.speedBonus;
        const intervalMs =
          SURFACE_MINING_CONFIG.settlementIntervalMs /
          Math.max(0.001, 1 + speedBonus);
        if (player.surfaceMiningAccumulatorMs + 0.000_001 < intervalMs) {
          break;
        }
        player.surfaceMiningAccumulatorMs -= intervalMs;
        const settled = this.settleSurfaceMining(player, vein);
        if (!settled) {
          break;
        }
        if (poweredMining.manaCost > 0) {
          player.mana = Math.max(
            0,
            player.mana - poweredMining.manaCost,
          );
        }
      }
    }
  }

  private resetSurfaceMiningTarget(player: SimPlayer): void {
    player.surfaceMiningVeinId = undefined;
    player.surfaceMiningAccumulatorMs = 0;
    player.surfaceDevourVeinId = undefined;
    player.surfaceDevourSourceSlot = undefined;
    player.surfaceDevourProgressMs = 0;
  }

  private getPoweredSurfaceMiningSpeed(player: SimPlayer): {
    speedBonus: number;
    manaCost: number;
  } {
    const minerSetActive = player.synergies.includes("miners_set");
    let remainingMana = player.mana;
    let speedBonus = 0;
    let manaCost = 0;
    for (const moduleId of player.modules) {
      if (!moduleId) {
        continue;
      }
      const module = getModule(moduleId);
      const powered = module.surfaceMining?.manaPoweredSpeed;
      if (!powered || remainingMana < powered.manaCostPerSettlement) {
        continue;
      }
      remainingMana -= powered.manaCostPerSettlement;
      manaCost += powered.manaCostPerSettlement;
      speedBonus += powered.speedBonus * (minerSetActive ? 2 : 1);
    }
    return { speedBonus, manaCost };
  }

  private getSurfaceDevourSource(
    player: SimPlayer,
  ): { slot: number; module: ModuleDefinition } | undefined {
    for (let slot = 0; slot < player.modules.length; slot += 1) {
      const moduleId = player.modules[slot];
      if (!moduleId) {
        continue;
      }
      const module = getModule(moduleId);
      if (module.surfaceMining?.devour) {
        return { slot, module };
      }
    }
    return undefined;
  }

  private tryDevourSurfaceVein(candidate: {
    player: SimPlayer;
    veinId: number;
    sourceSlot: number;
    source: ModuleDefinition;
  }): boolean {
    const { player, veinId, sourceSlot, source } = candidate;
    const devour = source.surfaceMining?.devour;
    const vein = this.surfaceVeins.get(veinId);
    if (
      !devour ||
      player.status === "downed" ||
      player.modules[sourceSlot] !== source.id ||
      player.surfaceDevourVeinId !== veinId ||
      player.surfaceDevourSourceSlot !== sourceSlot ||
      this.elapsedMs < player.surfaceDevourCooldownUntilMs ||
      player.surfaceDevourProgressMs < devour.channelMs ||
      !vein ||
      vein.remaining <= 0 ||
      Math.hypot(player.x - vein.x, player.y - vein.y) > vein.radius
    ) {
      player.surfaceDevourVeinId = undefined;
      player.surfaceDevourSourceSlot = undefined;
      player.surfaceDevourProgressMs = 0;
      return false;
    }

    const remaining = vein.remaining;
    const totalGold = Math.max(0, Math.floor(remaining * devour.goldPerRemaining));
    this.surfaceVeins.delete(vein.id);
    player.scrap += totalGold;
    player.contribution.scrapEarned += totalGold;
    player.contribution.surfaceMiningGoldEarned += totalGold;
    player.surfaceDevourCount += 1;
    player.surfaceDevourMovePenaltyRatio += devour.moveSpeedPenaltyPerSuccess;
    player.surfaceDevourCooldownUntilMs = this.elapsedMs + devour.cooldownMs;
    player.surfaceMiningVeinId = undefined;
    player.surfaceMiningAccumulatorMs = 0;
    player.surfaceDevourVeinId = undefined;
    player.surfaceDevourSourceSlot = undefined;
    player.surfaceDevourProgressMs = 0;

    let evolved = false;
    const evolution = source.conditionalEvolution;
    if (
      evolution?.kind === "surface_mining_devour_count" &&
      player.surfaceDevourCount >= evolution.requiredCount &&
      player.modules[sourceSlot] === source.id
    ) {
      const target = getModule(evolution.targetModuleId);
      this.replaceModuleInSlot(player, sourceSlot, target.id, {
        suppressVaultPayout: true,
        lockSlot: true,
      });
      evolved = true;
    }

    this.events.push({
      type: "surfaceMiningDevour",
      playerId: player.id,
      totalGold,
      devourCount: player.surfaceDevourCount,
      evolved,
    });
    if (evolved) {
      this.events.push({
        type: "notice",
        message: `${player.name} 的「${source.name}」完成条件进阶，化为「${getModule(evolution!.targetModuleId).name}」。`,
      });
    }
    return true;
  }

  private settleSurfaceMining(player: SimPlayer, vein: SurfaceVeinState): boolean {
    const baseAmount = SURFACE_MINING_CONFIG.baseAmountPerSettlement;
    const actualBaseGold = Math.min(baseAmount, Math.max(0, vein.remaining));
    if (actualBaseGold <= 0) {
      this.surfaceVeins.delete(vein.id);
      return false;
    }
    vein.remaining = Math.max(0, vein.remaining - actualBaseGold);
    const completionRatio = actualBaseGold / baseAmount;
    const exactBonusGold = Math.max(
      0,
      player.stats.surfaceMiningExtraGoldFlat * completionRatio +
        actualBaseGold *
          (player.stats.surfaceMiningExtraGoldMultiplier +
            player.stats.surfaceMiningGoldYieldBonus) +
        player.surfaceMiningGoldRemainder,
    );
    const bonusGold = Math.floor(exactBonusGold + 1e-9);
    player.surfaceMiningGoldRemainder = exactBonusGold - bonusGold;
    const ordinaryGold = actualBaseGold + bonusGold;
    const replicationChance = Math.min(
      1,
      player.modules.reduce((sum, moduleId) => {
        if (!moduleId) return sum;
        return sum + (getModule(moduleId).surfaceMining?.replicationChance ?? 0);
      }, 0) * (player.synergies.includes("miners_set") ? 2 : 1),
    );
    const replicatedGold =
      replicationChance > 0 && this.random.next() < replicationChance
        ? ordinaryGold
        : 0;
    const totalGold = ordinaryGold + replicatedGold;
    const exactCrystals = Math.max(
      0,
      totalGold * player.stats.surfaceMiningCrystalYieldRatio +
        player.surfaceMiningCrystalRemainder,
    );
    const crystals = Math.floor(exactCrystals + 1e-9);
    player.surfaceMiningCrystalRemainder = exactCrystals - crystals;
    player.scrap += totalGold;
    player.core += crystals;
    player.contribution.scrapEarned += totalGold;
    player.contribution.surfaceMiningGoldEarned += totalGold;
    const depleted = vein.remaining <= 0;
    let exhaustionGold = 0;
    if (depleted) {
      const exhaustion = this.getActiveSynergyMechanic(
        player,
        "mining_exhaustion_bonus",
      );
      if (exhaustion) {
        exhaustionGold = Math.floor(
          vein.maxAmount * exhaustion.goldRatioOfInitialReserve,
        );
        player.scrap += exhaustionGold;
        player.contribution.scrapEarned += exhaustionGold;
        player.contribution.surfaceMiningGoldEarned += exhaustionGold;
      }
      this.surfaceVeins.delete(vein.id);
      player.surfaceMiningVeinId = undefined;
      player.surfaceMiningAccumulatorMs = 0;
    }
    this.events.push({
      type: "surfaceMining",
      playerId: player.id,
      baseGold: actualBaseGold,
      bonusGold: bonusGold + replicatedGold + exhaustionGold,
      totalGold: totalGold + exhaustionGold,
      crystals,
      depleted,
    });
    return true;
  }

  drainEvents(): SimulationEvent[] {
    return this.events.splice(0, this.events.length);
  }

  injectEnemies(count: number, definitionId = "moss_crawler"): void {
    for (let index = 0; index < count; index += 1) {
      const angle = (index / Math.max(1, count)) * Math.PI * 2;
      const distance = 420 + (index % 10) * 24;
      this.spawnEnemy(
        definitionId,
        this.core.x + Math.cos(angle) * distance,
        this.core.y + Math.sin(angle) * distance,
      );
    }
  }

  private validateSlotAction(
    player: SimPlayer | undefined,
    slot: number,
  ): ActionResult<true> {
    if (!player || this.phase !== "playing") {
      return { ok: false, error: "当前不能操作秘印。" };
    }
    if (!Number.isInteger(slot) || slot < 0 || slot >= player.modules.length) {
      return { ok: false, error: "无效秘印槽。" };
    }
    return { ok: true, value: true };
  }

  private updatePlayerRecovery(deltaMs: number): void {
    for (const player of this.players.values()) {
      if (player.status === "downed") {
        if (this.elapsedMs >= player.respawnAtMs) {
          player.status = "active";
          player.health = player.stats.maxHealth;
          player.mana = player.stats.manaMax;
          player.x = this.core.x;
          player.y = this.core.y + 185;
          player.respawnAtMs = 0;
          player.reviveProgressMs = 0;
          this.events.push({
            type: "notice",
            message: `${player.name} 已在营地旁重新凝聚`,
          });
        }
      } else if (
        player.status === "lich" &&
        !this.isPermanentLich(player)
      ) {
        const rescuers = [...this.players.values()].filter(
          (candidate) =>
            candidate.id !== player.id &&
            (candidate.status === "active" ||
              (candidate.status === "lich" &&
                this.isPermanentLich(candidate))) &&
            Math.hypot(candidate.x - player.x, candidate.y - player.y) <=
              REVIVE_RADIUS,
        );
        if (rescuers.length > 0) {
          player.reviveProgressMs += deltaMs * Math.min(2, rescuers.length);
        } else {
          player.reviveProgressMs = Math.max(
            0,
            player.reviveProgressMs - deltaMs * 0.25,
          );
        }
        if (player.reviveProgressMs >= player.reviveRequiredMs) {
          player.status = "active";
          this.recomputePlayer(player);
          player.health = Math.max(
            1,
            Math.round(player.stats.maxHealth * 0.55),
          );
          player.reviveProgressMs = 0;
          player.respawnAtMs = 0;
          rescuers[0].contribution.revives += 1;
          this.events.push({
            type: "notice",
            message: `${rescuers[0].name} 令 ${player.name} 从临时巫妖形态复生`,
          });
        } else if (this.elapsedMs >= player.respawnAtMs) {
          player.status = "active";
          this.recomputePlayer(player);
          player.health = player.stats.maxHealth;
          player.mana = player.stats.manaMax;
          player.x = this.core.x;
          player.y = this.core.y + 185;
          player.reviveProgressMs = 0;
          player.respawnAtMs = 0;
          this.events.push({
            type: "notice",
            message: `${player.name} 结束临时巫妖形态并在营地复活`,
          });
        }
      }
    }
  }

  private updatePlayers(deltaMs: number): void {
    for (const [index, player] of [...this.players.values()].entries()) {
      if (player.status === "downed") {
        this.resetInterruptedLabor(player, true);
        const movement = player.aiControlled ? { x: 0, y: 0 } : player.input;
        const moveSpeed = this.getPlayerMoveSpeed(player);
        this.movePlayerWithDefenseCollision(
          player,
          movement.x * moveSpeed * (deltaMs / 1_000),
          movement.y * moveSpeed * (deltaMs / 1_000),
        );
        player.laborLastNormalPosition = { x: player.x, y: player.y };
        player.lastPosition = { x: player.x, y: player.y };
        continue;
      }
      const forcedBeforeMovement =
        Math.hypot(
          player.x - player.laborLastNormalPosition.x,
          player.y - player.laborLastNormalPosition.y,
        ) > 0.001;
      if (forcedBeforeMovement) {
        this.resetInterruptedLabor(player, false);
      }
      const movementStart = { x: player.x, y: player.y };
      const contactSource = this.getSurfaceContactSource(player);
      const hiddenAura = this.getHiddenAllyAuraAt(player.x, player.y);
      const movement = player.aiControlled ? this.getAiMovement(player, index) : player.input;
      if (player.status !== "lich") {
        this.updateSynergyRuntime(player, movement, deltaMs);
      }
      const moveSpeed =
        this.getPlayerMoveSpeed(player) *
        (contactSource ? 1 : hiddenAura.moveSpeedMultiplier);
      this.movePlayerWithDefenseCollision(
        player,
        movement.x * moveSpeed * (deltaMs / 1_000),
        movement.y * moveSpeed * (deltaMs / 1_000),
      );

      const moved = Math.hypot(player.x - player.lastPosition.x, player.y - player.lastPosition.y);
      const actualTickMovement = Math.hypot(
        player.x - movementStart.x,
        player.y - movementStart.y,
      );
      const normallyMoved =
        actualTickMovement > 0.000_1 && Math.hypot(movement.x, movement.y) > 0.000_1;
      this.updateLaborBehavior(
        player,
        deltaMs,
        actualTickMovement,
        normallyMoved,
        forcedBeforeMovement,
      );
      player.laborLastNormalPosition = { x: player.x, y: player.y };
      if (
        contactSource &&
        actualTickMovement > 0.000_1 &&
        (player.status === "active" || player.status === "lich")
      ) {
        this.applySurfaceContactAttack(
          player,
          contactSource,
          movementStart,
          { x: player.x, y: player.y },
        );
      }
      if (moved > 0) {
        this.updateMovementDamageFields(player, moved);
        this.triggerGrowth(player, "move_distance", moved);
        this.triggerLabor(player, "move_distance", moved);
        player.lastPosition = { x: player.x, y: player.y };
      }

      player.mana = Math.min(
        player.stats.manaMax,
        player.mana +
          this.getPlayerManaRegen(player) * (deltaMs / 1_000),
      );
      const passiveHeal =
        this.getPlayerHealthRegen(player) + player.level * 0.04;
      for (const [sourceId, recovery] of player.activeDeathRecoveries) {
        const tickStartedAtMs = this.elapsedMs - deltaMs;
        const effectiveMs = Math.max(
          0,
          Math.min(deltaMs, recovery.endsAtMs - tickStartedAtMs),
        );
        const scheduledHealing = Math.min(
          recovery.remainingHealing,
          recovery.healingPerMs * effectiveMs,
        );
        const before = player.health;
        player.health = Math.min(
          player.stats.maxHealth,
          player.health + scheduledHealing,
        );
        player.contribution.healingDone += player.health - before;
        recovery.remainingHealing -= scheduledHealing;
        if (
          this.elapsedMs >= recovery.endsAtMs ||
          recovery.remainingHealing <= 0.001
        ) {
          player.activeDeathRecoveries.delete(sourceId);
        }
      }
      player.health = Math.min(
        player.stats.maxHealth,
        player.health + passiveHeal * (deltaMs / 1_000),
      );

      const distanceToCore = Math.hypot(player.x - this.core.x, player.y - this.core.y);
      const repairPerSecond =
        player.status === "lich" ? 0 : player.stats.repairPerSecond;
      if (repairPerSecond > 0) {
        let repaired = 0;
        if (distanceToCore < 320 && this.core.health < this.core.maxHealth) {
          const previous = this.core.health;
          this.core.health = Math.min(
            this.core.maxHealth,
            this.core.health + repairPerSecond * (deltaMs / 1_000),
          );
          repaired = this.core.health - previous;
        } else {
          const building = [...this.cityBuildings.values()]
            .filter(
              (candidate) =>
                !candidate.destroyed &&
                candidate.health > 0 &&
                candidate.health < candidate.maxHealth &&
                Math.hypot(candidate.x - player.x, candidate.y - player.y) <=
                  candidate.radius + 220,
            )
            .sort(
              (a, b) =>
                distanceSquared(a.x, a.y, player.x, player.y) -
                  distanceSquared(b.x, b.y, player.x, player.y) ||
                a.id.localeCompare(b.id),
            )[0];
          if (building) {
            const previous = building.health;
            building.health = Math.min(
              building.maxHealth,
              building.health + repairPerSecond * (deltaMs / 1_000),
            );
            repaired = building.health - previous;
          }
        }
        player.contribution.repairDone += repaired;
        if (repaired > 0) {
          this.triggerGrowth(player, "repair", repaired);
          this.triggerLabor(player, "repair", repaired);
        }
      }

      if (player.status !== "active") {
        continue;
      }
      if (
        this.elapsedMs >= player.nextAttackAtMs &&
        this.elapsedMs >= player.basicAttackJammedUntilMs
      ) {
        const target = this.pickTarget(
          player.x,
          player.y,
          this.getPlayerAttackRange(player),
          "random",
        );
        if (target) {
          this.performBaseAttack(player, target);
          const teamInterval =
            1 /
            (1 + this.teamTechLevels.haste_chant * 0.02) /
            hiddenAura.attackSpeedMultiplier;
          player.nextAttackAtMs =
            this.elapsedMs +
            Math.max(120, this.getPlayerAttackIntervalMs(player) * teamInterval);
        }
      }
      this.updateAbilityAttacks(player);
      this.updateEconomyAbilities(player);
      this.updateHealingAbilities(player);
      this.updateSummoningAbilities(player);
      this.triggerGrowth(player, "second", deltaMs / 1_000);
    }
  }

  private updateSynergyRuntime(
    player: SimPlayer,
    movement: Vec2,
    deltaMs: number,
  ): void {
    const moving = Math.hypot(movement.x, movement.y) > 0.001;
    const stackSource = this.getStationaryStackMechanic(player);
    const stackKey = "perpetual_core";
    if (!stackSource) {
      player.synergyStackExpirations.delete(stackKey);
      player.synergyNextStackAtMs.delete(stackKey);
    } else {
      let expirations = (
        player.synergyStackExpirations.get(stackKey) ?? []
      ).filter((expiresAtMs) => expiresAtMs > this.elapsedMs);
      if (moving) {
        player.synergyNextStackAtMs.delete(stackKey);
      } else {
        let nextAt = player.synergyNextStackAtMs.get(stackKey);
        if (nextAt === undefined) {
          nextAt =
            this.elapsedMs - deltaMs + stackSource.initialDelayMs;
        }
        while (this.elapsedMs >= nextAt) {
          const expiresAtMs = this.elapsedMs + stackSource.stackDurationMs;
          if (expirations.length < stackSource.maxStacks) {
            expirations.push(expiresAtMs);
          }
          if (stackSource.refreshAllOnStack) {
            expirations = expirations.map(() => expiresAtMs);
          }
          nextAt += stackSource.stackIntervalMs;
        }
        player.synergyNextStackAtMs.set(stackKey, nextAt);
      }
      player.synergyStackExpirations.set(stackKey, expirations);
    }

    this.updateDayNightCycle(player);
    this.updateGreedParadise(player);
    this.updateSlackingOff(player, moving);
    this.updateTierFiveSixSynergyRuntime(player, deltaMs);

    const turret = this.getTurretMechanic(player);
    if (!turret) {
      player.turretMoveRatio = 1;
      player.turretMovementAccumulatorMs = 0;
      player.turretLastMoving = null;
      return;
    }
    const wasDeployed = this.isTurretDeployed(player);
    if (player.turretLastMoving !== moving) {
      player.turretLastMoving = moving;
      player.turretMovementAccumulatorMs = 0;
    }
    player.turretMovementAccumulatorMs += deltaMs;
    while (player.turretMovementAccumulatorMs >= turret.movementStepMs) {
      player.turretMovementAccumulatorMs -= turret.movementStepMs;
      player.turretMoveRatio = moving
        ? Math.min(
            turret.maxBaseMoveSpeedRatio,
            player.turretMoveRatio + turret.movingBaseSpeedGainRatio,
          )
        : Math.max(
            0,
            player.turretMoveRatio - turret.stationaryBaseSpeedLossRatio,
          );
    }
    if (wasDeployed !== this.isTurretDeployed(player)) {
      this.recomputePlayer(player);
    }
  }

  private updateTierFiveSixSynergyRuntime(
    player: SimPlayer,
    deltaMs: number,
  ): void {
    let substitutionExpired = false;
    for (const [key, substitution] of player.synergySubstitutions) {
      if (substitution.expiresAtMs <= this.elapsedMs) {
        player.synergySubstitutions.delete(key);
        substitutionExpired = true;
      }
    }
    if (substitutionExpired) {
      this.recomputePlayer(player);
    }
    if (player.status !== "active") return;
    const regeneration = this.getActiveSynergyMechanic(
      player,
      "max_health_regeneration",
    );
    if (regeneration) {
      const before = player.health;
      player.health = Math.min(
        player.stats.maxHealth,
        player.health +
          player.stats.maxHealth * regeneration.ratioPerSecond * (deltaMs / 1_000),
      );
      player.contribution.healingDone += player.health - before;
    }

    const wealth = this.getActiveSynergyMechanic(player, "greed_and_wealth");
    if (!wealth) return;
    const tickKey = "synergy:greed_and_wealth:area";
    const nextTickAt = player.mechanicCooldowns.get(tickKey) ?? 0;
    if (this.elapsedMs >= nextTickAt) {
      player.mechanicCooldowns.set(
        tickKey,
        this.elapsedMs + wealth.tickIntervalMs,
      );
      const phase = (this.elapsedMs % wealth.cycleMs) / wealth.cycleMs;
      const travel = phase <= 0.5 ? phase * 2 : (1 - phase) * 2;
      const radius =
        wealth.minRadius + (wealth.maxRadius - wealth.minRadius) * travel;
      const radiusRatio =
        (radius - wealth.minRadius) /
        Math.max(1, wealth.maxRadius - wealth.minRadius);
      const white =
        player.health / Math.max(1, player.stats.maxHealth) <=
        wealth.whiteHealthThresholdInclusive;
      const minimumDamage = white
        ? wealth.whiteMinRadiusDamage
        : wealth.blackMinRadiusDamage;
      const maximumDamage = white
        ? wealth.whiteMaxRadiusDamage
        : wealth.blackMaxRadiusDamage;
      const rawDamage = minimumDamage + (maximumDamage - minimumDamage) * radiusRatio;
      const hitIds: number[] = [];
      for (const enemy of [...this.enemies.values()]) {
        if (
          enemy.spaceId !== player.spaceId ||
          Math.hypot(enemy.x - player.x, enemy.y - player.y) > radius
        ) {
          continue;
        }
        const rank = getEnemy(enemy.definitionId).rank;
        const eliteMultiplier =
          rank === "normal" ? 1 : player.stats.eliteDamageMultiplier;
        this.damageEnemy(
          enemy,
          rawDamage *
            player.stats.damageMultiplier *
            this.getPlayerAbilityDamageMultiplier(player) *
            eliteMultiplier,
          player.id,
          { kind: "ability", abilityId: "greed_and_wealth" },
        );
        hitIds.push(enemy.id);
        if (white && this.enemies.has(enemy.id)) {
          this.displaceEnemy(enemy, player, wealth.whiteKnockbackDistance);
        }
      }
      if (hitIds.length > 0) {
        this.events.push({
          type: "shot",
          playerId: player.id,
          targetIds: hitIds,
          abilityId: white ? "greed_and_wealth:white_hole" : "greed_and_wealth:black_hole",
        });
      }
    }

    const heavyKey = "synergy:greed_and_wealth:heavy";
    const nextHeavyAt = player.mechanicCooldowns.get(heavyKey) ?? 0;
    if (this.elapsedMs < nextHeavyAt) return;
    player.mechanicCooldowns.set(
      heavyKey,
      this.elapsedMs + wealth.heavyIntervalMs,
    );
    const white =
      player.health / Math.max(1, player.stats.maxHealth) <=
      wealth.whiteHealthThresholdInclusive;
    const target = this.pickTarget(
      player.x,
      player.y,
      white ? wealth.whiteHeavyRange : wealth.blackHeavyRange,
      "elite_only",
      undefined,
      player.spaceId,
    );
    if (!target) return;
    const rank = getEnemy(target.definitionId).rank;
    const eliteMultiplier =
      rank === "normal" ? 1 : player.stats.eliteDamageMultiplier;
    this.damageEnemy(
      target,
      wealth.heavyDamage *
        player.stats.damageMultiplier *
        this.getPlayerAbilityDamageMultiplier(player) *
        eliteMultiplier,
      player.id,
      { kind: "ability", abilityId: "greed_and_wealth:heavy" },
    );
    if (this.enemies.has(target.id)) {
      const distance = Math.hypot(target.x - player.x, target.y - player.y);
      this.displaceEnemy(
        target,
        player,
        white
          ? Math.max(0, wealth.whiteHeavyKnockbackOutsideRadius - distance + 1)
          : -Math.max(0, distance - 20),
      );
    }
    this.events.push({
      type: "shot",
      playerId: player.id,
      targetIds: [target.id],
      abilityId: white ? "greed_and_wealth:white_heavy" : "greed_and_wealth:black_heavy",
    });
  }

  private updateDayNightCycle(player: SimPlayer): void {
    const mechanic = this.getActiveSynergyMechanic(player, "day_night_cycle");
    if (!mechanic || player.status !== "active") {
      player.dayNightPhase = "day";
      player.dayNightSwitchAtMs = 0;
      player.dayNightNextAttackAtMs = 0;
      return;
    }
    if (player.dayNightSwitchAtMs <= 0) {
      player.dayNightPhase = "day";
      player.dayNightSwitchAtMs = this.elapsedMs + mechanic.dayDurationMs;
      player.dayNightNextAttackAtMs =
        this.elapsedMs + mechanic.dayIntervalMs;
    }
    while (this.elapsedMs >= player.dayNightSwitchAtMs) {
      const phaseStartedAtMs = player.dayNightSwitchAtMs;
      if (player.dayNightPhase === "day") {
        player.dayNightPhase = "night";
        player.dayNightSwitchAtMs += mechanic.nightDurationMs;
        player.dayNightNextAttackAtMs =
          phaseStartedAtMs + mechanic.nightIntervalMs;
      } else {
        player.dayNightPhase = "day";
        player.dayNightSwitchAtMs += mechanic.dayDurationMs;
        player.dayNightNextAttackAtMs =
          phaseStartedAtMs + mechanic.dayIntervalMs;
      }
    }
    const intervalMs =
      player.dayNightPhase === "day"
        ? mechanic.dayIntervalMs
        : mechanic.nightIntervalMs;
    const damage =
      player.dayNightPhase === "day"
        ? mechanic.dayDamage
        : mechanic.nightDamage;
    let casts = 0;
    while (
      this.elapsedMs >= player.dayNightNextAttackAtMs &&
      casts < 64
    ) {
      const target = this.pickTarget(
        player.x,
        player.y,
        mechanic.range,
        player.dayNightPhase === "day" ? "elite_only" : "random",
      );
      if (!target) {
        player.dayNightNextAttackAtMs = this.elapsedMs + intervalMs;
        break;
      }
      const rank = getEnemy(target.definitionId).rank;
      const eliteMultiplier =
        rank === "normal" ? 1 : player.stats.eliteDamageMultiplier;
      this.damageEnemy(
        target,
        damage *
          player.stats.damageMultiplier *
          this.getPlayerAbilityDamageMultiplier(player) *
          (1 + this.teamTechLevels.war_chant * 0.03) *
          eliteMultiplier,
        player.id,
        { kind: "ability", abilityId: "cycle_of_day_and_night" },
      );
      this.events.push({
        type: "shot",
        playerId: player.id,
        targetIds: [target.id],
        abilityId:
          player.dayNightPhase === "day" ? "sun_wheel" : "night_wheel",
      });
      player.dayNightNextAttackAtMs += intervalMs;
      casts += 1;
    }
    if (casts === 64 && this.elapsedMs >= player.dayNightNextAttackAtMs) {
      player.dayNightNextAttackAtMs = this.elapsedMs + intervalMs;
    }
  }

  private updateGreedParadise(player: SimPlayer): void {
    const mechanic = this.getActiveSynergyMechanic(player, "greed_paradise");
    if (
      !mechanic ||
      player.status !== "active" ||
      !this.advancedMechanicReady(
        player,
        "synergy:greed_paradise:forced_damage",
        mechanic.forcedDamageIntervalMs,
      )
    ) {
      return;
    }
    const hitIds: number[] = [];
    for (const enemy of [...this.enemies.values()]) {
      const forcedPlayer = this.getForcedAggroPlayer(enemy);
      if (
        forcedPlayer?.id !== player.id ||
        enemy.forcedAggroAbilityId !== mechanic.forcedAggroAbilityId
      ) {
        continue;
      }
      const rank = getEnemy(enemy.definitionId).rank;
      const eliteMultiplier =
        rank === "normal" ? 1 : player.stats.eliteDamageMultiplier;
      this.damageEnemy(
        enemy,
        mechanic.forcedDamage *
          player.stats.damageMultiplier *
          this.getPlayerAbilityDamageMultiplier(player) *
          (1 + this.teamTechLevels.war_chant * 0.03) *
          eliteMultiplier,
        player.id,
        { kind: "ability", abilityId: "paradise_of_greed" },
      );
      hitIds.push(enemy.id);
    }
    if (hitIds.length > 0) {
      this.events.push({
        type: "shot",
        playerId: player.id,
        targetIds: hitIds,
        abilityId: "paradise_of_greed",
      });
    }
  }

  private updateSlackingOff(player: SimPlayer, moving: boolean): void {
    const mechanic = this.getActiveSynergyMechanic(player, "slacking_off");
    const key = "synergy:slacking_off:auto_repair";
    if (!mechanic || player.status !== "active") {
      player.mechanicCooldowns.delete(key);
      return;
    }
    if (moving) {
      player.mechanicCooldowns.set(key, this.elapsedMs + mechanic.intervalMs);
      return;
    }
    if (
      !this.advancedMechanicReady(player, key, mechanic.intervalMs)
    ) {
      return;
    }
    let repaired = 0;
    if (
      this.core.health < this.core.maxHealth &&
      Math.hypot(player.x - this.core.x, player.y - this.core.y) <=
        mechanic.stationaryRadius
    ) {
      const before = this.core.health;
      this.core.health = Math.min(
        this.core.maxHealth,
        this.core.health + mechanic.repair,
      );
      repaired = this.core.health - before;
    } else {
      const defense = this.defenses
        .filter(
          (candidate) =>
            !candidate.destroyed &&
            candidate.health > 0 &&
            candidate.health < candidate.maxHealth &&
            Math.hypot(candidate.x - player.x, candidate.y - player.y) <=
              mechanic.stationaryRadius,
        )
        .sort(
          (a, b) =>
            distanceSquared(a.x, a.y, player.x, player.y) -
              distanceSquared(b.x, b.y, player.x, player.y) ||
            a.id - b.id,
        )[0];
      if (defense) {
        const before = defense.health;
        defense.health = Math.min(
          defense.maxHealth,
          defense.health + mechanic.repair,
        );
        repaired = defense.health - before;
      } else {
        const building = [...this.cityBuildings.values()]
          .filter(
            (candidate) =>
              !candidate.destroyed &&
              candidate.health > 0 &&
              candidate.health < candidate.maxHealth &&
              Math.hypot(candidate.x - player.x, candidate.y - player.y) <=
                mechanic.stationaryRadius,
          )
          .sort(
            (a, b) =>
              distanceSquared(a.x, a.y, player.x, player.y) -
                distanceSquared(b.x, b.y, player.x, player.y) ||
              a.id.localeCompare(b.id),
          )[0];
        if (building) {
          const before = building.health;
          building.health = Math.min(
            building.maxHealth,
            building.health + mechanic.repair,
          );
          repaired = building.health - before;
        }
      }
    }
    if (repaired > 0) {
      player.contribution.repairDone += repaired;
      this.triggerGrowth(player, "repair", repaired);
      this.triggerLabor(player, "repair", repaired);
    }
  }

  private getStationaryStackMechanic(
    player: SimPlayer,
  ): Extract<SynergyMechanicDefinition, { kind: "stationary_stacks" }> | undefined {
    if (player.status === "lich") {
      return undefined;
    }
    for (const synergyId of player.synergies) {
      const mechanic = getSynergy(synergyId).mechanics?.find(
        (entry) => entry.kind === "stationary_stacks",
      );
      if (mechanic?.kind === "stationary_stacks") {
        return mechanic;
      }
    }
    return undefined;
  }

  private getTurretMechanic(
    player: SimPlayer,
  ): Extract<SynergyMechanicDefinition, { kind: "turret_transformation" }> | undefined {
    if (player.status === "lich") {
      return undefined;
    }
    for (const synergyId of player.synergies) {
      const mechanic = getSynergy(synergyId).mechanics?.find(
        (entry) => entry.kind === "turret_transformation",
      );
      if (mechanic?.kind === "turret_transformation") {
        return mechanic;
      }
    }
    return undefined;
  }

  private getActiveCannon(player: SimPlayer):
    | {
        ability: ModuleDefinition;
        mechanic: Extract<
          AbilityMechanicDefinition,
          { kind: "basic_attack_cannon" }
        >;
      }
    | undefined {
    return player.modules
      .flatMap((abilityId) => {
        if (!abilityId) {
          return [];
        }
        const ability = getModule(abilityId);
        return (ability.mechanics ?? []).flatMap((mechanic) =>
          mechanic.kind === "basic_attack_cannon"
            ? [{ ability, mechanic }]
            : [],
        );
      })
      .sort(
        (a, b) =>
          b.mechanic.priority - a.mechanic.priority ||
          b.ability.tier - a.ability.tier,
      )[0];
  }

  private getPerpetualCoreStackCount(player: SimPlayer): number {
    if (!this.getStationaryStackMechanic(player)) {
      return 0;
    }
    return (player.synergyStackExpirations.get("perpetual_core") ?? []).filter(
      (expiresAtMs) => expiresAtMs > this.elapsedMs,
    ).length;
  }

  private getPlayerAbilityDamageMultiplier(player: SimPlayer): number {
    const mechanic = this.getStationaryStackMechanic(player);
    const companionAura = [...this.abilityEntities.values()].reduce(
      (sum, entity) => {
        if (
          entity.ownerId !== player.id ||
          entity.health <= 0 ||
          entity.spaceId !== player.spaceId
        ) {
          return sum;
        }
        let aura: CompanionDefinition["ownerAura"] | undefined;
        try {
          aura = getModule(baseAbilityId(entity.abilityId)).companion?.ownerAura;
        } catch {
          aura = undefined;
        }
        return aura && Math.hypot(entity.x - player.x, entity.y - player.y) <= aura.range
          ? sum + aura.abilityDamageBonus
          : sum;
      },
      0,
    );
    return (
      player.stats.abilityDamageMultiplier +
      companionAura +
      this.getPerpetualCoreStackCount(player) *
        (mechanic?.abilityDamageBonusPerStack ?? 0)
    );
  }

  private getPlayerManaRegen(player: SimPlayer): number {
    const mechanic = this.getStationaryStackMechanic(player);
    const ratio =
      player.stats.manaRegenMaxRatio +
      this.getPerpetualCoreStackCount(player) *
        (mechanic?.manaRegenMaxRatioPerStack ?? 0);
    return player.stats.manaRegen + player.stats.manaMax * ratio;
  }

  private getPlayerHealthRegen(player: SimPlayer): number {
    return (
      player.stats.healthRegen +
      player.stats.healingPerSecond +
      player.stats.maxHealth * player.stats.healthRegenMaxRatio
    );
  }

  private getPlayerBaseAttackSpeedModifier(player: SimPlayer): number {
    const cannonModifier =
      this.getActiveCannon(player)?.mechanic.baseAttackSpeedModifier ?? 0;
    const turret = this.getTurretMechanic(player);
    const turretModifier =
      turret && this.isTurretDeployed(player)
        ? turret.deployedBaseAttackSpeedModifier
        : 0;
    return (
      player.stats.baseAttackSpeedModifier +
      cannonModifier +
      turretModifier
    );
  }

  private getPlayerAttackSpeedBonus(player: SimPlayer): number {
    const mechanic = this.getStationaryStackMechanic(player);
    return (
      player.stats.attackSpeedBonus +
      this.getPerpetualCoreStackCount(player) *
        (mechanic?.attackSpeedBonusPerStack ?? 0)
    );
  }

  private getPlayerAttackIntervalMs(player: SimPlayer): number {
    const baseBucket = Math.max(
      0.05,
      1 + this.getPlayerBaseAttackSpeedModifier(player),
    );
    const bonusBucket = Math.max(
      0.05,
      1 + this.getPlayerAttackSpeedBonus(player),
    );
    return player.stats.fireIntervalMs / baseBucket / bonusBucket;
  }

  private getPlayerAttackRange(player: SimPlayer): number {
    const turret = this.getTurretMechanic(player);
    return (
      player.stats.range *
      (turret && this.isTurretDeployed(player)
        ? turret.deployedRangeMultiplier
        : 1)
    );
  }

  private getPlayerMoveSpeed(player: SimPlayer): number {
    const turret = this.getTurretMechanic(player);
    const turretRatio = turret ? player.turretMoveRatio : 1;
    const contactSource = this.getSurfaceContactSource(player);
    if (contactSource?.surfaceMining?.contactAttack) {
      if (player.status === "downed") {
        return 0;
      }
      const fixedSpeed =
        PLAYER_BASE_MOVE_SPEED *
        contactSource.surfaceMining.contactAttack.fixedBaseMoveSpeedRatio *
        turretRatio;
      return fixedSpeed <= 0
        ? 0
        : Math.max(
            0,
            fixedSpeed +
              (player.financeStatAdjustments.get("final_move_speed") ?? 0),
          );
    }
    const permanentDevourMultiplier = Math.max(
      0,
      1 - player.surfaceDevourMovePenaltyRatio,
    );
    const hiddenBaseMoveMultiplier = this.getHiddenRuntimeEffects(player)
      .filter((runtime) => runtime.effect.kind === "base_move_speed_multiplier")
      .reduce(
        (product, runtime) =>
          product *
          (runtime.effect.kind === "base_move_speed_multiplier"
            ? runtime.effect.multiplier
            : 1),
        1,
      );
    const enemySlowImmune = this.getHiddenRuntimeEffects(player).some(
      (runtime) => runtime.effect.kind === "enemy_slow_immunity",
    );
    const enemySlowMultiplier =
      enemySlowImmune || this.elapsedMs >= player.enemySlowUntilMs
        ? 1
        : Math.max(0, 1 - player.enemySlowRatio);
    const calculated =
      player.stats.moveSpeed *
      player.stats.moveSpeedMultiplier *
      permanentDevourMultiplier *
      hiddenBaseMoveMultiplier *
      enemySlowMultiplier *
      turretRatio;
    return calculated <= 0
      ? 0
      : Math.max(
          0,
          calculated +
            (player.financeStatAdjustments.get("final_move_speed") ?? 0),
        );
  }

  private getSurfaceContactSource(player: SimPlayer): ModuleDefinition | undefined {
    for (const moduleId of player.modules) {
      if (!moduleId) {
        continue;
      }
      const module = getModule(moduleId);
      if (module.surfaceMining?.contactAttack) {
        return module;
      }
    }
    return undefined;
  }

  private applySurfaceContactAttack(
    player: SimPlayer,
    source: ModuleDefinition,
    start: Vec2,
    end: Vec2,
  ): void {
    const contact = source.surfaceMining?.contactAttack;
    if (!contact) {
      return;
    }
    for (const [enemyId, nextAtMs] of player.gourmetContactCooldowns) {
      if (nextAtMs <= this.elapsedMs || !this.enemies.has(enemyId)) {
        player.gourmetContactCooldowns.delete(enemyId);
      }
    }
    const tuning = getAbilityTuning(player.modules, source.id);
    const hitIds: number[] = [];
    for (const enemy of [...this.enemies.values()].sort(
      (left, right) => left.id - right.id,
    )) {
      if (
        enemy.spaceId !== player.spaceId ||
        (player.gourmetContactCooldowns.get(enemy.id) ?? 0) > this.elapsedMs
      ) {
        continue;
      }
      const definition = getEnemy(enemy.definitionId);
      const collisionRadius = definition.radius * 1.18 + 2 + 20;
      if (distanceToSegment(enemy, start, end) > collisionRadius) {
        continue;
      }
      const eliteMultiplier =
        definition.rank === "normal" ? 1 : player.stats.eliteDamageMultiplier;
      player.gourmetContactCooldowns.set(
        enemy.id,
        this.elapsedMs + contact.enemyCooldownMs,
      );
      this.damageEnemy(
        enemy,
        contact.damage *
          tuning.damageMultiplier *
          player.stats.damageMultiplier *
          this.getPlayerAbilityDamageMultiplier(player) *
          (1 + this.teamTechLevels.war_chant * 0.03) *
          eliteMultiplier,
        player.id,
        { kind: "ability", abilityId: source.id },
      );
      hitIds.push(enemy.id);
      if (!this.enemies.has(enemy.id)) {
        const before = player.health;
        player.health = Math.min(
          player.stats.maxHealth,
          player.health + contact.healOnKill,
        );
        player.contribution.healingDone += player.health - before;
        continue;
      }
      if (definition.rank !== "boss") {
        this.pendingContactKnockbacks.push({
          enemyId: enemy.id,
          origin: { ...end },
          distance: contact.knockbackDistance,
        });
      }
    }
    if (hitIds.length > 0) {
      this.events.push({
        type: "shot",
        playerId: player.id,
        targetIds: hitIds,
        abilityId: source.id,
      });
    }
  }

  private knockbackEnemyFromPoint(
    enemy: SimEnemy,
    origin: Vec2,
    distance: number,
  ): void {
    const radius = getEnemy(enemy.definitionId).radius * 1.18 + 2;
    let directionX = enemy.x - origin.x;
    let directionY = enemy.y - origin.y;
    let length = Math.hypot(directionX, directionY);
    if (length < 0.001) {
      const angle = ((enemy.id * 1_103) % 360) * (Math.PI / 180);
      directionX = Math.cos(angle);
      directionY = Math.sin(angle);
      length = 1;
    }
    const stepCount = Math.max(1, Math.ceil(distance / 5));
    const stepX = (directionX / length) * (distance / stepCount);
    const stepY = (directionY / length) * (distance / stepCount);
    for (let step = 0; step < stepCount; step += 1) {
      const nextX = clamp(enemy.x + stepX, radius, WORLD_WIDTH - radius);
      const nextY = clamp(enemy.y + stepY, radius, WORLD_HEIGHT - radius);
      if (this.findBlockingDefense(nextX, nextY, radius)) {
        break;
      }
      enemy.x = nextX;
      enemy.y = nextY;
    }
  }

  private applyPendingContactKnockbacks(): void {
    for (const pending of this.pendingContactKnockbacks.splice(
      0,
      this.pendingContactKnockbacks.length,
    )) {
      const enemy = this.enemies.get(pending.enemyId);
      if (enemy && getEnemy(enemy.definitionId).rank !== "boss") {
        this.knockbackEnemyFromPoint(enemy, pending.origin, pending.distance);
      }
    }
  }

  private isTurretDeployed(player: SimPlayer): boolean {
    return Boolean(
      this.getTurretMechanic(player) && player.turretMoveRatio <= 0.0001,
    );
  }

  private movePlayerWithDefenseCollision(
    player: SimPlayer,
    deltaX: number,
    deltaY: number,
  ): void {
    // Small swept steps keep server collision stable at high movement speeds and
    // allow a player to slide along wall and gate edges instead of snapping back.
    const steps = Math.max(1, Math.ceil(Math.hypot(deltaX, deltaY) / 7));
    const stepX = deltaX / steps;
    const stepY = deltaY / steps;
    for (let step = 0; step < steps; step += 1) {
      const nextX = clamp(player.x + stepX, 24, WORLD_WIDTH - 24);
      const nextY = clamp(player.y + stepY, 24, WORLD_HEIGHT - 24);
      if (!this.findBlockingDefense(nextX, nextY, 20)) {
        player.x = nextX;
        player.y = nextY;
        continue;
      }
      if (!this.findBlockingDefense(nextX, player.y, 20)) {
        player.x = nextX;
      }
      if (!this.findBlockingDefense(player.x, nextY, 20)) {
        player.y = nextY;
      }
    }
  }

  private getAiMovement(player: SimPlayer, index: number): Vec2 {
    const downed = [...this.players.values()]
      .filter((candidate) => candidate.id !== player.id && candidate.status === "downed")
      .sort(
        (a, b) =>
          distanceSquared(player.x, player.y, a.x, a.y) -
          distanceSquared(player.x, player.y, b.x, b.y),
      )[0];
    if (downed) {
      return normalizedDirection(player.x, player.y, downed.x, downed.y, 18);
    }
    const angle = (index / Math.max(1, this.players.size)) * Math.PI * 2;
    return normalizedDirection(
      player.x,
      player.y,
      this.core.x + Math.cos(angle) * 260,
      this.core.y + Math.sin(angle) * 260,
      18,
    );
  }

  private performBaseAttack(player: SimPlayer, target: SimEnemy): void {
    const targetWasAliveAtAttackStart = this.enemies.has(target.id);
    const targetRankAtAttackStart = getEnemy(target.definitionId).rank;
    const turret = this.getTurretMechanic(player);
    const deployedDamageMultiplier =
      turret && this.isTurretDeployed(player)
        ? turret.deployedDamageMultiplier
        : 1;
    const damage =
      player.stats.damage *
      player.stats.damageMultiplier *
      player.stats.attackDamageMultiplier *
      (1 + this.teamTechLevels.war_chant * 0.03) *
      deployedDamageMultiplier;
    this.triggerBasicAttackChains(player, target);
    const cannon = this.getActiveCannon(player);
    let hitIds: number[];
    if (cannon) {
      const maxManaCost = player.stats.manaMax * cannon.mechanic.maxManaCostRatio;
      const consumedMana = Math.min(player.mana, maxManaCost);
      player.mana -= consumedMana;
      const consumedRatio =
        player.stats.manaMax > 0 ? consumedMana / player.stats.manaMax : 0;
      const tuning = getAbilityTuning(player.modules, cannon.ability.id);
      const diameter =
        cannon.mechanic.baseDiameter *
        tuning.cannonBaseDiameterMultiplier *
        (1 +
          consumedRatio * cannon.mechanic.diameterMultiplierPerManaRatio);
      const radius = diameter / 2;
      hitIds = this.applyAreaDamage(
        player,
        target.x,
        target.y,
        radius,
        damage,
        { kind: "body" },
      );
      const secondaryDamage =
        consumedMana *
        cannon.mechanic.secondaryDamagePerMana *
        player.stats.damageMultiplier *
        this.getPlayerAbilityDamageMultiplier(player) *
        (1 + this.teamTechLevels.war_chant * 0.03);
      if (secondaryDamage > 0) {
        hitIds.push(
          ...this.applyAreaDamage(
            player,
            target.x,
            target.y,
            radius,
            secondaryDamage,
            { kind: "ability", abilityId: cannon.ability.id },
          ),
        );
      }
    } else {
      hitIds = this.applyHitPattern(
        player,
        target,
        damage,
        { kind: "body" },
        player.stats.splashRadius,
        player.stats.chainTargets + player.stats.pierceTargets,
      );
    }
    const healthCostAbilities = player.modules.flatMap((abilityId, slot) => {
      if (!abilityId) return [];
      const ability = getModule(abilityId);
      if (!this.isAbilityEnabled(player, ability)) return [];
      return (ability.mechanics ?? []).flatMap((mechanic) =>
        mechanic.kind === "basic_attack_health_cost"
          ? [{ ability, mechanic, slot }]
          : [],
      );
    });
    for (const { ability, mechanic, slot } of healthCostAbilities) {
      const healthCost = player.stats.maxHealth * mechanic.maxHealthCostRatio;
      player.health = mechanic.cannotKillOwner
        ? Math.max(1, player.health - healthCost)
        : Math.max(0, player.health - healthCost);
      const bloodDebt = this.getActiveSynergyMechanic(player, "blood_debt");
      if (bloodDebt?.healthCostAbilityId === ability.id) {
        player.synergyCounters.set(
          "blood_debt:empowered_until",
          this.elapsedMs + bloodDebt.durationMs,
        );
      }
      if (this.enemies.has(target.id)) {
        this.damageEnemy(
          target,
          mechanic.bonusDamage *
            player.stats.damageMultiplier *
            player.stats.attackDamageMultiplier *
            (1 + this.teamTechLevels.war_chant * 0.03),
          player.id,
          { kind: "ability", abilityId: ability.id, sourceSlot: slot },
        );
        hitIds.push(target.id);
      }
      if (targetWasAliveAtAttackStart && !this.enemies.has(target.id)) {
        const healing = mechanic.healOnKillFlat ??
          player.stats.maxHealth *
            (mechanic.healOnKillMaxHealthRatio?.[targetRankAtAttackStart] ?? 0);
        if (healing > 0) {
          const before = player.health;
          player.health = Math.min(player.stats.maxHealth, player.health + healing);
          player.contribution.healingDone += player.health - before;
        }
      }
    }
    this.events.push({
      type: "shot",
      playerId: player.id,
      targetIds: [...new Set(hitIds)],
      abilityId: cannon?.ability.id,
    });
    const maintenance = this.getActiveSynergyMechanic(
      player,
      "overmaintenance",
    );
    if (maintenance) {
      const key = "synergy:overmaintenance:basic_attacks";
      const count = (player.synergyCounters.get(key) ?? 0) + 1;
      if (count >= maintenance.jamEveryBasicAttacks) {
        player.synergyCounters.set(key, 0);
        player.basicAttackJammedUntilMs = Math.max(
          player.basicAttackJammedUntilMs,
          this.elapsedMs + maintenance.jamDurationMs,
        );
      } else {
        player.synergyCounters.set(key, count);
      }
    }
    this.triggerGrowth(player, "attack", 1);
    this.triggerLabor(player, "attack", 1);
  }

  private triggerBasicAttackChains(player: SimPlayer, initialTarget: SimEnemy): void {
    for (const [slot, abilityId] of player.modules.entries()) {
      if (!abilityId) continue;
      const ability = getModule(abilityId);
      const mechanic = ability.mechanics?.find(
        (entry) => entry.kind === "basic_attack_chain",
      );
      if (!mechanic || mechanic.kind !== "basic_attack_chain") continue;
      const soul = this.getActiveSynergyMechanic(player, "soul_weighing");
      const initialDamage =
        mechanic.initialDamage +
        (soul?.boomerangAbilityId === ability.id
          ? soul.boomerangInitialDamageAdd
          : 0);
      const tuning = getAbilityTuning(player.modules, ability.id);
      const hitIds: number[] = [];
      let previous = initialTarget;
      const used = new Set<number>();
      for (let bounce = 0; bounce < mechanic.targets; bounce += 1) {
        const target = bounce === 0
          ? initialTarget
          : [...this.enemies.values()]
              .filter(
                (enemy) =>
                  enemy.spaceId === previous.spaceId &&
                  !used.has(enemy.id) &&
                  Math.hypot(enemy.x - previous.x, enemy.y - previous.y) <=
                    mechanic.bounceRange,
              )
              .sort(
                (a, b) =>
                  distanceSquared(a.x, a.y, previous.x, previous.y) -
                    distanceSquared(b.x, b.y, previous.x, previous.y) ||
                  a.id - b.id,
              )[0];
        if (!target || !this.enemies.has(target.id)) break;
        used.add(target.id);
        previous = target;
        const rank = getEnemy(target.definitionId).rank;
        const eliteMultiplier =
          rank === "normal" ? 1 : player.stats.eliteDamageMultiplier;
        const damage =
          Math.max(0, initialDamage - bounce * mechanic.damageLossPerBounce) *
          tuning.damageMultiplier *
          player.stats.damageMultiplier *
          this.getPlayerAbilityDamageMultiplier(player) *
          (1 + this.teamTechLevels.war_chant * 0.03) *
          eliteMultiplier;
        this.damageEnemy(target, damage, player.id, {
          kind: "ability",
          abilityId: ability.id,
          sourceSlot: slot,
        });
        hitIds.push(target.id);
      }
      if (hitIds.length > 0) {
        this.events.push({
          type: "shot",
          playerId: player.id,
          targetIds: hitIds,
          abilityId: ability.id,
        });
      }
    }
  }

  private getSynergyRangeAdd(player: SimPlayer, abilityId: string): number {
    let result = 0;
    const distant = this.getActiveSynergyMechanic(
      player,
      "distant_target_bonus",
    );
    if (distant?.abilityIds.includes(abilityId)) {
      result += distant.rangeAdd;
    }
    const hotFloor = this.getActiveSynergyMechanic(player, "hot_floor");
    if (hotFloor?.abilityIds.includes(abilityId)) {
      result += hotFloor.rangeAdd;
    }
    return result;
  }

  private getSynergyRadiusAdd(player: SimPlayer, abilityId: string): number {
    const hotFloor = this.getActiveSynergyMechanic(player, "hot_floor");
    return hotFloor?.abilityIds.includes(abilityId) ? hotFloor.rangeAdd : 0;
  }

  private applyAreaDamage(
    player: SimPlayer,
    x: number,
    y: number,
    radius: number,
    damage: number,
    attribution: DamageAttribution,
  ): number[] {
    const hitIds: number[] = [];
    for (const enemy of [...this.enemies.values()]) {
      if (Math.hypot(enemy.x - x, enemy.y - y) > radius) {
        continue;
      }
      this.damageEnemy(enemy, damage, player.id, attribution);
      hitIds.push(enemy.id);
    }
    return hitIds;
  }

  private updateAbilityAttacks(player: SimPlayer): void {
    for (const [slot, abilityId] of player.modules.entries()) {
      if (!abilityId) {
        continue;
      }
      const ability = getModule(abilityId);
      const combat = ability.combat;
      if (!combat) {
        continue;
      }
      const tuning = getAbilityTuning(player.modules, abilityId);
      const manaCost = (combat.manaCost ?? 0) * tuning.manaCostMultiplier;
      const cooldownKey = slot === 0 ? abilityId : `${abilityId}:slot:${slot}`;
      const nextAt = player.abilityCooldowns.get(cooldownKey) ?? 0;
      if (this.elapsedMs < nextAt || player.mana < manaCost) {
        continue;
      }
        const targets = this.pickCombatTargets(
        player,
        ability,
        cooldownKey,
          combat.range * tuning.rangeMultiplier +
            this.getSynergyRangeAdd(player, ability.id),
        (combat.projectiles ?? 1) + tuning.projectilesAdd,
      );
      if (targets.length === 0) {
        continue;
      }
      const castExecutions = this.registerDiscreteSkillCast(
        player,
        `slot:${slot}`,
      );
      player.mana -= manaCost;
      const hiddenAttackSpeed =
        this.getHiddenAllyAuraAt(player.x, player.y).attackSpeedMultiplier;
      const intervalMultiplier =
        (1 / (1 + this.teamTechLevels.haste_chant * 0.02)) *
        (1 / hiddenAttackSpeed) *
        (player.health / player.stats.maxHealth < 0.3 &&
        this.hasSpecial(player, "emergency_overdrive")
          ? 0.6
          : 1);
      player.abilityCooldowns.set(
        cooldownKey,
        this.elapsedMs +
          Math.max(
            120,
            combat.intervalMs * intervalMultiplier * tuning.intervalMultiplier,
          ),
      );
      const allHitIds: number[] = [];
      const nativeEvery = combat.doubleEveryAttacks;
      const spin = this.getActiveSynergyMechanic(player, "spin_it");
      const doubleEvery = nativeEvery
        ? Math.max(
            2,
            nativeEvery -
              (spin?.gearAbilityId === ability.id ? spin.triggerReduction : 0),
          )
        : undefined;
      const ordinalKey = `native-double:${cooldownKey}`;
      const ordinal = (player.abilityTargetOrdinals.get(ordinalKey) ?? 0) + 1;
      player.abilityTargetOrdinals.set(ordinalKey, ordinal);
      const attackExecutions = doubleEvery && ordinal % doubleEvery === 0 ? 2 : 1;
      for (const target of targets) {
        const rank = getEnemy(target.definitionId).rank;
        const eliteMultiplier =
          rank === "normal" ? 1 : player.stats.eliteDamageMultiplier;
        const damage =
          (combat.damage +
            (player.abilityDamageGrowth.get(abilityId) ?? 0)) *
          tuning.damageMultiplier *
          player.stats.damageMultiplier *
          this.getPlayerAbilityDamageMultiplier(player) *
          (1 + this.teamTechLevels.war_chant * 0.03) *
          eliteMultiplier;
        for (let execution = 0; execution < attackExecutions; execution += 1) {
        for (let hit = 0; hit < (combat.hits ?? 1); hit += 1) {
          if (!this.enemies.has(target.id)) break;
          allHitIds.push(
            ...this.applyHitPattern(
              player,
              target,
              damage * this.getCombatContextDamageMultiplier(player, ability, target),
              {
                kind: "ability",
                abilityId,
                sourceSlot: slot,
                variant: execution > 0 ? "native_double" : undefined,
              },
              (combat.radius ?? 0) +
                tuning.radiusAdd +
                this.getSynergyRadiusAdd(player, ability.id),
              (combat.chainTargets ?? 0) +
                (combat.pierceTargets ?? 0) +
                tuning.extraTargetsAdd,
              combat.slowRatio,
              combat.slowDurationMs,
              combat.areaDamageMultiplier,
              combat.stunDurationMs,
              combat.stunBosses ?? true,
              combat.areaOrigin ?? "target",
              combat.pullDistance ?? 0,
              combat.knockbackDistance ?? 0,
            ),
          );
        }
        }
        this.scheduleCombatEchoes(player, ability, slot, target, damage);
      }
      player.lastPlayerSkillTargetId = targets[0]?.id;
      this.events.push({
        type: "shot",
        playerId: player.id,
        targetIds: [...new Set(allHitIds)],
        abilityId,
      });
      this.triggerGrowth(player, "attack", 1);
      this.triggerLabor(player, "attack", 1);
      if (
        (ability.special === "portal_on_hit" ||
          this.hasSynergySpecial(player, "portal_on_hit")) &&
        this.random.next() < 0.12
      ) {
        const center = targets[0];
        for (const enemy of this.enemies.values()) {
          if (Math.hypot(enemy.x - center.x, enemy.y - center.y) <= 190) {
            this.damageEnemy(
              enemy,
              damageFromTier(ability.tier) * 1.6,
              player.id,
              { kind: "ability", abilityId, sourceSlot: slot },
            );
          }
        }
      }
      if (combat.executeNormalEveryMs) {
        const key = `${abilityId}:execute`;
        const executeAt = player.abilityCooldowns.get(key) ?? 0;
        if (this.elapsedMs >= executeAt) {
          const normal = this.pickTarget(player.x, player.y, combat.range, "random", "normal");
          if (normal) {
            this.damageEnemy(
              normal,
              normal.health + 1,
              player.id,
              { kind: "ability", abilityId },
            );
          }
          player.abilityCooldowns.set(key, this.elapsedMs + combat.executeNormalEveryMs);
        }
      }
      if (castExecutions === 2) {
        this.executeFreeCombatCast(player, ability, slot);
      }
      const debt = this.getActiveSynergyMechanic(player, "debt_outsourcing");
      if (
        debt?.crowAbilityId === ability.id &&
        this.elapsedMs < player.debtCrowDoubleUntilMs
      ) {
        this.executeFreeCombatCast(player, ability, slot);
      }
    }
  }

  private executeFreeCombatCast(
    player: SimPlayer,
    ability: ModuleDefinition,
    slot: number,
  ): void {
    const combat = ability.combat;
    if (!combat) return;
    const tuning = getAbilityTuning(player.modules, ability.id);
    const targets = this.pickCombatTargets(
      player,
      ability,
      `${ability.id}:free:${slot}`,
      combat.range * tuning.rangeMultiplier +
        this.getSynergyRangeAdd(player, ability.id),
      (combat.projectiles ?? 1) + tuning.projectilesAdd,
    );
    if (targets.length === 0) return;
    const allHitIds: number[] = [];
    for (const target of targets) {
      const rank = getEnemy(target.definitionId).rank;
      const eliteMultiplier =
        rank === "normal" ? 1 : player.stats.eliteDamageMultiplier;
      const damage =
        (combat.damage + (player.abilityDamageGrowth.get(ability.id) ?? 0)) *
        tuning.damageMultiplier *
        player.stats.damageMultiplier *
        this.getPlayerAbilityDamageMultiplier(player) *
        (1 + this.teamTechLevels.war_chant * 0.03) *
        eliteMultiplier;
      for (let hit = 0; hit < (combat.hits ?? 1); hit += 1) {
        if (!this.enemies.has(target.id)) break;
        allHitIds.push(
          ...this.applyHitPattern(
            player,
            target,
            damage * this.getCombatContextDamageMultiplier(player, ability, target),
            { kind: "ability", abilityId: ability.id, sourceSlot: slot },
            (combat.radius ?? 0) +
              tuning.radiusAdd +
              this.getSynergyRadiusAdd(player, ability.id),
            (combat.chainTargets ?? 0) +
              (combat.pierceTargets ?? 0) +
              tuning.extraTargetsAdd,
            combat.slowRatio,
            combat.slowDurationMs,
            combat.areaDamageMultiplier,
            combat.stunDurationMs,
            combat.stunBosses ?? true,
            combat.areaOrigin ?? "target",
            combat.pullDistance ?? 0,
            combat.knockbackDistance ?? 0,
          ),
        );
      }
      this.scheduleCombatEchoes(player, ability, slot, target, damage);
    }
    player.lastPlayerSkillTargetId = targets[0]?.id;
    this.events.push({
      type: "shot",
      playerId: player.id,
      targetIds: [...new Set(allHitIds)],
      abilityId: ability.id,
    });
  }

  private applyHitPattern(
    player: SimPlayer,
    target: SimEnemy,
    damage: number,
    attribution: DamageAttribution,
    radius = 0,
    extraTargets = 0,
    slowRatio = 0,
    slowDurationMs = 0,
    areaDamageMultiplier = 0.72,
    stunDurationMs = 0,
    stunBosses = true,
    areaOrigin: "target" | "caster" = "target",
    pullDistance = 0,
    knockbackDistance = 0,
  ): number[] {
    const hitIds: number[] = [];
    this.damageEnemy(target, damage, player.id, attribution);
    hitIds.push(target.id);
    if (this.enemies.has(target.id)) {
      this.applyAuthoredAbilityOnHit(player, target, attribution);
    }
    if (slowRatio > 0) {
      this.applyAbilitySlow(player, target, attribution.abilityId, slowRatio, slowDurationMs);
    }
    this.applyCombatStun(target, stunDurationMs, stunBosses);
    this.applyAbsoluteDomainOnFreeze(
      player,
      target,
      attribution.abilityId,
      stunDurationMs,
    );
    if (radius > 0) {
      const center = areaOrigin === "caster" ? player : target;
      for (const enemy of this.enemies.values()) {
        if (
          enemy.id !== target.id &&
          Math.hypot(enemy.x - center.x, enemy.y - center.y) <= radius
        ) {
          this.damageEnemy(
            enemy,
            damage * areaDamageMultiplier,
            player.id,
            attribution,
          );
          if (slowRatio > 0) {
            this.applyAbilitySlow(player, enemy, attribution.abilityId, slowRatio, slowDurationMs);
          }
          this.applyCombatStun(enemy, stunDurationMs, stunBosses);
          this.applyAbsoluteDomainOnFreeze(
            player,
            enemy,
            attribution.abilityId,
            stunDurationMs,
          );
          if (this.enemies.has(enemy.id)) {
            this.applyAuthoredAbilityOnHit(player, enemy, attribution);
          }
          hitIds.push(enemy.id);
        }
      }
    }
    if (extraTargets > 0) {
      const candidates = [...this.enemies.values()]
        .filter((enemy) => enemy.id !== target.id)
        .sort(
          (a, b) =>
            distanceSquared(a.x, a.y, target.x, target.y) -
            distanceSquared(b.x, b.y, target.x, target.y),
        )
        .slice(0, extraTargets);
      for (const enemy of candidates) {
        this.damageEnemy(enemy, damage * 0.62, player.id, attribution);
        this.applyCombatStun(enemy, stunDurationMs, stunBosses);
        this.applyAbsoluteDomainOnFreeze(
          player,
          enemy,
          attribution.abilityId,
          stunDurationMs,
        );
        if (this.enemies.has(enemy.id)) {
          this.applyAuthoredAbilityOnHit(player, enemy, attribution);
        }
        hitIds.push(enemy.id);
      }
    }
    const center = areaOrigin === "caster" ? player : target;
    for (const enemyId of new Set(hitIds)) {
      const enemy = this.enemies.get(enemyId);
      if (!enemy || getEnemy(enemy.definitionId).rank === "boss") continue;
      if (pullDistance > 0) {
        this.displaceEnemy(enemy, center, -pullDistance);
      } else if (knockbackDistance > 0) {
        this.displaceEnemy(enemy, center, knockbackDistance);
      }
    }
    return hitIds;
  }

  private pickCombatTargets(
    player: SimPlayer,
    ability: ModuleDefinition,
    counterKey: string,
    range: number,
    count: number,
  ): SimEnemy[] {
    if (ability.combat?.stickyTarget) {
      const key = `sticky:${counterKey}`;
      const lockedId = player.abilityTargetOrdinals.get(key);
      const locked = lockedId === undefined ? undefined : this.enemies.get(lockedId);
      if (
        locked &&
        locked.spaceId === player.spaceId &&
        Math.hypot(locked.x - player.x, locked.y - player.y) <= range
      ) {
        return [locked];
      }
      const selected = this.pickTargets(
        player.x,
        player.y,
        range,
        ability.combat.target,
        1,
      );
      if (selected[0]) player.abilityTargetOrdinals.set(key, selected[0].id);
      else player.abilityTargetOrdinals.delete(key);
      return selected;
    }
    const dual = this.getActiveSynergyMechanic(player, "dual_extremes_nth");
    if (dual?.abilityIds.includes(ability.id)) {
      const key = `dual_extremes:${ability.id}:${counterKey}`;
      const ordinal = (player.abilityTargetOrdinals.get(key) ?? 0) + 1;
      player.abilityTargetOrdinals.set(key, ordinal);
      if (ordinal % dual.every === 0) {
        const nearest = this.pickTarget(player.x, player.y, range, "closest");
        const farthest = this.pickTarget(player.x, player.y, range, "farthest");
        return [...new Map(
          [nearest, farthest]
            .filter((enemy): enemy is SimEnemy => Boolean(enemy))
            .map((enemy) => [enemy.id, enemy]),
        ).values()];
      }
    }
    if (ability.combat?.target === "alternating_extremes") {
      const key = `alternating:${counterKey}`;
      const ordinal = player.abilityTargetOrdinals.get(key) ?? 0;
      player.abilityTargetOrdinals.set(key, ordinal + 1);
      return this.pickTargets(
        player.x,
        player.y,
        range,
        ordinal % 2 === 0 ? "closest" : "farthest",
        count,
      );
    }
    return this.pickTargets(
      player.x,
      player.y,
      range,
      ability.combat?.target ?? "random",
      count,
    );
  }

  private scheduleCombatEchoes(
    player: SimPlayer,
    ability: ModuleDefinition,
    sourceSlot: number,
    target: SimEnemy,
    calculatedDamagePerHit: number,
  ): void {
    const authored = ability.combat?.delayedEcho;
    if (authored) {
      const soul = this.getActiveSynergyMechanic(player, "soul_weighing");
      const echoMultiplier =
        soul?.echoAbilityId === ability.id ? soul.echoDamageMultiplier : 1;
      player.pendingDelayedHits.push({
        executeAtMs: this.elapsedMs + authored.delayMs,
        originEnemyId: target.id,
        originX: target.x,
        originY: target.y,
        originSpaceId: target.spaceId,
        damage:
          calculatedDamagePerHit *
          (authored.damage / Math.max(1, ability.combat?.damage ?? 1)) *
          echoMultiplier,
        abilityId: ability.id,
        sourceSlot,
        targetMode: authored.target,
      });
    }
    const synergy = this.getActiveSynergyMechanic(player, "echo_transfer");
    if (synergy?.abilityIds.includes(ability.id)) {
      const authoredTotalRatio =
        (((ability.combat?.damage ?? 0) * (ability.combat?.hits ?? 1)) +
          (ability.combat?.delayedEcho?.damage ?? 0)) /
        Math.max(1, ability.combat?.damage ?? 1);
      player.pendingDelayedHits.push({
        executeAtMs:
          this.elapsedMs + (ability.combat?.delayedEcho?.delayMs ?? 0),
        originEnemyId: target.id,
        originX: target.x,
        originY: target.y,
        originSpaceId: target.spaceId,
        damage:
          calculatedDamagePerHit *
          authoredTotalRatio *
          synergy.damageRatio,
        abilityId: "firepower_reverberation",
        sourceSlot,
      });
    }
  }

  private updatePendingDelayedHits(): void {
    for (const player of this.players.values()) {
      const ready = player.pendingDelayedHits.filter(
        (entry) => entry.executeAtMs <= this.elapsedMs,
      );
      player.pendingDelayedHits = player.pendingDelayedHits.filter(
        (entry) => entry.executeAtMs > this.elapsedMs,
      );
      for (const entry of ready) {
        const original = this.enemies.get(entry.originEnemyId);
        const target = entry.targetMode === "same_target" &&
          original?.spaceId === entry.originSpaceId
          ? original
          : [...this.enemies.values()]
              .filter(
                (enemy) =>
                  enemy.id !== entry.originEnemyId &&
                  enemy.spaceId === entry.originSpaceId,
              )
              .sort(
                (left, right) =>
                  distanceSquared(left.x, left.y, entry.originX, entry.originY) -
                    distanceSquared(right.x, right.y, entry.originX, entry.originY) ||
                  left.id - right.id,
              )[0];
        if (!target) continue;
        this.damageEnemy(target, entry.damage, player.id, {
          kind: "ability",
          abilityId: entry.abilityId,
          sourceSlot: entry.sourceSlot,
        });
        this.events.push({
          type: "shot",
          playerId: player.id,
          targetIds: [target.id],
          abilityId: entry.abilityId,
        });
      }
    }
  }

  private getCombatContextDamageMultiplier(
    player: SimPlayer,
    ability: ModuleDefinition,
    target: SimEnemy,
  ): number {
    const chaotic = this.getActiveSynergyMechanic(player, "chaotic_mess");
    if (
      chaotic?.acidAbilityId === ability.id &&
      Math.hypot(target.x - player.x, target.y - player.y) <= 200
    ) {
      return chaotic.acidDamageMultiplierInBrazier;
    }
    const distant = this.getActiveSynergyMechanic(
      player,
      "distant_target_bonus",
    );
    if (
      distant?.abilityIds.includes(ability.id) &&
      Math.hypot(target.x - player.x, target.y - player.y) >
        distant.distanceThreshold
    ) {
      return distant.damageMultiplier;
    }
    const longRange = this.getActiveSynergyMechanic(player, "long_range_strike");
    if (
      longRange?.abilityIds.includes(ability.id) &&
      Math.hypot(target.x - player.x, target.y - player.y) >
        longRange.distanceThreshold
    ) {
      return longRange.damageMultiplier;
    }
    const hotFloor = this.getActiveSynergyMechanic(player, "hot_floor");
    if (
      hotFloor?.abilityIds.includes(ability.id) &&
      target.hotFloorEmpoweredUntilMs > this.elapsedMs
    ) {
      return hotFloor.damageMultiplier;
    }
    const bloodDebt = this.getActiveSynergyMechanic(player, "blood_debt");
    if (
      bloodDebt?.empoweredAbilityId === ability.id &&
      (player.synergyCounters.get("blood_debt:empowered_until") ?? 0) >
        this.elapsedMs
    ) {
      return bloodDebt.damageMultiplier;
    }
    return 1;
  }

  private applyAuthoredAbilityOnHit(
    player: SimPlayer,
    enemy: SimEnemy,
    attribution: DamageAttribution,
  ): void {
    const abilityId = attribution.abilityId
      ? baseAbilityId(attribution.abilityId)
      : undefined;
    if (!abilityId) return;
    let ability: ModuleDefinition;
    try {
      ability = getModule(abilityId);
    } catch {
      return;
    }
    const stacking = this.getActiveSynergyMechanic(
      player,
      "stacking_armor_break",
    );
    let armorAmount = ability.combat?.armorReduction?.amount ?? 0;
    let armorDuration = ability.combat?.armorReduction?.durationMs ?? 0;
    if (stacking?.abilityIds.includes(abilityId)) {
      armorAmount =
        abilityId === "caltrop_launcher"
          ? 1
          : abilityId === "acid_splash"
            ? 2
            : 5;
      armorDuration = stacking.durationMs;
      if (stacking.refreshAllOnHit) {
        enemy.armorReductionStacks = enemy.armorReductionStacks.map((entry) => ({
          ...entry,
          expiresAtMs: this.elapsedMs + armorDuration,
        }));
      }
      const current = this.getEnemyArmorReduction(enemy);
      armorAmount = Math.min(armorAmount, Math.max(0, stacking.maxReduction - current));
    } else if (armorAmount > 0) {
      enemy.armorReductionStacks = enemy.armorReductionStacks.filter(
        (entry) => entry.sourceId !== abilityId,
      );
    }
    if (armorAmount > 0) {
      enemy.armorReductionStacks.push({
        sourceId: abilityId,
        amount: armorAmount,
        expiresAtMs: this.elapsedMs + armorDuration,
      });
    }

    const root = ability.combat?.rootEveryHits;
    if (root) {
      const key = `root:${abilityId}:${player.id}`;
      const count = (enemy.hitCounters.get(key) ?? 0) + 1;
      if (count >= root.hits) {
        enemy.hitCounters.set(key, 0);
        if (!root.bossesImmune || getEnemy(enemy.definitionId).rank !== "boss") {
          enemy.rootedUntilMs = Math.max(
            enemy.rootedUntilMs,
            this.elapsedMs + root.durationMs,
          );
        }
      } else {
        enemy.hitCounters.set(key, count);
      }
    }

    const needle = this.getActiveSynergyMechanic(player, "needle_heal_counter");
    if (needle?.abilityIds.includes(abilityId)) {
      const key = "synergy:needlework:hits";
      const count = (player.synergyCounters.get(key) ?? 0) + 1;
      if (count >= needle.hitsRequired) {
        player.synergyCounters.set(key, 0);
        this.applyHealingWithOptionalShield(
          player,
          player,
          needle.healing,
          needle.healAbilityId,
        );
      } else {
        player.synergyCounters.set(key, count);
      }
    }

    const chaotic = this.getActiveSynergyMechanic(player, "chaotic_mess");
    if (chaotic?.mixerAbilityId === abilityId) {
      const before = enemy.armorReductionStacks.length;
      enemy.armorReductionStacks = enemy.armorReductionStacks.filter(
        (entry) => entry.sourceId !== chaotic.acidAbilityId,
      );
      if (enemy.armorReductionStacks.length < before) {
        this.damageEnemy(enemy, chaotic.mixerBonusDamage, player.id, {
          kind: "ability",
          abilityId: chaotic.mixerAbilityId,
        });
      }
    }

    this.applyTierFourSynergyOnHit(player, enemy, abilityId, attribution);
  }

  private applyAbilitySlow(
    player: SimPlayer,
    enemy: SimEnemy,
    authoredAbilityId: string | undefined,
    ratio: number,
    durationMs: number,
  ): void {
    const abilityId = authoredAbilityId
      ? baseAbilityId(authoredAbilityId)
      : undefined;
    if (abilityId) {
      try {
        if (
          getModule(abilityId).combat?.slowBosses === false &&
          getEnemy(enemy.definitionId).rank === "boss"
        ) {
          return;
        }
      } catch {
        // Internal effect IDs do not have authored combat definitions.
      }
    }
    const freeze = this.getActiveSynergyMechanic(player, "freeze_combo");
    if (abilityId && freeze?.abilityIds.includes(abilityId)) {
      const existing = enemy.slowUntilMs > this.elapsedMs ? enemy.slowRatio : 0;
      const stacked = Math.min(freeze.maxSlowRatio, existing + ratio);
      if (stacked >= freeze.maxSlowRatio - 1e-9) {
        enemy.slowRatio = 0;
        enemy.slowUntilMs = this.elapsedMs;
        const absolute = this.getActiveSynergyMechanic(
          player,
          "absolute_domain",
        );
        const bossProtected =
          getEnemy(enemy.definitionId).rank === "boss" &&
          absolute?.sourceAbilityIds.includes(abilityId);
        if (bossProtected && absolute) {
          enemy.slowRatio = Math.max(enemy.slowRatio, absolute.bossSlowRatio);
          enemy.slowUntilMs = Math.max(
            enemy.slowUntilMs,
            this.elapsedMs + absolute.bossSlowDurationMs,
          );
        } else {
          enemy.stunnedUntilMs = Math.max(
            enemy.stunnedUntilMs,
            this.elapsedMs + freeze.freezeDurationMs,
          );
          this.applyAbsoluteDomainOnFreeze(
            player,
            enemy,
            abilityId,
            freeze.freezeDurationMs,
          );
        }
        this.damageEnemy(
          enemy,
          freeze.freezeDamage *
            player.stats.damageMultiplier *
            this.getPlayerAbilityDamageMultiplier(player) *
            (1 + this.teamTechLevels.war_chant * 0.03),
          player.id,
          { kind: "ability", abilityId: "freeze_dont_move" },
        );
        return;
      }
      enemy.slowRatio = stacked;
    } else {
      enemy.slowRatio = Math.max(enemy.slowRatio, ratio);
    }
    enemy.slowUntilMs = Math.max(
      enemy.slowUntilMs,
      this.elapsedMs + durationMs,
    );
  }

  private applyTierFourSynergyOnHit(
    player: SimPlayer,
    enemy: SimEnemy,
    abilityId: string,
    attribution: DamageAttribution,
  ): void {
    const thunder = this.getActiveSynergyMechanic(
      player,
      "thunderstorm_counter",
    );
    if (thunder?.abilityIds.includes(abilityId)) {
      const key = "synergy:thunderstorm:hits";
      const indulgence = this.getActiveSynergyMechanic(
        player,
        "thunder_god_indulgence",
      );
      const hits =
        (player.synergyCounters.get(key) ?? 0) +
        (indulgence?.abilityIds.includes(abilityId)
          ? indulgence.countMultiplier
          : 1);
      const required = indulgence?.triggerCount ?? thunder.hitsRequired;
      if (hits >= required) {
        player.synergyCounters.set(key, 0);
        const hitIds = this.applyAreaDamage(
          player,
          enemy.x,
          enemy.y,
          thunder.radius,
          (indulgence?.triggeredDamage ?? thunder.damage) *
            player.stats.damageMultiplier *
            this.getPlayerAbilityDamageMultiplier(player) *
            (1 + this.teamTechLevels.war_chant * 0.03),
          { kind: "ability", abilityId: "thunderstorm_alert" },
        );
        if (hitIds.length > 0) {
          this.events.push({
            type: "shot",
            playerId: player.id,
            targetIds: hitIds,
            abilityId: "thunderstorm_alert",
          });
        }
      } else {
        player.synergyCounters.set(key, hits);
      }
    }

    const hotFloor = this.getActiveSynergyMechanic(player, "hot_floor");
    if (hotFloor?.abilityIds.includes(abilityId)) {
      for (const [sourceId, expiresAtMs] of enemy.recentAbilityHits) {
        if (expiresAtMs <= this.elapsedMs) enemy.recentAbilityHits.delete(sourceId);
      }
      enemy.recentAbilityHits.set(
        abilityId,
        this.elapsedMs + hotFloor.triggerWindowMs,
      );
      if (enemy.recentAbilityHits.size >= 2) {
        enemy.hotFloorEmpoweredUntilMs =
          this.elapsedMs + hotFloor.empoweredDurationMs;
      }
    }

    const spin = this.getActiveSynergyMechanic(player, "spin_it");
    if (
      spin?.gearAbilityId === abilityId &&
      attribution.variant === "native_double"
    ) {
      enemy.armorReductionStacks = enemy.armorReductionStacks.filter(
        (entry) => entry.sourceId !== "spin_it",
      );
      enemy.armorReductionStacks.push({
        sourceId: "spin_it",
        amount: spin.armorReduction,
        expiresAtMs: this.elapsedMs + spin.armorDurationMs,
      });
    }
  }

  private getEnemyArmorReduction(enemy: SimEnemy): number {
    enemy.armorReductionStacks = enemy.armorReductionStacks.filter(
      (entry) => entry.expiresAtMs > this.elapsedMs,
    );
    return enemy.armorReductionStacks.reduce(
      (sum, entry) => sum + entry.amount,
      0,
    );
  }

  private displaceEnemy(enemy: SimEnemy, origin: Vec2, distance: number): void {
    const dx = enemy.x - origin.x;
    const dy = enemy.y - origin.y;
    const length = Math.max(0.001, Math.hypot(dx, dy));
    enemy.x = clamp(enemy.x + (dx / length) * distance, 30, WORLD_WIDTH - 30);
    enemy.y = clamp(enemy.y + (dy / length) * distance, 30, WORLD_HEIGHT - 30);
  }

  private applyCombatStun(
    enemy: SimEnemy,
    durationMs = 0,
    stunBosses = true,
  ): void {
    if (
      durationMs <= 0 ||
      (!stunBosses && getEnemy(enemy.definitionId).rank === "boss")
    ) {
      return;
    }
    enemy.stunnedUntilMs = Math.max(
      enemy.stunnedUntilMs,
      this.elapsedMs + durationMs,
    );
  }

  private applyAbsoluteDomainOnFreeze(
    player: SimPlayer,
    enemy: SimEnemy,
    authoredAbilityId: string | undefined,
    freezeDurationMs: number,
  ): void {
    if (!authoredAbilityId || freezeDurationMs <= 0) return;
    const mechanic = this.getActiveSynergyMechanic(player, "absolute_domain");
    const abilityId = baseAbilityId(authoredAbilityId);
    if (!mechanic?.sourceAbilityIds.includes(abilityId)) return;
    if (getEnemy(enemy.definitionId).rank === "boss") {
      enemy.slowRatio = Math.max(enemy.slowRatio, mechanic.bossSlowRatio);
      enemy.slowUntilMs = Math.max(
        enemy.slowUntilMs,
        this.elapsedMs + mechanic.bossSlowDurationMs,
      );
      return;
    }
    const candidates = [...this.enemies.values()].filter(
      (candidate) =>
        candidate.id !== enemy.id &&
        candidate.spaceId === enemy.spaceId &&
        getEnemy(candidate.definitionId).rank !== "boss" &&
        Math.hypot(candidate.x - enemy.x, candidate.y - enemy.y) <=
          mechanic.chainRadius,
    );
    const chained = candidates.length > 0
      ? this.random.pick(candidates)
      : undefined;
    if (!chained) return;
    chained.stunnedUntilMs = Math.max(
      chained.stunnedUntilMs,
      this.elapsedMs + mechanic.freezeDurationMs,
    );
  }

  private updateEconomyAbilities(player: SimPlayer): void {
    for (const abilityId of player.modules) {
      if (!abilityId) {
        continue;
      }
      const economy = getModule(abilityId).economy;
      if (!economy || economy.trigger !== "periodic") {
        continue;
      }
      const nextAt = player.economyCooldowns.get(abilityId) ?? 0;
      if (this.elapsedMs < nextAt) {
        continue;
      }
      if (!this.economyConditionMet(player, economy.condition)) {
        player.economyCooldowns.set(abilityId, this.elapsedMs + 2_000);
        continue;
      }
      player.economyCooldowns.set(
        abilityId,
        this.elapsedMs + (economy.intervalMs ?? 10_000),
      );
      this.grantEconomy(player, economy.gold ?? 0, economy.experience ?? 0, economy.crystalChance);
    }
  }

  private triggerLabor(
    player: SimPlayer,
    event:
      | "move_distance"
      | "attack"
      | "repair"
      | "excavate",
    amount = 1,
  ): void {
    if (event === "repair") {
      for (const entry of this.getLaborSlots(player, "repair_payout")) {
        if (entry.labor.kind !== "repair_payout") continue;
        entry.state.laborDistanceRemainder += amount;
        const payouts = Math.floor(
          (entry.state.laborDistanceRemainder + 1e-9) /
            entry.labor.repairPerPayout,
        );
        if (payouts > 0) {
          entry.state.laborDistanceRemainder -=
            payouts * entry.labor.repairPerPayout;
          this.grantFinanceResource(
            player,
            "gold",
            payouts * entry.labor.goldPerPayout,
          );
        }
      }
      this.recordTeamRepairProgress(player, amount);
    }
    for (const abilityId of player.modules) {
      if (!abilityId) {
        continue;
      }
      const economy = getModule(abilityId).economy;
      if (!economy || economy.trigger !== event) {
        continue;
      }
      if (event === "move_distance") {
        const progressKey = `${abilityId}:distance`;
        const progress = (player.growthCounters.get(progressKey) ?? 0) + amount;
        const needed = economy.distance ?? 180;
        if (progress < needed) {
          player.growthCounters.set(progressKey, progress);
          continue;
        }
        player.growthCounters.set(progressKey, progress % needed);
      } else {
        const nextAt = player.economyCooldowns.get(abilityId) ?? 0;
        if (this.elapsedMs < nextAt) {
          continue;
        }
        player.economyCooldowns.set(
          abilityId,
          this.elapsedMs + (economy.intervalMs ?? 1_000),
        );
      }
      this.grantEconomy(player, economy.gold ?? 0, economy.experience ?? 0, economy.crystalChance);
    }
  }

  private recordTeamRepairProgress(source: SimPlayer, amount: number): void {
    for (const beneficiary of this.players.values()) {
      for (const entry of this.getLaborSlots(beneficiary, "team_repair_payout")) {
        if (entry.labor.kind !== "team_repair_payout") continue;
        if (
          beneficiary.spaceId !== source.spaceId ||
          Math.hypot(beneficiary.x - source.x, beneficiary.y - source.y) >
            entry.labor.allyRadius
        ) {
          continue;
        }
        const credited =
          amount *
          (beneficiary.id === source.id
            ? entry.labor.ownRepairMultiplier
            : 1);
        entry.state.laborDistanceRemainder += credited;
        const payouts = Math.floor(
          (entry.state.laborDistanceRemainder + 1e-9) /
            entry.labor.repairPerPayout,
        );
        if (payouts <= 0) continue;
        entry.state.laborDistanceRemainder -=
          payouts * entry.labor.repairPerPayout;
        this.grantFinanceResource(
          beneficiary,
          "gold",
          payouts * entry.labor.goldPerPayout,
        );
      }
    }
  }

  private hasLaborKind(
    player: SimPlayer,
    kind: LaborDefinition["kind"],
  ): boolean {
    return player.modules.some((moduleId) =>
      moduleId ? getModule(moduleId).labor?.kind === kind : false,
    );
  }

  private getLaborSlots(
    player: SimPlayer,
    kind: LaborDefinition["kind"],
  ) {
    return player.modules.flatMap((moduleId, slot) => {
      if (!moduleId) return [];
      const module = getModule(moduleId);
      if (module.labor?.kind !== kind) return [];
      const state = this.getModuleSlotState(player, slot);
      return state ? [{ slot, module, labor: module.labor, state }] : [];
    });
  }

  private resetInterruptedLabor(player: SimPlayer, downed: boolean): void {
    const circle = this.getLaborSlots(player, "circle_traversal")[0]?.labor;
    if (circle?.kind === "circle_traversal") {
      cancelCircleTraversal(
        player.laborCircleTracker,
        this.elapsedMs,
        circle.sampleIntervalMs,
      );
    }
    for (const entry of this.getLaborSlots(player, "stationary_work")) {
      entry.state.laborStationaryRemainderMs = 0;
    }
    player.laborStationaryAchievementMs = 0;
    player.laborRouteArmed = undefined;
    player.laborRouteQualifiedMovement = false;
    if (downed) {
      player.laborRouteInsideKey = undefined;
    }
  }

  private updateLaborBehavior(
    player: SimPlayer,
    deltaMs: number,
    actualMovement: number,
    normallyMoved: boolean,
    forcedBeforeMovement: boolean,
  ): void {
    if (
      !player.modules.some((moduleId) =>
        moduleId ? Boolean(getModule(moduleId).labor) : false,
      )
    ) {
      return;
    }
    const distanceSlots = this.getLaborSlots(player, "movement_distance");
    if (normallyMoved && distanceSlots.length > 0) {
      player.laborMovementDistance += actualMovement;
      for (const entry of distanceSlots) {
        const labor = entry.labor;
        if (labor.kind !== "movement_distance") continue;
        entry.state.laborDistanceRemainder += actualMovement;
        const payouts = Math.floor(
          (entry.state.laborDistanceRemainder + 1e-9) /
            labor.payoutDistance,
        );
        if (payouts > 0) {
          entry.state.laborDistanceRemainder -= payouts * labor.payoutDistance;
          this.grantFinanceResource(
            player,
            "gold",
            payouts * labor.goldPerPayout,
          );
        }
      }
      const labor = distanceSlots[0].labor;
      if (
        labor.kind === "movement_distance" &&
        player.laborMovementDistance + 1e-9 >= labor.achievementDistance
      ) {
        player.laborMovementDistance = Math.max(
          player.laborMovementDistance,
          labor.achievementDistance,
        );
        this.grantPermanentHiddenEffect(player, labor.hiddenEffectId);
      }
    }

    const circleSlots = this.getLaborSlots(player, "circle_traversal");
    const circleLabor = circleSlots[0]?.labor;
    if (circleLabor?.kind === "circle_traversal") {
      if (forcedBeforeMovement) {
        cancelCircleTraversal(
          player.laborCircleTracker,
          this.elapsedMs,
          circleLabor.sampleIntervalMs,
        );
      }
      const result = updateCircleTraversal(
        player.laborCircleTracker,
        circleLabor,
        { x: player.x, y: player.y },
        this.elapsedMs,
        normallyMoved,
      );
      if (result.completed) {
        for (const entry of circleSlots) {
          if (entry.labor.kind === "circle_traversal") {
            this.grantFinanceResource(
              player,
              "gold",
              entry.labor.goldPerCompletion,
            );
          }
        }
        player.laborCircleCount += 1;
        if (player.laborCircleCount >= circleLabor.achievementCount) {
          this.grantPermanentHiddenEffect(player, circleLabor.hiddenEffectId);
        }
      }
    }

    const stationarySlots = this.getLaborSlots(player, "stationary_work");
    if (stationarySlots.length === 0) {
      player.laborStationaryAchievementMs = 0;
    } else if (actualMovement > 0.000_1 || forcedBeforeMovement) {
      player.laborStationaryAchievementMs = 0;
      for (const entry of stationarySlots) {
        entry.state.laborStationaryRemainderMs = 0;
        if (!normallyMoved || entry.labor.kind !== "stationary_work") continue;
        entry.state.laborMovingRemainderMs += deltaMs;
        while (
          entry.state.laborMovingRemainderMs + 1e-9 >=
          entry.labor.movingDrainIntervalMs
        ) {
          entry.state.laborMovingRemainderMs -=
            entry.labor.movingDrainIntervalMs;
          player.scrap = Math.max(
            0,
            player.scrap - entry.labor.movingDrainGold,
          );
        }
      }
    } else {
      player.laborStationaryAchievementMs += deltaMs;
      for (const entry of stationarySlots) {
        if (entry.labor.kind !== "stationary_work") continue;
        entry.state.laborMovingRemainderMs = 0;
        entry.state.laborStationaryRemainderMs += deltaMs;
        while (
          entry.state.laborStationaryRemainderMs + 1e-9 >=
          entry.labor.payoutIntervalMs
        ) {
          entry.state.laborStationaryRemainderMs -=
            entry.labor.payoutIntervalMs;
          this.grantFinanceResource(
            player,
            "gold",
            entry.labor.goldPerPayout,
          );
        }
      }
      const labor = stationarySlots[0].labor;
      if (
        labor.kind === "stationary_work" &&
        player.laborStationaryAchievementMs + 1e-9 >=
          labor.achievementStationaryMs
      ) {
        player.laborStationaryAchievementMs = labor.achievementStationaryMs;
        this.grantPermanentHiddenEffect(player, labor.hiddenEffectId);
      }
    }

    this.updateLaborRoute(player, normallyMoved, forcedBeforeMovement);
    this.updateGatePatrol(player, normallyMoved, forcedBeforeMovement);
  }

  private updateLaborRoute(
    player: SimPlayer,
    normallyMoved: boolean,
    forcedBeforeMovement: boolean,
  ): void {
    const routeSlots = this.getLaborSlots(player, "route_leg");
    const route = routeSlots[0]?.labor;
    if (route?.kind !== "route_leg") {
      player.laborRouteArmed = undefined;
      player.laborRouteInsideKey = undefined;
      player.laborRouteQualifiedMovement = false;
      return;
    }
    if (forcedBeforeMovement) {
      player.laborRouteArmed = undefined;
      player.laborRouteQualifiedMovement = false;
      player.laborRouteInsideKey = this.getLaborRouteEndpoint(
        player,
        route.endpointRadius,
      )?.key;
      return;
    }
    if (normallyMoved && player.laborRouteArmed) {
      player.laborRouteQualifiedMovement = true;
    }
    const endpoint = this.getLaborRouteEndpoint(player, route.endpointRadius);
    if (!endpoint) {
      player.laborRouteInsideKey = undefined;
      return;
    }
    if (endpoint.key === player.laborRouteInsideKey) {
      return;
    }
    const previousEndpointKey = player.laborRouteInsideKey;
    player.laborRouteInsideKey = endpoint.key;
    if (!player.laborRouteArmed) {
      player.laborRouteArmed = endpoint.type;
      player.laborRouteQualifiedMovement = false;
      return;
    }
    if (
      player.laborRouteArmed === endpoint.type ||
      !player.laborRouteQualifiedMovement
    ) {
      return;
    }
    const handoff = this.getActiveSynergyMechanic(
      player,
      "night_shift_handoff",
    );
    for (const entry of routeSlots) {
      if (entry.labor.kind === "route_leg") {
        this.grantFinanceResource(
          player,
          "gold",
          entry.labor.goldPerLeg * (handoff?.payoutMultiplier ?? 1),
        );
      }
    }
    const associatedGateKey = endpoint.type === "outer_gate"
      ? endpoint.key
      : previousEndpointKey?.startsWith("outer_gate:")
        ? previousEndpointKey
        : undefined;
    if (handoff && associatedGateKey) {
      if (
        player.laborTransportLastGateKey &&
        player.laborTransportLastGateKey !== associatedGateKey
      ) {
        for (const entry of this.getLaborSlots(player, "gate_to_gate_patrol")) {
          if (entry.labor.kind === "gate_to_gate_patrol") {
            this.grantFinanceResource(
              player,
              "gold",
              entry.labor.goldPerPatrol * handoff.payoutMultiplier,
            );
          }
        }
      }
      player.laborTransportLastGateKey = associatedGateKey;
    }
    player.laborRouteLegs += 1;
    player.laborRouteArmed = endpoint.type;
    player.laborRouteQualifiedMovement = false;
    if (player.laborRouteLegs >= route.achievementLegs) {
      this.grantPermanentHiddenEffect(player, route.hiddenEffectId);
    }
  }

  private updateGatePatrol(
    player: SimPlayer,
    normallyMoved: boolean,
    forcedBeforeMovement: boolean,
  ): void {
    const slots = this.getLaborSlots(player, "gate_to_gate_patrol");
    const labor = slots[0]?.labor;
    if (labor?.kind !== "gate_to_gate_patrol") {
      player.laborPatrolLastGateKey = undefined;
      return;
    }
    if (forcedBeforeMovement || !normallyMoved) return;
    const endpoint = this.getLaborRouteEndpoint(player, labor.endpointRadius);
    if (!endpoint || endpoint.type !== "outer_gate") return;
    if (!player.laborPatrolLastGateKey) {
      player.laborPatrolLastGateKey = endpoint.key;
      return;
    }
    if (player.laborPatrolLastGateKey === endpoint.key) return;
    const handoff = this.getActiveSynergyMechanic(
      player,
      "night_shift_handoff",
    );
    for (const entry of slots) {
      if (entry.labor.kind === "gate_to_gate_patrol") {
        this.grantFinanceResource(
          player,
          "gold",
          entry.labor.goldPerPatrol * (handoff?.payoutMultiplier ?? 1),
        );
      }
    }
    player.laborPatrolLastGateKey = endpoint.key;
  }

  private getLaborRouteEndpoint(
    player: SimPlayer,
    radius: number,
  ): { key: string; type: "core" | "outer_gate" } | undefined {
    if (Math.hypot(player.x - this.core.x, player.y - this.core.y) <= radius) {
      return { key: "core", type: "core" };
    }
    const grouped = new Map<string, Vec2[]>();
    for (const gate of this.defenses) {
      if (gate.kind !== "gate" || gate.ring !== 3) continue;
      const dx = gate.x - this.core.x;
      const dy = gate.y - this.core.y;
      const key = Math.abs(dx) > Math.abs(dy)
        ? dx >= 0 ? "east" : "west"
        : dy >= 0 ? "south" : "north";
      const points = grouped.get(key) ?? [];
      points.push({ x: gate.x, y: gate.y });
      grouped.set(key, points);
    }
    for (const [key, points] of grouped) {
      const center = points.reduce(
        (sum, point) => ({ x: sum.x + point.x, y: sum.y + point.y }),
        { x: 0, y: 0 },
      );
      center.x /= points.length;
      center.y /= points.length;
      if (Math.hypot(player.x - center.x, player.y - center.y) <= radius) {
        return { key: `outer_gate:${key}`, type: "outer_gate" };
      }
    }
    return undefined;
  }

  private grantEconomy(
    player: SimPlayer,
    gold: number,
    experience: number,
    crystalChance = 0,
  ): void {
    const earnedGold = Math.round(gold * player.stats.goldMultiplier);
    player.scrap += earnedGold;
    player.contribution.scrapEarned += earnedGold;
    if (experience > 0) {
      this.addExperience(player, experience);
    }
    if (crystalChance > 0 && this.random.next() < crystalChance) {
      player.core += 1;
    }
  }

  private economyConditionMet(
    player: SimPlayer,
    condition: "alone" | "near_ally" | "mana_above_half" | undefined,
  ): boolean {
    if (!condition) {
      return true;
    }
    if (condition === "mana_above_half") {
      return player.mana >= player.stats.manaMax * 0.5;
    }
    const nearAlly = [...this.players.values()].some(
      (other) =>
        other.id !== player.id &&
        Math.hypot(other.x - player.x, other.y - player.y) <= 420,
    );
    return condition === "near_ally" ? nearAlly : !nearAlly;
  }

  private updateHealingAbilities(player: SimPlayer): void {
    if (this.elapsedMs < player.nextHealAtMs) {
      return;
    }
    const abilities = player.modules.flatMap((id, slot) => {
      if (!id) return [];
      const ability = getModule(id);
      return ability.healing ? [{ ability, slot }] : [];
    });
    if (abilities.length === 0) {
      return;
    }
    for (const { ability, slot } of abilities) {
      const healing = ability.healing!;
      const key = `${ability.id}:slot:${slot}:heal`;
      const nextAt = player.abilityCooldowns.get(key) ?? 0;
      if (this.elapsedMs < nextAt) {
        continue;
      }
      player.abilityCooldowns.set(key, this.elapsedMs + healing.intervalMs);
      const castExecutions = this.registerDiscreteSkillCast(
        player,
        `slot:${slot}`,
      );
      for (let cast = 0; cast < castExecutions; cast += 1) {
      if (healing.target === "core") {
        const before = this.core.health;
        this.core.health = Math.min(this.core.maxHealth, this.core.health + healing.amount);
        player.contribution.healingDone += this.core.health - before;
      } else {
        const bitter = this.getActiveSynergyMechanic(player, "bitter_medicine");
        const canTargetGuards =
          healing.target === "lowest_ally" ||
          Boolean(bitter?.healingAbilityIds.includes(ability.id));
        const range = healing.range ?? (canTargetGuards ? 400 : 500);
        const playerTargets = [...this.players.values()].filter(
          (candidate) =>
            candidate.status === "active" &&
            Math.hypot(candidate.x - player.x, candidate.y - player.y) <= range,
        );
        const guardTargets = canTargetGuards
          ? [...this.abilityEntities.values()].filter(
              (candidate) =>
                candidate.ownerId === player.id &&
                candidate.kind === "guard" &&
                candidate.health > 0 &&
                Math.hypot(candidate.x - player.x, candidate.y - player.y) <= range,
            )
          : [];
        const target = healing.target === "self" && !canTargetGuards
          ? player
          : [...playerTargets, ...guardTargets].sort((left, right) => {
              const leftRatio = "stats" in left
                ? left.health / left.stats.maxHealth
                : left.health / left.maxHealth;
              const rightRatio = "stats" in right
                ? right.health / right.stats.maxHealth
                : right.health / right.maxHealth;
              return leftRatio - rightRatio;
            })[0];
        if (target) {
          this.applyHealingWithOptionalShield(
            player,
            target,
            healing.amount,
            ability.id,
          );
        }
      }
      }
    }
    player.nextHealAtMs = this.elapsedMs + 100;
  }

  private applyHealingWithOptionalShield(
    healer: SimPlayer,
    target: SimPlayer | SimAbilityEntity,
    amount: number,
    abilityId: string,
  ): void {
    const maxHealth = "stats" in target
      ? target.stats.maxHealth
      : target.maxHealth;
    const before = target.health;
    target.health = Math.min(maxHealth, target.health + amount);
    const effective = target.health - before;
    healer.contribution.healingDone += effective;
    const bitter = this.getActiveSynergyMechanic(healer, "bitter_medicine");
    if (!bitter?.healingAbilityIds.includes(abilityId)) return;
    const overheal = Math.max(0, amount - effective);
    if (overheal <= 0) return;
    target.temporaryShield = Math.min(
      maxHealth * bitter.shieldMaxHealthRatio,
      target.temporaryShield + overheal,
    );
    target.temporaryShieldUntilMs =
      this.elapsedMs + bitter.shieldDurationMs;
  }

  private updateSummoningAbilities(player: SimPlayer): void {
    for (const [slot, abilityId] of player.modules.entries()) {
      if (!abilityId) {
        continue;
      }
      const ability = getModule(abilityId);
      const companion = ability.companion;
      if (!companion || companion.kind !== "summon") {
        continue;
      }
      const earthMechanic = this.getEarthSpiritSummonMechanic(ability);
      const cooldownKey = slot === 0 ? abilityId : `${abilityId}:slot:${slot}`;
      const tuning = getAbilityTuning(player.modules, abilityId);
      const intervalMs =
        (companion.spawnIntervalMs ?? 10_000) *
        tuning.companionSpawnIntervalMultiplier;
      const storedNextAt = player.companionCooldowns.get(cooldownKey);
      if (earthMechanic && storedNextAt === undefined) {
        player.companionCooldowns.set(
          cooldownKey,
          this.elapsedMs + intervalMs,
        );
        continue;
      }
      const nextAt = storedNextAt ?? 0;
      if (this.elapsedMs < nextAt) {
        continue;
      }
      const activeSummons = [...this.abilityEntities.values()].filter(
        (entity) =>
          entity.ownerId === player.id &&
          entity.kind === "summon" &&
          !entity.undeadUnitId &&
          !entity.earthSpirit &&
          entity.health > 0,
      ).length;
      const desiredSpawnCount = companion.count + tuning.companionCountAdd;
      const spawnCount = earthMechanic
        ? desiredSpawnCount
        : companion.ignoreSummonCap
          ? desiredSpawnCount
        : Math.min(
            desiredSpawnCount,
            Math.max(0, player.stats.summonCap - activeSummons),
          );
      const manaCost =
        (companion.manaCost ?? 0) * tuning.manaCostMultiplier;
      if (spawnCount <= 0 || player.mana < manaCost) {
        continue;
      }
      player.mana -= manaCost;
      player.companionCooldowns.set(
        cooldownKey,
        this.elapsedMs + intervalMs,
      );
      const castExecutions = this.registerDiscreteSkillCast(
        player,
        `slot:${slot}`,
      );
      for (let cast = 0; cast < castExecutions; cast += 1) {
      for (let index = 0; index < spawnCount; index += 1) {
        this.spawnAbilityEntity(player, ability, index, spawnCount, 1, slot);
      }
      this.maybeSpawnSummonCommander(player, ability, slot);
      if (
        ability.special === "summon_echoes" ||
        this.hasSynergySpecial(player, "summon_echoes")
      ) {
        const echo = { ...ability, id: `${ability.id}:echo` };
        this.spawnAbilityEntity(
          player,
          echo,
          spawnCount,
          spawnCount + 1,
          0.55,
          slot,
        );
      }
      }
    }
  }

  private ensurePersistentCompanions(
    player: SimPlayer,
    ability: ModuleDefinition,
    sourceSlot?: number,
  ): void {
    const companion = ability.companion;
    if (
      !companion ||
      companion.kind === "summon" ||
      this.isGuardAbilityConsumed(player, ability.id)
    ) {
      return;
    }
    const total =
      companion.count +
      getAbilityTuning(player.modules, ability.id).companionCountAdd;
    for (let index = 0; index < total; index += 1) {
      this.spawnAbilityEntity(player, ability, index, total, 1, sourceSlot);
    }
  }

  private refreshPersistentCompanions(player: SimPlayer): void {
    for (const entity of [...this.abilityEntities.values()]) {
      if (
        entity.ownerId === player.id &&
        entity.kind !== "summon" &&
        entity.kind !== "field"
      ) {
        this.abilityEntities.delete(entity.id);
      }
    }
    for (const [slot, abilityId] of player.modules.entries()) {
      if (abilityId) {
        this.ensurePersistentCompanions(player, getModule(abilityId), slot);
      }
    }
  }

  private spawnAbilityEntity(
    player: SimPlayer,
    ability: ModuleDefinition,
    index: number,
    total: number,
    scale = 1,
    sourceSlot?: number,
  ): void {
    const companion = ability.companion;
    if (!companion) {
      return;
    }
    const earthMechanic = this.getEarthSpiritSummonMechanic(ability);
    if (earthMechanic) {
      const angle = this.random.next() * Math.PI * 2;
      const distance =
        Math.sqrt(this.random.next()) * earthMechanic.spawnRadius;
      const position = {
        x: clamp(
          player.x + Math.cos(angle) * distance,
          30,
          WORLD_WIDTH - 30,
        ),
        y: clamp(
          player.y + Math.sin(angle) * distance,
          30,
          WORLD_HEIGHT - 30,
        ),
      };
      const aggregation = this.getActiveSynergyMechanic(
        player,
        "earth_spirit_aggregation",
      );
      this.spawnEarthSpiritEntity(
        player,
        ability,
        aggregation ? 1 : 0,
        position,
        position,
      );
      return;
    }
    const offset = (index / Math.max(1, total)) * Math.PI * 2 + this.random.next() * 0.4;
    const baseAbilityId = ability.id.includes(":")
      ? ability.id.split(":")[0]
      : ability.id;
    const tuning = getAbilityTuning(player.modules, baseAbilityId);
    const ethereal = companion.ethereal ?? false;
    const guardProfile =
      companion.kind === "guard"
        ? this.getGuardRuntimeProfile(player, ability)
        : undefined;
    const guardSynergy = this.getGenericGuardSynergyProfile(
      player,
      baseAbilityId,
    );
    const paper = this.getActiveSynergyMechanic(player, "paper_units");
    const paperAttackSpeed = paper?.abilityIds.includes(baseAbilityId)
      ? paper.attackSpeedBonus
      : 0;
    const undeadGuard = this.getActiveSynergyMechanic(player, "undead_guard");
    const undeadGuardMultiplier =
      undeadGuard?.targetAbilityId === baseAbilityId
        ? (this.getLichMechanic(player)?.undeadHealthDamageMultiplier ?? 1)
        : 1;
    const maxHealth =
      ethereal
        ? Number.POSITIVE_INFINITY
        : (guardProfile?.health ??
            (companion.health ?? Number.POSITIVE_INFINITY) +
            guardSynergy.healthAdd) *
          scale *
          tuning.companionHealthMultiplier *
          undeadGuardMultiplier;
    const entity: SimAbilityEntity = {
      id: this.nextAbilityEntityId++,
      ownerId: player.id,
      abilityId: ability.id,
      sourceSlot,
      kind: companion.kind,
      presentationId: guardProfile?.presentationId ?? companion.presentationId,
      spaceId: player.spaceId,
      x: player.x + Math.cos(offset) * 45,
      y: player.y + Math.sin(offset) * 45,
      health: maxHealth,
      maxHealth,
      armor:
        ethereal
          ? 0
          : (guardProfile?.armor ?? companion.armor ?? 0) +
            guardSynergy.armorAdd +
            this.teamTechLevels.familiar_ward * 3,
      damage:
        ethereal
          ? 0
          : ((guardProfile?.damage ?? companion.damage ?? 0) +
              guardSynergy.damageAdd) *
            scale *
            tuning.damageMultiplier *
            undeadGuardMultiplier,
      intervalMs:
        (guardProfile?.intervalMs ?? companion.intervalMs ?? 1_000) *
        tuning.intervalMultiplier /
        Math.max(0.05, 1 + guardSynergy.attackSpeedBonus + paperAttackSpeed),
      range:
        (guardProfile?.range ?? companion.range ?? 0) *
        tuning.rangeMultiplier,
      moveSpeed: guardProfile?.moveSpeed ?? companion.moveSpeed,
      orbitRadius: companion.orbitRadius,
      orbitOffset: offset,
      nextAttackAtMs: this.elapsedMs + this.random.integer(0, 400),
      attackCount: 0,
      deathProcessed: false,
      nextPeriodicHealAtMs: this.elapsedMs,
      temporaryShield: 0,
      temporaryShieldUntilMs: 0,
      nextIncomeAtMs: this.elapsedMs + (companion.incomeIntervalMs ?? 10_000),
      incomeGold: companion.incomeGold ?? 0,
      respawnMs:
        guardSynergy.respawnMs ??
        guardProfile?.respawnMs ??
        companion.respawnMs ??
        0,
      respawnAtMs: 0,
      auraMultiplier: 1,
      ethereal,
      nextHuntAtMs:
        this.elapsedMs +
        (this.getPastureHuntMechanic(player)?.intervalMs ?? 500),
      guardForm: guardProfile?.form,
      guardAttackCount: guardProfile ? 0 : undefined,
      guardHasFlame: guardProfile?.hasFlame,
      guardHasCritical: guardProfile?.hasCritical,
      guardHasDash: guardProfile?.hasDash,
      guardAbilityDamageMultiplier: guardProfile?.abilityDamageMultiplier,
      guardAbilityCooldowns:
        guardProfile ||
        ability.mechanics?.some(
          (entry) =>
            entry.kind === "guard_charge" ||
            entry.kind === "guard_periodic_strike" ||
            entry.kind === "summon_damage_aura",
        )
          ? new Map()
          : undefined,
      followerAbilityCooldowns:
        companion.kind === "follower" && ethereal ? new Map() : undefined,
      movement: companion.movement
        ? {
            ...companion.movement,
            returning: false,
            target: this.randomPastureTarget(
              player,
              companion.movement.kind === "player_centered_wander"
                ? companion.movement.wanderRadius
                : companion.movement.radius,
            ),
          }
        : undefined,
    };
    this.abilityEntities.set(entity.id, entity);
    this.events.push({
      type: "abilityEntities",
      entities: [this.toWireAbilityEntity(entity)],
    });
    player.contribution.companionsSummoned += 1;
  }

  private maybeSpawnSummonCommander(
    player: SimPlayer,
    ability: ModuleDefinition,
    sourceSlot: number,
  ): void {
    const mechanic = ability.mechanics?.find(
      (entry) => entry.kind === "every_nth_summon_commander",
    );
    if (!mechanic || mechanic.kind !== "every_nth_summon_commander") return;
    const key = `summon_commander:${ability.id}:slot:${sourceSlot}`;
    const count = (player.synergyCounters.get(key) ?? 0) + 1;
    player.synergyCounters.set(key, count % mechanic.every);
    if (count % mechanic.every !== 0) return;
    const entity: SimAbilityEntity = {
      id: this.nextAbilityEntityId++,
      ownerId: player.id,
      abilityId: `${ability.id}:commander`,
      sourceSlot,
      kind: "summon",
      presentationId: mechanic.presentationId,
      spaceId: player.spaceId,
      x: player.x,
      y: player.y,
      health: mechanic.health,
      maxHealth: mechanic.health,
      armor: mechanic.armor + this.teamTechLevels.familiar_ward * 3,
      damage: mechanic.damage,
      intervalMs: mechanic.intervalMs,
      range: mechanic.range,
      moveSpeed: PLAYER_BASE_MOVE_SPEED * mechanic.moveSpeedBaseRatio,
      orbitRadius: 100,
      orbitOffset: this.random.next() * Math.PI * 2,
      nextAttackAtMs: this.elapsedMs + mechanic.intervalMs,
      attackCount: 0,
      deathProcessed: false,
      nextPeriodicHealAtMs: this.elapsedMs,
      temporaryShield: 0,
      temporaryShieldUntilMs: 0,
      nextIncomeAtMs: Number.POSITIVE_INFINITY,
      incomeGold: 0,
      respawnMs: 0,
      respawnAtMs: 0,
      auraMultiplier: 1,
      ethereal: false,
      nextHuntAtMs: Number.POSITIVE_INFINITY,
    };
    this.abilityEntities.set(entity.id, entity);
    player.contribution.companionsSummoned += 1;
    this.events.push({
      type: "abilityEntities",
      entities: [this.toWireAbilityEntity(entity)],
    });
  }

  private getEarthSpiritSummonMechanic(
    ability: ModuleDefinition,
  ):
    | Extract<AbilityMechanicDefinition, { kind: "earth_spirit_summon" }>
    | undefined {
    const mechanic = ability.mechanics?.find(
      (entry) => entry.kind === "earth_spirit_summon",
    );
    return mechanic?.kind === "earth_spirit_summon"
      ? mechanic
      : undefined;
  }

  private initializeEarthSpiritSummonCooldown(
    player: SimPlayer,
    slot: number,
    abilityId: string,
  ): void {
    const ability = getModule(abilityId);
    const companion = ability.companion;
    if (!companion || !this.getEarthSpiritSummonMechanic(ability)) {
      return;
    }
    const tuning = getAbilityTuning(player.modules, abilityId);
    player.companionCooldowns.set(
      slot === 0 ? abilityId : `${abilityId}:slot:${slot}`,
      this.elapsedMs +
        (companion.spawnIntervalMs ?? 10_000) *
          tuning.companionSpawnIntervalMultiplier,
    );
  }

  private resetEarthSpiritSummonCooldowns(player: SimPlayer): void {
    for (const [slot, abilityId] of player.modules.entries()) {
      if (abilityId) {
        this.initializeEarthSpiritSummonCooldown(player, slot, abilityId);
      }
    }
  }

  private spawnEarthSpiritEntity(
    player: SimPlayer,
    ability: ModuleDefinition,
    star: EarthSpiritStar,
    position: Vec2,
    activityCenter: Vec2,
  ): SimAbilityEntity {
    const companion = ability.companion;
    const summonMechanic = this.getEarthSpiritSummonMechanic(ability);
    if (!companion || !summonMechanic) {
      throw new Error(`技能 ${ability.id} 缺少土灵召唤配置。`);
    }
    const tuning = getAbilityTuning(player.modules, ability.id);
    const maxHealth =
      (companion.health ?? 1) * tuning.companionHealthMultiplier;
    const entity: SimAbilityEntity = {
      id: this.nextAbilityEntityId++,
      ownerId: player.id,
      abilityId: ability.id,
      kind: "summon",
      presentationId: companion.presentationId,
      spaceId: player.spaceId,
      x: position.x,
      y: position.y,
      health: maxHealth,
      maxHealth,
      armor: (companion.armor ?? 0) + this.teamTechLevels.familiar_ward * 3,
      damage: (companion.damage ?? 0) * tuning.damageMultiplier,
      intervalMs:
        (companion.intervalMs ?? 1_000) * tuning.intervalMultiplier,
      range: (companion.range ?? 100) * tuning.rangeMultiplier,
      moveSpeed: companion.moveSpeed,
      orbitRadius: 0,
      orbitOffset: 0,
      nextAttackAtMs: this.elapsedMs + (companion.intervalMs ?? 1_000),
      attackCount: 0,
      deathProcessed: false,
      nextPeriodicHealAtMs: this.elapsedMs,
      temporaryShield: 0,
      temporaryShieldUntilMs: 0,
      nextIncomeAtMs: Number.POSITIVE_INFINITY,
      incomeGold: 0,
      respawnMs: 0,
      respawnAtMs: 0,
      auraMultiplier: 1,
      ethereal: false,
      nextHuntAtMs: Number.POSITIVE_INFINITY,
      earthSpirit: {
        star,
        behavior: "alert",
        activityCenter: { ...activityCenter },
        alertRange: summonMechanic.alertRange,
        returnDelayMs: summonMechanic.returnDelayMs,
        waitUntilMs: 0,
      },
    };
    const aggregation = this.getActiveSynergyMechanic(
      player,
      "earth_spirit_aggregation",
    );
    if (star > 0 && aggregation) {
      this.applyEarthSpiritProfile(
        entity,
        player,
        star as Exclude<EarthSpiritStar, 0>,
        aggregation,
        true,
      );
    }
    this.abilityEntities.set(entity.id, entity);
    this.events.push({
      type: "abilityEntities",
      entities: [this.toWireAbilityEntity(entity)],
    });
    player.contribution.companionsSummoned += 1;
    return entity;
  }

  private activateMountainSeed(
    player: SimPlayer,
    mechanic: Extract<
      SynergyMechanicDefinition,
      { kind: "earth_spirit_aggregation" }
    >,
  ): void {
    for (const entity of this.abilityEntities.values()) {
      if (
        entity.ownerId !== player.id ||
        baseAbilityId(entity.abilityId) !== mechanic.targetAbilityId ||
        !entity.earthSpirit ||
        entity.earthSpirit.star !== 0
      ) {
        continue;
      }
      this.applyEarthSpiritProfile(entity, player, 1, mechanic, true);
    }
  }

  private applyEarthSpiritProfile(
    entity: SimAbilityEntity,
    player: SimPlayer,
    star: Exclude<EarthSpiritStar, 0>,
    mechanic: Extract<
      SynergyMechanicDefinition,
      { kind: "earth_spirit_aggregation" }
    >,
    fullHeal: boolean,
  ): void {
    const profile = this.getEarthSpiritProfile(star, mechanic);
    const tuning = getAbilityTuning(player.modules, mechanic.targetAbilityId);
    const auraMultiplier = entity.auraMultiplier;
    const healthRatio =
      entity.maxHealth > 0
        ? clamp(entity.health / entity.maxHealth, 0, 1)
        : 1;
    entity.presentationId = `${mechanic.rockPresentationIdPrefix}${star}`;
    entity.maxHealth =
      profile.health *
      tuning.companionHealthMultiplier *
      auraMultiplier;
    entity.health = fullHeal
      ? entity.maxHealth
      : entity.maxHealth * healthRatio;
    entity.armor =
      (profile.armor + this.teamTechLevels.familiar_ward * 3) *
      auraMultiplier;
    entity.damage =
      profile.damage * tuning.damageMultiplier * auraMultiplier;
    entity.intervalMs =
      (profile.intervalMs * tuning.intervalMultiplier) / auraMultiplier;
    entity.range =
      profile.range * tuning.rangeMultiplier * auraMultiplier;
    entity.moveSpeed =
      PLAYER_BASE_MOVE_SPEED *
      profile.moveSpeedBaseRatio *
      auraMultiplier;
    entity.nextAttackAtMs = Math.max(
      entity.nextAttackAtMs,
      this.elapsedMs + entity.intervalMs,
    );
    entity.earthSpirit!.star = star;
  }

  private getEarthSpiritProfile(
    star: Exclude<EarthSpiritStar, 0>,
    mechanic: Extract<
      SynergyMechanicDefinition,
      { kind: "earth_spirit_aggregation" }
    >,
  ): {
    health: number;
    armor: number;
    damage: number;
    intervalMs: number;
    range: number;
    moveSpeedBaseRatio: number;
  } {
    let health = mechanic.baseHealth;
    let armor = mechanic.baseArmor;
    let damage = mechanic.baseDamage;
    for (let current = 2; current <= star; current += 1) {
      health = roundUpTo(
        health * mechanic.healthMultiplierPerStar,
        mechanic.healthRoundUpTo,
      );
      armor = roundUpTo(
        armor * mechanic.armorMultiplierPerStar,
        mechanic.armorRoundUpTo,
      );
      damage = roundUpTo(
        damage * mechanic.damageMultiplierPerStar,
        mechanic.damageRoundUpTo,
      );
    }
    return {
      health,
      armor,
      damage,
      intervalMs: mechanic.baseIntervalMs,
      range: mechanic.baseRange,
      moveSpeedBaseRatio: mechanic.baseMoveSpeedRatio,
    };
  }

  private updateAbilityEntities(deltaMs: number): void {
    this.prepareEarthSpiritAggregations();
    for (const entity of [...this.abilityEntities.values()]) {
      const owner = this.players.get(entity.ownerId);
      if (!owner) {
        this.cancelEarthAggregationForEntity(entity);
        this.abilityEntities.delete(entity.id);
        continue;
      }
      if (entity.kind === "field") {
        this.updateBlackHoleEntity(entity);
        continue;
      }
      if (
        !entity.undeadUnitId &&
        !owner.modules.some((id) => id && entity.abilityId.startsWith(id))
      ) {
        this.cancelEarthAggregationForEntity(entity);
        this.abilityEntities.delete(entity.id);
        continue;
      }
      this.applyCompanionAura(entity);
      this.updateGuardCouncilProfile(entity, owner);
      if (owner.status === "downed" && entity.kind === "guard" && entity.health > 0) {
        entity.health = 0;
        entity.respawnAtMs =
          entity.respawnMs > 0 ? this.elapsedMs + entity.respawnMs : 0;
      }
      if (entity.health <= 0) {
        if (!entity.deathProcessed) {
          entity.deathProcessed = true;
          this.handleAbilityEntityDeath(entity, owner);
        }
        if (entity.kind === "guard" && entity.respawnMs > 0) {
          if (entity.respawnAtMs === 0) {
            entity.respawnAtMs = this.elapsedMs + entity.respawnMs;
          } else if (
            owner.status !== "downed" &&
            this.elapsedMs >= entity.respawnAtMs
          ) {
            entity.health = entity.maxHealth;
            entity.deathProcessed = false;
            entity.respawnAtMs = 0;
            entity.x = owner.x;
            entity.y = owner.y;
            if (
              this.hasSpecial(owner, "companion_rebirth_burst") ||
              this.hasSynergySpecial(owner, "companion_rebirth_burst")
            ) {
              this.rebirthBurst(owner, entity);
            }
            const undeadGuard = this.getActiveSynergyMechanic(
              owner,
              "undead_guard",
            );
            if (undeadGuard?.targetAbilityId === baseAbilityId(entity.abilityId)) {
              this.spawnUndeadEntity(
                owner,
                undeadGuard.respawnSummonUnitId,
                entity.abilityId,
                entity.x,
                entity.y,
              );
            }
          }
        } else {
          this.cancelEarthAggregationForEntity(entity);
          this.abilityEntities.delete(entity.id);
        }
        continue;
      }
      if (owner.status === "lich" && !entity.undeadUnitId) {
        continue;
      }

      this.updateCompanionPeriodicHeal(entity, owner);

      if (entity.undeadUnitId) {
        const target = this.findClosestEnemy(
          entity.x,
          entity.y,
          Number.POSITIVE_INFINITY,
          entity.spaceId,
        );
        if (target) {
          this.moveEntityToward(
            entity,
            target,
            deltaMs,
            Math.max(20, entity.range * 0.7),
          );
        }
      } else if (
        entity.kind === "summon" &&
        entity.spaceId !== owner.spaceId
      ) {
        const target = this.findClosestEnemy(
          entity.x,
          entity.y,
          850,
          entity.spaceId,
        );
        if (target) {
          this.moveEntityToward(
            entity,
            target,
            deltaMs,
            Math.max(20, entity.range * 0.7),
          );
        }
      } else if (entity.ethereal && entity.movement) {
        if (entity.movement.kind === "player_centered_wander") {
          this.updatePastureSpiritMovement(entity, owner, deltaMs);
        } else {
          this.updateFollowerMovement(entity, owner, deltaMs);
        }
      } else if (entity.earthSpirit) {
        this.updateEarthSpiritMovement(entity, deltaMs);
      } else {
        const desiredAngle =
          entity.orbitOffset +
          this.elapsedMs / (entity.kind === "pasture" ? 2_800 : 1_900);
        const targetPosition = {
          x: owner.x + Math.cos(desiredAngle) * entity.orbitRadius,
          y: owner.y + Math.sin(desiredAngle) * entity.orbitRadius,
        };
        if (entity.kind === "summon") {
          const target = this.findClosestEnemy(
            entity.x,
            entity.y,
            850,
            entity.spaceId,
          );
          if (target) {
            this.moveEntityToward(entity, target, deltaMs, entity.range * 0.7);
          } else {
            this.moveEntityToward(entity, targetPosition, deltaMs, 60);
          }
        } else {
          this.moveEntityToward(entity, targetPosition, deltaMs, 25);
        }
      }

      if (entity.guardForm) {
        this.updateGuardEntityAbilities(entity, owner);
      }
      if (entity.kind === "guard") {
        this.updateStandaloneGuardCharge(entity, owner);
      }

      if (entity.incomeGold > 0 && this.elapsedMs >= entity.nextIncomeAtMs) {
        const ability = getModule(
          entity.abilityId.includes(":") ? entity.abilityId.split(":")[0] : entity.abilityId,
        );
        const interval = ability.companion?.incomeIntervalMs ?? 10_000;
        const gold = Math.round(entity.incomeGold * owner.stats.goldMultiplier);
        owner.scrap += gold;
        owner.contribution.scrapEarned += gold;
        entity.nextIncomeAtMs = this.elapsedMs + interval;
        this.triggerGrowth(owner, "companion_income", 1);
      }

      if (entity.kind === "pasture" && entity.ethereal) {
        this.updatePastureHunt(entity, owner);
      }
      if (entity.kind === "follower" && entity.ethereal) {
        this.updateFollowerAbilities(entity, owner);
      }
      if (!entity.undeadUnitId) {
        this.updateAuthoredCompanionMechanics(
          entity,
          owner,
          getModule(baseAbilityId(entity.abilityId)),
        );
      }

      if (entity.undeadUnitId) {
        this.updateUndeadEntityAbilities(entity, owner);
        this.updateUndeadEntityAttack(entity, owner);
        continue;
      }

      let damage = entity.damage;
      let interval = entity.intervalMs;
      let range = entity.range;
      if (
        entity.kind === "pasture" &&
        (this.hasSpecial(owner, "pasture_inherits_attack") ||
          this.hasSynergySpecial(owner, "pasture_inherits_attack"))
      ) {
        const inherited = this.bestCombatAbility(owner);
        if (inherited?.combat) {
          const ratio = getModule(entity.abilityId).tier === 6 ? 1 : 0.42;
          damage = inherited.combat.damage * ratio;
          interval = inherited.combat.intervalMs / Math.max(0.5, ratio);
          range = inherited.combat.range;
        }
      }
      if (
        !entity.ethereal &&
        damage > 0 &&
        range > 0 &&
        this.elapsedMs >= entity.nextAttackAtMs
      ) {
        const target = entity.earthSpirit
          ? this.getEarthSpiritAttackTarget(entity, range)
          : this.pickCompanionTargets(
              entity,
              owner,
              getModule(baseAbilityId(entity.abilityId)),
              range,
              "basic",
            )[0];
        if (target) {
          const critical = this.getGuardCriticalProfile(entity, owner);
          if (entity.guardForm) {
            entity.guardAttackCount = (entity.guardAttackCount ?? 0) + 1;
          }
          const criticalMultiplier =
            critical &&
            (entity.guardAttackCount ?? 0) % critical.attacksPerCritical === 0
              ? critical.damageMultiplier
              : 1;
          entity.attackCount += 1;
          const paper = this.getActiveSynergyMechanic(owner, "paper_units");
          const attackExecutions =
            paper?.abilityIds.includes(baseAbilityId(entity.abilityId)) &&
            entity.attackCount % paper.doubleEveryAttacks === 0
              ? 2
              : 1;
          const applied =
            damage *
            this.getStoredExperienceCompanionDamageMultiplier(
              owner,
              baseAbilityId(entity.abilityId),
            ) *
            criticalMultiplier *
            owner.stats.companionDamageMultiplier *
            (1 + this.teamTechLevels.familiar_fury * 0.04);
          for (let attack = 0; attack < attackExecutions; attack += 1) {
            if (!this.enemies.has(target.id)) break;
            this.damageEnemy(target, applied, owner.id, {
              kind: entity.kind,
              abilityId: baseAbilityId(entity.abilityId),
              entityId: entity.id,
              sourceSlot: entity.sourceSlot,
            });
            this.applyGuardBasicAttackSplash(
              entity,
              owner,
              getModule(baseAbilityId(entity.abilityId)),
              target,
              applied,
            );
            if (this.enemies.has(target.id)) {
              this.applyCompanionOnHit(
                entity,
                owner,
                getModule(baseAbilityId(entity.abilityId)),
                target,
              );
            }
          }
          const hiddenAttackSpeed =
            this.getHiddenAllyAuraAt(
              entity.x,
              entity.y,
            ).attackSpeedMultiplier;
          const earthAttackSpeed =
            this.getEarthSpiritRulerAttackSpeedMultiplier(entity, owner);
          const nestAttackSpeed =
            this.getNestRiotAttackSpeedMultiplier(entity, owner);
          const commanderAttackSpeed =
            this.getSummonCommanderAura(entity).attackSpeedMultiplier;
          entity.nextAttackAtMs =
            this.elapsedMs +
            Math.max(
              160,
              interval /
                hiddenAttackSpeed /
                earthAttackSpeed /
                nestAttackSpeed /
                commanderAttackSpeed,
            );
          this.events.push({
            type: "shot",
            playerId: owner.id,
            targetIds: [target.id],
            abilityId: entity.abilityId,
          });
        }
      }
    }
    this.completeEarthSpiritAggregations();
  }

  private updateAuthoredCompanionMechanics(
    entity: SimAbilityEntity,
    owner: SimPlayer,
    ability: ModuleDefinition,
  ): void {
    const cooldowns = entity.guardAbilityCooldowns ?? new Map<string, number>();
    entity.guardAbilityCooldowns = cooldowns;
    for (const [index, mechanic] of (ability.mechanics ?? []).entries()) {
      if (
        mechanic.kind !== "guard_periodic_strike" &&
        mechanic.kind !== "summon_damage_aura"
      ) {
        continue;
      }
      const key = `authored:${index}`;
      const nextAt = cooldowns.get(key);
      if (nextAt === undefined) {
        cooldowns.set(key, this.elapsedMs + mechanic.intervalMs);
        continue;
      }
      if (this.elapsedMs < nextAt) continue;
      cooldowns.set(key, this.elapsedMs + mechanic.intervalMs);
      if (mechanic.kind === "guard_periodic_strike") {
        const target = this.pickTarget(
          entity.x,
          entity.y,
          mechanic.range,
          "random",
          undefined,
          entity.spaceId,
        );
        if (!target) continue;
        const rank = getEnemy(target.definitionId).rank;
        const eliteMultiplier =
          rank === "normal" ? 1 : owner.stats.eliteDamageMultiplier;
        this.damageEnemy(
          target,
          mechanic.damage *
            (entity.guardAbilityDamageMultiplier ?? 1) *
            owner.stats.companionDamageMultiplier *
            owner.stats.damageMultiplier *
            this.getPlayerAbilityDamageMultiplier(owner) *
            eliteMultiplier,
          owner.id,
          {
            kind: "guard",
            abilityId: ability.id,
            entityId: entity.id,
            sourceSlot: entity.sourceSlot,
            variant: "periodic_strike",
          },
        );
        if ((mechanic.selfHealing ?? 0) > 0) {
          const before = entity.health;
          entity.health = Math.min(
            entity.maxHealth,
            entity.health + (mechanic.selfHealing ?? 0),
          );
          owner.contribution.healingDone += entity.health - before;
        }
        this.events.push({
          type: "shot",
          playerId: owner.id,
          targetIds: [target.id],
          abilityId: `${ability.id}:periodic_strike`,
        });
        continue;
      }
      const hitIds: number[] = [];
      for (const enemy of [...this.enemies.values()]) {
        if (
          enemy.spaceId !== entity.spaceId ||
          Math.hypot(enemy.x - entity.x, enemy.y - entity.y) > mechanic.radius
        ) {
          continue;
        }
        this.damageEnemy(
          enemy,
          mechanic.damage *
            owner.stats.companionDamageMultiplier *
            owner.stats.damageMultiplier *
            this.getPlayerAbilityDamageMultiplier(owner),
          owner.id,
          {
            kind: "summon",
            abilityId: ability.id,
            entityId: entity.id,
            sourceSlot: entity.sourceSlot,
            variant: "damage_aura",
          },
        );
        hitIds.push(enemy.id);
      }
      if (hitIds.length > 0) {
        this.events.push({
          type: "shot",
          playerId: owner.id,
          targetIds: hitIds,
          abilityId: `${ability.id}:damage_aura`,
        });
      }
    }
  }

  private applyGuardBasicAttackSplash(
    entity: SimAbilityEntity,
    owner: SimPlayer,
    ability: ModuleDefinition,
    primary: SimEnemy,
    baseDamage: number,
  ): void {
    const mechanic = ability.mechanics?.find(
      (entry) => entry.kind === "guard_basic_attack_splash",
    );
    if (!mechanic || mechanic.kind !== "guard_basic_attack_splash") return;
    for (const enemy of [...this.enemies.values()]) {
      if (
        enemy.id === primary.id ||
        enemy.spaceId !== primary.spaceId ||
        Math.hypot(enemy.x - primary.x, enemy.y - primary.y) > mechanic.radius
      ) {
        continue;
      }
      this.damageEnemy(
        enemy,
        baseDamage * mechanic.damageMultiplier,
        owner.id,
        {
          kind: "guard",
          abilityId: ability.id,
          entityId: entity.id,
          sourceSlot: entity.sourceSlot,
          variant: "basic_splash",
        },
      );
    }
  }

  private handleAbilityEntityDeath(
    entity: SimAbilityEntity,
    owner: SimPlayer,
  ): void {
    const abilityId = baseAbilityId(entity.abilityId);
    let ability: ModuleDefinition | undefined;
    try {
      ability = getModule(abilityId);
    } catch {
      ability = undefined;
    }
    const burst = ability?.companion?.deathBurst;
    if (burst) {
      const hitIds = this.applyAreaDamage(
        owner,
        entity.x,
        entity.y,
        burst.radius,
        burst.damage * owner.stats.companionDamageMultiplier,
        {
          kind: entity.kind === "field" ? "ability" : entity.kind,
          abilityId,
          entityId: entity.id,
        },
      );
      if (hitIds.length > 0) {
        this.events.push({
          type: "shot",
          playerId: owner.id,
          targetIds: hitIds,
          abilityId: `${abilityId}:death_burst`,
        });
      }
    }
    const fireLand = this.getActiveSynergyMechanic(owner, "fire_land");
    if (fireLand?.sourceAbilityIds.includes(abilityId)) {
      this.spawnFireLandField(owner, abilityId, entity, fireLand);
    }
    const cheap = this.getActiveSynergyMechanic(
      owner,
      "summon_death_cooldown",
    );
    if (!cheap?.abilityIds.includes(abilityId)) return;
    for (const targetAbilityId of cheap.abilityIds) {
      for (const [slot, moduleId] of owner.modules.entries()) {
        if (moduleId !== targetAbilityId) continue;
        const module = getModule(targetAbilityId);
        for (const [index, mechanic] of (module.mechanics ?? []).entries()) {
          if (mechanic.kind !== "undead_summon") continue;
          const key = mechanicCooldownKey(targetAbilityId, index, slot);
          const current = owner.mechanicCooldowns.get(key) ?? this.elapsedMs;
          owner.mechanicCooldowns.set(
            key,
            Math.max(this.elapsedMs, current - cheap.reductionMs),
          );
        }
        const key = slot === 0
          ? targetAbilityId
          : `${targetAbilityId}:slot:${slot}`;
        const current = owner.companionCooldowns.get(key) ?? this.elapsedMs;
        owner.companionCooldowns.set(
          key,
          Math.max(this.elapsedMs, current - cheap.reductionMs),
        );
      }
    }
  }

  private spawnFireLandField(
    owner: SimPlayer,
    sourceAbilityId: string,
    position: Vec2,
    mechanic: Extract<SynergyMechanicDefinition, { kind: "fire_land" }>,
  ): void {
    const entity: SimAbilityEntity = {
      id: this.nextAbilityEntityId++,
      ownerId: owner.id,
      abilityId: `${sourceAbilityId}:fire_land:${this.nextAbilityEntityId}`,
      kind: "field",
      presentationId: "field.fire_land",
      spaceId: owner.spaceId,
      x: position.x,
      y: position.y,
      health: 1,
      maxHealth: 1,
      armor: 0,
      damage: 0,
      intervalMs: 0,
      range: 0,
      moveSpeed: 0,
      orbitRadius: 0,
      orbitOffset: 0,
      nextAttackAtMs: Number.POSITIVE_INFINITY,
      attackCount: 0,
      deathProcessed: false,
      nextPeriodicHealAtMs: this.elapsedMs,
      temporaryShield: 0,
      temporaryShieldUntilMs: 0,
      nextIncomeAtMs: Number.POSITIVE_INFINITY,
      incomeGold: 0,
      respawnMs: 0,
      respawnAtMs: 0,
      auraMultiplier: 1,
      ethereal: false,
      nextHuntAtMs: Number.POSITIVE_INFINITY,
      field: {
        radius: mechanic.radius,
        damage:
          mechanic.damage *
          owner.stats.damageMultiplier *
          this.getPlayerAbilityDamageMultiplier(owner) *
          (1 + this.teamTechLevels.war_chant * 0.03),
        tickIntervalMs: mechanic.tickIntervalMs,
        nextTickAtMs: this.elapsedMs + mechanic.tickIntervalMs,
        expiresAtMs: this.elapsedMs + mechanic.durationMs,
        pullNormals: false,
      },
    };
    this.abilityEntities.set(entity.id, entity);
    this.events.push({
      type: "abilityEntities",
      entities: [this.toWireAbilityEntity(entity)],
    });
  }

  private updateCompanionPeriodicHeal(
    entity: SimAbilityEntity,
    owner: SimPlayer,
  ): void {
    let ability: ModuleDefinition;
    try {
      ability = getModule(baseAbilityId(entity.abilityId));
    } catch {
      return;
    }
    const healing = ability.companion?.periodicHeal;
    if (!healing || this.elapsedMs < entity.nextPeriodicHealAtMs) return;
    entity.nextPeriodicHealAtMs = this.elapsedMs + healing.intervalMs;
    if (healing.target === "lowest_ally") {
      const playerTargets = [...this.players.values()]
        .filter(
          (candidate) =>
            candidate.spaceId === entity.spaceId &&
            candidate.status === "active" &&
            Math.hypot(candidate.x - entity.x, candidate.y - entity.y) <=
              healing.range,
        )
        .map((candidate) => ({
          target: candidate as SimPlayer | SimAbilityEntity,
          ratio: candidate.health / candidate.stats.maxHealth,
          id: `player:${candidate.id}`,
        }));
      const guardTargets = [...this.abilityEntities.values()]
        .filter(
          (candidate) =>
            candidate.kind === "guard" &&
            candidate.spaceId === entity.spaceId &&
            candidate.health > 0 &&
            Math.hypot(candidate.x - entity.x, candidate.y - entity.y) <=
              healing.range,
        )
        .map((candidate) => ({
          target: candidate as SimPlayer | SimAbilityEntity,
          ratio: candidate.health / candidate.maxHealth,
          id: `guard:${candidate.id}`,
        }));
      const lowest = [...playerTargets, ...guardTargets].sort(
        (left, right) => left.ratio - right.ratio || left.id.localeCompare(right.id),
      )[0];
      if (lowest) {
        this.applyHealingWithOptionalShield(
          owner,
          lowest.target,
          healing.amount,
          ability.id,
        );
      }
      return;
    }
    this.applyHealingWithOptionalShield(owner, entity, healing.amount, ability.id);
    const target = [...this.abilityEntities.values()]
      .filter(
        (candidate) =>
          candidate.ownerId === owner.id &&
          candidate.kind === "guard" &&
          candidate.id !== entity.id &&
          candidate.health > 0 &&
          Math.hypot(candidate.x - entity.x, candidate.y - entity.y) <=
            healing.range,
      )
      .sort(
        (left, right) =>
          left.health / left.maxHealth - right.health / right.maxHealth ||
          left.id - right.id,
      )[0];
    if (target) {
      this.applyHealingWithOptionalShield(
        owner,
        target,
        healing.amount,
        ability.id,
      );
    }
  }

  private prepareEarthSpiritAggregations(): void {
    for (const group of [...this.earthAggregationGroups.values()]) {
      const valid = group.participantIds.every((entityId) => {
        const entity = this.abilityEntities.get(entityId);
        return Boolean(
          entity &&
            entity.health > 0 &&
            entity.earthSpirit?.aggregationId === group.id,
        );
      });
      if (!valid) {
        this.cancelEarthAggregation(group.id);
      }
    }

    for (const owner of this.players.values()) {
      const mechanic = this.getActiveSynergyMechanic(
        owner,
        "earth_spirit_aggregation",
      );
      if (!mechanic) {
        continue;
      }
      for (const star of [1, 2, 3, 4] as const) {
        if (
          star === 4 &&
          ([...this.abilityEntities.values()].some(
            (entity) =>
              entity.ownerId === owner.id &&
              entity.health > 0 &&
              entity.earthSpirit?.star === mechanic.maxStar,
          ) ||
            [...this.earthAggregationGroups.values()].some(
              (group) => group.ownerId === owner.id && group.star === 4,
            ))
        ) {
          continue;
        }
        while (true) {
          const candidates = [...this.abilityEntities.values()]
            .filter(
              (entity) =>
                entity.ownerId === owner.id &&
                entity.health > 0 &&
                entity.earthSpirit?.star === star &&
                entity.earthSpirit.aggregationId === undefined,
            )
            .sort((a, b) => a.id - b.id);
          const participants =
            this.findEarthSpiritAggregationParticipants(
              candidates,
              mechanic.aggregationRange,
              mechanic.participantsRequired,
            );
          if (!participants) {
            break;
          }
          const target = {
            x:
              participants.reduce((sum, entity) => sum + entity.x, 0) /
              participants.length,
            y:
              participants.reduce((sum, entity) => sum + entity.y, 0) /
              participants.length,
          };
          const id = this.nextEarthAggregationId++;
          const savedStates = new Map<number, EarthAggregationSavedState>();
          for (const entity of participants) {
            const earth = entity.earthSpirit!;
            savedStates.set(entity.id, {
              behavior:
                earth.behavior === "aggregating"
                  ? "alert"
                  : earth.behavior,
              targetEnemyId: earth.targetEnemyId,
              waitRemainingMs:
                earth.behavior === "waiting"
                  ? Math.max(0, earth.waitUntilMs - this.elapsedMs)
                  : 0,
            });
            earth.behavior = "aggregating";
            earth.aggregationId = id;
          }
          this.earthAggregationGroups.set(id, {
            id,
            ownerId: owner.id,
            star,
            target,
            participantIds: participants.map((entity) => entity.id),
            savedStates,
          });
          if (star === 4) {
            break;
          }
        }
      }
    }
  }

  private findEarthSpiritAggregationParticipants(
    candidates: SimAbilityEntity[],
    range: number,
    required: number,
  ): SimAbilityEntity[] | undefined {
    if (required !== 3) {
      throw new Error("当前土灵聚合运行时仅支持三个单位融合。");
    }
    for (let first = 0; first < candidates.length - 2; first += 1) {
      for (let second = first + 1; second < candidates.length - 1; second += 1) {
        if (
          Math.hypot(
            candidates[first].x - candidates[second].x,
            candidates[first].y - candidates[second].y,
          ) > range
        ) {
          continue;
        }
        for (let third = second + 1; third < candidates.length; third += 1) {
          if (
            Math.hypot(
              candidates[first].x - candidates[third].x,
              candidates[first].y - candidates[third].y,
            ) <= range &&
            Math.hypot(
              candidates[second].x - candidates[third].x,
              candidates[second].y - candidates[third].y,
            ) <= range
          ) {
            return [
              candidates[first],
              candidates[second],
              candidates[third],
            ];
          }
        }
      }
    }
    return undefined;
  }

  private updateEarthSpiritMovement(
    entity: SimAbilityEntity,
    deltaMs: number,
  ): void {
    const earth = entity.earthSpirit!;
    if (earth.behavior === "aggregating") {
      const group = earth.aggregationId
        ? this.earthAggregationGroups.get(earth.aggregationId)
        : undefined;
      if (!group) {
        earth.behavior = "alert";
        earth.aggregationId = undefined;
        return;
      }
      this.moveEntityToward(entity, group.target, deltaMs, 2);
      return;
    }

    if (earth.behavior === "attacking") {
      const target = earth.targetEnemyId
        ? this.enemies.get(earth.targetEnemyId)
        : undefined;
      if (target && target.health > 0) {
        this.moveEntityToward(
          entity,
          target,
          deltaMs,
          Math.max(20, entity.range * 0.7),
        );
        return;
      }
      this.acquireEarthSpiritTargetOrWait(entity);
      return;
    }

    if (earth.behavior === "waiting") {
      const target = this.findClosestEnemy(
        entity.x,
        entity.y,
        earth.alertRange,
      );
      if (target) {
        earth.behavior = "attacking";
        earth.targetEnemyId = target.id;
        return;
      }
      if (this.elapsedMs >= earth.waitUntilMs) {
        earth.behavior = "returning";
        earth.targetEnemyId = undefined;
      }
      return;
    }

    if (earth.behavior === "returning") {
      this.moveEntityToward(
        entity,
        earth.activityCenter,
        deltaMs,
        2,
      );
      if (
        Math.hypot(
          entity.x - earth.activityCenter.x,
          entity.y - earth.activityCenter.y,
        ) <= 2.001
      ) {
        earth.behavior = "alert";
      }
      return;
    }

    const target = this.findClosestEnemy(
      entity.x,
      entity.y,
      earth.alertRange,
    );
    if (target) {
      earth.behavior = "attacking";
      earth.targetEnemyId = target.id;
    }
  }

  private acquireEarthSpiritTargetOrWait(entity: SimAbilityEntity): void {
    const earth = entity.earthSpirit!;
    const target = this.findClosestEnemy(
      entity.x,
      entity.y,
      earth.alertRange,
    );
    if (target) {
      earth.behavior = "attacking";
      earth.targetEnemyId = target.id;
      return;
    }
    earth.behavior = "waiting";
    earth.targetEnemyId = undefined;
    earth.waitUntilMs = this.elapsedMs + earth.returnDelayMs;
  }

  private getEarthSpiritAttackTarget(
    entity: SimAbilityEntity,
    range: number,
  ): SimEnemy | undefined {
    const earth = entity.earthSpirit;
    if (earth?.behavior !== "attacking" || earth.targetEnemyId === undefined) {
      return undefined;
    }
    const target = this.enemies.get(earth.targetEnemyId);
    return target &&
      target.health > 0 &&
      Math.hypot(target.x - entity.x, target.y - entity.y) <= range
      ? target
      : undefined;
  }

  private getEarthSpiritRulerAttackSpeedMultiplier(
    entity: SimAbilityEntity,
    owner: SimPlayer,
  ): number {
    const star = entity.earthSpirit?.star;
    if (!star || star >= 5) {
      return 1;
    }
    const mechanic = this.getActiveSynergyMechanic(
      owner,
      "earth_spirit_aggregation",
    );
    if (!mechanic) {
      return 1;
    }
    const inAura = [...this.abilityEntities.values()].some(
      (candidate) =>
        candidate.id !== entity.id &&
        candidate.ownerId === entity.ownerId &&
        candidate.health > 0 &&
        candidate.earthSpirit?.star === mechanic.maxStar &&
        Math.hypot(candidate.x - entity.x, candidate.y - entity.y) <=
          mechanic.rulerAuraRadius,
    );
    return inAura ? 1 + mechanic.rulerAttackSpeedBonus : 1;
  }

  private completeEarthSpiritAggregations(): void {
    for (const group of [...this.earthAggregationGroups.values()]) {
      const participants = group.participantIds
        .map((entityId) => this.abilityEntities.get(entityId))
        .filter((entity): entity is SimAbilityEntity => Boolean(entity));
      if (
        participants.length !== group.participantIds.length ||
        participants.some((entity) => entity.health <= 0)
      ) {
        this.cancelEarthAggregation(group.id);
        continue;
      }
      if (
        participants.some(
          (entity) =>
            Math.hypot(
              entity.x - group.target.x,
              entity.y - group.target.y,
            ) > 2.001,
        )
      ) {
        continue;
      }
      const owner = this.players.get(group.ownerId);
      const mechanic = owner
        ? this.getActiveSynergyMechanic(
            owner,
            "earth_spirit_aggregation",
          )
        : undefined;
      if (!owner || !mechanic) {
        this.cancelEarthAggregation(group.id);
        continue;
      }
      const nextStar = (group.star + 1) as Exclude<EarthSpiritStar, 0>;
      if (
        nextStar === mechanic.maxStar &&
        [...this.abilityEntities.values()].some(
          (entity) =>
            !group.participantIds.includes(entity.id) &&
            entity.ownerId === owner.id &&
            entity.health > 0 &&
            entity.earthSpirit?.star === mechanic.maxStar,
        )
      ) {
        this.cancelEarthAggregation(group.id);
        continue;
      }
      this.earthAggregationGroups.delete(group.id);
      for (const participant of participants) {
        this.abilityEntities.delete(participant.id);
      }
      this.spawnEarthSpiritEntity(
        owner,
        getModule(mechanic.targetAbilityId),
        nextStar,
        group.target,
        group.target,
      );
    }
  }

  private cancelEarthAggregationForEntity(entity: SimAbilityEntity): void {
    const aggregationId = entity.earthSpirit?.aggregationId;
    if (aggregationId !== undefined) {
      this.cancelEarthAggregation(aggregationId);
    }
  }

  private cancelEarthAggregation(aggregationId: number): void {
    const group = this.earthAggregationGroups.get(aggregationId);
    if (!group) {
      return;
    }
    this.earthAggregationGroups.delete(aggregationId);
    for (const entityId of group.participantIds) {
      const entity = this.abilityEntities.get(entityId);
      const earth = entity?.earthSpirit;
      const saved = group.savedStates.get(entityId);
      if (!entity || !earth || !saved || entity.health <= 0) {
        continue;
      }
      earth.aggregationId = undefined;
      if (saved.behavior === "attacking") {
        const target = saved.targetEnemyId
          ? this.enemies.get(saved.targetEnemyId)
          : undefined;
        if (target && target.health > 0) {
          earth.behavior = "attacking";
          earth.targetEnemyId = target.id;
        } else {
          this.acquireEarthSpiritTargetOrWait(entity);
        }
      } else if (saved.behavior === "waiting") {
        earth.behavior = "waiting";
        earth.targetEnemyId = undefined;
        earth.waitUntilMs = this.elapsedMs + saved.waitRemainingMs;
      } else {
        earth.behavior = saved.behavior;
        earth.targetEnemyId = saved.targetEnemyId;
      }
    }
  }

  private getActiveSynergyMechanic<
    K extends SynergyMechanicDefinition["kind"],
  >(
    player: SimPlayer,
    kind: K,
  ): Extract<SynergyMechanicDefinition, { kind: K }> | undefined {
    for (const synergyId of player.synergies) {
      const mechanic = getSynergy(synergyId).mechanics?.find(
        (entry) => entry.kind === kind,
      );
      if (mechanic?.kind === kind) {
        return mechanic as Extract<SynergyMechanicDefinition, { kind: K }>;
      }
    }
    return undefined;
  }

  private isGuardAbilityConsumed(player: SimPlayer, abilityId: string): boolean {
    const oath = this.getActiveSynergyMechanic(player, "guard_oath_upgrade");
    const material = this.getActiveSynergyMechanic(
      player,
      "material_guard_upgrade",
    );
    return (
      oath?.consumedAbilityId === abilityId ||
      material?.consumedAbilityId === abilityId
    );
  }

  private getGenericGuardSynergyProfile(
    player: SimPlayer,
    abilityId: string,
  ): {
    healthAdd: number;
    armorAdd: number;
    damageAdd: number;
    attackSpeedBonus: number;
    respawnMs?: number;
  } {
    const result = {
      healthAdd: 0,
      armorAdd: 0,
      damageAdd: 0,
      attackSpeedBonus: 0,
      respawnMs: undefined as number | undefined,
    };
    const material = this.getActiveSynergyMechanic(
      player,
      "material_guard_upgrade",
    );
    if (material?.targetAbilityId === abilityId) {
      result.healthAdd += material.healthAdd;
      result.armorAdd += material.armorAdd;
      result.damageAdd += material.damageAdd;
      result.respawnMs = material.respawnMs;
    }
    const coordination = this.getActiveSynergyMechanic(
      player,
      "guard_coordination",
    );
    if (coordination?.abilityIds.includes(abilityId)) {
      result.armorAdd += coordination.armorAdd;
      result.attackSpeedBonus += coordination.attackSpeedBonus;
    }
    return result;
  }

  private updateGuardCouncilProfile(
    entity: SimAbilityEntity,
    owner: SimPlayer,
  ): void {
    if (entity.kind !== "guard" || !Number.isFinite(entity.maxHealth)) return;
    const mechanic = this.getActiveSynergyMechanic(owner, "guard_council");
    const abilityId = baseAbilityId(entity.abilityId);
    let healthMultiplier = 1;
    let damageMultiplier = 1;
    if (mechanic?.abilityIds.includes(abilityId)) {
      const sources = new Set(
        [...this.abilityEntities.values()]
          .filter(
            (candidate) =>
              candidate.ownerId === owner.id &&
              candidate.kind === "guard" &&
              candidate.health > 0 &&
              mechanic.abilityIds.includes(baseAbilityId(candidate.abilityId)),
          )
          .map((candidate) => baseAbilityId(candidate.abilityId)),
      );
      const count = Math.min(mechanic.maxSources, sources.size);
      healthMultiplier += count * mechanic.healthBonusPerSource;
      damageMultiplier += count * mechanic.damageBonusPerSource;
    }
    const previous = entity.guardCouncilMultiplier ?? 1;
    if (Math.abs(previous - healthMultiplier) < 1e-9) return;
    const healthRatio = entity.maxHealth > 0 ? entity.health / entity.maxHealth : 1;
    entity.maxHealth *= healthMultiplier / previous;
    entity.health = Math.max(0, entity.maxHealth * healthRatio);
    entity.damage *= damageMultiplier / previous;
    entity.guardCouncilMultiplier = healthMultiplier;
  }

  private getNestRiotAttackSpeedMultiplier(
    entity: SimAbilityEntity,
    owner: SimPlayer,
  ): number {
    const mechanic = this.getActiveSynergyMechanic(owner, "nest_riot");
    if (!mechanic?.abilityIds.includes(baseAbilityId(entity.abilityId))) return 1;
    const nearbyOthers = [...this.abilityEntities.values()].filter(
      (candidate) =>
        candidate.id !== entity.id &&
        candidate.ownerId === owner.id &&
        candidate.health > 0 &&
        mechanic.abilityIds.includes(baseAbilityId(candidate.abilityId)) &&
        Math.hypot(candidate.x - entity.x, candidate.y - entity.y) <=
          mechanic.radius,
    ).length;
    return (
      1 +
      Math.min(
        mechanic.maxAttackSpeedBonus,
        nearbyOthers * mechanic.attackSpeedBonusPerOther,
      )
    );
  }

  private updateStandaloneGuardCharge(
    entity: SimAbilityEntity,
    owner: SimPlayer,
  ): void {
    const ability = getModule(baseAbilityId(entity.abilityId));
    const mechanic = ability.mechanics?.find(
      (entry) => entry.kind === "guard_charge",
    );
    if (!mechanic || mechanic.kind !== "guard_charge") return;
    const cooldowns = entity.guardAbilityCooldowns ?? new Map<string, number>();
    entity.guardAbilityCooldowns = cooldowns;
    const key = "standalone_charge";
    const nextAt = cooldowns.get(key);
    if (nextAt === undefined) {
      cooldowns.set(key, this.elapsedMs + mechanic.intervalMs);
      return;
    }
    if (this.elapsedMs < nextAt) return;
    cooldowns.set(key, this.elapsedMs + mechanic.intervalMs);
    const target = this.pickTarget(
      entity.x,
      entity.y,
      mechanic.acquireRange,
      "farthest",
      undefined,
      entity.spaceId,
    );
    if (!target) return;
    const start = { x: entity.x, y: entity.y };
    const end = { x: target.x, y: target.y };
    const hitIds: number[] = [];
    for (const enemy of [...this.enemies.values()]) {
      if (
        enemy.spaceId !== entity.spaceId ||
        distanceToSegment(enemy, start, end) > mechanic.pathWidth / 2
      ) {
        continue;
      }
      this.damageEnemy(
        enemy,
        mechanic.damage * owner.stats.companionDamageMultiplier,
        owner.id,
        {
          kind: "guard",
          abilityId: ability.id,
          entityId: entity.id,
          sourceSlot: entity.sourceSlot,
        },
      );
      hitIds.push(enemy.id);
    }
    entity.x = end.x;
    entity.y = end.y;
    if (hitIds.length > 0) {
      this.events.push({
        type: "shot",
        playerId: owner.id,
        targetIds: hitIds,
        abilityId: `${ability.id}:charge`,
      });
    }
  }

  private getStoredExperienceCompanionDamageMultiplier(
    player: SimPlayer,
    abilityId: string,
  ): number {
    const mechanic = this.getActiveSynergyMechanic(
      player,
      "stored_experience_companion_damage",
    );
    if (!mechanic?.abilityIds.includes(abilityId)) return 1;
    const stored = [...player.moduleSlotStates.values()].reduce(
      (sum, state) =>
        getModule(state.moduleId).stateFamily?.canonicalModuleId === "ancient_ring" ||
        state.moduleId === "ancient_ring"
          ? sum + state.experienceStored
          : sum,
      0,
    );
    return (
      1 +
      Math.floor(stored / mechanic.experiencePerStep) *
        mechanic.damageBonusPerStep
    );
  }

  private getGuardRuntimeProfile(
    player: SimPlayer,
    ability: ModuleDefinition,
  ): GuardRuntimeProfile | undefined {
    const companion = ability.companion;
    if (!companion || companion.kind !== "guard") {
      return undefined;
    }
    const abilityId = baseAbilityId(ability.id);
    if (abilityId === "heroic_soul") {
      return {
        form: "heroic_soul",
        presentationId: "guard.heroic_soul",
        health: companion.health ?? 1,
        armor: companion.armor ?? 0,
        damage: companion.damage ?? 0,
        intervalMs: companion.intervalMs ?? 1_000,
        range: companion.range ?? 100,
        moveSpeed: companion.moveSpeed,
        respawnMs: companion.respawnMs ?? 0,
        hasFlame: true,
        hasCritical: false,
        hasDash: false,
        abilityDamageMultiplier: 1,
      };
    }
    if (abilityId !== "armor_golem") {
      return undefined;
    }

    const critical = this.getActiveSynergyMechanic(player, "guard_critical");
    const dash = this.getActiveSynergyMechanic(player, "guard_dash");
    const oath = this.getActiveSynergyMechanic(player, "guard_oath_upgrade");
    const divine = this.getActiveSynergyMechanic(
      player,
      "divine_guard_upgrade",
    );
    let form: GuardFormId = "armor_golem";
    let presentationId = "guard.armor_golem";
    let health = companion.health ?? 1;
    let armor = companion.armor ?? 0;
    let damage = companion.damage ?? 0;
    let baseIntervalMs = companion.intervalMs ?? 1_000;
    let range = companion.range ?? 100;
    let moveSpeedBaseRatio = companion.moveSpeed / PLAYER_BASE_MOVE_SPEED;
    let respawnMs = companion.respawnMs ?? 0;
    let hasFlame = false;
    let attackSpeedBonus = 0;
    let abilityDamageMultiplier = 1;

    if (oath?.targetAbilityId === abilityId) {
      form = "oathbound_guard";
      presentationId = "guard.oathbound_guard";
      health = oath.health;
      armor = oath.armor;
      damage = oath.damage;
      baseIntervalMs = oath.intervalMs;
      range = oath.range;
      moveSpeedBaseRatio = oath.moveSpeedBaseRatio;
      respawnMs = oath.respawnMs;
      hasFlame = true;
    }
    if (critical?.targetAbilityId === abilityId) {
      damage += critical.damageAdd;
    }
    if (dash?.targetAbilityId === abilityId) {
      attackSpeedBonus += dash.attackSpeedBonus;
      moveSpeedBaseRatio += dash.moveSpeedBonus;
    }
    if (divine?.targetAbilityId === abilityId) {
      form = "divine_agent";
      presentationId = "guard.divine_agent";
      health =
        health * divine.positiveStatMultiplier + divine.healthAdd;
      armor = armor * divine.positiveStatMultiplier + divine.armorAdd;
      damage = damage * divine.positiveStatMultiplier + divine.damageAdd;
      attackSpeedBonus += divine.attackSpeedBonus;
      moveSpeedBaseRatio += divine.moveSpeedBonus;
      range = divine.range;
      respawnMs = divine.respawnMs;
      abilityDamageMultiplier += divine.abilityDamageBonus;
    }

    return {
      form,
      presentationId,
      health,
      armor,
      damage,
      intervalMs: baseIntervalMs / (1 + attackSpeedBonus),
      range,
      moveSpeed: PLAYER_BASE_MOVE_SPEED * moveSpeedBaseRatio,
      respawnMs,
      hasFlame,
      hasCritical: critical?.targetAbilityId === abilityId,
      hasDash: dash?.targetAbilityId === abilityId,
      abilityDamageMultiplier,
    };
  }

  private getGuardCriticalProfile(
    entity: SimAbilityEntity,
    owner: SimPlayer,
  ):
    | {
        attacksPerCritical: number;
        damageMultiplier: number;
      }
    | undefined {
    if (!entity.guardHasCritical) {
      return undefined;
    }
    if (entity.guardForm === "divine_agent") {
      const divine = this.getActiveSynergyMechanic(
        owner,
        "divine_guard_upgrade",
      );
      if (divine) {
        return {
          attacksPerCritical: divine.judgmentAttacksPerCritical,
          damageMultiplier: divine.judgmentDamageMultiplier,
        };
      }
    }
    const critical = this.getActiveSynergyMechanic(owner, "guard_critical");
    return critical
      ? {
          attacksPerCritical: critical.attacksPerCritical,
          damageMultiplier: critical.criticalDamageMultiplier,
        }
      : undefined;
  }

  private updateGuardEntityAbilities(
    entity: SimAbilityEntity,
    owner: SimPlayer,
  ): void {
    if (!entity.guardAbilityCooldowns) {
      return;
    }
    if (entity.guardHasFlame) {
      const divine =
        entity.guardForm === "divine_agent"
          ? this.getActiveSynergyMechanic(owner, "divine_guard_upgrade")
          : undefined;
      const flame = getModule("heroic_soul").mechanics?.find(
        (
          mechanic,
        ): mechanic is Extract<
          AbilityMechanicDefinition,
          { kind: "guard_periodic_area_damage" }
        > => mechanic.kind === "guard_periodic_area_damage",
      );
      const intervalMs = divine?.holyFireIntervalMs ?? flame?.intervalMs;
      const radius = divine?.holyFireRadius ?? flame?.radius;
      const damage = divine?.holyFireDamage ?? flame?.damage;
      if (intervalMs && radius && damage) {
        const nextAt = entity.guardAbilityCooldowns.get("area");
        if (nextAt === undefined) {
          entity.guardAbilityCooldowns.set(
            "area",
            this.elapsedMs + intervalMs,
          );
        } else if (this.elapsedMs >= nextAt) {
          entity.guardAbilityCooldowns.set(
            "area",
            this.elapsedMs + intervalMs,
          );
          this.castGuardAreaDamage(entity, owner, radius, damage);
        }
      }
    }
    if (!entity.guardHasDash) {
      return;
    }
    const divine =
      entity.guardForm === "divine_agent"
        ? this.getActiveSynergyMechanic(owner, "divine_guard_upgrade")
        : undefined;
    const dash = this.getActiveSynergyMechanic(owner, "guard_dash");
    const intervalMs = divine?.descentIntervalMs ?? dash?.intervalMs;
    if (!intervalMs) {
      return;
    }
    const nextAt = entity.guardAbilityCooldowns.get("mobility");
    if (nextAt === undefined) {
      entity.guardAbilityCooldowns.set(
        "mobility",
        this.elapsedMs + intervalMs,
      );
      return;
    }
    if (this.elapsedMs < nextAt) {
      return;
    }
    const cast = divine
      ? this.castGuardDescent(entity, owner, divine)
      : dash
        ? this.castGuardDash(entity, owner, dash)
        : false;
    if (cast) {
      entity.guardAbilityCooldowns.set(
        "mobility",
        this.elapsedMs + intervalMs,
      );
    }
  }

  private castGuardAreaDamage(
    entity: SimAbilityEntity,
    owner: SimPlayer,
    radius: number,
    baseDamage: number,
  ): void {
    const damage =
      baseDamage *
      (entity.guardAbilityDamageMultiplier ?? 1) *
      owner.stats.companionDamageMultiplier *
      (1 + this.teamTechLevels.familiar_fury * 0.04);
    const hitIds: number[] = [];
    for (const enemy of [...this.enemies.values()]) {
      if (Math.hypot(enemy.x - entity.x, enemy.y - entity.y) > radius) {
        continue;
      }
      this.damageEnemy(enemy, damage, owner.id, {
        kind: "guard",
        abilityId: baseAbilityId(entity.abilityId),
        entityId: entity.id,
      });
      hitIds.push(enemy.id);
    }
    if (hitIds.length > 0) {
      this.events.push({
        type: "shot",
        playerId: owner.id,
        targetIds: hitIds,
        abilityId: entity.abilityId,
      });
    }
  }

  private castGuardDash(
    entity: SimAbilityEntity,
    owner: SimPlayer,
    mechanic: Extract<SynergyMechanicDefinition, { kind: "guard_dash" }>,
  ): boolean {
    const target = [...this.enemies.values()]
      .filter(
        (enemy) =>
          Math.hypot(enemy.x - entity.x, enemy.y - entity.y) <=
          mechanic.acquireRange,
      )
      .sort(
        (a, b) =>
          distanceSquared(b.x, b.y, entity.x, entity.y) -
          distanceSquared(a.x, a.y, entity.x, entity.y),
      )[0];
    if (!target) {
      return false;
    }
    const start = { x: entity.x, y: entity.y };
    const end = { x: target.x, y: target.y };
    entity.x = end.x;
    entity.y = end.y;
    const damage =
      mechanic.damage *
      owner.stats.companionDamageMultiplier *
      (1 + this.teamTechLevels.familiar_fury * 0.04);
    const hitIds: number[] = [];
    for (const enemy of [...this.enemies.values()]) {
      if (
        distanceToSegment(enemy, start, end) >
        mechanic.pathWidth / 2
      ) {
        continue;
      }
      this.damageEnemy(enemy, damage, owner.id, {
        kind: "guard",
        abilityId: baseAbilityId(entity.abilityId),
        entityId: entity.id,
      });
      hitIds.push(enemy.id);
      if (
        this.enemies.has(enemy.id) &&
        (mechanic.knockbackBosses ||
          getEnemy(enemy.definitionId).rank !== "boss")
      ) {
        knockEnemyOutOfPath(enemy, start, end, mechanic.pathWidth);
      }
    }
    if (hitIds.length > 0) {
      this.events.push({
        type: "shot",
        playerId: owner.id,
        targetIds: hitIds,
        abilityId: entity.abilityId,
      });
    }
    return true;
  }

  private castGuardDescent(
    entity: SimAbilityEntity,
    owner: SimPlayer,
    mechanic: Extract<
      SynergyMechanicDefinition,
      { kind: "divine_guard_upgrade" }
    >,
  ): boolean {
    const available = [...this.enemies.values()].filter(
      (enemy) =>
        Math.hypot(enemy.x - entity.x, enemy.y - entity.y) <=
        mechanic.descentAcquireRange,
    );
    const heavy = available.filter(
      (enemy) => getEnemy(enemy.definitionId).rank !== "normal",
    );
    const pool =
      mechanic.descentPreferHeavy && heavy.length > 0 ? heavy : available;
    if (pool.length === 0) {
      return false;
    }
    const target = this.random.pick(pool);
    entity.x = target.x;
    entity.y = target.y;
    const damage =
      mechanic.descentDamage *
      (entity.guardAbilityDamageMultiplier ?? 1) *
      owner.stats.companionDamageMultiplier *
      (1 + this.teamTechLevels.familiar_fury * 0.04);
    const hitIds: number[] = [];
    for (const enemy of [...this.enemies.values()]) {
      if (
        Math.hypot(enemy.x - entity.x, enemy.y - entity.y) >
        mechanic.descentRadius
      ) {
        continue;
      }
      this.damageEnemy(enemy, damage, owner.id, {
        kind: "guard",
        abilityId: baseAbilityId(entity.abilityId),
        entityId: entity.id,
      });
      hitIds.push(enemy.id);
      if (
        this.enemies.has(enemy.id) &&
        (mechanic.descentKnockbackBosses ||
          getEnemy(enemy.definitionId).rank !== "boss")
      ) {
        knockEnemyOutOfCircle(
          enemy,
          entity,
          mechanic.descentRadius,
          this.random,
        );
      }
    }
    if (hitIds.length > 0) {
      this.events.push({
        type: "shot",
        playerId: owner.id,
        targetIds: hitIds,
        abilityId: entity.abilityId,
      });
    }
    return true;
  }

  private updateUndeadEntityAbilities(
    entity: SimAbilityEntity,
    owner: SimPlayer,
  ): void {
    if (!entity.undeadUnitId || !entity.undeadAbilityCooldowns) {
      return;
    }
    const unit = getUndeadUnit(entity.undeadUnitId);
    for (const [index, ability] of unit.abilities.entries()) {
      const nextAt =
        entity.undeadAbilityCooldowns.get(index) ??
        Number.POSITIVE_INFINITY;
      if (this.elapsedMs < nextAt) {
        continue;
      }
      if (ability.kind === "charge") {
        const target = [...this.enemies.values()]
          .filter(
            (enemy) =>
              Math.hypot(enemy.x - entity.x, enemy.y - entity.y) <=
              ability.acquireRange,
          )
          .sort(
            (a, b) =>
              distanceSquared(b.x, b.y, entity.x, entity.y) -
              distanceSquared(a.x, a.y, entity.x, entity.y),
          )[0];
        if (!target) {
          continue;
        }
        entity.undeadAbilityCooldowns.set(
          index,
          this.elapsedMs + ability.intervalMs,
        );
        const start = { x: entity.x, y: entity.y };
        const end = { x: target.x, y: target.y };
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const distance = Math.max(0.001, Math.hypot(dx, dy));
        entity.x = end.x - (dx / distance) * Math.max(20, entity.range * 0.7);
        entity.y = end.y - (dy / distance) * Math.max(20, entity.range * 0.7);
        const hitIds: number[] = [];
        const damage =
          ability.damage *
          owner.stats.companionDamageMultiplier *
          (1 + this.teamTechLevels.familiar_fury * 0.04);
        for (const enemy of [...this.enemies.values()]) {
          if (
            distanceToSegment(enemy, start, end) >
            ability.pathWidth / 2
          ) {
            continue;
          }
          this.damageEnemy(enemy, damage, owner.id, {
            kind: "summon",
            abilityId: baseAbilityId(entity.abilityId),
            entityId: entity.id,
          });
          hitIds.push(enemy.id);
          if (
            this.enemies.has(enemy.id) &&
            (ability.stunBosses ||
              getEnemy(enemy.definitionId).rank !== "boss")
          ) {
            enemy.stunnedUntilMs = Math.max(
              enemy.stunnedUntilMs,
              this.elapsedMs + ability.stunDurationMs,
            );
          }
        }
        if (hitIds.length > 0) {
          this.events.push({
            type: "shot",
            playerId: owner.id,
            targetIds: hitIds,
            abilityId: entity.abilityId,
          });
        }
      } else if (ability.kind === "periodic_area_damage") {
        entity.undeadAbilityCooldowns.set(
          index,
          this.elapsedMs + ability.intervalMs,
        );
        const hitIds: number[] = [];
        const damage =
          ability.damage *
          owner.stats.companionDamageMultiplier *
          (1 + this.teamTechLevels.familiar_fury * 0.04);
        for (const enemy of [...this.enemies.values()]) {
          if (
            Math.hypot(enemy.x - entity.x, enemy.y - entity.y) >
            ability.radius
          ) {
            continue;
          }
          this.damageEnemy(enemy, damage, owner.id, {
            kind: "summon",
            abilityId: baseAbilityId(entity.abilityId),
            entityId: entity.id,
          });
          hitIds.push(enemy.id);
        }
        if (hitIds.length > 0) {
          this.events.push({
            type: "shot",
            playerId: owner.id,
            targetIds: hitIds,
            abilityId: entity.abilityId,
          });
        }
      }
    }
  }

  private updateUndeadEntityAttack(
    entity: SimAbilityEntity,
    owner: SimPlayer,
  ): void {
    if (
      !entity.undeadUnitId ||
      entity.damage <= 0 ||
      entity.range <= 0 ||
      this.elapsedMs < entity.nextAttackAtMs
    ) {
      return;
    }
    const target = this.pickTarget(
      entity.x,
      entity.y,
      entity.range,
      "closest",
    );
    if (!target) {
      return;
    }
    const hitIds = [target.id];
    const applied =
      entity.damage *
      owner.stats.companionDamageMultiplier *
      (1 + this.teamTechLevels.familiar_fury * 0.04);
    const attribution: DamageAttribution = {
      kind: "summon",
      abilityId: baseAbilityId(entity.abilityId),
      entityId: entity.id,
    };
    this.damageEnemy(target, applied, owner.id, attribution);
    const unit = getUndeadUnit(entity.undeadUnitId);
    const cone = unit.abilities.find(
      (ability) => ability.kind === "cone_splash",
    );
    if (cone?.kind === "cone_splash") {
      const facingX = target.x - entity.x;
      const facingY = target.y - entity.y;
      const facingLength = Math.max(
        0.001,
        Math.hypot(facingX, facingY),
      );
      const minimumDot = Math.cos(
        (cone.angleDegrees * Math.PI) / 360,
      );
      for (const enemy of [...this.enemies.values()]) {
        if (enemy.id === target.id) {
          continue;
        }
        const dx = enemy.x - entity.x;
        const dy = enemy.y - entity.y;
        const distance = Math.hypot(dx, dy);
        if (
          distance > cone.radius ||
          distance <= 0.001 ||
          (dx * facingX + dy * facingY) /
            (distance * facingLength) <
            minimumDot
        ) {
          continue;
        }
        this.damageEnemy(
          enemy,
          applied * cone.damageMultiplier,
          owner.id,
          attribution,
        );
        hitIds.push(enemy.id);
      }
    }
    const hiddenAttackSpeed = this.getHiddenAllyAuraAt(
      entity.x,
      entity.y,
    ).attackSpeedMultiplier;
    entity.nextAttackAtMs =
      this.elapsedMs +
      Math.max(50, entity.intervalMs / hiddenAttackSpeed);
    this.events.push({
      type: "shot",
      playerId: owner.id,
      targetIds: hitIds,
      abilityId: entity.abilityId,
    });
  }

  private getPastureHuntMechanic(
    player: SimPlayer,
  ): Extract<
    SynergyMechanicDefinition,
    { kind: "pasture_hunt" }
  > | undefined {
    for (const synergyId of player.synergies) {
      const mechanic = getSynergy(synergyId).mechanics?.find(
        (entry) => entry.kind === "pasture_hunt",
      );
      if (mechanic?.kind === "pasture_hunt") {
        return mechanic;
      }
    }
    return undefined;
  }

  private getPastureTotalTier(player: SimPlayer): number {
    return [...new Set(player.modules.filter((id): id is string => Boolean(id)))]
      .map((id) => getModule(id))
      .filter((module) => module.tags.includes("pasture_spirit"))
      .reduce((total, module) => total + module.tier, 0);
  }

  private updatePastureHunt(
    entity: SimAbilityEntity,
    owner: SimPlayer,
  ): void {
    const mechanic = this.getPastureHuntMechanic(owner);
    if (!mechanic || this.elapsedMs < entity.nextHuntAtMs) {
      return;
    }
    entity.nextHuntAtMs = this.elapsedMs + mechanic.intervalMs;
    const target = this.pickTarget(
      entity.x,
      entity.y,
      mechanic.range,
      "random",
    );
    if (!target) {
      return;
    }
    const damage =
      this.getPastureTotalTier(owner) *
      mechanic.damagePerTotalTier *
      owner.stats.damageMultiplier *
      this.getPlayerAbilityDamageMultiplier(owner) *
      (1 + this.teamTechLevels.war_chant * 0.03);
    this.damageEnemy(target, damage, owner.id, {
      kind: "pasture",
      abilityId: baseAbilityId(entity.abilityId),
    });
    this.events.push({
      type: "shot",
      playerId: owner.id,
      targetIds: [target.id],
      abilityId: entity.abilityId,
    });
  }

  private randomPastureTarget(owner: Vec2, radius: number): Vec2 {
    const angle = this.random.next() * Math.PI * 2;
    const distance = Math.sqrt(this.random.next()) * radius;
    return {
      x: clamp(owner.x + Math.cos(angle) * distance, 24, WORLD_WIDTH - 24),
      y: clamp(owner.y + Math.sin(angle) * distance, 24, WORLD_HEIGHT - 24),
    };
  }

  private updatePastureSpiritMovement(
    entity: SimAbilityEntity,
    owner: SimPlayer,
    deltaMs: number,
  ): void {
    const movement = entity.movement;
    if (!movement || movement.kind !== "player_centered_wander") {
      return;
    }
    const distanceToOwner = Math.hypot(entity.x - owner.x, entity.y - owner.y);
    if (distanceToOwner > movement.returnTriggerRadius) {
      movement.returning = true;
    }
    const playerMoveSpeed =
      this.getPlayerMoveSpeed(owner) *
      this.getHiddenAllyAuraAt(owner.x, owner.y).moveSpeedMultiplier;
    if (movement.returning) {
      this.moveEntityToward(
        entity,
        owner,
        deltaMs,
        movement.returnReleaseRadius,
        playerMoveSpeed * movement.returnSpeedRatio,
        false,
      );
      if (
        Math.hypot(entity.x - owner.x, entity.y - owner.y) <=
        movement.returnReleaseRadius
      ) {
        movement.returning = false;
        movement.target = this.randomPastureTarget(
          owner,
          movement.wanderRadius,
        );
      }
      return;
    }
    if (
      Math.hypot(movement.target.x - owner.x, movement.target.y - owner.y) >
        movement.wanderRadius ||
      Math.hypot(movement.target.x - entity.x, movement.target.y - entity.y) <= 8
    ) {
      movement.target = this.randomPastureTarget(owner, movement.wanderRadius);
    }
    this.moveEntityToward(
      entity,
      movement.target,
      deltaMs,
      5,
      playerMoveSpeed * movement.wanderSpeedRatio,
      false,
    );
  }

  private updateFollowerMovement(
    entity: SimAbilityEntity,
    owner: SimPlayer,
    deltaMs: number,
  ): void {
    const movement = entity.movement;
    if (!movement || movement.kind !== "player_centered_random") {
      return;
    }
    const carrier = this.getActiveSynergyMechanic(
      owner,
      "paper_aircraft_carrier",
    );
    const whale =
      carrier?.kiteAbilityId === baseAbilityId(entity.abilityId)
        ? [...this.abilityEntities.values()].find(
            (candidate) =>
              candidate.ownerId === owner.id &&
              candidate.health > 0 &&
              baseAbilityId(candidate.abilityId) === carrier.whaleAbilityId,
          )
        : undefined;
    const center = whale ?? owner;
    const distanceToOwner = Math.hypot(entity.x - center.x, entity.y - center.y);
    if (distanceToOwner > movement.radius) {
      movement.returning = true;
    }
    const speed = PLAYER_BASE_MOVE_SPEED * movement.speedBaseRatio;
    if (movement.returning) {
      const releaseRadius = movement.radius * 0.8;
      this.moveEntityToward(
        entity,
        center,
        deltaMs,
        releaseRadius,
        speed,
        false,
      );
      if (
        Math.hypot(entity.x - center.x, entity.y - center.y) <= releaseRadius
      ) {
        movement.returning = false;
        movement.target = this.randomPastureTarget(center, movement.radius);
      }
      return;
    }
    if (
      Math.hypot(movement.target.x - center.x, movement.target.y - center.y) >
        movement.radius ||
      Math.hypot(movement.target.x - entity.x, movement.target.y - entity.y) <= 8
    ) {
      movement.target = this.randomPastureTarget(center, movement.radius);
    }
    this.moveEntityToward(
      entity,
      movement.target,
      deltaMs,
      5,
      speed,
      false,
    );
  }

  private updateFollowerAbilities(
    entity: SimAbilityEntity,
    owner: SimPlayer,
  ): void {
    const ability = getModule(baseAbilityId(entity.abilityId));
    const cooldowns = entity.followerAbilityCooldowns ?? new Map<string, number>();
    entity.followerAbilityCooldowns = cooldowns;
    for (const [index, mechanic] of (ability.mechanics ?? []).entries()) {
      if (mechanic.kind === "periodic_health_extremes_damage") {
        this.updateFollowerHealthExtremes(entity, owner, ability, mechanic, index);
        continue;
      }
      if (mechanic.kind === "follower_periodic_area_damage") {
        this.updateFollowerAreaDamage(entity, owner, ability, mechanic, index);
        continue;
      }
      if (mechanic.kind !== "follower_periodic_damage") {
        continue;
      }
      const key = `native:${index}`;
      const paper = this.getActiveSynergyMechanic(owner, "paper_units");
      const intervalMs = mechanic.intervalMs /
        Math.max(
          0.05,
          1 + (paper?.abilityIds.includes(ability.id) ? paper.attackSpeedBonus : 0),
        );
      const nextAt = cooldowns.get(key);
      if (nextAt === undefined) {
        cooldowns.set(key, this.elapsedMs + intervalMs);
        continue;
      }
      if (this.elapsedMs < nextAt) {
        continue;
      }
      if (mechanic.consumeEmptyTick) {
        cooldowns.set(key, this.elapsedMs + intervalMs);
      }
      const initialTargets = this.pickCompanionTargets(
        entity,
        owner,
        ability,
        mechanic.range,
        key,
      );
      if (initialTargets.length === 0) {
        continue;
      }
      if (!mechanic.consumeEmptyTick) {
        cooldowns.set(key, this.elapsedMs + intervalMs);
      }
      const castExecutions = entity.sourceSlot === undefined
        ? 1
        : this.registerDiscreteSkillCast(owner, `slot:${entity.sourceSlot}`);
      for (let cast = 0; cast < castExecutions; cast += 1) {
        const castTargets = cast === 0
          ? initialTargets
          : this.pickCompanionTargets(
              entity,
              owner,
              ability,
              mechanic.range,
              `${key}:double`,
            );
        for (const castTarget of castTargets) {
          const rank = getEnemy(castTarget.definitionId).rank;
          const eliteMultiplier =
            rank === "normal" ? 1 : owner.stats.eliteDamageMultiplier;
          const damage =
            mechanic.damage *
            this.getStoredExperienceCompanionDamageMultiplier(owner, ability.id) *
            owner.stats.damageMultiplier *
            this.getPlayerAbilityDamageMultiplier(owner) *
            (1 + this.teamTechLevels.war_chant * 0.03) *
            eliteMultiplier;
          entity.attackCount += 1;
          const attackExecutions =
            paper?.abilityIds.includes(ability.id) &&
            entity.attackCount % paper.doubleEveryAttacks === 0
              ? 2
              : 1;
          for (let attack = 0; attack < attackExecutions; attack += 1) {
            for (let hit = 0; hit < mechanic.hits; hit += 1) {
              if (!this.enemies.has(castTarget.id)) break;
              this.damageEnemy(castTarget, damage, owner.id, {
                kind: "follower",
                abilityId: ability.id,
                entityId: entity.id,
                sourceSlot: entity.sourceSlot,
              });
              if (this.enemies.has(castTarget.id)) {
                this.applyCompanionOnHit(entity, owner, ability, castTarget);
              }
            }
          }
          this.events.push({
            type: "shot",
            playerId: owner.id,
            targetIds: [castTarget.id],
            abilityId: ability.id,
          });
        }
      }
    }

    const granted = this.getActiveSynergyMechanic(
      owner,
      "follower_granted_skill",
    );
    if (
      !granted ||
      granted.targetAbilityId !== ability.id
    ) {
      return;
    }
    const grantedAbility = getModule(granted.grantedAbilityId);
    const combat = grantedAbility.combat;
    if (!combat) {
      return;
    }
    const key = `granted:${grantedAbility.id}`;
    const nextAt = cooldowns.get(key);
    if (nextAt === undefined) {
      cooldowns.set(key, this.elapsedMs + combat.intervalMs);
      return;
    }
    if (this.elapsedMs < nextAt) {
      return;
    }
    const target = this.pickTarget(
      entity.x,
      entity.y,
      combat.range,
      combat.target,
    );
    if (!target) {
      return;
    }
    cooldowns.set(key, this.elapsedMs + combat.intervalMs);
    const rank = getEnemy(target.definitionId).rank;
    const eliteMultiplier =
      rank === "normal" ? 1 : owner.stats.eliteDamageMultiplier;
    this.damageEnemy(
      target,
      (combat.damage +
        (owner.abilityDamageGrowth.get(grantedAbility.id) ?? 0)) *
        owner.stats.damageMultiplier *
        this.getPlayerAbilityDamageMultiplier(owner) *
        (1 + this.teamTechLevels.war_chant * 0.03) *
        eliteMultiplier,
      owner.id,
      {
        kind: "follower",
        abilityId: grantedAbility.id,
        entityId: entity.id,
      },
    );
    this.applyCombatStun(
      target,
      combat.stunDurationMs,
      combat.stunBosses ?? true,
    );
    this.events.push({
      type: "shot",
      playerId: owner.id,
      targetIds: [target.id],
      abilityId: grantedAbility.id,
    });
  }

  private updateFollowerAreaDamage(
    entity: SimAbilityEntity,
    owner: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "follower_periodic_area_damage" }
    >,
    mechanicIndex: number,
  ): void {
    const cooldowns = entity.followerAbilityCooldowns ?? new Map<string, number>();
    entity.followerAbilityCooldowns = cooldowns;
    const key = `native-area:${mechanicIndex}`;
    const nextAt = cooldowns.get(key);
    if (nextAt === undefined) {
      cooldowns.set(key, this.elapsedMs + mechanic.intervalMs);
      return;
    }
    if (this.elapsedMs < nextAt) return;
    cooldowns.set(key, this.elapsedMs + mechanic.intervalMs);
    const hitIds: number[] = [];
    for (const enemy of [...this.enemies.values()]) {
      if (
        enemy.spaceId !== entity.spaceId ||
        Math.hypot(enemy.x - entity.x, enemy.y - entity.y) > mechanic.radius
      ) {
        continue;
      }
      const rank = getEnemy(enemy.definitionId).rank;
      this.damageEnemy(
        enemy,
        mechanic.damage *
          owner.stats.damageMultiplier *
          this.getPlayerAbilityDamageMultiplier(owner) *
          (1 + this.teamTechLevels.war_chant * 0.03) *
          (rank === "normal" ? 1 : owner.stats.eliteDamageMultiplier),
        owner.id,
        {
          kind: "follower",
          abilityId: ability.id,
          entityId: entity.id,
          sourceSlot: entity.sourceSlot,
        },
      );
      if ((mechanic.slowRatio ?? 0) > 0 && this.enemies.has(enemy.id)) {
        this.applyAbilitySlow(
          owner,
          enemy,
          ability.id,
          mechanic.slowRatio ?? 0,
          mechanic.slowDurationMs ?? 0,
        );
      }
      hitIds.push(enemy.id);
    }
    if (hitIds.length > 0) {
      this.events.push({
        type: "shot",
        playerId: owner.id,
        targetIds: hitIds,
        abilityId: ability.id,
      });
    }
  }

  private updateFollowerHealthExtremes(
    entity: SimAbilityEntity,
    owner: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "periodic_health_extremes_damage" }
    >,
    mechanicIndex: number,
  ): void {
    const cooldowns = entity.followerAbilityCooldowns ?? new Map<string, number>();
    entity.followerAbilityCooldowns = cooldowns;
    const key = `health-extremes:${mechanicIndex}`;
    const nextAt = cooldowns.get(key);
    if (nextAt === undefined) {
      cooldowns.set(key, this.elapsedMs + mechanic.intervalMs);
      return;
    }
    if (this.elapsedMs < nextAt) return;
    cooldowns.set(key, this.elapsedMs + mechanic.intervalMs);
    const highest = this.pickTarget(
      entity.x,
      entity.y,
      mechanic.range,
      "highest_health",
      undefined,
      entity.spaceId,
    );
    const lowest = this.pickTarget(
      entity.x,
      entity.y,
      mechanic.range,
      "lowest_health",
      undefined,
      entity.spaceId,
    );
    const hitIds: number[] = [];
    const common =
      owner.stats.damageMultiplier *
      this.getPlayerAbilityDamageMultiplier(owner) *
      (1 + this.teamTechLevels.war_chant * 0.03);
    if (highest) {
      const rank = getEnemy(highest.definitionId).rank;
      this.damageEnemy(
        highest,
        mechanic.highestDamage * common *
          (rank === "normal" ? 1 : owner.stats.eliteDamageMultiplier),
        owner.id,
        {
          kind: "follower",
          abilityId: ability.id,
          entityId: entity.id,
          sourceSlot: entity.sourceSlot,
          variant: "highest_health",
        },
      );
      hitIds.push(highest.id);
    }
    if (lowest && this.enemies.has(lowest.id)) {
      const rank = getEnemy(lowest.definitionId).rank;
      this.damageEnemy(
        lowest,
        mechanic.lowestDamage * common *
          (rank === "normal" ? 1 : owner.stats.eliteDamageMultiplier),
        owner.id,
        {
          kind: "follower",
          abilityId: ability.id,
          entityId: entity.id,
          sourceSlot: entity.sourceSlot,
          variant: "lowest_health",
        },
      );
      hitIds.push(lowest.id);
    }
    if (hitIds.length > 0) {
      this.events.push({
        type: "shot",
        playerId: owner.id,
        targetIds: [...new Set(hitIds)],
        abilityId: ability.id,
      });
    }
  }

  private pickCompanionTargets(
    entity: SimAbilityEntity,
    owner: SimPlayer,
    ability: ModuleDefinition,
    range: number,
    counterKey: string,
  ): SimEnemy[] {
    const dual = this.getActiveSynergyMechanic(owner, "dual_extremes_nth");
    if (dual?.abilityIds.includes(ability.id)) {
      const key = `dual_extremes:entity:${entity.id}:${counterKey}`;
      const ordinal = (owner.abilityTargetOrdinals.get(key) ?? 0) + 1;
      owner.abilityTargetOrdinals.set(key, ordinal);
      if (ordinal % dual.every === 0) {
        const nearest = this.pickTarget(
          entity.x,
          entity.y,
          range,
          "closest",
          undefined,
          entity.spaceId,
        );
        const farthest = this.pickTarget(
          entity.x,
          entity.y,
          range,
          "farthest",
          undefined,
          entity.spaceId,
        );
        return [...new Map(
          [nearest, farthest]
            .filter((enemy): enemy is SimEnemy => Boolean(enemy))
            .map((enemy) => [enemy.id, enemy]),
        ).values()];
      }
    }
    const priority = ability.companion?.targetPriority ?? "random";
    if (priority === "last_player_skill_target") {
      const target = owner.lastPlayerSkillTargetId
        ? this.enemies.get(owner.lastPlayerSkillTargetId)
        : undefined;
      if (
        target &&
        target.spaceId === entity.spaceId &&
        Math.hypot(target.x - entity.x, target.y - entity.y) <= range
      ) {
        return [target];
      }
      return this.pickTargets(
        entity.x,
        entity.y,
        range,
        "closest",
        1,
        undefined,
        entity.spaceId,
      );
    }
    if (priority === "alternating_extremes") {
      const key = `alternating:entity:${entity.id}:${counterKey}`;
      const ordinal = owner.abilityTargetOrdinals.get(key) ?? 0;
      owner.abilityTargetOrdinals.set(key, ordinal + 1);
      return this.pickTargets(
        entity.x,
        entity.y,
        range,
        ordinal % 2 === 0 ? "closest" : "farthest",
        1,
        undefined,
        entity.spaceId,
      );
    }
    return this.pickTargets(
      entity.x,
      entity.y,
      range,
      priority,
      1,
      undefined,
      entity.spaceId,
    );
  }

  private applyCompanionOnHit(
    entity: SimAbilityEntity,
    owner: SimPlayer,
    ability: ModuleDefinition,
    enemy: SimEnemy,
  ): void {
    this.applyTierFourSynergyOnHit(owner, enemy, ability.id, {
      kind: entity.kind === "field" ? "ability" : entity.kind,
      abilityId: ability.id,
      entityId: entity.id,
      sourceSlot: entity.sourceSlot,
    });
    const effect = ability.companion?.onHit;
    if (effect?.revealDurationMs) {
      enemy.revealedUntilMs = Math.max(
        enemy.revealedUntilMs,
        this.elapsedMs + effect.revealDurationMs,
      );
    }
    if (effect?.pullDistance && getEnemy(enemy.definitionId).rank !== "boss") {
      this.displaceEnemy(enemy, entity, -effect.pullDistance);
    }
    if (effect?.slowRatio) {
      this.applyAbilitySlow(
        owner,
        enemy,
        ability.id,
        effect.slowRatio,
        effect.slowDurationMs ?? 0,
      );
    }
    if (
      effect?.forcedRetreatDistance &&
      getEnemy(enemy.definitionId).rank !== "boss"
    ) {
      this.displaceEnemy(enemy, entity, effect.forcedRetreatDistance);
      enemy.slowRatio = Math.max(
        enemy.slowRatio,
        effect.forcedRetreatSlowRatio ?? 0,
      );
      enemy.slowUntilMs = Math.max(enemy.slowUntilMs, this.elapsedMs + 500);
    }
    const bossDelay = ability.mechanics?.find(
      (mechanic) => mechanic.kind === "boss_attack_delay_on_hit",
    );
    if (
      bossDelay?.kind === "boss_attack_delay_on_hit" &&
      getEnemy(enemy.definitionId).rank === "boss"
    ) {
      enemy.nextAttackAtMs += bossDelay.delayMs;
      enemy.nextPlayerAttackAtMs += bossDelay.delayMs;
    }
    const combo = this.getActiveSynergyMechanic(owner, "follower_trio_combo");
    if (!combo?.abilityIds.includes(ability.id)) return;
    for (const [sourceId, expiresAtMs] of enemy.followerComboHits) {
      if (expiresAtMs <= this.elapsedMs) {
        enemy.followerComboHits.delete(sourceId);
      }
    }
    enemy.followerComboHits.set(
      ability.id,
      this.elapsedMs + combo.windowMs,
    );
    if (combo.abilityIds.every((id) => enemy.followerComboHits.has(id))) {
      enemy.followerComboHits.clear();
      this.damageEnemy(enemy, combo.damage, owner.id, {
        kind: "follower",
        abilityId: "not_so_hot_blooded_combo",
        entityId: entity.id,
      });
    }
  }

  private moveEntityToward(
    entity: SimAbilityEntity,
    target: Vec2,
    deltaMs: number,
    stopDistance: number,
    moveSpeed = entity.moveSpeed,
    applyLocalAura = true,
  ): void {
    const dx = target.x - entity.x;
    const dy = target.y - entity.y;
    const distance = Math.max(0.001, Math.hypot(dx, dy));
    if (distance <= stopDistance) {
      return;
    }
    const hiddenMoveSpeed = applyLocalAura
      ? this.getHiddenAllyAuraAt(entity.x, entity.y).moveSpeedMultiplier
      : 1;
    const step = Math.min(
      distance - stopDistance,
      moveSpeed *
        hiddenMoveSpeed *
        this.getSummonCommanderAura(entity).moveSpeedMultiplier *
        (deltaMs / 1_000),
    );
    entity.x += (dx / distance) * step;
    entity.y += (dy / distance) * step;
  }

  private getSummonCommanderAura(entity: SimAbilityEntity): {
    attackSpeedMultiplier: number;
    moveSpeedMultiplier: number;
  } {
    if (entity.presentationId === "summon.star_gate_commander") {
      return { attackSpeedMultiplier: 1, moveSpeedMultiplier: 1 };
    }
    let attackSpeedBonus = 0;
    let moveSpeedBonus = 0;
    const targetAbilityId = baseAbilityId(entity.abilityId);
    for (const commander of this.abilityEntities.values()) {
      if (
        commander.ownerId !== entity.ownerId ||
        commander.spaceId !== entity.spaceId ||
        commander.health <= 0 ||
        commander.presentationId !== "summon.star_gate_commander"
      ) {
        continue;
      }
      const commanderAbility = getModule(baseAbilityId(commander.abilityId));
      const mechanic = commanderAbility.mechanics?.find(
        (entry) => entry.kind === "every_nth_summon_commander",
      );
      if (
        mechanic?.kind !== "every_nth_summon_commander" ||
        !mechanic.auraTargetAbilityIds.includes(targetAbilityId) ||
        Math.hypot(commander.x - entity.x, commander.y - entity.y) >
          mechanic.auraRadius
      ) {
        continue;
      }
      attackSpeedBonus = Math.max(
        attackSpeedBonus,
        mechanic.auraAttackSpeedBonus,
      );
      moveSpeedBonus = Math.max(moveSpeedBonus, mechanic.auraMoveSpeedBonus);
    }
    return {
      attackSpeedMultiplier: 1 + attackSpeedBonus,
      moveSpeedMultiplier: 1 + moveSpeedBonus,
    };
  }

  private rebirthBurst(owner: SimPlayer, entity: SimAbilityEntity): void {
    for (const enemy of this.enemies.values()) {
      if (Math.hypot(enemy.x - entity.x, enemy.y - entity.y) <= 260) {
        this.damageEnemy(
          enemy,
          180 * owner.stats.companionDamageMultiplier,
          owner.id,
          {
            kind: "guard",
            abilityId: baseAbilityId(entity.abilityId),
          },
        );
      }
    }
    for (const player of this.players.values()) {
      if (Math.hypot(player.x - entity.x, player.y - entity.y) <= 420) {
        const before = player.health;
        player.health = Math.min(player.stats.maxHealth, player.health + 90);
        owner.contribution.healingDone += player.health - before;
      }
    }
  }

  private damageEnemy(
    enemy: SimEnemy,
    rawDamage: number,
    playerId: string | undefined,
    attribution: DamageAttribution | "tower",
  ): number {
    if (!this.enemies.has(enemy.id)) {
      return 0;
    }
    const effectiveArmor = Math.max(
      0,
      enemy.armor - this.getEnemyArmorReduction(enemy),
    );
    const marshalShieldMultiplier = this.getMarshalShieldMultiplier(
      enemy,
      playerId,
      attribution,
    );
    const damage =
      rawDamage * marshalShieldMultiplier * (100 / (100 + effectiveArmor));
    const applied = Math.max(0, Math.min(enemy.health, damage));
    enemy.health -= damage;
    const player = playerId
      ? this.players.get(playerId)
      : undefined;
    if (applied > 0 && player && attribution !== "tower") {
      const entity = attribution.entityId
        ? this.abilityEntities.get(attribution.entityId)
        : undefined;
      enemy.recentAttacker = entity
        ? {
            kind: "ability",
            id: entity.id,
            expiresAtMs:
              this.elapsedMs + CITY_ENEMY_AGGRO_CONFIG.retaliationMemoryMs,
          }
        : {
            kind: "player",
            id: player.id,
            expiresAtMs:
              this.elapsedMs + CITY_ENEMY_AGGRO_CONFIG.retaliationMemoryMs,
          };
    }
    if (player) {
      player.contribution.damageDealt += applied;
      if (attribution !== "tower") {
        this.recordDamageSource(player, attribution, applied);
      }
    }
    if (enemy.health <= 0) {
      this.killEnemy(
        enemy,
        playerId,
        attribution === "tower" ? "tower" : "player",
        attribution === "tower" ? undefined : attribution,
      );
      if (
        player &&
        attribution !== "tower" &&
        attribution.kind === "ability" &&
        attribution.abilityId &&
        attribution.variant !== "long_range_kill_echo"
      ) {
        this.triggerLongRangeKillEcho(
          player,
          baseAbilityId(attribution.abilityId),
          rawDamage,
        );
      }
    }
    return applied;
  }

  private triggerLongRangeKillEcho(
    player: SimPlayer,
    abilityId: string,
    originalDamage: number,
  ): void {
    const mechanic = this.getActiveSynergyMechanic(player, "long_range_strike");
    if (!mechanic?.abilityIds.includes(abilityId)) return;
    const target = this.pickTarget(
      player.x,
      player.y,
      Number.POSITIVE_INFINITY,
      "farthest",
      undefined,
      player.spaceId,
    );
    if (!target) return;
    this.damageEnemy(
      target,
      originalDamage * mechanic.killEchoRatio,
      player.id,
      {
        kind: "ability",
        abilityId,
        variant: "long_range_kill_echo",
      },
    );
    this.events.push({
      type: "shot",
      playerId: player.id,
      targetIds: [target.id],
      abilityId: `${abilityId}:long_range_kill_echo`,
    });
  }

  private recordDamageSource(
    player: SimPlayer,
    attribution: DamageAttribution,
    damage: number,
  ): void {
    if (damage <= 0) {
      return;
    }
    const abilityId = attribution.abilityId
      ? baseAbilityId(attribution.abilityId)
      : null;
    const key =
      attribution.kind === "body"
        ? "body"
        : attribution.kind === "hidden_effect"
          ? `hidden_effect:${attribution.hiddenSourceId ?? "unknown"}`
          : `${attribution.kind}:${abilityId ?? "unknown"}`;
    let entry = player.contribution.damageBySource.find(
      (candidate) => candidate.key === key,
    );
    if (!entry) {
      entry = {
        key,
        kind: attribution.kind,
        abilityId,
        hiddenSourceId: attribution.hiddenSourceId ?? null,
        damage: 0,
      } satisfies DamageSourceEntry;
      player.contribution.damageBySource.push(entry);
    }
    entry.damage += damage;
  }

  private killEnemy(
    enemy: SimEnemy,
    playerId?: string,
    source: "player" | "tower" = "player",
    attribution?: DamageAttribution,
  ): void {
    if (!this.enemies.delete(enemy.id)) {
      return;
    }
    const definition = getEnemy(enemy.definitionId);
    const difficulty = DIFFICULTIES[this.difficulty];
    const killer = playerId
      ? this.players.get(playerId)
      : undefined;
    if (enemy.objectiveId === "rift") {
      const rift = this.objectives.find((objective) => objective.id === "rift");
      const playerParticipated = Boolean(
        killer &&
          killer.status !== "downed" &&
          Math.hypot(killer.x - (rift?.x ?? 0), killer.y - (rift?.y ?? 0)) <=
            RIFT_WANDER_RADIUS,
      );
      if (rift?.status === "active" && playerParticipated) {
        rift.playerEngaged = true;
        rift.kills = Math.min(
          rift.requiredKills ?? RIFT_REQUIRED_KILLS,
          (rift.kills ?? 0) + 1,
        );
        if ((rift.kills ?? 0) >= (rift.requiredKills ?? RIFT_REQUIRED_KILLS)) {
          this.completeRiftObjective(rift);
        }
      }
    }
    if (definition.id === "storm_tyrant") {
      this.finalBossDefeated = true;
    }
    if (killer) {
      killer.kills += 1;
      this.applyTierFourKillEffects(killer, attribution);
      this.triggerGrowth(killer, "kill", 1);
      this.applyAbilityKillGrowth(killer, attribution);
      this.applyKillCharge(killer, attribution);
      if (attribution?.abilityId) {
        this.applyDebtOutsourcingKill(
          killer,
          baseAbilityId(attribution.abilityId),
        );
      }
      if (source === "player") {
        this.applyLaborKillBonus(killer);
      }
      if (attribution?.entityId !== undefined) {
        this.applyUndeadKillBenefits(attribution.entityId);
      }
      if (
        attribution?.abilityId &&
        baseAbilityId(attribution.abilityId) ===
          this.getUndeadCatastropheMechanic(killer)
            ?.triggerAbilityId
      ) {
        this.trySpawnUndeadCatastrophe(killer, enemy, definition.rank);
      }
    }
    const killRewardMultiplier =
      source === "player"
        ? this.getKillRewardMultiplier(attribution)
        : 1;
    if (source === "tower") {
      this.towerGoldPool += Math.max(
        1,
        Math.round(
          definition.scrapReward * difficulty.rewardMultiplier,
        ),
      );
    }
    for (const player of this.players.values()) {
      const personalRewardMultiplier =
        player.id === playerId ? killRewardMultiplier : 1;
      const normalGold = definition.id === "siege_marshal"
        ? 0
        : Math.max(
            1,
            Math.round(
              definition.scrapReward *
                difficulty.rewardMultiplier *
                player.stats.goldMultiplier,
            ),
          );
      const normalExperience = Math.max(
        1,
        Math.round(
          definition.experienceReward *
            difficulty.rewardMultiplier,
        ),
      );
      const gold = normalGold * personalRewardMultiplier;
      const experience = normalExperience * personalRewardMultiplier;
      if (source !== "tower") {
        player.scrap += gold;
        player.contribution.scrapEarned += gold;
      }
      this.addExperience(player, experience);
      if (source !== "tower" && player.id === playerId) {
        // Every attributed kill always grants exactly one personal crystal.
        player.core += 1;
        if (this.random.next() < player.stats.crystalDropChance) {
          player.core += 1;
        }
      }
    }
    if (definition.id === "siege_marshal") {
      this.core.health = Math.min(
        this.core.maxHealth,
        this.core.health + this.core.maxHealth * 0.05,
      );
      this.rewardTeam(500, 0, 0);
      this.events.push({
        type: "notice",
        message: "王国破城元帅·赫克托被击败：核心恢复5%最大生命",
      });
    }
    this.events.push({ type: "enemyDeath", enemyIds: [enemy.id] });
    if (definition.splitInto) {
      for (let index = 0; index < 2; index += 1) {
        this.spawnEnemy(
          definition.splitInto,
          enemy.x + this.random.integer(-20, 20),
          enemy.y + this.random.integer(-20, 20),
          true,
          enemy.laneId,
        );
      }
    }
  }

  private applyTierFourKillEffects(
    killer: SimPlayer,
    attribution?: DamageAttribution,
  ): void {
    const abilityId = attribution?.abilityId
      ? baseAbilityId(attribution.abilityId)
      : undefined;
    if (!abilityId) return;
    try {
      const healing = getModule(abilityId).combat?.healOnKill ?? 0;
      if (healing > 0) {
        const before = killer.health;
        killer.health = Math.min(killer.stats.maxHealth, killer.health + healing);
        killer.contribution.healingDone += killer.health - before;
      }
    } catch {
      // Internal synergy damage sources are not required to be catalog modules.
    }
    const soul = this.getActiveSynergyMechanic(killer, "soul_weighing");
    if (
      !soul ||
      soul.balanceAbilityId !== abilityId ||
      attribution?.variant !== "lowest_health"
    ) {
      return;
    }
    const balance = getModule(soul.balanceAbilityId);
    const mechanic = balance.mechanics?.find(
      (entry) => entry.kind === "periodic_health_extremes_damage",
    );
    if (!mechanic || mechanic.kind !== "periodic_health_extremes_damage") return;
    const source = attribution?.entityId
      ? (this.abilityEntities.get(attribution.entityId) ?? killer)
      : killer;
    const target = this.pickTarget(
      source.x,
      source.y,
      mechanic.range,
      "highest_health",
      undefined,
      source.spaceId,
    );
    if (!target) return;
    const rank = getEnemy(target.definitionId).rank;
    this.damageEnemy(
      target,
      mechanic.highestDamage *
        soul.balanceEchoRatio *
        killer.stats.damageMultiplier *
        this.getPlayerAbilityDamageMultiplier(killer) *
        (1 + this.teamTechLevels.war_chant * 0.03) *
        (rank === "normal" ? 1 : killer.stats.eliteDamageMultiplier),
      killer.id,
      { kind: "ability", abilityId: "soul_weighing_echo" },
    );
    this.events.push({
      type: "shot",
      playerId: killer.id,
      targetIds: [target.id],
      abilityId: "soul_weighing_echo",
    });
  }

  private applyDebtOutsourcingKill(
    player: SimPlayer,
    abilityId: string,
  ): void {
    const mechanic = this.getActiveSynergyMechanic(
      player,
      "debt_outsourcing",
    );
    if (mechanic?.crowAbilityId !== abilityId) return;
    for (let slot = 0; slot < player.modules.length; slot += 1) {
      const moduleId = player.modules[slot];
      if (!moduleId) continue;
      const module = getModule(moduleId);
      const familyId = module.financeState?.familyId;
      if (
        !module.financeState?.timedOutcome ||
        (familyId !== "fraud_imp_chain" &&
          familyId !== "wandering_merchant_chain")
      ) {
        continue;
      }
      const state = this.getModuleSlotState(player, slot);
      if (!state?.financeNextAtMs) continue;
      const applied = player.debtReductionBySlot.get(slot) ?? 0;
      const reduction = Math.min(
        mechanic.reductionPerKillMs,
        Math.max(0, mechanic.maxReductionPerRoundMs - applied),
      );
      if (reduction <= 0) continue;
      state.financeNextAtMs = Math.max(
        this.elapsedMs,
        state.financeNextAtMs - reduction,
      );
      player.debtReductionBySlot.set(slot, applied + reduction);
    }
  }

  private getKillRewardMultiplier(
    attribution: DamageAttribution | undefined,
  ): number {
    if (!attribution?.abilityId) {
      return 1;
    }
    let ability: ModuleDefinition;
    try {
      ability = getModule(baseAbilityId(attribution.abilityId));
    } catch {
      return 1;
    }
    const mechanic = ability.mechanics?.find(
      (entry) => entry.kind === "periodic_normal_execute_reward",
    );
    return mechanic?.kind === "periodic_normal_execute_reward"
      ? mechanic.rewardMultiplier
      : 1;
  }

  private applyAbilityKillGrowth(
    player: SimPlayer,
    attribution: DamageAttribution | undefined,
  ): void {
    if (!attribution?.abilityId) {
      return;
    }
    const abilityId = baseAbilityId(attribution.abilityId);
    let ability: ModuleDefinition;
    try {
      ability = getModule(abilityId);
    } catch {
      return;
    }
    const growth = ability.mechanics?.find(
      (entry) => entry.kind === "ability_kill_growth",
    );
    if (growth?.kind !== "ability_kill_growth") {
      return;
    }
    const growthKey = `${abilityId}:ability_kill_growth`;
    let growthKills = (player.growthCounters.get(growthKey) ?? 0) + 1;
    let changed = false;
    while (growthKills >= growth.killsPerSettlement) {
      growthKills -= growth.killsPerSettlement;
      if (this.random.next() < 0.5) {
        player.growthBonuses.maxHealth =
          (player.growthBonuses.maxHealth ?? 0) +
          growth.maxHealthAdd;
      } else {
        player.growthBonuses.damage =
          (player.growthBonuses.damage ?? 0) +
          growth.damageAdd;
      }
      changed = true;
    }
    player.growthCounters.set(growthKey, growthKills);

    const paradise = this.getActiveSynergyMechanic(
      player,
      "greed_paradise",
    );
    if (paradise?.growthAbilityId === abilityId) {
      const damageKey = `${abilityId}:greed_paradise_damage`;
      let damageKills = (player.growthCounters.get(damageKey) ?? 0) + 1;
      while (damageKills >= paradise.killsPerDamageGrowth) {
        damageKills -= paradise.killsPerDamageGrowth;
        player.abilityDamageGrowth.set(
          abilityId,
          (player.abilityDamageGrowth.get(abilityId) ?? 0) +
            paradise.damagePerSettlement,
        );
      }
      player.growthCounters.set(damageKey, damageKills);
    }
    if (changed) {
      this.recomputePlayer(player);
    }
  }

  private applyKillCharge(
    player: SimPlayer,
    attribution: DamageAttribution | undefined,
  ): void {
    if (
      attribution?.kind !== "ability" ||
      attribution.sourceSlot === undefined ||
      !attribution.abilityId
    ) {
      return;
    }
    const slot = attribution.sourceSlot;
    const abilityId = baseAbilityId(attribution.abilityId);
    if (player.modules[slot] !== abilityId) {
      return;
    }
    const ability = getModule(abilityId);
    const charge = ability.killCharge;
    if (!charge || charge.trigger !== "direct_kill") {
      return;
    }
    const state = this.getModuleSlotState(player, slot);
    if (!state || state.moduleId !== abilityId) {
      return;
    }
    state.killCharge += 1;
    if (state.killCharge < charge.requiredCount) {
      return;
    }
    this.replaceModuleInSlot(player, slot, charge.targetModuleId, {
      suppressVaultPayout: true,
      lockSlot: false,
    });
    this.events.push({
      type: "notice",
      message: `${player.name} 的「${ability.name}」凝聚了${charge.requiredCount}点灵魂力量，在原槽位进阶为「${getModule(charge.targetModuleId).name}」。`,
    });
  }

  private applyLaborKillBonus(player: SimPlayer): void {
    const entries = this.getLaborSlots(player, "kill_bonus");
    if (entries.length === 0) {
      return;
    }
    let granted = 0;
    for (const entry of entries) {
      if (entry.labor.kind !== "kill_bonus") continue;
      granted += entry.labor.goldPerOwnedKill;
    }
    this.grantFinanceResource(player, "gold", granted);
    player.laborBarbecueBonusGold += granted;
    const labor = entries[0].labor;
    if (
      labor.kind === "kill_bonus" &&
      player.laborBarbecueBonusGold >= labor.achievementBonusGold
    ) {
      this.grantPermanentHiddenEffect(player, labor.hiddenEffectId);
    }
  }

  private applyUndeadKillBenefits(entityId: number): void {
    const entity = this.abilityEntities.get(entityId);
    if (!entity?.undeadUnitId || entity.health <= 0) {
      return;
    }
    const unit = getUndeadUnit(entity.undeadUnitId);
    for (const ability of unit.abilities) {
      if (ability.kind === "kill_heal") {
        entity.health = Math.min(
          entity.maxHealth,
          entity.health + ability.healing,
        );
      } else if (ability.kind === "kill_growth") {
        entity.maxHealth += ability.maxHealthAdd;
        entity.health = Math.min(
          entity.maxHealth,
          entity.health + ability.currentHealthAdd,
        );
        entity.damage += ability.damageAdd;
      }
    }
  }

  private trySpawnUndeadCatastrophe(
    player: SimPlayer,
    corpse: SimEnemy,
    rank: "normal" | "elite" | "boss",
  ): void {
    const mechanic = this.getUndeadCatastropheMechanic(player);
    if (!mechanic || this.random.next() >= mechanic.summonChance) {
      return;
    }
    const pool =
      rank === "normal" ? mechanic.normalPool : mechanic.heavyPool;
    const selected = this.random.weightedPick(
      pool,
      (entry) => entry.weight,
    );
    const entity = this.spawnUndeadEntity(
      player,
      selected.unitId,
      `undead_catastrophe:${selected.unitId}`,
      corpse.x,
      corpse.y,
    );
    this.events.push({
      type: "notice",
      message: `${player.name} 的亡灵天灾从尸体上唤起了${getUndeadUnit(entity.undeadUnitId!).name}`,
    });
  }

  private replaceModuleInSlot(
    player: SimPlayer,
    slot: number,
    nextModuleId: string,
    options: {
      suppressVaultPayout: boolean;
      suppressReplacementReward?: boolean;
      lockSlot?: boolean;
      checkLevelEvolution?: boolean;
      financeStake?: {
        targetId: FinanceStakeTargetId;
        amount: number;
      };
    },
  ): void {
    const previousId = player.modules[slot];
    const previous = previousId ? getModule(previousId) : undefined;
    const previousState = player.moduleSlotStates.get(slot);
    const payoutBase =
      previous?.experienceVault &&
      previousState?.moduleId === previous.id &&
      !options.suppressVaultPayout
        ? previousState.experienceStored *
          previous.experienceVault.replacementPayoutMultiplier
        : 0;
    const payoutExperience = payoutBase * player.stats.experienceMultiplier;
    const replacementReward =
      previousId !== nextModuleId &&
      previous?.financeState?.onReplacementReward &&
      !options.suppressReplacementReward
        ? previous.financeState.onReplacementReward
        : undefined;

    player.moduleSlotStates.delete(slot);
    player.castReleaseCounts.delete(`slot:${slot}`);
    player.modules[slot] = nextModuleId;
    player.lockedSlots[slot] = options.lockSlot ?? false;
    player.declinedUpgradeOffers.delete(slot);
    player.upgradeRefreshSignatures.delete(slot);
    if (player.pendingOffer?.slot === slot) {
      player.pendingOffer = undefined;
    }
    this.removeEntitiesForAbility(player.id, previousId);

    const next = getModule(nextModuleId);
    if (next.experienceVault || next.killCharge || next.financeState || next.labor) {
      player.moduleSlotStates.set(slot, {
        moduleId: next.id,
        experienceStored: 0,
        killCharge: 0,
        financeEnteredAtMs: next.financeState ? this.elapsedMs : undefined,
        financeAccumulatedSpend: 0,
        financeCharge: 0,
        laborDistanceRemainder: 0,
        laborStationaryRemainderMs: 0,
        laborMovingRemainderMs: 0,
        financeStake: options.financeStake
          ? { ...options.financeStake }
          : undefined,
      });
    }
    this.applyOnAcquire(player, next);
    if (replacementReward) {
      this.grantFinanceResource(
        player,
        replacementReward.resource,
        replacementReward.amount,
      );
    }
    this.recomputePlayer(player);
    this.initializeEarthSpiritSummonCooldown(player, slot, nextModuleId);
    this.refreshPersistentCompanions(player);
    if (!this.hasLaborKind(player, "route_leg")) {
      player.laborRouteArmed = undefined;
      player.laborRouteInsideKey = undefined;
      player.laborRouteQualifiedMovement = false;
    }
    if (!this.hasLaborKind(player, "stationary_work")) {
      player.laborStationaryAchievementMs = 0;
    }

    if (payoutExperience > 0) {
      player.contribution.experienceEarned += payoutExperience;
      this.addExperienceToPlayer(player, payoutExperience);
    }
    if (options.checkLevelEvolution !== false) {
      this.evolvePlayerLevelModules(player);
    }
    if (player.modules[slot] === next.id && next.financeState) {
      this.initializeFinanceState(player, slot, next);
    }
  }

  private getModuleSlotState(player: SimPlayer, slot: number) {
    const moduleId = player.modules[slot];
    if (!moduleId) {
      player.moduleSlotStates.delete(slot);
      return undefined;
    }
    const module = getModule(moduleId);
    if (
      !module.experienceVault &&
      !module.killCharge &&
      !module.financeState &&
      !module.labor
    ) {
      player.moduleSlotStates.delete(slot);
      return undefined;
    }
    const current = player.moduleSlotStates.get(slot);
    if (current?.moduleId === moduleId) {
      return current;
    }
    const created = {
      moduleId,
      experienceStored: 0,
      killCharge: 0,
      financeEnteredAtMs: module.financeState ? this.elapsedMs : undefined,
      financeNextAtMs: undefined,
      financeAccumulatedSpend: 0,
      financeCharge: 0,
      laborDistanceRemainder: 0,
      laborStationaryRemainderMs: 0,
      laborMovingRemainderMs: 0,
      financeStake: undefined,
    };
    player.moduleSlotStates.set(slot, created);
    return created;
  }

  private getFinanceResource(
    player: SimPlayer,
    resource: FinanceResourceId,
  ): number {
    return resource === "gold" ? player.scrap : player.core;
  }

  private setFinanceResource(
    player: SimPlayer,
    resource: FinanceResourceId,
    value: number,
  ): void {
    const safe = Math.max(0, Number.isFinite(value) ? value : 0);
    if (resource === "gold") {
      player.scrap = safe;
    } else {
      player.core = safe;
    }
  }

  private grantFinanceResource(
    player: SimPlayer,
    resource: FinanceResourceId,
    amount: number,
  ): void {
    if (!Number.isFinite(amount) || amount <= 0) {
      return;
    }
    this.setFinanceResource(
      player,
      resource,
      this.getFinanceResource(player, resource) + amount,
    );
    if (resource === "gold") {
      player.contribution.scrapEarned += amount;
    }
  }

  private transitionFinanceState(
    player: SimPlayer,
    slot: number,
    targetModuleId: string,
    financeStake?: { targetId: FinanceStakeTargetId; amount: number },
  ): void {
    if (!player.modules[slot]) {
      return;
    }
    const target = getModule(targetModuleId);
    const inheritLock =
      player.lockedSlots[slot] ||
      target.stateFamily?.inheritsSlotLock === true;
    this.replaceModuleInSlot(player, slot, targetModuleId, {
      suppressVaultPayout: true,
      suppressReplacementReward: true,
      lockSlot: inheritLock,
      checkLevelEvolution: false,
      financeStake,
    });
  }

  private initializeFinanceState(
    player: SimPlayer,
    slot: number,
    module: ModuleDefinition,
  ): void {
    const finance = module.financeState;
    const state = this.getModuleSlotState(player, slot);
    if (!finance || !state || state.moduleId !== module.id) {
      return;
    }
    state.financeEnteredAtMs = this.elapsedMs;
    state.financeNextAtMs = undefined;
    state.financeAccumulatedSpend = 0;
    state.financeCharge = 0;
    if (
      finance.familyId === "fraud_imp_chain" ||
      finance.familyId === "wandering_merchant_chain"
    ) {
      player.debtReductionBySlot.set(slot, 0);
    }
    if (finance.onEnterGrant) {
      for (const grant of finance.onEnterGrant.resources ?? []) {
        this.grantFinanceResource(player, grant.resource, grant.amount);
      }
      if (
        finance.onEnterGrant.repeatableHiddenEffectId &&
        finance.onEnterGrant.count
      ) {
        for (let index = 0; index < finance.onEnterGrant.count; index += 1) {
          this.grantRepeatableHiddenEffect(
            player,
            finance.onEnterGrant.repeatableHiddenEffectId,
            module.tier,
          );
        }
      }
    }
    if (finance.timeScaledIncome) {
      state.financeNextAtMs =
        this.elapsedMs + finance.timeScaledIncome.intervalMs;
      return;
    }
    if (finance.resourceGate) {
      this.tryPayFinanceGate(player, slot, module);
      return;
    }
    if (finance.timedOutcome) {
      state.financeNextAtMs = this.elapsedMs + finance.timedOutcome.delayMs;
      return;
    }
    if (finance.timedTransition) {
      state.financeNextAtMs =
        this.elapsedMs + finance.timedTransition.delayMs;
      return;
    }
    if (finance.periodicDrain) {
      state.financeNextAtMs = this.elapsedMs + finance.periodicDrain.intervalMs;
      return;
    }
    if (finance.periodicSpendAccumulator) {
      state.financeNextAtMs =
        this.elapsedMs + finance.periodicSpendAccumulator.intervalMs;
      return;
    }
    if (finance.periodicCharge) {
      state.financeNextAtMs = this.elapsedMs + finance.periodicCharge.intervalMs;
      return;
    }
    if (finance.randomStake) {
      const ordinal = player.financeStakeOrdinals[slot] ?? 0;
      player.financeStakeOrdinals[slot] = ordinal + 1;
      const stakeRandom = new SeededRandom(
        hashSeed(
          `${this.runSeed}|finance-stake|${player.id}|${slot}|${ordinal}`,
        ),
      );
      const targetId = stakeRandom.pick(finance.randomStake.targetPool);
      const currentValue = Math.max(
        0,
        this.getFinanceStakeTargetValue(player, targetId),
      );
      const amount = currentValue * finance.randomStake.ratio;
      this.applyFinanceStakeDelta(player, targetId, -amount);
      this.transitionFinanceState(
        player,
        slot,
        finance.randomStake.targetModuleId,
        { targetId, amount },
      );
    }
  }

  private tryPayFinanceGate(
    player: SimPlayer,
    slot: number,
    module: ModuleDefinition,
  ): boolean {
    const gate = module.financeState?.resourceGate;
    if (
      !gate ||
      player.modules[slot] !== module.id ||
      this.getFinanceResource(player, gate.resource) + 1e-9 < gate.cost
    ) {
      return false;
    }
    this.setFinanceResource(
      player,
      gate.resource,
      this.getFinanceResource(player, gate.resource) - gate.cost,
    );
    this.transitionFinanceState(player, slot, gate.targetModuleId);
    return true;
  }

  private updateFinanceStates(): void {
    const players = [...this.players.values()].sort((left, right) =>
      left.id.localeCompare(right.id),
    );
    for (const player of players) {
      for (let slot = 0; slot < player.modules.length; slot += 1) {
        const moduleId = player.modules[slot];
        if (!moduleId) {
          continue;
        }
        const module = getModule(moduleId);
        const finance = module.financeState;
        if (!finance) {
          continue;
        }
        const state = this.getModuleSlotState(player, slot);
        if (!state || state.moduleId !== module.id) {
          continue;
        }
        if (finance.resourceGate) {
          this.tryPayFinanceGate(player, slot, module);
          continue;
        }
        const nextAtMs = state.financeNextAtMs;
        if (nextAtMs === undefined || this.elapsedMs < nextAtMs) {
          continue;
        }
        if (finance.timeScaledIncome) {
          const amount = Math.max(
            1,
            Math.ceil(this.elapsedMs / finance.timeScaledIncome.elapsedUnitMs),
          );
          this.grantFinanceResource(
            player,
            finance.timeScaledIncome.resource,
            amount,
          );
          state.financeNextAtMs =
            nextAtMs + finance.timeScaledIncome.intervalMs;
          continue;
        }
        if (finance.periodicDrain) {
          const drain = finance.periodicDrain;
          this.setFinanceResource(
            player,
            drain.resource,
            Math.max(
              drain.floor,
              this.getFinanceResource(player, drain.resource) - drain.amount,
            ),
          );
          state.financeNextAtMs = nextAtMs + drain.intervalMs;
          continue;
        }
        if (finance.periodicSpendAccumulator) {
          const accumulator = finance.periodicSpendAccumulator;
          const current = this.getFinanceResource(player, accumulator.resource);
          if (current + 1e-9 >= accumulator.costPerTick) {
            this.setFinanceResource(
              player,
              accumulator.resource,
              current - accumulator.costPerTick,
            );
            state.financeAccumulatedSpend = Math.min(
              accumulator.threshold,
              state.financeAccumulatedSpend + accumulator.costPerTick,
            );
          }
          if (state.financeAccumulatedSpend + 1e-9 >= accumulator.threshold) {
            const ordinal = player.financeAccumulatorOrdinals[slot] ?? 0;
            player.financeAccumulatorOrdinals[slot] = ordinal + 1;
            const resolution = resolvePeriodicSpendAccumulatorCycle(
              this.runSeed,
              player.id,
              slot,
              ordinal,
              accumulator,
            );
            this.grantFinanceResource(
              player,
              accumulator.thresholdReward.resource,
              resolution.rewardAmount,
            );
            if (resolution.rareSuccess) {
              this.transitionFinanceState(
                player,
                slot,
                accumulator.rareTransition.targetModuleId,
              );
              continue;
            }
            state.financeAccumulatedSpend = 0;
          }
          state.financeNextAtMs = nextAtMs + accumulator.intervalMs;
          continue;
        }
        if (finance.periodicCharge) {
          const charge = finance.periodicCharge;
          state.financeCharge = Math.min(
            charge.threshold,
            state.financeCharge + charge.amountPerTick,
          );
          if (state.financeCharge + 1e-9 >= charge.threshold) {
            this.transitionFinanceState(
              player,
              slot,
              charge.targetModuleId,
            );
            continue;
          }
          state.financeNextAtMs = nextAtMs + charge.intervalMs;
          continue;
        }
        if (finance.timedTransition) {
          this.transitionFinanceState(
            player,
            slot,
            finance.timedTransition.targetModuleId,
          );
          continue;
        }
        if (finance.timedOutcome) {
          this.resolveFinanceOutcome(player, slot, module);
        }
      }
    }
  }

  private nextFinanceOutcomeRoll(player: SimPlayer, slot: number): number {
    const ordinal = player.financeOutcomeOrdinals[slot] ?? 0;
    player.financeOutcomeOrdinals[slot] = ordinal + 1;
    return new SeededRandom(
      hashSeed(
        `${this.runSeed}|finance-outcome|${player.id}|${slot}|${ordinal}`,
      ),
    ).next();
  }

  private getFinanceSuccessPercent(
    player: SimPlayer,
    outcome: Extract<
      NonNullable<ModuleDefinition["financeState"]>["timedOutcome"],
      { kind: "stake_resolution" }
    >,
  ): number {
    if (outcome.fixedSuccessPercent !== undefined) {
      return clamp(outcome.fixedSuccessPercent, 0, 100);
    }
    const dynamic = outcome.dynamicSuccess!;
    const count =
      player.repeatableHiddenEffectGrants.get(
        dynamic.repeatableHiddenEffectId,
      )?.length ?? 0;
    return clamp(
      dynamic.basePercent + count * dynamic.percentPerCount,
      0,
      dynamic.maxPercent,
    );
  }

  private resolveFinanceOutcome(
    player: SimPlayer,
    slot: number,
    module: ModuleDefinition,
  ): void {
    const outcome = module.financeState?.timedOutcome;
    const state = this.getModuleSlotState(player, slot);
    if (!outcome || !state || state.moduleId !== module.id) {
      return;
    }
    const roll = this.nextFinanceOutcomeRoll(player, slot);
    if (outcome.kind === "weighted_branches") {
      let cursor = roll * 100;
      let selected = outcome.branches.at(-1)!;
      for (const branch of outcome.branches) {
        cursor -= branch.weight;
        if (cursor < 0) {
          selected = branch;
          break;
        }
      }
      if (selected.reward) {
        this.grantFinanceResource(
          player,
          selected.reward.resource,
          selected.reward.amount,
        );
      }
      const debt = this.getActiveSynergyMechanic(player, "debt_outsourcing");
      if (
        selected.reward &&
        debt &&
        (module.financeState?.familyId === "fraud_imp_chain" ||
          module.financeState?.familyId === "wandering_merchant_chain")
      ) {
        player.debtCrowDoubleUntilMs = Math.max(
          player.debtCrowDoubleUntilMs,
          this.elapsedMs + debt.successDoubleDurationMs,
        );
      }
      this.events.push({
        type: "notice",
        message: `${player.name} 的「${module.name}」${selected.reward ? "开奖成功" : "开奖失败"}，变为「${getModule(selected.targetModuleId).name}」。`,
      });
      this.transitionFinanceState(player, slot, selected.targetModuleId);
      return;
    }
    const successPercent = this.getFinanceSuccessPercent(player, outcome);
    const success = roll * 100 < successPercent;
    const stake = state.financeStake;
    if (success && stake) {
      this.applyFinanceStakeDelta(
        player,
        stake.targetId,
        stake.amount * outcome.stakeReturnMultiplier,
      );
    }
    if (success && outcome.successGrant) {
      for (let index = 0; index < outcome.successGrant.count; index += 1) {
        this.grantRepeatableHiddenEffect(
          player,
          outcome.successGrant.repeatableHiddenEffectId,
          module.tier,
        );
      }
    }
    this.events.push({
      type: "notice",
      message: `${player.name} 的「${module.name}」以${Number(successPercent.toFixed(2))}%成功率${success ? "赢得" : "失去"}了赌注。`,
    });
    this.transitionFinanceState(player, slot, outcome.returnTargetModuleId);
  }

  private getFinanceStakeTargetValue(
    player: SimPlayer,
    targetId: FinanceStakeTargetId,
  ): number {
    switch (targetId) {
      case "current_health": return player.health;
      case "max_health": return player.stats.maxHealth;
      case "health_regen": return player.stats.healthRegen;
      case "health_regen_ratio": return player.stats.healthRegenMaxRatio;
      case "current_mana": return player.mana;
      case "max_mana": return player.stats.manaMax;
      case "mana_regen": return player.stats.manaRegen;
      case "mana_regen_ratio": return player.stats.manaRegenMaxRatio;
      case "attack_damage": return player.stats.damage;
      case "damage_multiplier": return player.stats.damageMultiplier;
      case "attack_damage_multiplier": return player.stats.attackDamageMultiplier;
      case "ability_damage_multiplier": return player.stats.abilityDamageMultiplier;
      case "base_attack_speed": return Math.max(0, 1 + player.stats.baseAttackSpeedModifier);
      case "bonus_attack_speed": return Math.max(0, 1 + player.stats.attackSpeedBonus);
      case "attack_range": return player.stats.range;
      case "base_move_speed": return player.stats.moveSpeed;
      case "final_move_speed": return this.getPlayerMoveSpeed(player);
      case "move_speed_multiplier": return player.stats.moveSpeedMultiplier;
      case "armor": return player.stats.armor;
      case "dodge": return player.stats.dodge;
      case "repair_per_second": return player.stats.repairPerSecond;
      case "healing_per_second": return player.stats.healingPerSecond;
      case "scouting_radius": return player.stats.scoutingRadius;
      case "reveal_radius": return player.stats.revealRadius;
      case "splash_radius": return player.stats.splashRadius;
      case "chain_targets": return player.stats.chainTargets;
      case "pierce_targets": return player.stats.pierceTargets;
      case "crystal_drop_chance": return player.stats.crystalDropChance;
      case "elite_damage_multiplier": return player.stats.eliteDamageMultiplier;
      case "companion_damage_multiplier": return player.stats.companionDamageMultiplier;
      case "summon_cap": return player.stats.summonCap;
      case "gold_multiplier": return player.stats.goldMultiplier;
      case "experience_multiplier": return player.stats.experienceMultiplier;
      case "surface_mining_extra_gold_flat": return player.stats.surfaceMiningExtraGoldFlat;
      case "surface_mining_extra_gold_multiplier": return player.stats.surfaceMiningExtraGoldMultiplier;
      case "surface_mining_speed_bonus": return player.stats.surfaceMiningSpeedBonus;
      case "surface_mining_gold_yield_bonus": return player.stats.surfaceMiningGoldYieldBonus;
      case "surface_mining_crystal_yield_ratio": return player.stats.surfaceMiningCrystalYieldRatio;
      case "gold": return player.scrap;
      case "crystal": return player.core;
      case "experience": return player.experience;
      case "talent_points": return player.talentPoints;
      case "wage": return player.wage;
    }
  }

  private applyFinanceStakeDelta(
    player: SimPlayer,
    targetId: FinanceStakeTargetId,
    delta: number,
  ): void {
    if (!Number.isFinite(delta) || delta === 0) {
      return;
    }
    if (targetId === "current_health") {
      player.health = clamp(player.health + delta, 1, player.stats.maxHealth);
      return;
    }
    if (targetId === "current_mana") {
      player.mana = clamp(player.mana + delta, 0, player.stats.manaMax);
      return;
    }
    if (targetId === "gold") {
      this.setFinanceResource(player, "gold", player.scrap + delta);
      if (delta > 0) player.contribution.scrapEarned += delta;
      return;
    }
    if (targetId === "crystal") {
      this.setFinanceResource(player, "crystal", player.core + delta);
      return;
    }
    if (targetId === "experience") {
      if (delta > 0) {
        this.addExperienceToPlayer(player, delta);
      } else {
        player.experience = Math.max(0, player.experience + delta);
      }
      return;
    }
    if (targetId === "talent_points") {
      const invested = Object.values(player.talents).reduce(
        (sum, level) => sum + level,
        0,
      );
      player.talentPoints = clamp(
        player.talentPoints + delta,
        0,
        PLAYER_TALENT_POINT_CAP - invested,
      );
      return;
    }
    if (targetId === "wage") {
      player.wage = Math.max(0, player.wage + delta);
      return;
    }
    if (targetId === "max_health" || targetId === "max_mana") {
      player.pendingOneShotVitalClamp = {
        health: player.health,
        mana: player.mana,
      };
    }
    player.financeStatAdjustments.set(
      targetId,
      (player.financeStatAdjustments.get(targetId) ?? 0) + delta,
    );
    this.recomputePlayer(player);
  }

  private chooseVaultEvolutionTarget(
    player: SimPlayer,
    slot: number,
    ability: ModuleDefinition,
  ): string {
    const evolution = ability.experienceVault!.evolution;
    if (evolution.kind === "fixed") {
      return evolution.targetModuleId;
    }
    const totalWeight = evolution.branches.reduce(
      (sum, branch) => sum + branch.weight,
      0,
    );
    const ordinal = player.experienceVaultBranchOrdinals[slot] ?? 0;
    player.experienceVaultBranchOrdinals[slot] = ordinal + 1;
    const branchRandom = new SeededRandom(
      hashSeed(
        `${this.runSeed}|experience-vault|${player.id}|${slot}|${ordinal}`,
      ),
    );
    let roll = branchRandom.next() * totalWeight;
    for (const branch of evolution.branches) {
      roll -= branch.weight;
      if (roll < 0) {
        return branch.targetModuleId;
      }
    }
    return evolution.branches.at(-1)!.targetModuleId;
  }

  private evolveExperienceVault(player: SimPlayer, slot: number): void {
    const sourceId = player.modules[slot];
    if (!sourceId) {
      return;
    }
    const source = getModule(sourceId);
    if (!source.experienceVault) {
      return;
    }
    const targetId = this.chooseVaultEvolutionTarget(player, slot, source);
    this.replaceModuleInSlot(player, slot, targetId, {
      suppressVaultPayout: true,
      lockSlot: false,
    });
    this.events.push({
      type: "notice",
      message: `${player.name} 的「${source.name}」吸收圆满，在原槽位进阶为「${getModule(targetId).name}」。`,
    });
  }

  private evolvePlayerLevelModules(player: SimPlayer): void {
    for (let slot = 0; slot < player.modules.length; slot += 1) {
      const sourceId = player.modules[slot];
      if (!sourceId) {
        continue;
      }
      const source = getModule(sourceId);
      const evolution = source.conditionalEvolution;
      if (
        evolution?.kind !== "player_level" ||
        player.level < evolution.requiredLevel
      ) {
        continue;
      }
      this.replaceModuleInSlot(player, slot, evolution.targetModuleId, {
        suppressVaultPayout: true,
        lockSlot: false,
        checkLevelEvolution: false,
      });
      this.events.push({
        type: "notice",
        message: `${player.name} 达到满级，「${source.name}」在原槽位进阶为「${getModule(evolution.targetModuleId).name}」。`,
      });
    }
  }

  private addExperience(
    player: SimPlayer,
    amount: number,
    options: { bypassVault?: boolean } = {},
  ): void {
    if (!Number.isFinite(amount) || amount <= 0) {
      return;
    }
    const earned = amount * player.stats.experienceMultiplier;
    player.contribution.experienceEarned += earned;
    if (!options.bypassVault) {
      let remaining = earned;
      let safety = 0;
      while (remaining > 1e-9 && safety < 128) {
        safety += 1;
        const absorbingSlots = player.modules
          .map((moduleId, slot) => ({ moduleId, slot }))
          .filter(
            (entry) =>
              entry.moduleId && getModule(entry.moduleId).experienceVault,
          );
        if (absorbingSlots.length === 0) {
          this.addExperienceToPlayer(player, remaining);
          return;
        }
        const share = remaining / absorbingSlots.length;
        let absorbed = 0;
        for (const { moduleId, slot } of absorbingSlots) {
          if (!moduleId || player.modules[slot] !== moduleId) {
            continue;
          }
          const vault = getModule(moduleId).experienceVault!;
          const state = this.getModuleSlotState(player, slot)!;
          const available = Math.max(0, vault.capacity - state.experienceStored);
          const taken = Math.min(share, available);
          state.experienceStored += taken;
          absorbed += taken;
          if (state.experienceStored >= vault.capacity - 1e-9) {
            state.experienceStored = vault.capacity;
            this.evolveExperienceVault(player, slot);
          }
        }
        remaining = Math.max(0, remaining - absorbed);
        if (absorbed <= 1e-9) {
          this.addExperienceToPlayer(player, remaining);
          return;
        }
      }
      if (remaining > 1e-9) {
        this.addExperienceToPlayer(player, remaining);
      }
      return;
    }
    this.addExperienceToPlayer(player, earned);
  }

  private addExperienceToPlayer(player: SimPlayer, amount: number): void {
    if (player.level >= PLAYER_LEVEL_CAP) {
      player.experience = 0;
      this.evolvePlayerLevelModules(player);
      return;
    }
    player.experience += amount;
    while (
      player.level < PLAYER_LEVEL_CAP &&
      player.experience >= nextLevelExperience(player.level)
    ) {
      player.experience -= nextLevelExperience(player.level);
      player.level += 1;
      this.grantTalentPoints(player, 1);
      player.health = Math.min(player.stats.maxHealth, player.health + 28);
      player.mana = Math.min(player.stats.manaMax, player.mana + 18);
    }
    if (player.level >= PLAYER_LEVEL_CAP) {
      player.experience = 0;
    }
    this.evolvePlayerLevelModules(player);
  }

  private grantLevels(player: SimPlayer, amount: number): void {
    const preservedExperience = player.experience;
    let remaining = Math.max(0, Math.floor(amount));
    while (remaining > 0 && player.level < PLAYER_LEVEL_CAP) {
      player.level += 1;
      remaining -= 1;
      this.grantTalentPoints(player, 1);
      player.health = Math.min(player.stats.maxHealth, player.health + 28);
      player.mana = Math.min(player.stats.manaMax, player.mana + 18);
    }
    player.experience =
      player.level >= PLAYER_LEVEL_CAP ? 0 : preservedExperience;
    this.evolvePlayerLevelModules(player);
  }

  private grantTalentPoints(player: SimPlayer, amount: number): void {
    const invested = Object.values(player.talents).reduce(
      (sum, level) => sum + level,
      0,
    );
    player.talentPoints = Math.min(
      Math.max(0, PLAYER_TALENT_POINT_CAP - invested),
      player.talentPoints + Math.max(0, Math.floor(amount)),
    );
  }

  private triggerGrowth(player: SimPlayer, event: GrowthEvent, amount: number): void {
    for (const abilityId of player.modules) {
      if (!abilityId) {
        continue;
      }
      const growth = getModule(abilityId).growth;
      if (!growth || growth.event !== event) {
        continue;
      }
      const key = `${abilityId}:${event}`;
      const counter = (player.growthCounters.get(key) ?? 0) + amount;
      let remaining = counter;
      let applied = player.growthApplied.get(key) ?? 0;
      let changed = false;
      let settlements = growth.bonuses ? applied :
        applied / Math.max(1, growth.amount ?? 1);
      let threshold = Math.max(
        growth.minEvery ?? 0,
        growth.every -
          settlements * (growth.everyReductionPerSettlement ?? 0),
      );
      while (remaining >= threshold && applied < (growth.cap ?? Number.POSITIVE_INFINITY)) {
        remaining -= threshold;
        const nominalIncrement = growth.amount ?? 1;
        const increment = Math.min(
          nominalIncrement,
          (growth.cap ?? Number.POSITIVE_INFINITY) - applied,
        );
        if (growth.stat) {
          player.growthBonuses[growth.stat] =
            (player.growthBonuses[growth.stat] ?? 0) + increment;
        }
        if (growth.bonuses) {
          const scale = nominalIncrement > 0 ? increment / nominalIncrement : 1;
          for (const [stat, value] of Object.entries(growth.bonuses) as Array<
            [keyof typeof growth.bonuses, number]
          >) {
            if (!stat || value === undefined) continue;
            player.growthBonuses[stat] =
              (player.growthBonuses[stat] ?? 0) + value * scale;
          }
        }
        applied += increment;
        settlements += 1;
        threshold = Math.max(
          growth.minEvery ?? 0,
          growth.every -
            settlements * (growth.everyReductionPerSettlement ?? 0),
        );
        changed = true;
        const pain = this.getActiveSynergyMechanic(player, "pain_enjoyment");
        if (pain?.growthAbilityId === abilityId) {
          player.health = Math.min(
            player.stats.maxHealth,
            player.health + pain.healPerSettlement,
          );
        }
      }
      player.growthCounters.set(key, remaining);
      player.growthApplied.set(key, applied);
      if (changed) {
        this.recomputePlayer(player);
      }
    }
  }

  private applyOnAcquire(player: SimPlayer, ability: ModuleDefinition): void {
    for (const effectId of ability.grantsHiddenEffectIds ?? []) {
      this.grantPermanentHiddenEffect(player, effectId);
    }
    this.refreshHiddenSynergies(player);
    for (const [index, mechanic] of (ability.mechanics ?? []).entries()) {
      const intervalMs = mechanicIntervalMs(mechanic);
      if (intervalMs !== undefined) {
        player.mechanicCooldowns.set(
          mechanicCooldownKey(ability.id, index),
          this.elapsedMs + intervalMs,
        );
      }
    }
    if (ability.special === "copy_random_ability") {
      const copyTargets = player.modules
        .filter(
          (moduleId): moduleId is string =>
            Boolean(moduleId) && moduleId !== ability.id,
        )
        .map((moduleId) => getModule(moduleId))
        .filter((module) => Object.keys(module.bonuses).length > 0);
      if (copyTargets.length > 0) {
        const copied = this.random.pick(copyTargets);
        player.copiedBonuses.push({ ...copied.bonuses });
        this.events.push({
          type: "notice",
          message: `${player.name} 的宝箱怪复制了「${copied.name}」的静态强化`,
        });
      }
    }
    const reward = ability.onAcquire;
    if (!reward) {
      return;
    }
    player.scrap += reward.gold ?? 0;
    player.core += reward.crystals ?? 0;
    this.grantTalentPoints(player, reward.talentPoints ?? 0);
    if (reward.experience) {
      this.addExperience(player, reward.experience);
    }
    if (
      reward.currentLevelExperienceRatio &&
      player.level < PLAYER_LEVEL_CAP
    ) {
      this.addExperience(
        player,
        nextLevelExperience(player.level) * reward.currentLevelExperienceRatio,
      );
    }
    if (reward.grantLevels) {
      this.grantLevels(player, reward.grantLevels);
    }
    const permanent = player.permanentOneShotBonuses;
    permanent.damageAdd = (permanent.damageAdd ?? 0) + (reward.damage ?? 0);
    permanent.maxHealthAdd =
      (permanent.maxHealthAdd ?? 0) + (reward.maxHealth ?? 0);
    permanent.manaMaxAdd =
      (permanent.manaMaxAdd ?? 0) + (reward.maxMana ?? 0);
    permanent.armorAdd = (permanent.armorAdd ?? 0) + (reward.armor ?? 0);
    permanent.attackSpeedBonus =
      (permanent.attackSpeedBonus ?? 0) + (reward.attackSpeedBonus ?? 0);
    permanent.abilityDamageBonus =
      (permanent.abilityDamageBonus ?? 0) +
      (reward.abilityDamageBonus ?? 0);
    permanent.manaRegenMaxRatio =
      (permanent.manaRegenMaxRatio ?? 0) +
      (reward.manaRegenMaxRatio ?? 0);
    if (
      player.pendingOneShotVitalClamp ||
      reward.maxHealth !== undefined ||
      reward.maxMana !== undefined
    ) {
      player.pendingOneShotVitalClamp = {
        health: player.health,
        mana: player.mana,
      };
    }
    if (reward.repeatableHiddenEffectId) {
      this.grantRepeatableHiddenEffect(
        player,
        reward.repeatableHiddenEffectId,
        ability.tier,
      );
    }
    player.contribution.oneShotsTriggered += 1;
  }

  private grantPermanentHiddenEffect(
    player: SimPlayer,
    effectId: string,
  ): boolean {
    if (player.hiddenEffectIds.has(effectId)) {
      return false;
    }
    const effect = getHiddenEffect(effectId);
    player.hiddenEffectIds.add(effectId);
    if (effect.effects.some((entry) => entry.kind === "enemy_slow_immunity")) {
      player.enemySlowUntilMs = 0;
      player.enemySlowRatio = 0;
    }
    this.refreshHiddenSynergies(player);
    this.events.push({
      type: "notice",
      message:
        `${player.name} 永久获得隐藏效果「${effect.name}」` +
        (effect.startsLocked ? "，当前仍处于锁定状态" : ""),
    });
    return true;
  }

  private grantRepeatableHiddenEffect(
    player: SimPlayer,
    effectId: string,
    sourceTier: ModuleTier,
  ): void {
    const effect = getHiddenEffect(effectId);
    if (!effect.repeatable) {
      return;
    }
    const grants = player.repeatableHiddenEffectGrants.get(effectId) ?? [];
    grants.push(sourceTier);
    player.repeatableHiddenEffectGrants.set(effectId, grants);

    const activeBeforeGrant = new Set(player.hiddenSynergies);
    const newlyActivated: string[] = [];
    for (const synergy of HIDDEN_SYNERGIES) {
      const requirement = synergy.repeatableRequirement;
      if (
        !requirement ||
        requirement.effectId !== effectId ||
        activeBeforeGrant.has(synergy.id) ||
        (requirement.afterSynergyId &&
          !activeBeforeGrant.has(requirement.afterSynergyId)) ||
        (requirement.minSourceTier !== undefined &&
          sourceTier < requirement.minSourceTier) ||
        (requirement.exactSourceTier !== undefined &&
          sourceTier !== requirement.exactSourceTier)
      ) {
        continue;
      }
      const progress = (player.hiddenSynergyProgress.get(synergy.id) ?? 0) + 1;
      player.hiddenSynergyProgress.set(
        synergy.id,
        Math.min(requirement.count, progress),
      );
      if (progress < requirement.count) {
        continue;
      }
      player.hiddenSynergies.push(synergy.id);
      newlyActivated.push(synergy.id);
      for (const sourceId of synergy.suppressesSynergyIds ?? []) {
        getHiddenSynergy(sourceId).effects.forEach((_, effectIndex) => {
          player.hiddenEffectCooldowns.delete(`${sourceId}:${effectIndex}`);
        });
      }
      synergy.effects.forEach((runtimeEffect, effectIndex) => {
        const key = `${synergy.id}:${effectIndex}`;
        if ("intervalMs" in runtimeEffect) {
          player.hiddenEffectCooldowns.set(
            key,
            this.elapsedMs + runtimeEffect.intervalMs,
          );
        } else {
          player.hiddenEffectCooldowns.delete(key);
        }
      });
    }
    if (newlyActivated.length > 0) {
      player.contribution.firstSynergyAtMs ??= this.elapsedMs;
      this.events.push({
        type: "notice",
        message: `${player.name} 激活隐藏羁绊：${newlyActivated
          .map((synergyId) => getHiddenSynergy(synergyId).name)
          .join("、")}`,
      });
    }
  }

  private refreshHiddenSynergies(player: SimPlayer): void {
    const previous = new Set(player.hiddenSynergies);
    const active = evaluateHiddenSynergies(player.hiddenEffectIds);
    const repeatableActive = player.hiddenSynergies.filter(
      (synergyId) => Boolean(getHiddenSynergy(synergyId).repeatableRequirement),
    );
    player.hiddenSynergies = [
      ...active.map((synergy) => synergy.id),
      ...repeatableActive.filter(
        (synergyId) => !active.some((synergy) => synergy.id === synergyId),
      ),
    ];
    const newlyActivated = active.filter(
      (synergy) => !previous.has(synergy.id),
    );
    if (newlyActivated.length === 0) {
      return;
    }
    player.contribution.firstSynergyAtMs ??= this.elapsedMs;
    this.events.push({
      type: "notice",
      message: `${player.name} 激活隐藏羁绊：${newlyActivated
        .map((synergy) => synergy.name)
        .join("、")}`,
    });
  }

  private getHiddenRuntimeEffects(player: SimPlayer): Array<{
    sourceId: string;
    effectIndex: number;
    effect: HiddenRuntimeEffect;
  }> {
    const activeEffectIds = getActiveHiddenEffectIds(
      player.hiddenEffectIds,
      player.hiddenSynergies,
    );
    const effects = [...activeEffectIds].flatMap((effectId) =>
      getHiddenEffect(effectId).effects.map((effect, effectIndex) => {
        const multiplier = this.getHiddenEffectMultiplier(player, effectId);
        return {
          sourceId: effectId,
          effectIndex,
          effect: scaleHiddenRuntimeEffect(effect, multiplier),
        };
      }),
    );
    const suppressedSynergyIds = new Set(
      player.hiddenSynergies.flatMap(
        (synergyId) => getHiddenSynergy(synergyId).suppressesSynergyIds ?? [],
      ),
    );
    for (const synergyId of player.hiddenSynergies) {
      if (suppressedSynergyIds.has(synergyId)) {
        continue;
      }
      effects.push(
        ...getHiddenSynergy(synergyId).effects.map(
          (effect, effectIndex) => ({
            sourceId: synergyId,
            effectIndex,
            effect,
          }),
        ),
      );
    }
    return effects;
  }

  private getHiddenEffectMultiplier(
    player: SimPlayer,
    effectId: string,
  ): number {
    let multiplier = 1;
    for (const synergyId of player.synergies) {
      for (const mechanic of getSynergy(synergyId).mechanics ?? []) {
        if (
          mechanic.kind === "hidden_effect_amplifier" &&
          mechanic.hiddenEffectIds.includes(effectId)
        ) {
          multiplier = Math.max(multiplier, mechanic.effectMultiplier);
        }
      }
    }
    return multiplier;
  }

  private getHiddenAllyAuraAt(
    x: number,
    y: number,
  ): {
    armorAdd: number;
    attackSpeedMultiplier: number;
    moveSpeedMultiplier: number;
  } {
    let armorAdd = 0;
    let attackSpeedMultiplier = 1;
    let moveSpeedMultiplier = 1;
    const appliedSources = new Set<string>();
    for (const owner of this.players.values()) {
      if (owner.status === "downed") {
        continue;
      }
      for (const runtime of this.getHiddenRuntimeEffects(owner)) {
        const effect = runtime.effect;
        if (
          (effect.kind !== "ally_armor_aura" &&
            effect.kind !== "ally_speed_aura") ||
          Math.hypot(x - owner.x, y - owner.y) > effect.radius
        ) {
          continue;
        }
        const sourceKey = `${runtime.sourceId}:${runtime.effectIndex}`;
        if (appliedSources.has(sourceKey)) {
          continue;
        }
        appliedSources.add(sourceKey);
        if (effect.kind === "ally_armor_aura") {
          armorAdd += effect.armor;
        } else {
          attackSpeedMultiplier *= effect.attackSpeedMultiplier;
          moveSpeedMultiplier *= effect.moveSpeedMultiplier;
        }
      }
    }
    return { armorAdd, attackSpeedMultiplier, moveSpeedMultiplier };
  }

  private updateAdvancedAbilities(): void {
    for (const player of this.players.values()) {
      if (player.status !== "active" && player.status !== "lich") {
        continue;
      }
      for (const [slot, abilityId] of [...player.modules.entries()]) {
        if (!abilityId) {
          continue;
        }
        const ability = getModule(abilityId);
        if (!this.isAbilityEnabled(player, ability)) {
          continue;
        }
        for (const [index, mechanic] of (ability.mechanics ?? []).entries()) {
          if (mechanic.kind === "undead_summon") {
            this.updateUndeadSummonMechanic(
              player,
              ability,
              mechanic,
              index,
              slot,
            );
            continue;
          }
          if (
            ability.companion?.kind === "follower" &&
            mechanic.kind === "periodic_health_extremes_damage"
          ) {
            continue;
          }
          // Every slot owns its own cadence and 24-hour cast counter. This also
          // prevents duplicate copies of the same skill from sharing cooldowns.
          const slotScoped = true;
          let intervalMs = mechanicIntervalMs(mechanic);
          const world = this.getActiveSynergyMechanic(player, "the_world");
          if (
            intervalMs !== undefined &&
            mechanic.kind === "cooldown_acceleration" &&
            world?.sourceAbilityIds.includes(ability.id)
          ) {
            intervalMs = Math.max(50, intervalMs - world.cooldownReductionMs);
          }
          if (
            intervalMs === undefined ||
            !this.advancedMechanicReady(
              player,
              mechanicCooldownKey(
                ability.id,
                index,
                slotScoped && slot > 0 ? slot : undefined,
              ),
              intervalMs,
            )
          ) {
            continue;
          }
          const castExecutions = mechanic.kind === "periodic_reroll"
            ? 1
            : this.registerDiscreteSkillCast(player, `slot:${slot}`);
          for (let cast = 0; cast < castExecutions; cast += 1) {
          if (mechanic.kind === "periodic_target_damage") {
            this.castPeriodicTargetDamage(player, ability, mechanic, slot);
          } else if (mechanic.kind === "periodic_reroll") {
            this.rerollNonTierSixModules(player, ability, mechanic);
          } else if (mechanic.kind === "random_skill_cast") {
            this.castRandomHighTierSkill(player, ability, mechanic);
          } else if (mechanic.kind === "enemy_conversion") {
            this.convertRandomEnemy(player, ability, mechanic);
          } else if (mechanic.kind === "black_hole") {
            this.spawnBlackHole(player, ability, mechanic);
          } else if (mechanic.kind === "periodic_self_area_damage") {
            this.castPeriodicSelfAreaDamage(player, ability, mechanic, slot);
          } else if (
            mechanic.kind === "periodic_normal_execute_reward"
          ) {
            this.castNormalExecuteReward(player, ability, mechanic);
          } else if (mechanic.kind === "periodic_forced_aggro") {
            this.castForcedAggro(player, ability, mechanic);
          } else if (mechanic.kind === "periodic_health_extremes_damage") {
            this.castHealthExtremesDamage(player, ability, mechanic, slot);
          } else if (mechanic.kind === "periodic_distance_extremes_damage") {
            this.castDistanceExtremesDamage(player, ability, mechanic, slot);
          } else if (mechanic.kind === "gold_contract") {
            this.settleGoldContract(player, mechanic);
          } else if (mechanic.kind === "energy_reservoir") {
            this.settleEnergyReservoir(player, ability, mechanic, slot);
          } else if (mechanic.kind === "damage_lifesteal") {
            this.settleDamageLifesteal(player, ability, mechanic, slot);
          } else if (mechanic.kind === "cooldown_acceleration") {
            this.accelerateOwnedCooldowns(player, ability, mechanic);
          } else if (mechanic.kind === "synergy_substitution") {
            this.activateSynergySubstitution(
              player,
              ability,
              mechanic,
              slot,
            );
          }
          }
        }
      }
    }
  }

  private castDistanceExtremesDamage(
    player: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "periodic_distance_extremes_damage" }
    >,
    sourceSlot?: number,
  ): void {
    const nearest = this.pickTarget(
      player.x,
      player.y,
      mechanic.range,
      "closest",
      undefined,
      player.spaceId,
    );
    const farthest = this.pickTarget(
      player.x,
      player.y,
      mechanic.range,
      "farthest",
      undefined,
      player.spaceId,
    );
    const targets = [...new Map(
      [nearest, farthest]
        .filter((target): target is SimEnemy => Boolean(target))
        .map((target) => [target.id, target]),
    ).values()];
    if (targets.length === 0) return;
    const tuning = getAbilityTuning(player.modules, ability.id);
    for (const target of targets) {
      const rank = getEnemy(target.definitionId).rank;
      const eliteMultiplier =
        rank === "normal" ? 1 : player.stats.eliteDamageMultiplier;
      this.damageEnemy(
        target,
        mechanic.damage *
          tuning.damageMultiplier *
          player.stats.damageMultiplier *
          this.getPlayerAbilityDamageMultiplier(player) *
          eliteMultiplier,
        player.id,
        { kind: "ability", abilityId: ability.id, sourceSlot },
      );
    }
    this.events.push({
      type: "shot",
      playerId: player.id,
      targetIds: targets.map((target) => target.id),
      abilityId: ability.id,
    });
  }

  private settleGoldContract(
    player: SimPlayer,
    mechanic: Extract<AbilityMechanicDefinition, { kind: "gold_contract" }>,
  ): void {
    if (player.scrap > 0) {
      const cost = mechanic.drainMode === "flat"
        ? Math.min(player.scrap, mechanic.drainValue)
        : Math.min(
            player.scrap,
            Math.max(1, Math.ceil(player.scrap * mechanic.drainValue)),
          );
      player.scrap -= cost;
    }
    this.recomputePlayer(player);
  }

  private settleEnergyReservoir(
    owner: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "energy_reservoir" }
    >,
    sourceSlot: number,
  ): void {
    const key = `energy:${ability.id}:slot:${sourceSlot}`;
    let energy = (owner.synergyCounters.get(key) ?? 0) + mechanic.chargePerSecond;
    const candidates = [...this.players.values()].flatMap((target) => {
      if (
        target.status !== "active" ||
        target.spaceId !== owner.spaceId ||
        Math.hypot(target.x - owner.x, target.y - owner.y) > mechanic.range
      ) {
        return [];
      }
      const entries: Array<{
        target: SimPlayer;
        resource: "health" | "mana";
        ratio: number;
      }> = [];
      const healthRatio = target.health / Math.max(1, target.stats.maxHealth);
      const manaRatio = target.mana / Math.max(1, target.stats.manaMax);
      if (healthRatio < mechanic.triggerBelowRatio) {
        entries.push({ target, resource: "health", ratio: healthRatio });
      }
      if (manaRatio < mechanic.triggerBelowRatio) {
        entries.push({ target, resource: "mana", ratio: manaRatio });
      }
      return entries;
    }).sort(
      (left, right) =>
        left.ratio - right.ratio ||
        (left.resource === "health" ? -1 : 1) ||
        left.target.id.localeCompare(right.target.id),
    );
    const selected = candidates[0];
    if (selected && energy > 0) {
      const maximum = selected.resource === "health"
        ? selected.target.stats.maxHealth
        : selected.target.stats.manaMax;
      const current = selected.resource === "health"
        ? selected.target.health
        : selected.target.mana;
      const valuePerEnergy = selected.resource === "health"
        ? mechanic.healthPerEnergy
        : mechanic.manaPerEnergy;
      const required = Math.max(0, maximum - current) / valuePerEnergy;
      const spent = Math.min(energy, required);
      const restored = spent * valuePerEnergy;
      if (selected.resource === "health") {
        const before = selected.target.health;
        selected.target.health = Math.min(maximum, before + restored);
        owner.contribution.healingDone += selected.target.health - before;
      } else {
        selected.target.mana = Math.min(maximum, selected.target.mana + restored);
      }
      energy -= spent;
    }
    owner.synergyCounters.set(key, energy);
  }

  private settleDamageLifesteal(
    player: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "damage_lifesteal" }
    >,
    sourceSlot: number,
  ): void {
    const key = `lifesteal:${ability.id}:slot:${sourceSlot}`;
    const previous = player.synergyCounters.get(key) ?? player.contribution.damageDealt;
    const dealt = Math.max(0, player.contribution.damageDealt - previous);
    player.synergyCounters.set(key, player.contribution.damageDealt);
    const bloodDebt = this.getActiveSynergyMechanic(player, "blood_debt");
    const multiplier = bloodDebt?.lifestealAbilityId === ability.id
      ? bloodDebt.lifestealMultiplier
      : 1;
    const healing = Math.min(mechanic.healCap * multiplier, dealt * mechanic.damageRatio * multiplier);
    if (healing <= 0) return;
    const before = player.health;
    player.health = Math.min(player.stats.maxHealth, player.health + healing);
    player.contribution.healingDone += player.health - before;
  }

  private accelerateOwnedCooldowns(
    player: SimPlayer,
    source: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "cooldown_acceleration" }
    >,
  ): void {
    const eligibleIds = player.modules.flatMap((moduleId) => {
      if (!moduleId || moduleId === source.id) return [];
      const module = getModule(moduleId);
      return mechanic.includeTierSix || module.tier < 6 ? [module.id] : [];
    });
    const advanceMap = (map: Map<string, number>): void => {
      for (const [key, atMs] of map) {
        if (eligibleIds.some((id) => key === id || key.startsWith(`${id}:`))) {
          map.set(key, Math.max(this.elapsedMs, atMs - mechanic.advanceMs));
        }
      }
    };
    advanceMap(player.abilityCooldowns);
    advanceMap(player.mechanicCooldowns);
    advanceMap(player.companionCooldowns);
    for (const entity of this.abilityEntities.values()) {
      if (entity.ownerId !== player.id || !eligibleIds.includes(entity.abilityId)) continue;
      if (entity.respawnAtMs > this.elapsedMs) {
        entity.respawnAtMs = Math.max(this.elapsedMs, entity.respawnAtMs - mechanic.advanceMs);
      }
    }
    const world = this.getActiveSynergyMechanic(player, "the_world");
    if (world?.sourceAbilityIds.includes(source.id)) {
      for (const enemy of this.enemies.values()) {
        if (
          enemy.spaceId !== player.spaceId ||
          getEnemy(enemy.definitionId).rank === "boss" ||
          Math.hypot(enemy.x - player.x, enemy.y - player.y) > world.radius
        ) {
          continue;
        }
        enemy.rootedUntilMs = Math.max(
          enemy.rootedUntilMs,
          this.elapsedMs + world.pauseDurationMs,
        );
        enemy.stunnedUntilMs = Math.max(
          enemy.stunnedUntilMs,
          this.elapsedMs + world.pauseDurationMs,
        );
      }
    }
  }

  private activateSynergySubstitution(
    player: SimPlayer,
    source: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "synergy_substitution" }
    >,
    sourceSlot: number,
  ): void {
    const owned = player.modules.filter((id): id is string => Boolean(id));
    const activeIds = new Set(evaluateSynergies(owned).map((synergy) => synergy.id));
    const candidates = SYNERGIES.flatMap((synergy) => {
      if (activeIds.has(synergy.id)) return [];
      const unmet = synergy.requirements.filter((requirement) => {
        if (requirement.kind === "module") {
          return !owned.includes(requirement.moduleId);
        }
        if (requirement.kind === "one_of") {
          return !requirement.moduleIds.some((id) => owned.includes(id));
        }
        if (requirement.kind === "synergy") {
          return !activeIds.has(requirement.synergyId);
        }
        if (requirement.kind === "tag_count") {
          return new Set(
            owned.filter((id) => getModule(id).tags.includes(requirement.tag)),
          ).size < requirement.count;
        }
        return owned.filter((id) => getModule(id).tier >= requirement.minTier).length <
          requirement.count;
      });
      if (unmet.length !== 1) return [];
      const requirement = unmet[0];
      if (requirement.kind === "synergy") return [];
      const moduleIds = requirement.kind === "module"
        ? [requirement.moduleId]
        : requirement.kind === "one_of"
          ? requirement.moduleIds
          : [];
      return moduleIds.flatMap((moduleId) => {
        const module = getModule(moduleId);
        if (
          owned.includes(moduleId) ||
          (!mechanic.allowTierSixModule && module.tier === 6) ||
          (!mechanic.allowHiddenSkill && module.tags.includes("hidden"))
        ) {
          return [];
        }
        return [{ synergy, module }];
      });
    });
    if (candidates.length === 0) return;
    const selected = this.random.pick(candidates);
    const agreement = this.getActiveSynergyMechanic(
      player,
      "dual_gods_agreement",
    );
    const durationMs =
      mechanic.durationMs +
      (agreement?.contractAbilityIds.includes(source.id)
        ? agreement.durationAddMs
        : 0);
    player.synergySubstitutions.set(`slot:${sourceSlot}`, {
      moduleId: selected.module.id,
      expiresAtMs: this.elapsedMs + durationMs,
      sourceAbilityId: source.id,
    });
    this.recomputePlayer(player);
    if (agreement?.contractAbilityIds.includes(source.id)) {
      const participating = selected.synergy.requirements.flatMap((requirement) => {
        if (requirement.kind === "module") return [requirement.moduleId];
        if (requirement.kind === "one_of") return requirement.moduleIds;
        return [];
      }).map((id) => getModule(id)).filter(isInstantlyCastable);
      if (participating.length > 0) {
        this.castModuleOnce(player, this.random.pick(participating));
      }
    }
    this.events.push({
      type: "notice",
      message: `${player.name} 的「${source.name}」以「${selected.module.name}」临时补全「${selected.synergy.name}」`,
    });
  }

  private castPeriodicSelfAreaDamage(
    player: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "periodic_self_area_damage" }
    >,
    sourceSlot?: number,
  ): void {
    const hitIds: number[] = [];
    let dealtPositiveDamage = false;
    for (const enemy of [...this.enemies.values()]) {
      if (
        enemy.spaceId !== player.spaceId ||
        Math.hypot(enemy.x - player.x, enemy.y - player.y) >
        mechanic.radius
      ) {
        continue;
      }
      const rank = getEnemy(enemy.definitionId).rank;
      const eliteMultiplier =
        rank === "normal" ? 1 : player.stats.eliteDamageMultiplier;
      if ((mechanic.slowRatio ?? 0) > 0) {
        enemy.slowRatio = Math.max(
          enemy.slowRatio,
          mechanic.slowRatio ?? 0,
        );
        enemy.slowUntilMs = Math.max(
          enemy.slowUntilMs,
          this.elapsedMs + (mechanic.slowDurationMs ?? 0),
        );
      }
      const applied = this.damageEnemy(
        enemy,
        mechanic.damage *
          player.stats.damageMultiplier *
          this.getPlayerAbilityDamageMultiplier(player) *
          (1 + this.teamTechLevels.war_chant * 0.03) *
          eliteMultiplier,
        player.id,
        { kind: "ability", abilityId: ability.id, sourceSlot },
      );
      dealtPositiveDamage ||= applied > 0;
      hitIds.push(enemy.id);
    }
    if (dealtPositiveDamage && (mechanic.healOnPositiveHit ?? 0) > 0) {
      const before = player.health;
      player.health = Math.min(
        player.stats.maxHealth,
        player.health + (mechanic.healOnPositiveHit ?? 0),
      );
      player.contribution.healingDone += player.health - before;
    }
    if (hitIds.length > 0) {
      this.events.push({
        type: "shot",
        playerId: player.id,
        targetIds: hitIds,
        abilityId: ability.id,
      });
    }
  }

  private castNormalExecuteReward(
    player: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "periodic_normal_execute_reward" }
    >,
  ): boolean {
    const target = this.pickTarget(
      player.x,
      player.y,
      mechanic.range,
      "random",
      "normal",
    );
    if (!target) {
      return false;
    }
    const lethalDamage =
      target.health * ((100 + Math.max(0, target.armor)) / 100) + 1;
    this.damageEnemy(target, lethalDamage, player.id, {
      kind: "ability",
      abilityId: ability.id,
    });
    this.events.push({
      type: "shot",
      playerId: player.id,
      targetIds: [target.id],
      abilityId: ability.id,
    });
    return true;
  }

  private castForcedAggro(
    player: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "periodic_forced_aggro" }
    >,
  ): boolean {
    const candidates = [...this.enemies.values()].filter(
      (enemy) =>
        getEnemy(enemy.definitionId).rank !== "boss" &&
        Math.hypot(enemy.x - player.x, enemy.y - player.y) <=
          mechanic.range,
    );
    if (candidates.length === 0) {
      return false;
    }
    const target = this.random.pick(candidates);
    target.forcedAggroPlayerId = player.id;
    target.forcedAggroAbilityId = ability.id;
    target.forcedAggroRange = mechanic.range;
    target.forcedAggroSlowRatio = mechanic.slowRatio;
    return true;
  }

  private castHealthExtremesDamage(
    player: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "periodic_health_extremes_damage" }
    >,
    sourceSlot?: number,
  ): boolean {
    const highest = this.pickTarget(
      player.x,
      player.y,
      mechanic.range,
      "highest_health",
      undefined,
      player.spaceId,
    );
    const lowest = this.pickTarget(
      player.x,
      player.y,
      mechanic.range,
      "lowest_health",
      undefined,
      player.spaceId,
    );
    if (!highest && !lowest) return false;
    const tuning = getAbilityTuning(player.modules, ability.id);
    const common =
      tuning.damageMultiplier *
      player.stats.damageMultiplier *
      this.getPlayerAbilityDamageMultiplier(player) *
      (1 + this.teamTechLevels.war_chant * 0.03);
    const hitIds: number[] = [];
    if (highest) {
      const rank = getEnemy(highest.definitionId).rank;
      this.damageEnemy(
        highest,
        mechanic.highestDamage * common *
          (rank === "normal" ? 1 : player.stats.eliteDamageMultiplier),
        player.id,
        {
          kind: "ability",
          abilityId: ability.id,
          sourceSlot,
          variant: "highest_health",
        },
      );
      hitIds.push(highest.id);
    }
    if (lowest && this.enemies.has(lowest.id)) {
      const rank = getEnemy(lowest.definitionId).rank;
      this.damageEnemy(
        lowest,
        mechanic.lowestDamage * common *
          (rank === "normal" ? 1 : player.stats.eliteDamageMultiplier),
        player.id,
        {
          kind: "ability",
          abilityId: ability.id,
          sourceSlot,
          variant: "lowest_health",
        },
      );
      hitIds.push(lowest.id);
    }
    this.events.push({
      type: "shot",
      playerId: player.id,
      targetIds: [...new Set(hitIds)],
      abilityId: ability.id,
    });
    return true;
  }

  private advancedMechanicReady(
    player: SimPlayer,
    key: string,
    intervalMs: number,
  ): boolean {
    const nextAt = player.mechanicCooldowns.get(key);
    if (nextAt === undefined) {
      player.mechanicCooldowns.set(key, this.elapsedMs + intervalMs);
      return false;
    }
    if (this.elapsedMs < nextAt) {
      return false;
    }
    player.mechanicCooldowns.set(key, this.elapsedMs + intervalMs);
    return true;
  }

  private updateUndeadSummonMechanic(
    player: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "undead_summon" }
    >,
    mechanicIndex: number,
    sourceSlot: number,
  ): void {
    const key = mechanicCooldownKey(
      ability.id,
      mechanicIndex,
      sourceSlot > 0 ? sourceSlot : undefined,
    );
    const lichIntervalMultiplier =
      player.status === "lich"
        ? (this.getLichMechanic(player)
            ?.undeadSummonIntervalMultiplier ?? 1)
        : 1;
    const tuning = getAbilityTuning(player.modules, ability.id);
    const intervalMs =
      mechanic.intervalMs *
      tuning.intervalMultiplier *
      lichIntervalMultiplier;
    const nextAt = player.mechanicCooldowns.get(key);
    if (nextAt === undefined) {
      player.mechanicCooldowns.set(key, this.elapsedMs + intervalMs);
      return;
    }
    const manaCost = mechanic.manaCost * tuning.manaCostMultiplier;
    if (this.elapsedMs < nextAt || player.mana < manaCost) {
      return;
    }
    player.mana -= manaCost;
    player.mechanicCooldowns.set(key, this.elapsedMs + intervalMs);
    const castExecutions = this.registerDiscreteSkillCast(
      player,
      `slot:${sourceSlot}`,
    );
    for (let cast = 0; cast < castExecutions; cast += 1) {
      const entity = this.spawnUndeadEntity(
        player,
        mechanic.unitId,
        ability.id,
        player.x,
        player.y,
      );
      entity.sourceSlot = sourceSlot;
    }
  }

  private spawnUndeadEntity(
    player: SimPlayer,
    unitId: UndeadUnitId,
    sourceAbilityId: string,
    x: number,
    y: number,
  ): SimAbilityEntity {
    const unit = getUndeadUnit(unitId);
    const lichMultiplier =
      this.getLichMechanic(player)?.undeadHealthDamageMultiplier ?? 1;
    const maxHealth = unit.health * lichMultiplier;
    const entity: SimAbilityEntity = {
      id: this.nextAbilityEntityId++,
      ownerId: player.id,
      abilityId: sourceAbilityId,
      kind: "summon",
      presentationId: unit.presentationId,
      spaceId: player.spaceId,
      x,
      y,
      health: maxHealth,
      maxHealth,
      armor: unit.armor + this.teamTechLevels.familiar_ward * 3,
      damage: unit.damage * lichMultiplier,
      intervalMs: unit.intervalMs,
      range: unit.range,
      moveSpeed: PLAYER_BASE_MOVE_SPEED * unit.moveSpeedBaseRatio,
      orbitRadius: 0,
      orbitOffset: 0,
      nextAttackAtMs: this.elapsedMs + unit.intervalMs,
      attackCount: 0,
      deathProcessed: false,
      nextPeriodicHealAtMs: this.elapsedMs,
      temporaryShield: 0,
      temporaryShieldUntilMs: 0,
      nextIncomeAtMs: Number.POSITIVE_INFINITY,
      incomeGold: 0,
      respawnMs: 0,
      respawnAtMs: 0,
      auraMultiplier: 1,
      ethereal: false,
      nextHuntAtMs: Number.POSITIVE_INFINITY,
      undeadUnitId: unit.id,
      undeadAbilityCooldowns: new Map(
        unit.abilities.map((ability, index) => [
          index,
          this.elapsedMs +
            (ability.kind === "charge" ||
            ability.kind === "periodic_area_damage"
              ? ability.intervalMs
              : Number.POSITIVE_INFINITY),
        ]),
      ),
    };
    this.abilityEntities.set(entity.id, entity);
    this.events.push({
      type: "abilityEntities",
      entities: [this.toWireAbilityEntity(entity)],
    });
    player.contribution.companionsSummoned += 1;
    return entity;
  }

  private castPeriodicTargetDamage(
    player: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "periodic_target_damage" }
    >,
    sourceSlot?: number,
  ): boolean {
    const targets = this.pickTargets(
      player.x,
      player.y,
      mechanic.range,
      mechanic.target === "heavy" ? "elite_only" : "random",
      mechanic.hits ?? 1,
    );
    const inSpace = targets.filter((target) => target.spaceId === player.spaceId);
    if (inSpace.length === 0) {
      return false;
    }
    const tuning = getAbilityTuning(player.modules, ability.id);
    for (const target of inSpace) {
      const rank = getEnemy(target.definitionId).rank;
      const eliteMultiplier =
        rank === "normal" ? 1 : player.stats.eliteDamageMultiplier;
      const damage =
        mechanic.damage *
        tuning.damageMultiplier *
        player.stats.damageMultiplier *
        this.getPlayerAbilityDamageMultiplier(player) *
        (1 + this.teamTechLevels.war_chant * 0.03) *
        eliteMultiplier;
      this.damageEnemy(target, damage, player.id, {
        kind: "ability",
        abilityId: ability.id,
        sourceSlot,
      });
    }
    this.events.push({
      type: "shot",
      playerId: player.id,
      targetIds: inSpace.map((target) => target.id),
      abilityId: ability.id,
    });
    return true;
  }

  private rerollNonTierSixModules(
    player: SimPlayer,
    source: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "periodic_reroll" }
    >,
  ): void {
    const occupied = new Set(
      player.modules.filter((moduleId): moduleId is string => Boolean(moduleId)),
    );
    const replacements: Array<{
      slot: number;
      previousId: string;
      nextId: string;
    }> = [];
    for (const [slot, currentId] of player.modules.entries()) {
      if (!currentId) {
        continue;
      }
      const current = getModule(currentId);
      if (
        current.tier === 6 ||
        (mechanic.excludeSelf && currentId === source.id)
      ) {
        continue;
      }
      let pool = MODULES.filter(
        (candidate) =>
          candidate.tier === current.tier &&
          candidate.tier < 6 &&
          candidate.weight > 0 &&
          candidate.id !== currentId &&
          !occupied.has(candidate.id),
      );
      if (pool.length === 0) {
        pool = MODULES.filter(
          (candidate) =>
            candidate.tier === current.tier &&
            candidate.tier < 6 &&
            candidate.weight > 0 &&
            candidate.id !== currentId,
        );
      }
      if (pool.length === 0) {
        continue;
      }
      const next = this.random.weightedPick(
        pool,
        (candidate) => candidate.weight,
      );
      occupied.delete(currentId);
      occupied.add(next.id);
      replacements.push({ slot, previousId: currentId, nextId: next.id });
    }
    if (replacements.length === 0) {
      return;
    }
    player.pendingOffer = undefined;
    for (const replacement of replacements) {
      if (player.modules[replacement.slot] !== replacement.previousId) {
        continue;
      }
      this.replaceModuleInSlot(
        player,
        replacement.slot,
        replacement.nextId,
        {
          suppressVaultPayout: false,
          lockSlot: false,
        },
      );
    }
    this.events.push({
      type: "notice",
      message:
        `${player.name} 的「${source.name}」重随了：` +
        replacements
          .map(
            ({ previousId, nextId }) =>
              `${getModule(previousId).name}→${getModule(nextId).name}`,
          )
          .join("、"),
    });
  }

  private castRandomHighTierSkill(
    player: SimPlayer,
    source: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "random_skill_cast" }
    >,
  ): void {
    const pool = MODULES.filter(
      (candidate) =>
        mechanic.tiers.includes(candidate.tier) &&
        (!mechanic.categories ||
          mechanic.categories.includes(candidate.category)) &&
        candidate.id !== source.id &&
        !candidate.tags.includes("conditional_source") &&
        !candidate.mechanics?.some(
          (entry) => entry.kind === "random_skill_cast",
        ) &&
        isInstantlyCastable(candidate),
    );
    if (pool.length === 0) {
      return;
    }
    const selected = this.random.pick(pool);
    this.castModuleOnce(player, selected);
    this.events.push({
      type: "notice",
      message: `${player.name} 的「${source.name}」释放了「${selected.name}」`,
    });
  }

  private castModuleOnce(player: SimPlayer, ability: ModuleDefinition): void {
    if (ability.combat) {
      this.castCombatOnce(player, ability);
    }
    if (ability.healing) {
      const healing = ability.healing;
      if (healing.target === "self") {
        const before = player.health;
        player.health = Math.min(
          player.stats.maxHealth,
          player.health + healing.amount,
        );
        player.contribution.healingDone += player.health - before;
      } else if (healing.target === "core") {
        const before = this.core.health;
        this.core.health = Math.min(
          this.core.maxHealth,
          this.core.health + healing.amount,
        );
        player.contribution.healingDone += this.core.health - before;
      } else {
        const target = [...this.players.values()]
          .filter(
            (candidate) =>
              candidate.status === "active" &&
              Math.hypot(candidate.x - player.x, candidate.y - player.y) <=
                (healing.range ?? 500),
          )
          .sort(
            (a, b) =>
              a.health / a.stats.maxHealth - b.health / b.stats.maxHealth,
          )[0];
        if (target) {
          const before = target.health;
          target.health = Math.min(
            target.stats.maxHealth,
            target.health + healing.amount,
          );
          player.contribution.healingDone += target.health - before;
        }
      }
    }
    for (const mechanic of ability.mechanics ?? []) {
      if (mechanic.kind === "periodic_target_damage") {
        this.castPeriodicTargetDamage(player, ability, mechanic);
      } else if (mechanic.kind === "enemy_conversion") {
        this.convertRandomEnemy(player, ability, mechanic);
      } else if (mechanic.kind === "black_hole") {
        this.spawnBlackHole(player, ability, mechanic);
      } else if (mechanic.kind === "periodic_normal_execute_reward") {
        this.castNormalExecuteReward(player, ability, mechanic);
      } else if (mechanic.kind === "periodic_forced_aggro") {
        this.castForcedAggro(player, ability, mechanic);
      }
    }
  }

  private castCombatOnce(
    player: SimPlayer,
    ability: ModuleDefinition,
  ): boolean {
    const combat = ability.combat;
    if (!combat) {
      return false;
    }
    const tuning = getAbilityTuning(player.modules, ability.id);
    const targets = this.pickTargets(
      player.x,
      player.y,
      combat.range * tuning.rangeMultiplier,
      combat.target,
      (combat.projectiles ?? 1) + tuning.projectilesAdd,
    );
    if (targets.length === 0) {
      return false;
    }
    const allHitIds: number[] = [];
    for (const target of targets) {
      const rank = getEnemy(target.definitionId).rank;
      const eliteMultiplier =
        rank === "normal" ? 1 : player.stats.eliteDamageMultiplier;
      const damage =
        (combat.damage +
          (player.abilityDamageGrowth.get(ability.id) ?? 0)) *
        tuning.damageMultiplier *
        player.stats.damageMultiplier *
        this.getPlayerAbilityDamageMultiplier(player) *
        (1 + this.teamTechLevels.war_chant * 0.03) *
        eliteMultiplier;
      allHitIds.push(
        ...this.applyHitPattern(
          player,
          target,
          damage,
          { kind: "ability", abilityId: ability.id },
          (combat.radius ?? 0) +
            tuning.radiusAdd +
            this.getSynergyRadiusAdd(player, ability.id),
          (combat.chainTargets ?? 0) +
            (combat.pierceTargets ?? 0) +
            tuning.extraTargetsAdd,
          combat.slowRatio,
          combat.slowDurationMs,
          combat.areaDamageMultiplier,
          combat.stunDurationMs,
          combat.stunBosses ?? true,
        ),
      );
    }
    this.events.push({
      type: "shot",
      playerId: player.id,
      targetIds: [...new Set(allHitIds)],
      abilityId: ability.id,
    });
    return true;
  }

  private convertRandomEnemy(
    player: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "enemy_conversion" }
    >,
  ): boolean {
    const target = this.pickTarget(
      player.x,
      player.y,
      mechanic.range,
      "random",
      mechanic.targetRank,
    );
    if (!target) {
      return false;
    }
    const definition = getEnemy(target.definitionId);
    const multiplier = mechanic.statMultiplier;
    this.enemies.delete(target.id);
    this.events.push({ type: "enemyDeath", enemyIds: [target.id] });
    const maxHealth = target.maxHealth * multiplier;
    const entity: SimAbilityEntity = {
      id: this.nextAbilityEntityId++,
      ownerId: player.id,
      abilityId: ability.id,
      kind: "summon",
      presentationId: `converted_${definition.id}`,
      spaceId: target.spaceId,
      x: target.x,
      y: target.y,
      health: target.health * multiplier,
      maxHealth,
      armor: target.armor * multiplier,
      damage: definition.coreDamage * multiplier,
      intervalMs: definition.attackIntervalMs / multiplier,
      range: 120 * multiplier,
      moveSpeed: definition.speed * multiplier,
      orbitRadius: 150,
      orbitOffset: this.random.next() * Math.PI * 2,
      nextAttackAtMs: this.elapsedMs + definition.attackIntervalMs / multiplier,
      attackCount: 0,
      deathProcessed: false,
      nextPeriodicHealAtMs: this.elapsedMs,
      temporaryShield: 0,
      temporaryShieldUntilMs: 0,
      nextIncomeAtMs: Number.POSITIVE_INFINITY,
      incomeGold: 0,
      respawnMs: 0,
      respawnAtMs: 0,
      auraMultiplier: 1,
      ethereal: false,
      nextHuntAtMs: Number.POSITIVE_INFINITY,
    };
    this.abilityEntities.set(entity.id, entity);
    this.events.push({
      type: "abilityEntities",
      entities: [this.toWireAbilityEntity(entity)],
    });
    player.contribution.companionsSummoned += 1;
    return true;
  }

  private spawnBlackHole(
    player: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<AbilityMechanicDefinition, { kind: "black_hole" }>,
  ): boolean {
    const target = this.pickTarget(
      player.x,
      player.y,
      mechanic.castRange,
      "random",
    );
    if (!target) {
      return false;
    }
    const entity: SimAbilityEntity = {
      id: this.nextAbilityEntityId++,
      ownerId: player.id,
      abilityId: ability.id,
      kind: "field",
      presentationId: "void_black_hole",
      spaceId: player.spaceId,
      x: target.x,
      y: target.y,
      health: 1,
      maxHealth: 1,
      armor: 0,
      damage: 0,
      intervalMs: 0,
      range: 0,
      moveSpeed: 0,
      orbitRadius: 0,
      orbitOffset: 0,
      nextAttackAtMs: Number.POSITIVE_INFINITY,
      attackCount: 0,
      deathProcessed: false,
      nextPeriodicHealAtMs: this.elapsedMs,
      temporaryShield: 0,
      temporaryShieldUntilMs: 0,
      nextIncomeAtMs: Number.POSITIVE_INFINITY,
      incomeGold: 0,
      respawnMs: 0,
      respawnAtMs: 0,
      auraMultiplier: 1,
      ethereal: false,
      nextHuntAtMs: Number.POSITIVE_INFINITY,
      field: {
        radius: mechanic.diameter / 2,
        damage:
          mechanic.damage *
          getAbilityTuning(player.modules, ability.id).damageMultiplier *
          player.stats.damageMultiplier *
          this.getPlayerAbilityDamageMultiplier(player) *
          (1 + this.teamTechLevels.war_chant * 0.03),
        tickIntervalMs: mechanic.tickIntervalMs,
        nextTickAtMs: this.elapsedMs + mechanic.tickIntervalMs,
        expiresAtMs: this.elapsedMs + mechanic.durationMs,
        pullNormals: true,
      },
    };
    this.abilityEntities.set(entity.id, entity);
    this.events.push({
      type: "abilityEntities",
      entities: [this.toWireAbilityEntity(entity)],
    });
    return true;
  }

  private updateMovementDamageFields(player: SimPlayer, moved: number): void {
    for (const [slot, abilityId] of player.modules.entries()) {
      if (!abilityId) continue;
      const ability = getModule(abilityId);
      for (const [index, mechanic] of (ability.mechanics ?? []).entries()) {
        if (mechanic.kind !== "movement_damage_field") continue;
        const key = `movement-field:${slot}:${index}`;
        let distance = (player.growthCounters.get(key) ?? 0) + moved;
        while (distance >= mechanic.distancePerField) {
          distance -= mechanic.distancePerField;
          this.spawnMovementDamageField(player, ability, mechanic, slot);
        }
        player.growthCounters.set(key, distance);
      }
    }
  }

  private spawnMovementDamageField(
    player: SimPlayer,
    ability: ModuleDefinition,
    mechanic: Extract<
      AbilityMechanicDefinition,
      { kind: "movement_damage_field" }
    >,
    sourceSlot: number,
  ): void {
    const effectiveRadius =
      mechanic.radius + this.getSynergyRadiusAdd(player, ability.id);
    if (
      mechanic.suppressOverlap &&
      [...this.abilityEntities.values()].some(
        (entity) =>
          entity.kind === "field" &&
          entity.ownerId === player.id &&
          baseAbilityId(entity.abilityId) === ability.id &&
          Math.hypot(entity.x - player.x, entity.y - player.y) <
            effectiveRadius,
      )
    ) {
      return;
    }
    const tuning = getAbilityTuning(player.modules, ability.id);
    const entity: SimAbilityEntity = {
      id: this.nextAbilityEntityId++,
      ownerId: player.id,
      abilityId: ability.id,
      sourceSlot,
      kind: "field",
      presentationId: "lava_footprint",
      spaceId: player.spaceId,
      x: player.x,
      y: player.y,
      health: 1,
      maxHealth: 1,
      armor: 0,
      damage: 0,
      intervalMs: 0,
      range: 0,
      moveSpeed: 0,
      orbitRadius: 0,
      orbitOffset: 0,
      nextAttackAtMs: Number.POSITIVE_INFINITY,
      attackCount: 0,
      deathProcessed: false,
      nextPeriodicHealAtMs: this.elapsedMs,
      temporaryShield: 0,
      temporaryShieldUntilMs: 0,
      nextIncomeAtMs: Number.POSITIVE_INFINITY,
      incomeGold: 0,
      respawnMs: 0,
      respawnAtMs: 0,
      auraMultiplier: 1,
      ethereal: false,
      nextHuntAtMs: Number.POSITIVE_INFINITY,
      field: {
        radius: effectiveRadius,
        damage:
          mechanic.damage *
          tuning.damageMultiplier *
          player.stats.damageMultiplier *
          this.getPlayerAbilityDamageMultiplier(player) *
          (1 + this.teamTechLevels.war_chant * 0.03),
        tickIntervalMs: mechanic.tickIntervalMs,
        nextTickAtMs: this.elapsedMs + mechanic.tickIntervalMs,
        expiresAtMs: this.elapsedMs + mechanic.durationMs,
        pullNormals: false,
      },
    };
    this.abilityEntities.set(entity.id, entity);
    this.events.push({
      type: "abilityEntities",
      entities: [this.toWireAbilityEntity(entity)],
    });
  }

  private updateBlackHoleEntity(entity: SimAbilityEntity): void {
    const field = entity.field;
    const owner = this.players.get(entity.ownerId);
    if (!field || !owner) {
      this.abilityEntities.delete(entity.id);
      return;
    }
    while (
      this.elapsedMs >= field.nextTickAtMs &&
      field.nextTickAtMs <= field.expiresAtMs
    ) {
      for (const enemy of [...this.enemies.values()]) {
        const dx = entity.x - enemy.x;
        const dy = entity.y - enemy.y;
        const distance = Math.hypot(dx, dy);
        if (distance > field.radius) {
          continue;
        }
        const hotFloor = this.getActiveSynergyMechanic(owner, "hot_floor");
        const abilityId = baseAbilityId(entity.abilityId);
        const contextMultiplier =
          hotFloor?.abilityIds.includes(abilityId) &&
          enemy.hotFloorEmpoweredUntilMs > this.elapsedMs
            ? hotFloor.damageMultiplier
            : 1;
        this.damageEnemy(enemy, field.damage * contextMultiplier, owner.id, {
          kind: "ability",
          abilityId,
          sourceSlot: entity.sourceSlot,
        });
        if (this.enemies.has(enemy.id)) {
          this.applyTierFourSynergyOnHit(owner, enemy, abilityId, {
            kind: "ability",
            abilityId,
            sourceSlot: entity.sourceSlot,
          });
        }
        if (
          field.pullNormals !== false &&
          this.enemies.has(enemy.id) &&
          getEnemy(enemy.definitionId).rank === "normal" &&
          distance > 0.001
        ) {
          enemy.x += dx * 0.5;
          enemy.y += dy * 0.5;
        }
      }
      field.nextTickAtMs += field.tickIntervalMs;
    }
    if (this.elapsedMs >= field.expiresAtMs) {
      this.abilityEntities.delete(entity.id);
    }
  }

  private getCompanionAuraMultiplier(entity: SimAbilityEntity): number {
    if (entity.kind !== "summon" && entity.kind !== "guard") {
      return 1;
    }
    let multiplier = 1;
    for (const player of this.players.values()) {
      if (player.status === "downed" || player.status === "lich") {
        continue;
      }
      for (const abilityId of player.modules) {
        if (!abilityId) {
          continue;
        }
        for (const mechanic of getModule(abilityId).mechanics ?? []) {
          if (
            mechanic.kind === "companion_aura" &&
            mechanic.targets.includes(entity.kind) &&
            Math.hypot(entity.x - player.x, entity.y - player.y) <=
              mechanic.range
          ) {
            multiplier = Math.max(multiplier, mechanic.statMultiplier);
          }
        }
      }
    }
    return multiplier;
  }

  private applyCompanionAura(entity: SimAbilityEntity): void {
    const nextMultiplier = this.getCompanionAuraMultiplier(entity);
    if (Math.abs(nextMultiplier - entity.auraMultiplier) < 0.0001) {
      return;
    }
    const scale = nextMultiplier / entity.auraMultiplier;
    if (Number.isFinite(entity.maxHealth)) {
      const healthRatio =
        entity.maxHealth > 0 ? clamp(entity.health / entity.maxHealth, 0, 1) : 0;
      entity.maxHealth *= scale;
      entity.health = entity.maxHealth * healthRatio;
    }
    entity.armor *= scale;
    entity.damage *= scale;
    entity.intervalMs /= scale;
    entity.range *= scale;
    entity.moveSpeed *= scale;
    entity.auraMultiplier = nextMultiplier;
  }

  private updateHiddenEffects(deltaMs: number): void {
    for (const owner of this.players.values()) {
      if (owner.status === "downed") {
        continue;
      }
      for (const runtime of this.getHiddenRuntimeEffects(owner)) {
        const key = `${runtime.sourceId}:${runtime.effectIndex}`;
        const effect = runtime.effect;
        if (effect.kind === "ally_healing_aura") {
          const healing = effect.healingPerSecond * (deltaMs / 1_000);
          for (const ally of this.players.values()) {
            if (
              ally.status === "downed" ||
              Math.hypot(ally.x - owner.x, ally.y - owner.y) > effect.radius
            ) {
              continue;
            }
            const before = ally.health;
            ally.health = Math.min(ally.stats.maxHealth, ally.health + healing);
            owner.contribution.healingDone += ally.health - before;
          }
          for (const entity of this.abilityEntities.values()) {
            if (
              entity.ethereal ||
              entity.health <= 0 ||
              Math.hypot(entity.x - owner.x, entity.y - owner.y) >
                effect.radius
            ) {
              continue;
            }
            entity.health = Math.min(entity.maxHealth, entity.health + healing);
          }
          continue;
        }
        if (effect.kind === "low_health_execute") {
          for (const enemy of [...this.enemies.values()]) {
            if (
              enemy.health > 0 &&
              enemy.health / enemy.maxHealth < effect.healthRatio &&
              Math.hypot(enemy.x - owner.x, enemy.y - owner.y) <= effect.radius
            ) {
              this.damageEnemy(
                enemy,
                enemy.health *
                    ((100 + Math.max(0, enemy.armor)) / 100) +
                  1,
                owner.id,
                {
                  kind: "hidden_effect",
                  hiddenSourceId: runtime.sourceId,
                },
              );
            }
          }
          continue;
        }
        if (
          effect.kind === "ally_armor_aura" ||
          effect.kind === "ally_speed_aura" ||
          effect.kind === "enemy_slow_immunity" ||
          effect.kind === "base_move_speed_multiplier" ||
          effect.kind === "normal_enemy_taunt_aura" ||
          effect.kind === "every_nth_cast_double" ||
          !("intervalMs" in effect) ||
          !this.hiddenPeriodicReady(owner, key, effect.intervalMs)
        ) {
          continue;
        }
        const castExecutions = this.registerDiscreteSkillCast(
          owner,
          `hidden:${runtime.sourceId}`,
        );
        for (let cast = 0; cast < castExecutions; cast += 1) {
        if (effect.kind === "periodic_area_damage") {
          for (const enemy of [...this.enemies.values()]) {
            if (
              Math.hypot(enemy.x - owner.x, enemy.y - owner.y) <= effect.radius
            ) {
              this.damageEnemy(enemy, effect.damage, owner.id, {
                kind: "hidden_effect",
                hiddenSourceId: runtime.sourceId,
              });
            }
          }
        } else if (effect.kind === "periodic_heavy_damage") {
          const target = this.pickTarget(
            owner.x,
            owner.y,
            effect.radius,
            "elite_only",
          );
          if (target) {
            this.damageEnemy(target, effect.damage, owner.id, {
              kind: "hidden_effect",
              hiddenSourceId: runtime.sourceId,
            });
          }
        } else if (effect.kind === "periodic_normal_execute") {
          const target = this.pickTarget(
            owner.x,
            owner.y,
            effect.radius,
            "random",
            "normal",
          );
          if (target) {
            this.damageEnemy(
              target,
              target.health *
                  ((100 + Math.max(0, target.armor)) / 100) +
                1,
              owner.id,
              {
                kind: "hidden_effect",
                hiddenSourceId: runtime.sourceId,
              },
            );
          }
        } else if (effect.kind === "periodic_random_damage") {
          const target = this.pickTarget(
            owner.x,
            owner.y,
            effect.radius,
            "random",
            undefined,
            owner.spaceId,
          );
          if (target) {
            const eliteMultiplier =
              getEnemy(target.definitionId).rank === "normal"
                ? 1
                : owner.stats.eliteDamageMultiplier;
            this.damageEnemy(
              target,
              effect.damage *
                owner.stats.damageMultiplier *
                this.getPlayerAbilityDamageMultiplier(owner) *
                (1 + this.teamTechLevels.war_chant * 0.03) *
                eliteMultiplier,
              owner.id,
              {
                kind: "hidden_effect",
                hiddenSourceId: runtime.sourceId,
              },
            );
          }
        } else if (effect.kind === "periodic_non_boss_push_damage") {
          for (const enemy of [...this.enemies.values()]) {
            const rank = getEnemy(enemy.definitionId).rank;
            const distance = Math.hypot(enemy.x - owner.x, enemy.y - owner.y);
            if (
              rank === "boss" ||
              enemy.spaceId !== owner.spaceId ||
              distance > effect.radius
            ) {
              continue;
            }
            const eliteMultiplier =
              rank === "normal" ? 1 : owner.stats.eliteDamageMultiplier;
            this.damageEnemy(
              enemy,
              effect.damage *
                owner.stats.damageMultiplier *
                this.getPlayerAbilityDamageMultiplier(owner) *
                (1 + this.teamTechLevels.war_chant * 0.03) *
                eliteMultiplier,
              owner.id,
              {
                kind: "hidden_effect",
                hiddenSourceId: runtime.sourceId,
              },
            );
            // A same-instant free repeat resolves against the same source area;
            // postpone displacement until the final execution so the first push
            // cannot remove an otherwise valid target from the bonus cast.
            if (
              cast === castExecutions - 1 &&
              this.enemies.has(enemy.id)
            ) {
              this.knockbackEnemyFromPoint(
                enemy,
                owner,
                Math.max(0, effect.pushOutsideRadius - distance) + 1,
              );
            }
          }
        }
        }
      }
      if (owner.status === "lich") {
        continue;
      }
      for (const synergyId of owner.synergies) {
        const synergy = getSynergy(synergyId);
        for (const [index, mechanic] of (synergy.mechanics ?? []).entries()) {
          if (
            mechanic.kind !== "periodic_heavy_damage" ||
            !this.hiddenPeriodicReady(
              owner,
              `synergy:${synergyId}:${index}`,
              mechanic.intervalMs,
            )
          ) {
            continue;
          }
          const target = this.pickTarget(
            owner.x,
            owner.y,
            mechanic.range,
            "elite_only",
          );
          if (target) {
            this.damageEnemy(target, mechanic.damage, owner.id, {
              kind: "hidden_effect",
              hiddenSourceId: synergyId,
            });
          }
        }
      }
    }
  }

  private hiddenPeriodicReady(
    player: SimPlayer,
    key: string,
    intervalMs: number,
  ): boolean {
    const nextAt = player.hiddenEffectCooldowns.get(key);
    if (nextAt === undefined) {
      player.hiddenEffectCooldowns.set(key, this.elapsedMs + intervalMs);
      return false;
    }
    if (this.elapsedMs < nextAt) {
      return false;
    }
    player.hiddenEffectCooldowns.set(key, this.elapsedMs + intervalMs);
    return true;
  }

  private registerDiscreteSkillCast(
    player: SimPlayer,
    sourceKey: string,
  ): 1 | 2 {
    if (!player.hiddenSynergies.includes(ROUND_CLOCK_LABOR_SYNERGY_ID)) {
      return 1;
    }
    const doubleCast = getHiddenSynergy(ROUND_CLOCK_LABOR_SYNERGY_ID)
      .effects.find((effect) => effect.kind === "every_nth_cast_double");
    if (doubleCast?.kind !== "every_nth_cast_double") {
      return 1;
    }
    const next = (player.castReleaseCounts.get(sourceKey) ?? 0) + 1;
    if (next >= doubleCast.every) {
      player.castReleaseCounts.set(sourceKey, 0);
      return 2;
    }
    player.castReleaseCounts.set(sourceKey, next);
    return 1;
  }

  private updateObjectives(deltaMs: number): void {
    for (const objective of this.objectives) {
      if (objective.status === "waiting" && this.elapsedMs >= objective.activateAtMs) {
        this.activateObjective(objective);
      }
      if (objective.status !== "active") {
        continue;
      }
      if (objective.id === "moonwell") {
        this.updateMoonwellObjective(objective, deltaMs);
      } else if (objective.id === "salvage") {
        this.updateSalvageObjective(objective, deltaMs);
      } else {
        this.updateRiftObjective(objective, deltaMs);
      }
    }

    const moonwell = this.objectives.find(
      (objective) => objective.id === "moonwell" && objective.status === "completed",
    );
    if (moonwell) this.applyMoonwellRegeneration(deltaMs);
  }

  private activateObjective(objective: SideObjectiveState): void {
    objective.status = "active";
    if (objective.id === "salvage") {
      const gate = this.findNearestLinkedGate(objective.x, objective.y, 3);
      objective.routeGateId = gate?.id;
      objective.routePhase = "to_gate";
      objective.moveSpeed = gate
        ? Math.hypot(gate.x - objective.x, gate.y - objective.y) / 60
        : 45;
      objective.playerEngaged = false;
    } else if (objective.id === "rift") {
      const position = this.randomObjectivePosition();
      objective.x = position.x;
      objective.y = position.y;
      objective.kills = 0;
      objective.spawnedCount = 0;
      objective.playerEngaged = false;
      this.riftSpawnAccumulatorMs = 0;
    }
    this.events.push({ type: "notice", message: `支线开启：${objective.name}` });
  }

  private updateMoonwellObjective(
    objective: SideObjectiveState,
    deltaMs: number,
  ): void {
    if ((objective.health ?? 0) <= 0) {
      objective.health = 0;
      objective.status = "failed";
      this.events.push({ type: "notice", message: "月泉被远征军摧毁，净化失败" });
      return;
    }
    const participants = [...this.players.values()].filter(
      (player) =>
        player.status !== "downed" &&
        Math.hypot(player.x - objective.x, player.y - objective.y) <= 155,
    );
    if (participants.length > 0) {
      objective.progressMs += deltaMs;
      for (const participant of participants) {
        participant.contribution.objectiveMs += deltaMs;
      }
    }
    if (objective.progressMs >= objective.requiredMs) {
      objective.status = "completed";
      objective.invulnerable = true;
      objective.completedAtMs = this.elapsedMs;
      this.completeObjective(objective);
    }
  }

  private updateSalvageObjective(
    objective: SideObjectiveState,
    deltaMs: number,
  ): void {
    if ((objective.health ?? 0) <= 0) {
      objective.health = 0;
      objective.status = "failed";
      this.events.push({ type: "notice", message: "炼金车被摧毁，护送失败" });
      return;
    }
    if (!objective.playerEngaged) {
      objective.playerEngaged = [...this.players.values()].some(
        (player) =>
          player.status !== "downed" &&
          Math.hypot(player.x - objective.x, player.y - objective.y) <= 300,
      );
      if (!objective.playerEngaged) {
        return;
      }
      this.events.push({
        type: "notice",
        message: "炼金车已由玩家接应，开始驶向怪物宝城",
      });
    }
    let gate = objective.routeGateId
      ? this.defenses.find(
          (segment) => segment.id === objective.routeGateId && this.isLinkedGate(segment),
        )
      : undefined;
    if (!gate && objective.routePhase === "to_gate") {
      gate = this.findNearestLinkedGate(objective.x, objective.y, 3);
      objective.routeGateId = gate?.id;
      if (gate) {
        objective.moveSpeed = Math.hypot(gate.x - objective.x, gate.y - objective.y) / 60;
      }
    }
    const target = objective.routePhase === "to_gate" && gate
      ? { x: gate.x, y: gate.y, radius: 54 }
      : { x: this.core.x, y: this.core.y, radius: this.core.radius + 20 };
    const dx = target.x - objective.x;
    const dy = target.y - objective.y;
    const distance = Math.hypot(dx, dy);
    const speed = Math.max(1, objective.moveSpeed ?? 45) *
      (objective.routePhase === "to_core" ? 2 : 1);
    const moveDistance = Math.min(distance, speed * (deltaMs / 1_000));
    if (distance > 0.001) {
      objective.x += (dx / distance) * moveDistance;
      objective.y += (dy / distance) * moveDistance;
    }
    objective.progressMs += deltaMs;
    if (distance <= target.radius + moveDistance) {
      if (objective.routePhase === "to_gate") {
        objective.routePhase = "to_core";
      } else {
        objective.status = "completed";
        objective.completedAtMs = this.elapsedMs;
        this.completeObjective(objective);
      }
    }
  }

  private updateRiftObjective(
    objective: SideObjectiveState,
    deltaMs: number,
  ): void {
    if (!objective.playerEngaged) {
      objective.playerEngaged = [...this.players.values()].some(
        (player) =>
          player.status !== "downed" &&
          Math.hypot(player.x - objective.x, player.y - objective.y) <=
            RIFT_WANDER_RADIUS,
      );
    }
    objective.progressMs += deltaMs;
    this.riftSpawnAccumulatorMs += deltaMs;
    while (
      this.riftSpawnAccumulatorMs >= RIFT_SPAWN_INTERVAL_MS &&
      objective.status === "active"
    ) {
      this.riftSpawnAccumulatorMs -= RIFT_SPAWN_INTERVAL_MS;
      const angle = this.random.next() * Math.PI * 2;
      const radius = this.random.integer(45, 150);
      const enemy = this.spawnEnemy(
        "rift_invader",
        objective.x + Math.cos(angle) * radius,
        objective.y + Math.sin(angle) * radius,
      );
      enemy.objectiveId = "rift";
      enemy.objectiveOriginX = objective.x;
      enemy.objectiveOriginY = objective.y;
      objective.spawnedCount = (objective.spawnedCount ?? 0) + 1;
    }
    if ((objective.kills ?? 0) >= (objective.requiredKills ?? RIFT_REQUIRED_KILLS)) {
      this.completeRiftObjective(objective);
      return;
    }
    if (this.elapsedMs >= objective.expiresAtMs) {
      objective.status = "failed";
      for (const enemy of this.enemies.values()) {
        if (enemy.objectiveId !== "rift") continue;
        delete enemy.objectiveId;
        enemy.objectiveEmpowered = true;
        enemy.objectivePowerMultiplier = 2;
        enemy.health *= 2;
        enemy.maxHealth *= 2;
        enemy.armor *= 2;
      }
      this.events.push({
        type: "notice",
        message: "虚空裂口封印失败，残存虚空单位获得100%强化并开始进攻核心",
      });
    }
  }

  private completeRiftObjective(objective: SideObjectiveState): void {
    if (objective.status !== "active") return;
    objective.status = "completed";
    objective.completedAtMs = this.elapsedMs;
    for (const enemy of [...this.enemies.values()]) {
      if (enemy.objectiveId !== "rift") continue;
      this.enemies.delete(enemy.id);
      this.events.push({ type: "enemyDeath", enemyIds: [enemy.id] });
    }
    this.completeObjective(objective);
  }

  private applyMoonwellRegeneration(deltaMs: number): void {
    const ratio = MOONWELL_HEAL_RATIO_PER_SECOND * (deltaMs / 1_000);
    for (const player of this.players.values()) {
      if (player.status === "downed") continue;
      player.health = Math.min(player.stats.maxHealth, player.health + player.stats.maxHealth * ratio);
    }
    for (const ally of this.cityAllies.values()) {
      ally.health = Math.min(ally.maxHealth, ally.health + ally.maxHealth * ratio);
    }
    for (const entity of this.abilityEntities.values()) {
      if (!Number.isFinite(entity.health) || !Number.isFinite(entity.maxHealth)) continue;
      entity.health = Math.min(entity.maxHealth, entity.health + entity.maxHealth * ratio);
    }
  }

  private randomObjectivePosition(): Vec2 {
    for (let attempt = 0; attempt < 40; attempt += 1) {
      const x = this.random.integer(650, WORLD_WIDTH - 650);
      const y = this.random.integer(550, WORLD_HEIGHT - 550);
      if (Math.hypot(x - this.core.x, y - this.core.y) >= 2_650) return { x, y };
    }
    return { x: this.core.x + 3_200, y: this.core.y - 2_100 };
  }

  private completeObjective(objective: SideObjectiveState): void {
    if (objective.id === "moonwell") {
      this.events.push({
        type: "notice",
        message: "月泉净化完成：月泉已无敌，并为全地图友军每秒恢复0.5%最大生命",
      });
      return;
    }
    if (objective.id === "salvage") {
      this.rewardTeam(1_500, 200, 0);
      this.events.push({ type: "notice", message: "炼金车抵达：全队每人获得1500金币与200结晶" });
      return;
    }
    this.events.push({
      type: "notice",
      message: "裂口已封印：200名虚空单位被击杀，残余虚空单位全部消散",
    });
  }

  private rewardTeam(gold: number, crystals: number, experience: number): void {
    for (const player of this.players.values()) {
      player.scrap += gold;
      player.core += crystals;
      player.contribution.scrapEarned += gold;
      this.addExperience(player, experience);
    }
  }

  private updateSpawning(deltaMs: number): void {
    const difficulty = DIFFICULTIES[this.difficulty];
    const progress = clamp(this.elapsedMs / this.durationMs, 0, 1);
    const playerMultiplier = 1 + (this.players.size - 1) * 0.52;
    const pulse =
      Math.max(0, Math.sin((this.elapsedMs / 60_000) * Math.PI * 2)) *
      1.8;
    const spawnsPerSecond =
      (1.1 + progress * 8.8 + pulse) *
      playerMultiplier *
      difficulty.spawnMultiplier;
    this.spawnAccumulator += (deltaMs / 1_000) * spawnsPerSecond;
    while (
      this.spawnAccumulator >= 1 &&
      [...this.enemies.values()].filter(
        (enemy) => enemy.spaceId === "surface",
      ).length < MAX_ENEMIES
    ) {
      this.spawnAccumulator -= 1;
      const available = ENEMIES.filter(
        (enemy) =>
          enemy.rank === "normal" &&
          enemy.spawnSource !== "objective" &&
          enemy.weight > 0 &&
          enemy.unlockAtMs <= this.elapsedMs,
      );
      const definition = this.random.weightedPick(available, (enemy) => enemy.weight);
      this.spawnEnemy(definition.id);
    }
    if (this.elapsedMs >= this.nextEliteAtMs) {
      const available = ENEMIES.filter(
        (enemy) => enemy.rank === "elite" && enemy.unlockAtMs <= this.elapsedMs,
      );
      if (available.length > 0) {
        for (
          let index = 0;
          index < getEliteSpawnBatchSize(this.difficulty, progress);
          index += 1
        ) {
          this.spawnEnemy(this.random.pick(available).id);
        }
      }
      this.nextEliteAtMs += getEliteSpawnIntervalMs(this.difficulty, progress);
    }
    if (!this.firstBossSpawned && this.elapsedMs >= FIRST_BOSS_AT_MS) {
      this.firstBossSpawned = true;
      this.spawnEnemy("elder_maw");
      this.events.push({ type: "notice", message: "贪金大主教率领夺宝军团抵达" });
    }
    if (!this.secondBossSpawned && this.elapsedMs >= SECOND_BOSS_AT_MS) {
      this.secondBossSpawned = true;
      this.spawnEnemy("siege_marshal");
      this.events.push({
        type: "notice",
        message: "第二首领：王国破城元帅·赫克托开始摧毁怪物城防",
      });
    }
    if (!this.finalBossSpawned && this.elapsedMs >= FINAL_BOSS_AT_MS) {
      this.finalBossSpawned = true;
      this.spawnEnemy("storm_tyrant");
      this.events.push({ type: "notice", message: "最终首领：远征王·奥雷恩亲临战场" });
    }
  }

  private spawnEnemy(
    definitionId: string,
    x?: number,
    y?: number,
    reducedReward = false,
    forcedLaneId?: LaneId,
  ): SimEnemy {
    const definition = getEnemy(definitionId);
    const difficulty = DIFFICULTIES[this.difficulty];
    const playerScale = 1 + (this.players.size - 1) * 0.43;
    const rankScale =
      definition.rank === "normal" ? 1 : difficulty.eliteMultiplier;
    const bossHealthScale =
      definition.rank === "boss" ? difficulty.bossHealthMultiplier : 1;
    const position =
      x === undefined || y === undefined
        ? this.edgePosition(forcedLaneId)
        : { x, y, laneId: forcedLaneId ?? this.closestLaneId(x, y) };
    const timeScaling = getEnemyTimeScaling(
      this.difficulty,
      definition.rank,
      this.elapsedMs / this.durationMs,
    );
    const health =
      definition.health *
      difficulty.enemyHealthMultiplier *
      playerScale *
      rankScale *
      bossHealthScale *
      timeScaling.health *
      (reducedReward ? 0.7 : 1);
    const enemy: SimEnemy = {
      id: this.nextEnemyId++,
      definitionId,
      spaceId: "surface",
      x: position.x,
      y: position.y,
      health,
      maxHealth: health,
      armor: (definition.armor ?? 0) * timeScaling.armor,
      laneId: position.laneId,
      nextAttackAtMs: this.elapsedMs,
      nextPlayerAttackAtMs: this.elapsedMs,
      slowUntilMs: 0,
      slowRatio: 0,
      stunnedUntilMs: 0,
      rootedUntilMs: 0,
      revealedUntilMs: 0,
      armorReductionStacks: [],
      hitCounters: new Map(),
      followerComboHits: new Map(),
      recentAbilityHits: new Map(),
      hotFloorEmpoweredUntilMs: 0,
      velocityX: 0,
      velocityY: 0,
      marshalNextOrderAtMs:
        definition.ability === "siege_marshal" ? this.elapsedMs + 20_000 : undefined,
      marshalNextChargeAtMs:
        definition.ability === "siege_marshal" ? this.elapsedMs + 12_000 : undefined,
      marshalShieldTriggered: false,
    };
    this.enemies.set(enemy.id, enemy);
    this.events.push({ type: "enemySpawn", enemies: [this.toWireEnemy(enemy)] });
    return enemy;
  }

  private edgePosition(forcedLaneId?: LaneId): Vec2 & { laneId: LaneId } {
    const padding = 120;
    const laneId = forcedLaneId ?? LANE_CONFIGS[this.random.integer(0, 3)].id;
    const horizontalJitter = this.random.integer(-2_800, 2_800);
    const verticalJitter = this.random.integer(-1_850, 1_850);
    switch (laneId) {
      case "north":
        return {
          x: clamp(WORLD_WIDTH / 2 + horizontalJitter, padding, WORLD_WIDTH - padding),
          y: padding,
          laneId,
        };
      case "east":
        return {
          x: WORLD_WIDTH - padding,
          y: clamp(WORLD_HEIGHT / 2 + verticalJitter, padding, WORLD_HEIGHT - padding),
          laneId,
        };
      case "south":
        return {
          x: clamp(WORLD_WIDTH / 2 + horizontalJitter, padding, WORLD_WIDTH - padding),
          y: WORLD_HEIGHT - padding,
          laneId,
        };
      case "west":
        return {
          x: padding,
          y: clamp(WORLD_HEIGHT / 2 + verticalJitter, padding, WORLD_HEIGHT - padding),
          laneId,
        };
    }
  }

  private updateCityBuildingsAndAllies(deltaMs: number): void {
    for (const building of this.cityBuildings.values()) {
      building.activeUnitCount = [...this.cityAllies.values()].filter(
        (ally) => ally.sourceBuildingId === building.id && ally.health > 0,
      ).length;
      if (
        building.destroyed ||
        building.function !== "producer" ||
        !building.spawnModelId ||
        !building.spawnIntervalMs ||
        this.elapsedMs < building.nextSpawnAtMs
      ) {
        continue;
      }
      const modelId = building.spawnModelId;
      const globalCount = [...this.cityAllies.values()].filter(
        (ally) => ally.modelId === modelId && ally.health > 0,
      ).length;
      const room = Math.max(0, (building.globalUnitCap ?? 0) - globalCount);
      const batchSize = Math.min(room, building.spawnBatchSize ?? 1);
      for (let index = 0; index < batchSize; index += 1) {
        this.spawnCityAlly(building);
      }
      building.nextSpawnAtMs = this.elapsedMs + building.spawnIntervalMs;
    }

    for (const ally of [...this.cityAllies.values()].sort((a, b) => a.id - b.id)) {
      if (ally.health <= 0) {
        this.cityAllies.delete(ally.id);
        continue;
      }
      let gate = ally.rallyGateId
        ? this.defenses.find(
            (segment) =>
              segment.id === ally.rallyGateId && this.isLinkedGate(segment),
          )
        : undefined;
      if (!gate) {
        gate = this.findNearestLinkedGate(ally.x, ally.y);
        ally.rallyGateId = gate?.id;
        if (gate) {
          ally.activityCenterX = gate.x;
          ally.activityCenterY = gate.y;
          ally.behavior = "rallying";
          delete ally.targetEnemyId;
        }
      }
      if (!gate) {
        continue;
      }

      if (ally.behavior === "rallying") {
        if (
          this.moveCityAllyToward(
            ally,
            gate.x,
            gate.y,
            CITY_ALLY_RALLY_RADIUS,
            deltaMs,
          )
        ) {
          ally.behavior = "active";
        }
        continue;
      }

      let target = ally.targetEnemyId
        ? this.enemies.get(ally.targetEnemyId)
        : undefined;
      if (
        !target ||
        target.spaceId !== "surface" ||
        Math.hypot(
          target.x - ally.activityCenterX,
          target.y - ally.activityCenterY,
        ) > ally.activityRadius
      ) {
        target = [...this.enemies.values()]
          .filter(
            (enemy) =>
              enemy.spaceId === "surface" &&
              Math.hypot(enemy.x - ally.x, enemy.y - ally.y) <= ally.alertRadius &&
              Math.hypot(
                enemy.x - ally.activityCenterX,
                enemy.y - ally.activityCenterY,
              ) <= ally.activityRadius,
          )
          .sort(
            (a, b) =>
              distanceSquared(a.x, a.y, ally.x, ally.y) -
                distanceSquared(b.x, b.y, ally.x, ally.y) ||
              a.id - b.id,
          )[0];
        ally.targetEnemyId = target?.id;
      }

      if (!target) {
        delete ally.targetEnemyId;
        const arrived = this.moveCityAllyToward(
          ally,
          ally.activityCenterX,
          ally.activityCenterY,
          CITY_ALLY_RALLY_RADIUS,
          deltaMs,
        );
        ally.behavior = arrived ? "active" : "returning";
        continue;
      }

      ally.behavior = "active";
      const distance = Math.hypot(target.x - ally.x, target.y - ally.y);
      if (distance > ally.attackRange) {
        this.moveCityAllyToward(
          ally,
          target.x,
          target.y,
          ally.attackRange * 0.88,
          deltaMs,
        );
      } else if (this.elapsedMs >= ally.nextAttackAtMs) {
        target.recentAttacker = {
          kind: "city_ally",
          id: ally.id,
          expiresAtMs:
            this.elapsedMs + CITY_ENEMY_AGGRO_CONFIG.retaliationMemoryMs,
        };
        this.damageEnemy(target, ally.damage, undefined, "tower");
        ally.nextAttackAtMs = this.elapsedMs + ally.attackIntervalMs;
      }
    }
  }

  private moveCityAllyToward(
    ally: SimCityAlly,
    targetX: number,
    targetY: number,
    stopDistance: number,
    deltaMs: number,
  ): boolean {
    const dx = targetX - ally.x;
    const dy = targetY - ally.y;
    const distance = Math.hypot(dx, dy);
    if (distance <= stopDistance) {
      return true;
    }
    const moveDistance = Math.min(
      distance - stopDistance,
      ally.moveSpeed * (deltaMs / 1_000),
    );
    ally.x += (dx / Math.max(0.001, distance)) * moveDistance;
    ally.y += (dy / Math.max(0.001, distance)) * moveDistance;
    return distance - moveDistance <= stopDistance;
  }

  private spawnCityAlly(building: SimCityBuilding): void {
    if (!building.spawnModelId) {
      return;
    }
    const definition = getCityAllyDefinition(building.spawnModelId);
    const angle = ((this.nextCityAllyId * 137.5) % 360) * (Math.PI / 180);
    const ally: SimCityAlly = {
      id: this.nextCityAllyId++,
      sourceBuildingId: building.id,
      modelId: definition.modelId,
      presentationId: definition.presentationId,
      x: building.x + Math.cos(angle) * (building.radius + 24),
      y: building.y + Math.sin(angle) * (building.radius + 24),
      health: definition.maxHealth,
      maxHealth: definition.maxHealth,
      armor: definition.armor,
      behavior: "rallying",
      activityCenterX: building.x,
      activityCenterY: building.y,
      damage: definition.damage,
      attackIntervalMs: definition.attackIntervalMs,
      attackRange: definition.attackRange,
      moveSpeed: definition.moveSpeed,
      alertRadius: definition.alertRadius,
      activityRadius: definition.activityRadius,
      nextAttackAtMs: this.elapsedMs,
    };
    const gate = this.findNearestLinkedGate(ally.x, ally.y);
    if (gate) {
      ally.rallyGateId = gate.id;
      ally.activityCenterX = gate.x;
      ally.activityCenterY = gate.y;
    }
    this.cityAllies.set(ally.id, ally);
    building.activeUnitCount += 1;
  }

  private isLinkedGate(segment: SimDefenseSegment): boolean {
    if (segment.kind !== "gate" || segment.destroyed || segment.health <= 0) {
      return false;
    }
    return this.defenses.some(
      (candidate) =>
        candidate.kind === "wall" &&
        candidate.ring === segment.ring &&
        !candidate.destroyed &&
        candidate.health > 0 &&
        Math.hypot(candidate.x - segment.x, candidate.y - segment.y) <=
          CITY_GATE_LINK_RADIUS,
    );
  }

  private findNearestLinkedGate(
    x: number,
    y: number,
    preferredRing?: 1 | 2 | 3,
  ): SimDefenseSegment | undefined {
    return this.defenses
      .filter(
        (segment) =>
          this.isLinkedGate(segment) &&
          (preferredRing === undefined || segment.ring === preferredRing),
      )
      .sort(
        (a, b) =>
          distanceSquared(a.x, a.y, x, y) -
            distanceSquared(b.x, b.y, x, y) ||
          a.id - b.id,
      )[0];
  }

  private getForcedAggroPlayer(enemy: SimEnemy): SimPlayer | undefined {
    if (!enemy.forcedAggroPlayerId) {
      return undefined;
    }
    const player = this.players.get(enemy.forcedAggroPlayerId);
    const range = enemy.forcedAggroRange ?? 0;
    if (
      !player ||
      player.status !== "active" ||
      Math.hypot(player.x - enemy.x, player.y - enemy.y) > range
    ) {
      this.clearForcedAggro(enemy);
      return undefined;
    }
    return player;
  }

  private clearForcedAggro(enemy: SimEnemy): void {
    delete enemy.forcedAggroPlayerId;
    delete enemy.forcedAggroAbilityId;
    delete enemy.forcedAggroRange;
    delete enemy.forcedAggroSlowRatio;
  }

  private getEnemyMoveSpeedMultiplier(enemy: SimEnemy): number {
    const timedSlowRatio =
      this.elapsedMs < enemy.slowUntilMs ? enemy.slowRatio : 0;
    const forcedSlowRatio = enemy.forcedAggroPlayerId
      ? (enemy.forcedAggroSlowRatio ?? 0)
      : 0;
    return 1 - Math.max(timedSlowRatio, forcedSlowRatio);
  }

  private getAttractingObjective(enemy: SimEnemy): SideObjectiveState | undefined {
    const candidates = this.objectives.filter((objective) => {
      if (objective.status !== "active" || !objective.attractionRadius) return false;
      if (objective.id === "salvage" && getEnemy(enemy.definitionId).rank === "boss") {
        return false;
      }
      return (
        Math.hypot(objective.x - enemy.x, objective.y - enemy.y) <=
        objective.attractionRadius
      );
    });
    return candidates.sort(
      (a, b) =>
        distanceSquared(a.x, a.y, enemy.x, enemy.y) -
          distanceSquared(b.x, b.y, enemy.x, enemy.y) ||
        a.id.localeCompare(b.id),
    )[0];
  }

  private damageObjective(objective: SideObjectiveState, rawDamage: number): void {
    if (
      objective.status !== "active" ||
      objective.invulnerable ||
      objective.health === undefined
    ) {
      return;
    }
    objective.health = Math.max(0, objective.health - Math.max(0, rawDamage));
  }

  private updateRiftInvader(
    enemy: SimEnemy,
    definition: ReturnType<typeof getEnemy>,
    difficulty: DifficultyDefinition,
    progress: number,
    deltaMs: number,
  ): void {
    const originX = enemy.objectiveOriginX ?? enemy.x;
    const originY = enemy.objectiveOriginY ?? enemy.y;
    const targets: Array<EnemyAttackTarget> = [];
    for (const player of this.players.values()) {
      if (
        player.status === "active" &&
        Math.hypot(player.x - enemy.x, player.y - enemy.y) <= ENEMY_AGGRO_RADIUS
      ) targets.push({ kind: "player", value: player });
    }
    for (const entity of this.abilityEntities.values()) {
      if (
        entity.kind !== "field" &&
        entity.health > 0 &&
        Math.hypot(entity.x - enemy.x, entity.y - enemy.y) <= ENEMY_AGGRO_RADIUS
      ) targets.push({ kind: "ability", value: entity });
    }
    for (const ally of this.cityAllies.values()) {
      if (
        ally.health > 0 &&
        Math.hypot(ally.x - enemy.x, ally.y - enemy.y) <= ENEMY_AGGRO_RADIUS
      ) targets.push({ kind: "city_ally", value: ally });
    }
    const target = targets.sort((a, b) => {
      const ap = this.getEnemyTargetPoint(a, enemy);
      const bp = this.getEnemyTargetPoint(b, enemy);
      return distanceSquared(ap.x, ap.y, enemy.x, enemy.y) -
        distanceSquared(bp.x, bp.y, enemy.x, enemy.y);
    })[0];
    if (!target) {
      const angle = ((enemy.id * 137.5 + this.elapsedMs / 80) % 360) * (Math.PI / 180);
      const targetX = originX + Math.cos(angle) * RIFT_WANDER_RADIUS * 0.72;
      const targetY = originY + Math.sin(angle) * RIFT_WANDER_RADIUS * 0.72;
      const distance = Math.max(0.001, Math.hypot(targetX - enemy.x, targetY - enemy.y));
      const move = Math.min(distance, definition.speed * (deltaMs / 1_000));
      enemy.x += ((targetX - enemy.x) / distance) * move;
      enemy.y += ((targetY - enemy.y) / distance) * move;
      return;
    }
    const point = this.getEnemyTargetPoint(target, enemy);
    const distance = Math.max(0.001, Math.hypot(point.x - enemy.x, point.y - enemy.y));
    const contact = this.getEnemyTargetRadius(target) + definition.radius * 1.18 + 2;
    if (distance > contact) {
      const move = Math.min(
        distance - contact,
        definition.speed * (deltaMs / 1_000),
      );
      enemy.x += ((point.x - enemy.x) / distance) * move;
      enemy.y += ((point.y - enemy.y) / distance) * move;
      return;
    }
    if (this.elapsedMs < enemy.nextPlayerAttackAtMs) return;
    const threatScaling = getEnemyTimeScaling(
      this.difficulty,
      definition.rank,
      progress,
    );
    const rawDamage =
      definition.coreDamage * difficulty.enemyDamageMultiplier * threatScaling.damage;
    if (target.kind === "player") this.damagePlayer(target.value, rawDamage);
    else if (target.kind === "ability") this.damageAbilityEntity(target.value, rawDamage);
    else if (target.kind === "city_ally") {
      target.value.health = Math.max(0, target.value.health - rawDamage);
    }
    enemy.nextPlayerAttackAtMs = this.elapsedMs + definition.attackIntervalMs;
  }

  private updateSiegeMarshalAbilities(enemy: SimEnemy): void {
    if (
      !enemy.marshalShieldTriggered &&
      enemy.health <= enemy.maxHealth * 0.6
    ) {
      enemy.marshalShieldTriggered = true;
      enemy.marshalShieldUntilMs = this.elapsedMs + 10_000;
      for (let index = 0; index < 8; index += 1) {
        this.spawnEnemy(
          "stone_brute",
          enemy.x + this.random.integer(-120, 120),
          enemy.y + this.random.integer(-120, 120),
          false,
          enemy.laneId,
        );
      }
      this.events.push({
        type: "notice",
        message: "赫克托展开盾阵并召来一队重盾卫士，正面攻击受到减免",
      });
    }
    if (this.elapsedMs >= (enemy.marshalNextOrderAtMs ?? Number.POSITIVE_INFINITY)) {
      enemy.marshalNextOrderAtMs = this.elapsedMs + 20_000;
      for (const candidate of this.enemies.values()) {
        if (
          candidate.id !== enemy.id &&
          getEnemy(candidate.definitionId).rank === "normal" &&
          Math.hypot(candidate.x - enemy.x, candidate.y - enemy.y) <= 1_200
        ) {
          candidate.siegeBuffUntilMs = Math.max(
            candidate.siegeBuffUntilMs ?? 0,
            this.elapsedMs + 8_000,
          );
        }
      }
      this.events.push({ type: "notice", message: "赫克托发动破城号令" });
    }
    if (
      enemy.marshalChargeResolveAtMs &&
      this.elapsedMs >= enemy.marshalChargeResolveAtMs
    ) {
      const target = enemy.marshalChargeTarget;
      if (target?.kind === "building") {
        const building = this.cityBuildings.get(String(target.id));
        if (building && !building.destroyed) {
          enemy.x = building.x;
          enemy.y = building.y;
          this.damageCityBuilding(building, 1_400);
        }
      } else if (target?.kind === "defense") {
        const defense = this.defenses.find((entry) => entry.id === Number(target.id));
        if (defense && !defense.destroyed) {
          enemy.x = defense.x;
          enemy.y = defense.y;
          this.damageDefense(defense, 1_400);
        }
      }
      enemy.marshalChargeResolveAtMs = undefined;
      enemy.marshalChargeTarget = undefined;
    }
    if (
      !enemy.marshalChargeResolveAtMs &&
      this.elapsedMs >= (enemy.marshalNextChargeAtMs ?? Number.POSITIVE_INFINITY)
    ) {
      enemy.marshalNextChargeAtMs = this.elapsedMs + 12_000;
      const building = [...this.cityBuildings.values()]
        .filter((candidate) => !candidate.destroyed && candidate.health > 0)
        .sort((a, b) =>
          distanceSquared(a.x, a.y, enemy.x, enemy.y) -
          distanceSquared(b.x, b.y, enemy.x, enemy.y))[0];
      const defense = this.findAggroDefense(enemy);
      const buildingDistance = building
        ? distanceSquared(building.x, building.y, enemy.x, enemy.y)
        : Number.POSITIVE_INFINITY;
      const defenseDistance = defense
        ? distanceSquared(defense.x, defense.y, enemy.x, enemy.y)
        : Number.POSITIVE_INFINITY;
      if (building && buildingDistance <= defenseDistance) {
        enemy.marshalChargeTarget = { kind: "building", id: building.id };
        enemy.marshalChargeResolveAtMs = this.elapsedMs + 1_500;
      } else if (defense) {
        enemy.marshalChargeTarget = { kind: "defense", id: defense.id };
        enemy.marshalChargeResolveAtMs = this.elapsedMs + 1_500;
      }
    }
  }

  private getMarshalShieldMultiplier(
    enemy: SimEnemy,
    playerId: string | undefined,
    attribution: DamageAttribution | "tower",
  ): number {
    if (
      enemy.definitionId !== "siege_marshal" ||
      this.elapsedMs >= (enemy.marshalShieldUntilMs ?? 0) ||
      !playerId ||
      attribution === "tower"
    ) return 1;
    const player = this.players.get(playerId);
    if (!player) return 1;
    const toPlayerX = player.x - enemy.x;
    const toPlayerY = player.y - enemy.y;
    const toCoreX = this.core.x - enemy.x;
    const toCoreY = this.core.y - enemy.y;
    const denominator = Math.max(
      0.001,
      Math.hypot(toPlayerX, toPlayerY) * Math.hypot(toCoreX, toCoreY),
    );
    const facingDot = (toPlayerX * toCoreX + toPlayerY * toCoreY) / denominator;
    return facingDot >= 0 ? 0.45 : 1;
  }

  private selectEnemyAttackTarget(
    enemy: SimEnemy,
    forcedPlayer?: SimPlayer,
    tauntingPlayer?: SimPlayer,
    tauntingEntity?: SimAbilityEntity,
  ): EnemyAttackTarget {
    if (forcedPlayer) {
      return { kind: "player", value: forcedPlayer };
    }

    const retaliating = this.resolveRecentEnemyAttacker(enemy);
    if (retaliating) {
      return retaliating;
    }

    if (tauntingPlayer) {
      return { kind: "player", value: tauntingPlayer };
    }
    if (tauntingEntity) {
      return { kind: "ability", value: tauntingEntity };
    }

    const guard = [...this.abilityEntities.values()]
      .filter(
        (entity) =>
          entity.kind === "guard" &&
          entity.health > 0 &&
          Number.isFinite(entity.health) &&
          Math.hypot(entity.x - enemy.x, entity.y - enemy.y) <= ENEMY_AGGRO_RADIUS,
      )
      .sort(
        (a, b) =>
          distanceSquared(a.x, a.y, enemy.x, enemy.y) -
            distanceSquared(b.x, b.y, enemy.x, enemy.y) ||
          a.id - b.id,
      )[0];
    if (guard) {
      return { kind: "ability", value: guard };
    }

    const objectiveTarget = this.getAttractingObjective(enemy);
    if (objectiveTarget) {
      return { kind: "objective", value: objectiveTarget };
    }

    const building = [...this.cityBuildings.values()]
      .filter(
        (candidate) =>
          !candidate.destroyed &&
          candidate.health > 0 &&
          Math.hypot(candidate.x - enemy.x, candidate.y - enemy.y) -
            candidate.radius <=
            CITY_ENEMY_AGGRO_CONFIG.buildingRadius,
      )
      .sort(
        (a, b) =>
          distanceSquared(a.x, a.y, enemy.x, enemy.y) -
            distanceSquared(b.x, b.y, enemy.x, enemy.y) ||
          a.id.localeCompare(b.id),
      )[0];
    if (building) {
      return { kind: "building", value: building };
    }

    const equalPriority: Array<{ target: EnemyAttackTarget; distance: number; id: string }> = [];
    for (const player of this.players.values()) {
      if (
        (player.status === "active" ||
          (player.status === "lich" && this.isPermanentLich(player))) &&
        Math.hypot(player.x - enemy.x, player.y - enemy.y) <= ENEMY_AGGRO_RADIUS
      ) {
        equalPriority.push({
          target: { kind: "player", value: player },
          distance: distanceSquared(player.x, player.y, enemy.x, enemy.y),
          id: `player:${player.id}`,
        });
      }
    }
    for (const entity of this.abilityEntities.values()) {
      if (
        entity.kind !== "guard" &&
        entity.kind !== "field" &&
        entity.health > 0 &&
        Number.isFinite(entity.health) &&
        Math.hypot(entity.x - enemy.x, entity.y - enemy.y) <= ENEMY_AGGRO_RADIUS
      ) {
        equalPriority.push({
          target: { kind: "ability", value: entity },
          distance: distanceSquared(entity.x, entity.y, enemy.x, enemy.y),
          id: `ability:${entity.id}`,
        });
      }
    }
    for (const ally of this.cityAllies.values()) {
      if (
        ally.health > 0 &&
        Math.hypot(ally.x - enemy.x, ally.y - enemy.y) <= ENEMY_AGGRO_RADIUS
      ) {
        equalPriority.push({
          target: { kind: "city_ally", value: ally },
          distance: distanceSquared(ally.x, ally.y, enemy.x, enemy.y),
          id: `city:${ally.id}`,
        });
      }
    }
    const defense = this.findAggroDefense(enemy);
    if (defense) {
      equalPriority.push({
        target: { kind: "defense", value: defense },
        distance: distanceToRectangle(enemy.x, enemy.y, defense) ** 2,
        id: `defense:${defense.id}`,
      });
    }
    equalPriority.sort((a, b) => a.distance - b.distance || a.id.localeCompare(b.id));
    return equalPriority[0]?.target ?? { kind: "core", value: this.core };
  }

  private resolveRecentEnemyAttacker(enemy: SimEnemy): EnemyAttackTarget | undefined {
    const attacker = enemy.recentAttacker;
    if (!attacker || attacker.expiresAtMs <= this.elapsedMs) {
      delete enemy.recentAttacker;
      return undefined;
    }
    let target: EnemyAttackTarget | undefined;
    if (attacker.kind === "player") {
      const player = this.players.get(String(attacker.id));
      if (player && (player.status === "active" || player.status === "lich")) {
        target = { kind: "player", value: player };
      }
    } else if (attacker.kind === "ability") {
      const entity = this.abilityEntities.get(Number(attacker.id));
      if (entity?.health && Number.isFinite(entity.health)) {
        target = { kind: "ability", value: entity };
      }
    } else if (attacker.kind === "city_ally") {
      const ally = this.cityAllies.get(Number(attacker.id));
      if (ally?.health) {
        target = { kind: "city_ally", value: ally };
      }
    } else {
      const defense = this.defenses.find(
        (segment) => segment.id === Number(attacker.id) && !segment.destroyed,
      );
      if (defense) {
        target = { kind: "defense", value: defense };
      }
    }
    if (!target) {
      delete enemy.recentAttacker;
      return undefined;
    }
    const point = this.getEnemyTargetPoint(target, enemy);
    if (
      Math.hypot(point.x - enemy.x, point.y - enemy.y) >
      CITY_ENEMY_AGGRO_CONFIG.retaliationRadius
    ) {
      return undefined;
    }
    return target;
  }

  private getEnemyTargetPoint(target: EnemyAttackTarget, enemy: SimEnemy): Vec2 {
    if (target.kind === "defense") {
      return {
        x: clamp(
          enemy.x,
          target.value.x - target.value.width / 2,
          target.value.x + target.value.width / 2,
        ),
        y: clamp(
          enemy.y,
          target.value.y - target.value.height / 2,
          target.value.y + target.value.height / 2,
        ),
      };
    }
    return { x: target.value.x, y: target.value.y };
  }

  private getEnemyTargetRadius(target: EnemyAttackTarget): number {
    if (target.kind === "core") return target.value.radius;
    if (target.kind === "objective") return 70;
    if (target.kind === "building") return target.value.radius;
    if (target.kind === "defense") return 0;
    if (target.kind === "city_ally" || target.kind === "ability") return 22;
    return 28;
  }

  private updateEnemies(deltaMs: number): void {
    const difficulty = DIFFICULTIES[this.difficulty];
    const progress = clamp(this.elapsedMs / this.durationMs, 0, 1);
    for (const enemy of [...this.enemies.values()]) {
      const definition = getEnemy(enemy.definitionId);
      const threatScaling = getEnemyTimeScaling(
        this.difficulty,
        definition.rank,
        progress,
      );
      const damageRamp = threatScaling.damage;
      const startX = enemy.x;
      const startY = enemy.y;
      enemy.velocityX = 0;
      enemy.velocityY = 0;
      if (enemy.objectiveId === "rift") {
        this.updateRiftInvader(enemy, definition, difficulty, progress, deltaMs);
        continue;
      }
      if (definition.ability === "siege_marshal") {
        this.updateSiegeMarshalAbilities(enemy);
      }
      const forcedAggroPlayer = this.getForcedAggroPlayer(enemy);
      if (this.elapsedMs < enemy.stunnedUntilMs) {
        continue;
      }
      const collisionRadius = definition.radius * 1.18 + 2;
      let tauntingPlayer: SimPlayer | undefined = forcedAggroPlayer
        ? undefined
        : [...this.players.values()]
        .filter((candidate) => {
          const turret = this.getTurretMechanic(candidate);
          const laborTaunt = definition.rank === "normal"
            ? this.getHiddenRuntimeEffects(candidate).find(
                (runtime) => runtime.effect.kind === "normal_enemy_taunt_aura",
              )?.effect
            : undefined;
          const laborTauntRadius =
            laborTaunt?.kind === "normal_enemy_taunt_aura"
              ? laborTaunt.radius
              : 0;
          return (
            (candidate.status === "active" ||
              (candidate.status === "lich" &&
                this.isPermanentLich(candidate))) &&
            ((laborTauntRadius > 0 &&
              Math.hypot(candidate.x - enemy.x, candidate.y - enemy.y) <=
                laborTauntRadius) ||
              (Boolean(turret) &&
                this.isTurretDeployed(candidate) &&
                Math.hypot(candidate.x - enemy.x, candidate.y - enemy.y) <=
                  (turret?.tauntRadius ?? 0)))
          );
        })
        .sort(
          (a, b) =>
            distanceSquared(a.x, a.y, enemy.x, enemy.y) -
            distanceSquared(b.x, b.y, enemy.x, enemy.y),
        )[0];
      let tauntingEntity: SimAbilityEntity | undefined = forcedAggroPlayer
        ? undefined
        : [...this.abilityEntities.values()]
        .filter(
          (entity) =>
            entity.health > 0 &&
            this.isTauntingAbilityEntity(entity) &&
            Math.hypot(entity.x - enemy.x, entity.y - enemy.y) <=
              ENEMY_AGGRO_RADIUS,
        )
        .sort(
          (a, b) =>
            distanceSquared(a.x, a.y, enemy.x, enemy.y) -
            distanceSquared(b.x, b.y, enemy.x, enemy.y),
        )[0];
      if (tauntingPlayer && tauntingEntity) {
        const playerDistance = distanceSquared(
          tauntingPlayer.x,
          tauntingPlayer.y,
          enemy.x,
          enemy.y,
        );
        const entityDistance = distanceSquared(
          tauntingEntity.x,
          tauntingEntity.y,
          enemy.x,
          enemy.y,
        );
        if (playerDistance <= entityDistance) {
          tauntingEntity = undefined;
        } else {
          tauntingPlayer = undefined;
        }
      }

      const target = this.selectEnemyAttackTarget(
        enemy,
        forcedAggroPlayer,
        tauntingPlayer,
        tauntingEntity,
      );
      const targetPoint = this.getEnemyTargetPoint(target, enemy);
      const dx = targetPoint.x - enemy.x;
      const dy = targetPoint.y - enemy.y;
      const distance = Math.max(0.001, Math.hypot(dx, dy));
      const targetRadius = this.getEnemyTargetRadius(target);
      const contact = targetRadius + collisionRadius + 2;
      const slow = this.getEnemyMoveSpeedMultiplier(enemy);
      if (distance > contact) {
        const moveDistance = Math.min(
          distance - contact,
          (this.elapsedMs < enemy.rootedUntilMs ? 0 : definition.speed) *
            threatScaling.moveSpeed *
            (enemy.objectivePowerMultiplier ?? 1) *
            (this.elapsedMs < (enemy.siegeBuffUntilMs ?? 0) ? 1.25 : 1) *
            slow *
            (deltaMs / 1_000),
        );
        const nextX = enemy.x + (dx / distance) * moveDistance;
        const nextY = enemy.y + (dy / distance) * moveDistance;
        const blockingDefense = this.findBlockingDefense(
          nextX,
          nextY,
          collisionRadius,
        );
        if (blockingDefense) {
          if (this.elapsedMs >= enemy.nextAttackAtMs) {
            this.damageDefense(
              blockingDefense,
              definition.coreDamage *
                (enemy.objectivePowerMultiplier ?? 1) *
                ENEMY_SIEGE_DAMAGE_MULTIPLIER *
                (this.elapsedMs < (enemy.siegeBuffUntilMs ?? 0) ? 1.5 : 1) *
                difficulty.enemyDamageMultiplier *
                damageRamp,
            );
            enemy.nextAttackAtMs =
              this.elapsedMs + definition.attackIntervalMs;
          }
        } else {
          enemy.x = nextX;
          enemy.y = nextY;
        }
      } else if (this.elapsedMs >= enemy.nextPlayerAttackAtMs) {
        const rawDamage =
          definition.coreDamage *
          (enemy.objectivePowerMultiplier ?? 1) *
          difficulty.enemyDamageMultiplier *
          damageRamp;
        if (target.kind === "ability") {
          const targetEntity = target.value;
          const auraArmor = this.getHiddenAllyAuraAt(
            targetEntity.x,
            targetEntity.y,
          ).armorAdd;
          this.damageAbilityEntity(
            targetEntity,
            rawDamage *
              1.25 *
              (100 / (100 + targetEntity.armor + auraArmor)),
          );
        } else if (target.kind === "player") {
          const targetPlayer = target.value;
          if (this.random.next() >= targetPlayer.stats.dodge) {
            const auraArmor = this.getHiddenAllyAuraAt(
              targetPlayer.x,
              targetPlayer.y,
            ).armorAdd;
            const damage =
              rawDamage *
              0.78 *
              (100 / (100 + targetPlayer.stats.armor + auraArmor));
            const damaged = this.damagePlayer(targetPlayer, damage);
            if (damaged && definition.ability === "drain_mana") {
              targetPlayer.mana = Math.max(0, targetPlayer.mana - 12);
            }
          }
        } else if (target.kind === "city_ally") {
          target.value.health = Math.max(
            0,
            target.value.health -
              rawDamage * (100 / (100 + Math.max(0, target.value.armor))),
          );
        } else if (target.kind === "building") {
          this.damageCityBuilding(
            target.value,
            rawDamage * (this.elapsedMs < (enemy.siegeBuffUntilMs ?? 0) ? 1.5 : 1),
          );
        } else if (target.kind === "objective") {
          this.damageObjective(target.value, rawDamage);
        } else if (target.kind === "defense") {
          this.damageDefense(
            target.value,
            rawDamage *
              ENEMY_SIEGE_DAMAGE_MULTIPLIER *
              (this.elapsedMs < (enemy.siegeBuffUntilMs ?? 0) ? 1.5 : 1),
          );
        } else {
          this.core.health = Math.max(
            0,
            this.core.health -
              rawDamage * (100 / (100 + this.core.armor)),
          );
        }
        enemy.nextPlayerAttackAtMs =
          this.elapsedMs +
          definition.attackIntervalMs / (enemy.objectivePowerMultiplier ?? 1);
      }
      enemy.velocityX =
        (enemy.x - startX) / Math.max(0.001, deltaMs / 1_000);
      enemy.velocityY =
        (enemy.y - startY) / Math.max(0.001, deltaMs / 1_000);
    }
  }

  private damageAbilityEntity(entity: SimAbilityEntity, damage: number): void {
    if (entity.temporaryShieldUntilMs <= this.elapsedMs) {
      entity.temporaryShield = 0;
    }
    const safeDamage = Math.max(0, damage);
    const shieldAbsorbed = Math.min(entity.temporaryShield, safeDamage);
    entity.temporaryShield -= shieldAbsorbed;
    entity.health = Math.max(0, entity.health - (safeDamage - shieldAbsorbed));
  }

  private damagePlayer(player: SimPlayer, damage: number): boolean {
    if (
      (player.status !== "active" && player.status !== "lich") ||
      (player.status === "lich" && !this.isPermanentLich(player)) ||
      this.elapsedMs < player.invulnerableUntilMs
    ) {
      return false;
    }
    const safeDamage = Math.max(0, damage);
    const nullifier = player.modules.flatMap((abilityId) => {
      if (!abilityId) return [];
      const ability = getModule(abilityId);
      return (ability.mechanics ?? []).flatMap((mechanic, index) =>
        mechanic.kind === "damage_nullification"
          ? [{ ability, mechanic, index }]
          : [],
      );
    })[0];
    if (nullifier) {
      const key = `${nullifier.ability.id}:damage_nullification:${nullifier.index}`;
      if (
        safeDamage > player.stats.maxHealth * nullifier.mechanic.thresholdMaxHealthRatio &&
        (player.mechanicCooldowns.get(key) ?? 0) <= this.elapsedMs
      ) {
        player.mechanicCooldowns.set(
          key,
          this.elapsedMs + nullifier.mechanic.cooldownMs,
        );
        const pain = this.getActiveSynergyMechanic(player, "pain_enjoyment");
        if (pain?.blockedDamageCounts) {
          this.triggerGrowth(player, "damage_taken", safeDamage);
        }
        return false;
      }
    }
    if (player.temporaryShieldUntilMs <= this.elapsedMs) {
      player.temporaryShield = 0;
    }
    const shieldAbsorbed = Math.min(player.temporaryShield, safeDamage);
    player.temporaryShield -= shieldAbsorbed;
    const healthDamage = safeDamage - shieldAbsorbed;
    player.health = Math.max(0, player.health - healthDamage);
    if (healthDamage > 0) {
      player.lastDamageAtMs = this.elapsedMs;
      this.triggerGrowth(player, "damage_taken", healthDamage);
    }
    if (player.health > 0) {
      return true;
    }
    const availableGuards = player.modules.flatMap((abilityId) => {
      if (!abilityId) {
        return [];
      }
      const ability = getModule(abilityId);
      if (!this.isAbilityEnabled(player, ability)) {
        return [];
      }
      return (ability.mechanics ?? []).flatMap((mechanic, index) => {
        if (mechanic.kind !== "death_guard") {
          return [];
        }
        const key = `${ability.id}:death_guard:${index}`;
        return (player.deathGuardCooldowns.get(key) ?? 0) <= this.elapsedMs
          ? [{ ability, mechanic, key }]
          : [];
      });
    });
    availableGuards.sort(
      (a, b) =>
        b.mechanic.recoveryHealthRatio - a.mechanic.recoveryHealthRatio ||
        a.mechanic.cooldownMs - b.mechanic.cooldownMs,
    );
    const guard = availableGuards[0];
    if (guard) {
      player.health = 1;
      player.invulnerableUntilMs =
        this.elapsedMs + guard.mechanic.invulnerabilityMs;
      player.deathGuardCooldowns.set(
        guard.key,
        this.elapsedMs + guard.mechanic.cooldownMs,
      );
      const totalHealing =
        player.stats.maxHealth * guard.mechanic.recoveryHealthRatio;
      player.activeDeathRecoveries.set(guard.key, {
        remainingHealing: totalHealing,
        healingPerMs: totalHealing / guard.mechanic.recoveryDurationMs,
        endsAtMs: this.elapsedMs + guard.mechanic.recoveryDurationMs,
      });
      this.events.push({
        type: "notice",
        message: `${player.name} 的「${guard.ability.name}」阻止了死亡`,
      });
      return true;
    }
    const deathGrowthRespawnDelayMs = this.applyDeathGrowth(player);
    const lich = this.getLichMechanic(player);
    if (lich?.mode === "permanent" && lich.immediateBaseRespawn) {
      player.health = player.stats.maxHealth;
      player.mana = player.stats.manaMax;
      player.x = this.core.x;
      player.y = this.core.y + 185;
      player.spaceId = "surface";
      player.status = "lich";
      player.reviveProgressMs = 0;
      player.respawnAtMs = 0;
      player.contribution.timesDowned += 1;
      this.events.push({
        type: "notice",
        message: `${player.name} 的永久巫妖形态在营地立刻重组`,
      });
      return true;
    }
    if (lich?.mode === "temporary_on_death") {
      player.status = "lich";
      this.recomputePlayer(player);
      player.health = player.stats.maxHealth;
      player.reviveProgressMs = 0;
      player.respawnAtMs =
        this.elapsedMs +
        BASE_RESPAWN_MS +
        Math.max(0, player.level - 1) * RESPAWN_MS_PER_LEVEL_AFTER_FIRST +
        deathGrowthRespawnDelayMs;
      player.contribution.timesDowned += 1;
      this.events.push({
        type: "notice",
        message: `${player.name} 从尸体上化为临时巫妖`,
      });
      return true;
    }
    player.status = "downed";
    player.reviveProgressMs = 0;
    player.respawnAtMs =
      this.elapsedMs +
      BASE_RESPAWN_MS +
      Math.max(0, player.level - 1) * RESPAWN_MS_PER_LEVEL_AFTER_FIRST +
      deathGrowthRespawnDelayMs;
    player.contribution.timesDowned += 1;
    for (const entity of this.abilityEntities.values()) {
      if (entity.ownerId !== player.id || entity.kind !== "guard") continue;
      entity.health = 0;
      entity.deathProcessed = false;
      entity.respawnAtMs =
        entity.respawnMs > 0 ? this.elapsedMs + entity.respawnMs : 0;
    }
    this.events.push({
      type: "notice",
      message: `${player.name} 被击散，将在 ${Math.ceil((player.respawnAtMs - this.elapsedMs) / 1_000)} 秒后重生`,
    });
    return true;
  }

  private applyDeathGrowth(player: SimPlayer): number {
    let addedDelayMs = 0;
    let changed = false;
    for (const abilityId of player.modules) {
      if (!abilityId) continue;
      const ability = getModule(abilityId);
      if (!this.isAbilityEnabled(player, ability)) continue;
      for (const mechanic of ability.mechanics ?? []) {
        if (mechanic.kind !== "death_growth") continue;
        changed = true;
        player.growthBonuses.maxHealth =
          (player.growthBonuses.maxHealth ?? 0) + mechanic.maxHealthAdd;
        player.growthBonuses.damage =
          (player.growthBonuses.damage ?? 0) + mechanic.damageAdd;
        if (mechanic.respawnDelayAddMs > 0) {
          const key = `death_growth:${ability.id}:respawn_delay`;
          const accumulated =
            (player.synergyCounters.get(key) ?? 0) +
            mechanic.respawnDelayAddMs;
          player.synergyCounters.set(key, accumulated);
          addedDelayMs = Math.max(addedDelayMs, accumulated);
        }
      }
    }
    if (changed) {
      this.recomputePlayer(player);
      player.health = 0;
    }
    return addedDelayMs;
  }

  private findBlockingDefense(
    x: number,
    y: number,
    radius: number,
  ): SimDefenseSegment | undefined {
    return this.defenses
      .filter(
        (segment) =>
          segment.kind === "wall" &&
          !segment.destroyed &&
          segment.health > 0 &&
          circleIntersectsRectangle(x, y, radius, segment),
      )
      .sort(
        (a, b) =>
          Number(b.hasTower) - Number(a.hasTower) ||
          distanceSquared(a.x, a.y, x, y) -
            distanceSquared(b.x, b.y, x, y),
      )[0];
  }

  private findAggroDefense(enemy: SimEnemy): SimDefenseSegment | undefined {
    const candidates = this.defenses
      .filter(
        (segment) =>
          segment.kind === "wall" &&
          !segment.destroyed &&
          segment.health > 0,
      )
      .map((segment) => ({
        segment,
        distance: distanceToRectangle(enemy.x, enemy.y, segment),
      }))
      .filter(
        ({ segment, distance }) =>
          distance <=
          (segment.hasTower
            ? ENEMY_TOWER_AGGRO_RADIUS
            : ENEMY_WALL_AGGRO_RADIUS),
      );
    return candidates.sort(
      (a, b) =>
        Number(b.segment.hasTower) - Number(a.segment.hasTower) ||
        a.distance - b.distance,
    )[0]?.segment;
  }

  private damageDefense(
    segment: SimDefenseSegment,
    rawDamage: number,
  ): void {
    const damage = rawDamage * (100 / (100 + Math.max(0, segment.armor)));
    segment.health = Math.max(0, segment.health - damage);
    if (segment.health > 0 || segment.destroyed) {
      return;
    }
    segment.destroyed = true;
    this.events.push({
      type: "notice",
      message:
        `${segment.ring}环${segment.hasTower ? "箭塔墙段" : "城墙"}已被摧毁`,
    });
  }

  private damageCityBuilding(
    building: SimCityBuilding,
    rawDamage: number,
  ): void {
    if (building.destroyed || building.health <= 0) {
      return;
    }
    const damage = rawDamage * (100 / (100 + Math.max(0, building.armor)));
    building.health = Math.max(0, building.health - damage);
    if (building.health > 0) {
      return;
    }
    building.destroyed = true;
    this.events.push({
      type: "notice",
      message: `${building.name}已被远征军摧毁`,
    });
  }

  private resolveEnemySeparation(): void {
    const cells = new Map<string, SimEnemy[]>();
    for (const enemy of this.enemies.values()) {
      const cellX = Math.floor(enemy.x / ENEMY_SEPARATION_CELL);
      const cellY = Math.floor(enemy.y / ENEMY_SEPARATION_CELL);
      const key = `${cellX}:${cellY}`;
      const existing = cells.get(key);
      if (existing) {
        existing.push(enemy);
      } else {
        cells.set(key, [enemy]);
      }
    }
    for (const enemy of this.enemies.values()) {
      const cellX = Math.floor(enemy.x / ENEMY_SEPARATION_CELL);
      const cellY = Math.floor(enemy.y / ENEMY_SEPARATION_CELL);
      const enemyRadius =
        getEnemy(enemy.definitionId).radius * 1.18 + 2;
      for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
        for (let offsetY = -1; offsetY <= 1; offsetY += 1) {
          const neighbors = cells.get(
            `${cellX + offsetX}:${cellY + offsetY}`,
          );
          if (!neighbors) {
            continue;
          }
          for (const other of neighbors) {
            if (other.id <= enemy.id) {
              continue;
            }
            const otherRadius =
              getEnemy(other.definitionId).radius * 1.18 + 2;
            let dx = other.x - enemy.x;
            let dy = other.y - enemy.y;
            let distance = Math.hypot(dx, dy);
            const required = enemyRadius + otherRadius;
            if (distance >= required) {
              continue;
            }
            if (distance < 0.001) {
              const angle =
                ((enemy.id * 1_103 + other.id * 313) % 360) *
                (Math.PI / 180);
              dx = Math.cos(angle);
              dy = Math.sin(angle);
              distance = 1;
            }
            const push = Math.min(6, (required - distance) * 0.5);
            const pushX = (dx / distance) * push;
            const pushY = (dy / distance) * push;
            enemy.x -= pushX;
            enemy.y -= pushY;
            other.x += pushX;
            other.y += pushY;
          }
        }
      }
      this.resolveDefenseOverlap(enemy, enemyRadius);
      enemy.x = clamp(enemy.x, enemyRadius, WORLD_WIDTH - enemyRadius);
      enemy.y = clamp(enemy.y, enemyRadius, WORLD_HEIGHT - enemyRadius);
    }
  }

  private resolveDefenseOverlap(
    enemy: SimEnemy,
    radius: number,
  ): void {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const segment = this.findBlockingDefense(
        enemy.x,
        enemy.y,
        radius,
      );
      if (!segment) {
        return;
      }
      const left = segment.x - segment.width / 2 - radius;
      const right = segment.x + segment.width / 2 + radius;
      const top = segment.y - segment.height / 2 - radius;
      const bottom = segment.y + segment.height / 2 + radius;
      const candidates = [
        { distance: Math.abs(enemy.x - left), axis: "x" as const, value: left },
        { distance: Math.abs(enemy.x - right), axis: "x" as const, value: right },
        { distance: Math.abs(enemy.y - top), axis: "y" as const, value: top },
        { distance: Math.abs(enemy.y - bottom), axis: "y" as const, value: bottom },
      ].sort((a, b) => a.distance - b.distance);
      const nearest = candidates[0];
      if (nearest.axis === "x") {
        enemy.x = nearest.value;
      } else {
        enemy.y = nearest.value;
      }
    }
  }

  private payWages(): void {
    const currentMinute = Math.floor(this.elapsedMs / 60_000);
    if (currentMinute <= this.lastWageMinute) {
      return;
    }
    this.lastWageMinute = currentMinute;
    const playerCount = Math.max(1, this.players.size);
    const towerShare = Math.floor(this.towerGoldPool / playerCount);
    const paidFromTowers = towerShare * playerCount;
    this.towerGoldPool -= paidFromTowers;
    this.towerGoldPaid += paidFromTowers;
    for (const player of this.players.values()) {
      const earned =
        Math.round(player.wage * player.stats.goldMultiplier) +
        towerShare;
      player.scrap += earned;
      player.core += CRYSTAL_WAGE_PER_MINUTE;
      player.contribution.scrapEarned += earned;
    }
    this.events.push({
      type: "notice",
      message:
        "远征工资已发放（工资不再包含结晶）" +
        (towerShare > 0 ? ` · 箭塔战利品 ${towerShare} 金币` : ""),
    });
  }

  private createDefenses(): void {
    let id = 1;
    for (const configuration of MONSTER_CITY_LAYOUT.wallRings) {
      const count = Math.ceil(
        (configuration.extent * 2) / configuration.segmentLength,
      );
      const segmentLength = (configuration.extent * 2) / count;
      for (let side = 0; side < 4; side += 1) {
        for (let index = 0; index < count; index += 1) {
          const offset =
            -configuration.extent +
            segmentLength * (index + 0.5);
          const gate = Math.abs(offset) < segmentLength * 0.72;
          const horizontal = side === 0 || side === 2;
          const x = horizontal
            ? this.core.x + offset
            : this.core.x + (side === 1 ? configuration.extent : -configuration.extent);
          const y = horizontal
            ? this.core.y + (side === 2 ? configuration.extent : -configuration.extent)
            : this.core.y + offset;
          const hasTower =
            !gate &&
            (index + side * 2 + configuration.ring) % 6 === 0;
          const maxHealth =
            configuration.health + (hasTower ? 420 : 0);
          this.defenses.push({
            id: id++,
            ring: configuration.ring,
            kind: gate ? "gate" : "wall",
            x,
            y,
            width: horizontal ? segmentLength - 8 : 46,
            height: horizontal ? 46 : segmentLength - 8,
            health: gate ? 1 : maxHealth,
            maxHealth: gate ? 1 : maxHealth,
            armor: configuration.armor + (hasTower ? 5 : 0),
            hasTower,
            destroyed: false,
            nextAttackAtMs: 0,
          });
        }
      }
    }
  }

  private createCityBuildings(): void {
    for (const definition of CITY_BUILDING_DEFINITIONS) {
      const production = definition.production;
      this.cityBuildings.set(definition.id, {
        id: definition.id,
        definitionId: definition.id,
        name: definition.name,
        presentationId: definition.presentationId,
        function: definition.function,
        x: this.core.x + definition.offsetX,
        y: this.core.y + definition.offsetY,
        radius: definition.radius,
        health: definition.maxHealth,
        maxHealth: definition.maxHealth,
        armor: definition.armor,
        destroyed: false,
        spawnModelId: production?.allyModelId,
        spawnIntervalMs: production?.intervalMs,
        spawnBatchSize: production?.batchSize,
        activeUnitCount: 0,
        globalUnitCap: production
          ? CITY_ALLY_GLOBAL_CAPS[production.allyModelId]
          : undefined,
        nextSpawnAtMs: production?.intervalMs ?? Number.POSITIVE_INFINITY,
      });
    }
  }

  private updateDefenseTowers(): void {
    const damageMultiplier =
      1 + this.teamTechLevels.tower_power * 0.08;
    const interval =
      TOWER_BASE_INTERVAL_MS /
      (1 + this.teamTechLevels.tower_haste * 0.03);
    for (const segment of this.defenses) {
      if (
        !segment.hasTower ||
        segment.destroyed ||
        segment.health <= 0 ||
        this.elapsedMs < segment.nextAttackAtMs
      ) {
        continue;
      }
      const target = this.pickTarget(
        segment.x,
        segment.y,
        TOWER_RANGE,
        "closest",
      );
      if (!target) {
        continue;
      }
      this.damageEnemy(
        target,
        TOWER_BASE_DAMAGE * damageMultiplier,
        undefined,
        "tower",
      );
      if (this.enemies.has(target.id)) {
        target.recentAttacker = {
          kind: "defense",
          id: segment.id,
          expiresAtMs:
            this.elapsedMs + CITY_ENEMY_AGGRO_CONFIG.retaliationMemoryMs,
        };
      }
      segment.nextAttackAtMs = this.elapsedMs + interval;
      this.events.push({
        type: "defenseShot",
        sourceX: segment.x,
        sourceY: segment.y,
        targetId: target.id,
      });
    }
  }

  private generateOffer(
    player: SimPlayer,
    slot: number,
    targetTier: ModuleTier,
    mode: "upgrade" | "reforge",
    scrapCost: number,
    crystalCost: number,
    forbiddenSignature?: string,
  ): UpgradeOffer {
    const currentId = player.modules[slot];
    const pool = MODULES.filter(
      (module) =>
        module.tier === targetTier &&
        module.tier < 6 &&
        !module.hidden &&
        module.id !== currentId &&
        module.weight > 0,
    );
    const ownedTags = new Set(
      player.modules.flatMap((id) => (id ? getModule(id).tags : [])),
    );
    let candidates: string[] = [];
    for (let attempt = 0; attempt < 10; attempt += 1) {
      const remaining = [...pool];
      const picked: string[] = [];
      while (picked.length < 3 && remaining.length > 0) {
        const module = this.random.weightedPick(remaining, (candidate) => {
          const related = candidate.tags.some((tag) => ownedTags.has(tag));
          const combat =
            candidate.combat !== undefined ||
            candidate.category === "enhancement" ||
            candidate.category === "summon";
          return (
            candidate.weight *
            (related ? 1.35 : 1) *
            (player.offersSeen < 3 && combat ? 1.65 : 1)
          );
        });
        picked.push(module.id);
        remaining.splice(remaining.indexOf(module), 1);
      }
      candidates = [...new Set(picked)];
      const signature = [...candidates].sort().join(",");
      if (
        signature !== player.offerHistory.at(-1) &&
        signature !== forbiddenSignature &&
        candidates.length === 3
      ) {
        break;
      }
    }
    if (
      forbiddenSignature &&
      [...candidates].sort().join(",") === forbiddenSignature
    ) {
      const replacement = pool.find(
        (module) => !candidates.includes(module.id),
      );
      if (replacement && candidates.length === 3) {
        candidates[2] = replacement.id;
      }
    }
    const finalSignature = [...candidates].sort().join(",");
    if (finalSignature !== player.offerHistory.at(-1)) {
      player.offerHistory.push(finalSignature);
    }
    player.offerHistory = player.offerHistory.slice(-5);
    player.offersSeen += 1;
    return this.createOffer(
      player,
      slot,
      targetTier,
      mode,
      scrapCost,
      crystalCost,
      candidates,
      false,
    );
  }

  private createOffer(
    player: SimPlayer,
    slot: number,
    targetTier: ModuleTier,
    mode: "upgrade" | "reforge",
    scrapCost: number,
    crystalCost: number,
    candidates: string[],
    reusedCandidates: boolean,
  ): UpgradeOffer {
    const currentId = player.modules[slot];
    const currentTier = currentId ? getModule(currentId).tier : 0;
    return {
      id: `offer-${player.id}-${this.offerSequence++}`,
      mode,
      slot,
      currentModuleId: currentId,
      targetTier,
      scrapCost,
      crystalCost,
      rerollCost: REFORGE_CRYSTAL_COSTS[targetTier],
      candidates,
      candidateWeightMultipliers: Object.fromEntries(
        candidates.map((candidateId) => [candidateId, 1]),
      ),
      canAbandon:
        mode === "upgrade" &&
        currentId !== null &&
        currentTier >= 1 &&
        currentTier <= 4,
      reusedCandidates,
    };
  }

  private applyFinanceStatAdjustments(
    stats: PlayerStats,
    adjustments: ReadonlyMap<FinanceStakeTargetId, number>,
  ): void {
    for (const [targetId, amount] of adjustments) {
      switch (targetId) {
        case "max_health": stats.maxHealth += amount; break;
        case "health_regen": stats.healthRegen += amount; break;
        case "health_regen_ratio": stats.healthRegenMaxRatio += amount; break;
        case "max_mana": stats.manaMax += amount; break;
        case "mana_regen": stats.manaRegen += amount; break;
        case "mana_regen_ratio": stats.manaRegenMaxRatio += amount; break;
        case "attack_damage": stats.damage += amount; break;
        case "damage_multiplier": stats.damageMultiplier += amount; break;
        case "attack_damage_multiplier": stats.attackDamageMultiplier += amount; break;
        case "ability_damage_multiplier": stats.abilityDamageMultiplier += amount; break;
        case "base_attack_speed": stats.baseAttackSpeedModifier += amount; break;
        case "bonus_attack_speed": stats.attackSpeedBonus += amount; break;
        case "attack_range": stats.range += amount; break;
        case "base_move_speed": stats.moveSpeed += amount; break;
        case "move_speed_multiplier": stats.moveSpeedMultiplier += amount; break;
        case "armor": stats.armor += amount; break;
        case "dodge": stats.dodge += amount; break;
        case "repair_per_second": stats.repairPerSecond += amount; break;
        case "healing_per_second": stats.healingPerSecond += amount; break;
        case "scouting_radius": stats.scoutingRadius += amount; break;
        case "reveal_radius": stats.revealRadius += amount; break;
        case "splash_radius": stats.splashRadius += amount; break;
        case "chain_targets": stats.chainTargets += amount; break;
        case "pierce_targets": stats.pierceTargets += amount; break;
        case "crystal_drop_chance":
          stats.crystalDropChance += amount;
          stats.coreDropChance = stats.crystalDropChance;
          break;
        case "elite_damage_multiplier": stats.eliteDamageMultiplier += amount; break;
        case "companion_damage_multiplier": stats.companionDamageMultiplier += amount; break;
        case "summon_cap": stats.summonCap += amount; break;
        case "gold_multiplier":
          stats.goldMultiplier += amount;
          stats.scrapMultiplier = stats.goldMultiplier;
          break;
        case "experience_multiplier": stats.experienceMultiplier += amount; break;
        case "surface_mining_extra_gold_flat": stats.surfaceMiningExtraGoldFlat += amount; break;
        case "surface_mining_extra_gold_multiplier": stats.surfaceMiningExtraGoldMultiplier += amount; break;
        case "surface_mining_speed_bonus": stats.surfaceMiningSpeedBonus += amount; break;
        case "surface_mining_gold_yield_bonus": stats.surfaceMiningGoldYieldBonus += amount; break;
        case "surface_mining_crystal_yield_ratio": stats.surfaceMiningCrystalYieldRatio += amount; break;
        case "current_health":
        case "current_mana":
        case "final_move_speed":
        case "gold":
        case "crystal":
        case "experience":
        case "talent_points":
        case "wage":
          break;
      }
    }
    stats.maxHealth = Math.max(1, stats.maxHealth);
    stats.healthRegen = Math.max(0, stats.healthRegen);
    stats.healthRegenMaxRatio = Math.max(0, stats.healthRegenMaxRatio);
    stats.manaMax = Math.max(0, stats.manaMax);
    stats.manaRegen = Math.max(0, stats.manaRegen);
    stats.manaRegenMaxRatio = Math.max(0, stats.manaRegenMaxRatio);
    stats.damage = Math.max(0, stats.damage);
    stats.damageMultiplier = Math.max(0, stats.damageMultiplier);
    stats.attackDamageMultiplier = Math.max(0, stats.attackDamageMultiplier);
    stats.abilityDamageMultiplier = Math.max(0, stats.abilityDamageMultiplier);
    stats.baseAttackSpeedModifier = Math.max(-0.95, stats.baseAttackSpeedModifier);
    stats.attackSpeedBonus = Math.max(-0.95, stats.attackSpeedBonus);
    stats.range = Math.max(0, stats.range);
    stats.moveSpeed = Math.max(0, stats.moveSpeed);
    stats.moveSpeedMultiplier = Math.max(0.2, stats.moveSpeedMultiplier);
    stats.armor = Math.max(0, stats.armor);
    stats.dodge = Math.max(0, stats.dodge);
    stats.repairPerSecond = Math.max(0, stats.repairPerSecond);
    stats.healingPerSecond = Math.max(0, stats.healingPerSecond);
    stats.scoutingRadius = Math.max(0, stats.scoutingRadius);
    stats.revealRadius = Math.max(0, stats.revealRadius);
    stats.splashRadius = Math.max(0, stats.splashRadius);
    stats.chainTargets = Math.max(0, stats.chainTargets);
    stats.pierceTargets = Math.max(0, stats.pierceTargets);
    stats.crystalDropChance = Math.max(0, stats.crystalDropChance);
    stats.coreDropChance = stats.crystalDropChance;
    stats.eliteDamageMultiplier = Math.max(0, stats.eliteDamageMultiplier);
    stats.companionDamageMultiplier = Math.max(0, stats.companionDamageMultiplier);
    stats.summonCap = Math.max(0, stats.summonCap);
    stats.goldMultiplier = Math.max(0.2, stats.goldMultiplier);
    stats.scrapMultiplier = stats.goldMultiplier;
    stats.experienceMultiplier = Math.max(0.2, stats.experienceMultiplier);
    stats.surfaceMiningExtraGoldFlat = Math.max(0, stats.surfaceMiningExtraGoldFlat);
    stats.surfaceMiningExtraGoldMultiplier = Math.max(0, stats.surfaceMiningExtraGoldMultiplier);
    stats.surfaceMiningSpeedBonus = Math.max(0, stats.surfaceMiningSpeedBonus);
    stats.surfaceMiningGoldYieldBonus = Math.max(0, stats.surfaceMiningGoldYieldBonus);
    stats.surfaceMiningCrystalYieldRatio = Math.max(0, stats.surfaceMiningCrystalYieldRatio);
  }

  private recomputePlayer(player: SimPlayer): void {
    const previousMaxHealth = player.stats.maxHealth;
    const previousMaxMana = player.stats.manaMax;
    const previousSynergies = new Set(player.synergies);
    const substitutedModuleIds = [...player.synergySubstitutions.values()]
      .filter((entry) => entry.expiresAtMs > this.elapsedMs)
      .map((entry) => entry.moduleId);
    const activeSynergies = evaluateSynergies([
      ...player.modules,
      ...substitutedModuleIds,
    ]);
    player.synergies = activeSynergies.map((synergy) => synergy.id);
    const newlyActivated = activeSynergies.filter(
      (synergy) => !previousSynergies.has(synergy.id),
    );
    for (const synergy of newlyActivated) {
      for (const mechanic of synergy.mechanics ?? []) {
        if (mechanic.kind === "earth_spirit_aggregation") {
          this.activateMountainSeed(player, mechanic);
          continue;
        }
        if (mechanic.kind === "day_night_cycle") {
          player.dayNightPhase = "day";
          player.dayNightSwitchAtMs =
            this.elapsedMs + mechanic.dayDurationMs;
          player.dayNightNextAttackAtMs =
            this.elapsedMs + mechanic.dayIntervalMs;
          continue;
        }
        if (mechanic.kind !== "hidden_effect_amplifier") {
          continue;
        }
        for (const effectId of mechanic.hiddenEffectIds) {
          getHiddenEffect(effectId).effects.forEach((_, effectIndex) => {
            player.hiddenEffectCooldowns.delete(`${effectId}:${effectIndex}`);
          });
        }
      }
    }
    if (newlyActivated.some((synergy) => synergy.id === "became_cannon")) {
      player.turretMoveRatio = 0;
      player.turretMovementAccumulatorMs = 0;
      player.turretLastMoving = null;
    }
    if (newlyActivated.some((synergy) => synergy.id === "seed_of_rock")) {
      this.resetEarthSpiritSummonCooldowns(player);
    }
    if (!player.synergies.includes("became_cannon")) {
      player.turretMoveRatio = 1;
      player.turretMovementAccumulatorMs = 0;
      player.turretLastMoving = null;
    }
    if (this.isPermanentLich(player) && player.status !== "lich") {
      player.status = "lich";
      player.health = Math.max(1, player.health);
      player.reviveProgressMs = 0;
      player.respawnAtMs = 0;
    }
    const stats = computePlayerStats(
      this.getEffectiveModuleIds(player),
    );
    if (player.status !== "lich") {
      for (const bonuses of player.copiedBonuses) {
        applyStatBonuses(stats, bonuses);
      }
      stats.damage += player.growthBonuses.damage ?? 0;
      stats.maxHealth += player.growthBonuses.maxHealth ?? 0;
      stats.armor += player.growthBonuses.armor ?? 0;
      stats.moveSpeed += player.growthBonuses.moveSpeed ?? 0;
      stats.manaMax += player.growthBonuses.manaMax ?? 0;
    }
    applyStatBonuses(stats, player.permanentOneShotBonuses);
    stats.maxHealth += player.talents.vital_essence * 60;
    stats.manaMax += player.talents.mana_well * 30;
    stats.healthRegen += player.talents.life_font * 1.5;
    stats.manaRegen += player.talents.mana_tide * 0.75;
    stats.damage += player.talents.war_essence * 8;
    stats.fireIntervalMs *= Math.pow(0.94, player.talents.haste_essence);
    stats.moveSpeed += player.talents.swift_essence * 12;
    stats.armor +=
      player.talents.stone_skin * 8 +
      this.teamTechLevels.guardian_oath * 3;
    stats.revealRadius += [0, 240, 420, 600, 800, 1_050][
      player.talents.spirit_sight
    ];
    if (stats.revealRadius > 0) {
      stats.revealRadius *= 1 + stats.surfaceMiningRevealRadiusBonus;
    }
    stats.scoutingRadius += player.talents.far_sight * 120;
    const turret = this.getTurretMechanic(player);
    if (turret && this.isTurretDeployed(player)) {
      stats.maxHealth += turret.deployedMaxHealthAdd;
      stats.armor += turret.deployedArmorAdd;
    }
    this.applyFinanceStatAdjustments(stats, player.financeStatAdjustments);
    for (const moduleId of this.getEffectiveModuleIds(player)) {
      if (!moduleId) continue;
      const contract = getModule(moduleId).mechanics?.find(
        (mechanic) => mechanic.kind === "gold_contract",
      );
      if (!contract || contract.kind !== "gold_contract") continue;
      if (contract.damageEqualsCurrentGold) {
        stats.damage += player.scrap;
      } else if (player.scrap > 0) {
        stats.damage += contract.fixedDamageAdd ?? 0;
        stats.armor += contract.fixedArmorAdd ?? 0;
      }
    }
    player.stats = stats;
    if (previousSynergies.size === 0 && player.synergies.length > 0) {
      player.contribution.firstSynergyAtMs = this.elapsedMs;
    }
    if (newlyActivated.length > 0) {
      this.events.push({
        type: "notice",
        message: `${player.name} 激活羁绊：${newlyActivated
          .map((synergy) => synergy.name)
          .join("、")}`,
      });
    }
    const oneShotVitalClamp = player.pendingOneShotVitalClamp;
    player.pendingOneShotVitalClamp = undefined;
    if (player.status !== "downed") {
      player.health = clamp(
        oneShotVitalClamp
          ? oneShotVitalClamp.health
          : player.health + (stats.maxHealth - previousMaxHealth),
        1,
        stats.maxHealth,
      );
    }
    player.mana = clamp(
      oneShotVitalClamp
        ? oneShotVitalClamp.mana
        : player.mana + (stats.manaMax - previousMaxMana),
      0,
      stats.manaMax,
    );
  }

  private applyTeamTechLevel(techId: TeamTechId): void {
    if (techId === "core_vitality") {
      this.core.maxHealth += 300;
      this.core.health += 300;
    } else if (techId === "core_armor") {
      this.core.armor += 3;
    } else if (techId === "wall_vitality") {
      this.increaseDefenseHealth(140, false);
    } else if (techId === "tower_vitality") {
      this.increaseDefenseHealth(180, true);
    } else if (techId === "wall_armor") {
      for (const segment of this.defenses) {
        if (segment.kind === "wall") {
          segment.armor += 2;
        }
      }
    } else if (techId === "tower_ward") {
      for (const segment of this.defenses) {
        if (segment.hasTower) {
          segment.armor += 3;
        }
      }
    } else if (techId === "familiar_ward") {
      for (const entity of this.abilityEntities.values()) {
        if (!entity.ethereal) {
          entity.armor += 3;
        }
      }
    } else if (techId === "guardian_oath") {
      for (const player of this.players.values()) {
        this.recomputePlayer(player);
      }
    }
  }

  private increaseDefenseHealth(
    amount: number,
    towerOnly: boolean,
  ): void {
    for (const segment of this.defenses) {
      if (
        segment.kind !== "wall" ||
        (towerOnly && !segment.hasTower)
      ) {
        continue;
      }
      segment.maxHealth += amount;
      if (!segment.destroyed) {
        segment.health += amount;
      }
    }
  }

  private getTeamTechStates(): TeamTechState[] {
    return TEAM_TECH.map((definition) => {
      const level = this.teamTechLevels[definition.id];
      return {
        id: definition.id,
        level,
        maxLevel: definition.levels.length,
        nextCost: definition.levels[level]?.cost ?? null,
      };
    });
  }

  private toTalentStates(player: SimPlayer): TalentState[] {
    return TALENT_DEFINITIONS.map((definition) => {
      const level = player.talents[definition.id];
      return {
        id: definition.id,
        name: definition.name,
        description: definition.description,
        effect: describeTalentEffect(definition.id, level),
        level,
        maxLevel: definition.maxLevel,
        nextCost: level < definition.maxLevel ? 1 : null,
      };
    });
  }

  private toLaborPublicState(player: SimPlayer): PlayerPublicState["labor"] {
    const movement = MODULES.find(
      (module) => module.labor?.kind === "movement_distance",
    )?.labor;
    const circle = MODULES.find(
      (module) => module.labor?.kind === "circle_traversal",
    )?.labor;
    const stationary = MODULES.find(
      (module) => module.labor?.kind === "stationary_work",
    )?.labor;
    const route = MODULES.find(
      (module) => module.labor?.kind === "route_leg",
    )?.labor;
    const barbecue = MODULES.find(
      (module) => module.labor?.kind === "kill_bonus",
    )?.labor;
    const candidate = player.laborCircleTracker.candidate;
    return {
      movementDistance: movement?.kind === "movement_distance"
        ? Math.min(player.laborMovementDistance, movement.achievementDistance)
        : player.laborMovementDistance,
      movementDistanceTarget:
        movement?.kind === "movement_distance" ? movement.achievementDistance : 0,
      circleCount: circle?.kind === "circle_traversal"
        ? Math.min(player.laborCircleCount, circle.achievementCount)
        : player.laborCircleCount,
      circleTarget: circle?.kind === "circle_traversal"
        ? circle.achievementCount
        : 0,
      circleCandidateActive: Boolean(candidate),
      circleCandidatePathLength: candidate?.pathLength ?? 0,
      circleCandidateSpanX: candidate ? candidate.maxX - candidate.minX : 0,
      circleCandidateSpanY: candidate ? candidate.maxY - candidate.minY : 0,
      circleCandidateTurnDegrees:
        Math.abs(candidate?.signedTurnRadians ?? 0) * (180 / Math.PI),
      circleCandidateCloseDistance: candidate
        ? Math.hypot(
            candidate.last.x - candidate.start.x,
            candidate.last.y - candidate.start.y,
          )
        : 0,
      stationaryMs: stationary?.kind === "stationary_work"
        ? Math.min(
            player.laborStationaryAchievementMs,
            stationary.achievementStationaryMs,
          )
        : player.laborStationaryAchievementMs,
      stationaryTargetMs: stationary?.kind === "stationary_work"
        ? stationary.achievementStationaryMs
        : 0,
      routeLegs: route?.kind === "route_leg"
        ? Math.min(player.laborRouteLegs, route.achievementLegs)
        : player.laborRouteLegs,
      routeTarget: route?.kind === "route_leg" ? route.achievementLegs : 0,
      routeArmedLabel: player.laborRouteArmed === "core"
        ? "中央水晶"
        : player.laborRouteArmed === "outer_gate"
          ? "最外圈大门"
          : null,
      barbecueBonusGold: barbecue?.kind === "kill_bonus"
        ? Math.min(
            player.laborBarbecueBonusGold,
            barbecue.achievementBonusGold,
          )
        : player.laborBarbecueBonusGold,
      barbecueBonusGoldTarget: barbecue?.kind === "kill_bonus"
        ? barbecue.achievementBonusGold
        : 0,
    };
  }

  private toPublicPlayer(player: SimPlayer): PlayerPublicState {
    const activeHiddenEffectIds = getActiveHiddenEffectIds(
      player.hiddenEffectIds,
      player.hiddenSynergies,
    );
    const hiddenAura = this.getHiddenAllyAuraAt(player.x, player.y);
    const devourSource = this.getSurfaceDevourSource(player);
    const contactSource = this.getSurfaceContactSource(player);
    const conditionalEvolutionTargetId = contactSource?.id ?? null;
    const conditionalEvolutionSlot = contactSource
      ? player.modules.findIndex((moduleId) => moduleId === contactSource.id)
      : -1;
    return {
      id: player.id,
      name: player.name,
      spaceId: player.spaceId,
      x: player.x,
      y: player.y,
      health: player.health,
      maxHealth: player.stats.maxHealth,
      mana: player.mana,
      maxMana: player.stats.manaMax,
      experience: player.experience,
      nextLevelExperience:
        player.level >= PLAYER_LEVEL_CAP
          ? 0
          : nextLevelExperience(player.level),
      level: player.level,
      levelCap: PLAYER_LEVEL_CAP,
      talentPoints: player.talentPoints,
      talentPointCap: PLAYER_TALENT_POINT_CAP,
      talents: this.toTalentStates(player),
      wage: player.wage,
      archaeologyYield: player.archaeologyYield,
      healthRegen:
        this.getPlayerHealthRegen(player),
      ready: player.ready,
      connected: player.connected,
      aiControlled: player.aiControlled,
      inputSequence: player.inputSequence,
      scrap: player.scrap,
      core: player.core,
      kills: player.kills,
      status: player.status,
      reviveProgressMs: player.reviveProgressMs,
      reviveRequiredMs: player.reviveRequiredMs,
      respawnAtMs: player.respawnAtMs,
      contribution: {
        ...player.contribution,
        damageBySource: player.contribution.damageBySource.map((entry) => ({
          ...entry,
        })),
      },
      modules: [...player.modules],
      moduleRuntimeStates: player.modules.flatMap((moduleId, slot) => {
        if (!moduleId) {
          return [];
        }
        const module = getModule(moduleId);
        if (
          !module.experienceVault &&
          !module.killCharge &&
          !module.financeState &&
          !module.labor
        ) {
          return [];
        }
        const state = this.getModuleSlotState(player, slot);
        if (!state) {
          return [];
        }
        const finance = module.financeState;
        const waitingResource =
          finance?.resourceGate?.resource ??
          finance?.periodicSpendAccumulator?.resource;
        const waitingCurrent = waitingResource
          ? this.getFinanceResource(player, waitingResource)
          : undefined;
        const stake = state.financeStake;
        const stakeOutcome =
          finance?.timedOutcome?.kind === "stake_resolution"
            ? finance.timedOutcome
            : undefined;
        return [{
          slot,
          moduleId,
          experienceStored: module.experienceVault
            ? state.experienceStored
            : undefined,
          experienceCapacity: module.experienceVault?.capacity,
          replacementExperiencePayout: module.experienceVault
            ? state.experienceStored *
              module.experienceVault.replacementPayoutMultiplier *
              player.stats.experienceMultiplier
            : undefined,
          killCharge: module.killCharge ? state.killCharge : undefined,
          killChargeRequired: module.killCharge?.requiredCount,
          financeStatus: finance?.timeScaledIncome
            ? "income"
            : finance?.resourceGate
              ? "waiting_payment"
              : finance?.periodicDrain
                ? "drain"
                : finance?.periodicSpendAccumulator
                  ? "spend_accumulator"
                  : finance?.periodicCharge
                    ? "charge"
                : finance?.timedOutcome || finance?.timedTransition
                  ? "countdown"
                  : finance?.onEnterGrant
                    ? "claimed"
                    : finance?.onReplacementReward
                      ? "replacement_reward"
                  : undefined,
          financeRemainingMs: state.financeNextAtMs === undefined
            ? undefined
            : Math.max(0, state.financeNextAtMs - this.elapsedMs),
          financeWaitingResource: waitingResource === "gold"
            ? "金币"
            : waitingResource === "crystal"
              ? "结晶"
              : undefined,
          financeRequiredAmount:
            finance?.resourceGate?.cost ??
            finance?.periodicSpendAccumulator?.costPerTick,
          financeCurrentAmount: waitingCurrent,
          financeShortfall: waitingCurrent !== undefined
            ? Math.max(
                0,
                (finance?.resourceGate?.cost ??
                  finance?.periodicSpendAccumulator?.costPerTick ??
                  0) - waitingCurrent,
              )
            : undefined,
          financeStakeName: stake
            ? getFinanceStakeTarget(stake.targetId).name
            : undefined,
          financeStakeAmount: stake?.amount,
          financeSuccessPercent: stakeOutcome
            ? this.getFinanceSuccessPercent(player, stakeOutcome)
            : undefined,
          financeProgress: finance?.periodicSpendAccumulator
            ? state.financeAccumulatedSpend
            : finance?.periodicCharge
              ? state.financeCharge
              : undefined,
          financeProgressTarget:
            finance?.periodicSpendAccumulator?.threshold ??
            finance?.periodicCharge?.threshold,
          financeTickCost: finance?.periodicSpendAccumulator?.costPerTick,
          financeReplacementReward: finance?.onReplacementReward?.amount,
          financeRewardClaimed: finance?.onEnterGrant ? true : undefined,
          laborStatus: module.labor?.kind === "movement_distance"
            ? "distance"
            : module.labor?.kind === "circle_traversal"
              ? "circle"
              : module.labor?.kind === "stationary_work"
                ? "stationary"
                : module.labor?.kind === "route_leg"
                  ? "route"
                  : module.labor?.kind === "kill_bonus"
                    ? "kill_bonus"
                    : undefined,
          laborProgress: module.labor?.kind === "movement_distance"
            ? state.laborDistanceRemainder
            : module.labor?.kind === "circle_traversal"
              ? player.laborCircleCount
              : module.labor?.kind === "stationary_work"
                ? state.laborStationaryRemainderMs
                : module.labor?.kind === "route_leg"
                  ? player.laborRouteLegs
                  : module.labor?.kind === "kill_bonus"
                    ? Math.min(
                        player.laborBarbecueBonusGold,
                        module.labor.achievementBonusGold,
                      )
                    : undefined,
          laborProgressTarget: module.labor?.kind === "movement_distance"
            ? module.labor.payoutDistance
            : module.labor?.kind === "circle_traversal"
              ? module.labor.achievementCount
              : module.labor?.kind === "stationary_work"
                ? module.labor.payoutIntervalMs
                : module.labor?.kind === "route_leg"
                  ? module.labor.achievementLegs
                  : module.labor?.kind === "kill_bonus"
                    ? module.labor.achievementBonusGold
                    : undefined,
          laborSecondaryProgress: module.labor?.kind === "movement_distance"
            ? Math.min(player.laborMovementDistance, module.labor.achievementDistance)
            : module.labor?.kind === "stationary_work"
              ? player.laborStationaryAchievementMs
              : undefined,
          laborSecondaryTarget: module.labor?.kind === "movement_distance"
            ? module.labor.achievementDistance
            : module.labor?.kind === "stationary_work"
              ? module.labor.achievementStationaryMs
              : undefined,
          laborRemainingMs: module.labor?.kind === "stationary_work"
            ? module.labor.payoutIntervalMs - state.laborStationaryRemainderMs
            : undefined,
          laborMovingRemainingMs: module.labor?.kind === "stationary_work"
            ? module.labor.movingDrainIntervalMs - state.laborMovingRemainderMs
            : undefined,
          laborLabel: module.labor?.kind === "route_leg"
            ? player.laborRouteArmed === "core"
              ? "中央水晶"
              : player.laborRouteArmed === "outer_gate"
                ? "最外圈大门"
                : "尚未武装起点"
            : undefined,
          castProgress: player.hiddenSynergies.includes(ROUND_CLOCK_LABOR_SYNERGY_ID)
            ? (player.castReleaseCounts.get(`slot:${slot}`) ?? 0)
            : undefined,
          castRequired: player.hiddenSynergies.includes(ROUND_CLOCK_LABOR_SYNERGY_ID)
            ? 5
            : undefined,
        }];
      }),
      lockedSlots: [...player.lockedSlots],
      synergies: [...player.synergies],
      hiddenEffects: [...player.hiddenEffectIds].flatMap((effectId) => {
        const effect = getHiddenEffect(effectId);
        return effect.repeatable ? [] : [{
          id: effect.id,
          name: effect.name,
          description: effect.description,
          active: activeHiddenEffectIds.has(effect.id),
        }];
      }),
      hiddenSynergies: player.hiddenSynergies.map((synergyId) => {
        const synergy = getHiddenSynergy(synergyId);
        return {
          id: synergy.id,
          name: synergy.name,
          description: synergy.description,
        };
      }),
      luckyStarCount:
        player.repeatableHiddenEffectGrants.get(
          LUCKY_STAR_HIDDEN_EFFECT_ID,
        )?.length ?? 0,
      labor: this.toLaborPublicState(player),
      repeatableHiddenEffects: [
        ...player.repeatableHiddenEffectGrants.entries(),
      ].map(([effectId, grants]) => {
        const effect = getHiddenEffect(effectId);
        const sourceTierCounts: Record<ModuleTier, number> = {
          1: 0,
          2: 0,
          3: 0,
          4: 0,
          5: 0,
          6: 0,
        };
        if (effect.repeatable?.trackSourceTier) {
          for (const tier of grants) {
            sourceTierCounts[tier] += 1;
          }
        }
        return {
          id: effect.id,
          name: effect.name,
          description: effect.description,
          totalCount: grants.length,
          sourceTierCounts,
        };
      }),
      hiddenSynergyProgress: HIDDEN_SYNERGIES.filter(
        (synergy) => synergy.repeatableRequirement,
      ).map((synergy) => {
        const requirement = synergy.repeatableRequirement!;
        return {
          id: synergy.id,
          name: synergy.name,
          description: synergy.description,
          active: player.hiddenSynergies.includes(synergy.id),
          progress: player.hiddenSynergyProgress.get(synergy.id) ?? 0,
          requiredCount: requirement.count,
          eligibleTierLabel: requirement.exactSourceTier
            ? `${requirement.exactSourceTier}阶`
            : requirement.minSourceTier
              ? `${requirement.minSourceTier}阶及以上`
              : "任意品阶",
        };
      }),
      combatStats: {
        damage:
          player.stats.damage *
          player.stats.damageMultiplier *
          player.stats.attackDamageMultiplier *
          (1 + this.teamTechLevels.war_chant * 0.03) *
          (this.isTurretDeployed(player)
            ? (this.getTurretMechanic(player)?.deployedDamageMultiplier ?? 1)
            : 1),
        attackIntervalMs:
          this.getPlayerAttackIntervalMs(player) /
          (1 + this.teamTechLevels.haste_chant * 0.02) /
          hiddenAura.attackSpeedMultiplier,
        moveSpeed:
          this.getPlayerMoveSpeed(player) *
          (contactSource ? 1 : hiddenAura.moveSpeedMultiplier),
        armor: player.stats.armor + hiddenAura.armorAdd,
        healthRegen:
          this.getPlayerHealthRegen(player),
        manaRegen: this.getPlayerManaRegen(player),
        abilityDamageMultiplier:
          this.getPlayerAbilityDamageMultiplier(player),
        baseAttackSpeedModifier:
          this.getPlayerBaseAttackSpeedModifier(player),
        attackSpeedBonus: this.getPlayerAttackSpeedBonus(player),
        perpetualCoreStacks: this.getPerpetualCoreStackCount(player),
        turretMoveRatio: this.getTurretMechanic(player)
          ? player.turretMoveRatio
          : 1,
        dayNightPhase: this.getActiveSynergyMechanic(
          player,
          "day_night_cycle",
        )
          ? player.dayNightPhase
          : undefined,
        abilityDamageGrowth: Object.fromEntries(player.abilityDamageGrowth),
        revealRadius: player.stats.revealRadius,
        scoutingRadius: player.stats.scoutingRadius,
      },
      surfaceMiningState: {
        veinId: player.surfaceMiningVeinId ?? null,
        devourProgressMs: player.surfaceDevourProgressMs,
        devourRequiredMs:
          devourSource?.module.surfaceMining?.devour?.channelMs ?? 0,
        devourCooldownRemainingMs: devourSource
          ? Math.max(0, player.surfaceDevourCooldownUntilMs - this.elapsedMs)
          : 0,
        devourCount: player.surfaceDevourCount,
        conditionalEvolutionTargetId,
        slotLocked:
          conditionalEvolutionSlot >= 0 &&
          player.lockedSlots[conditionalEvolutionSlot] === true,
      },
    };
  }

  private toWireEnemy(enemy: SimEnemy): EnemyWireState {
    return {
      id: enemy.id,
      definitionId: enemy.definitionId,
      x: Math.round(enemy.x),
      y: Math.round(enemy.y),
      health: Math.max(0, Math.round(enemy.health)),
      maxHealth: Math.round(enemy.maxHealth),
      armor: enemy.armor,
      spaceId: enemy.spaceId,
      velocityX: Math.round(enemy.velocityX * 10) / 10,
      velocityY: Math.round(enemy.velocityY * 10) / 10,
      stunnedUntilMs:
        enemy.stunnedUntilMs > this.elapsedMs
          ? enemy.stunnedUntilMs
          : undefined,
    };
  }

  private toWireAbilityEntity(entity: SimAbilityEntity): AbilityEntityWireState {
    return {
      id: entity.id,
      ownerId: entity.ownerId,
      abilityId: entity.abilityId,
      kind: entity.kind,
      presentationId: entity.presentationId,
      spaceId: entity.spaceId,
      x: Math.round(entity.x),
      y: Math.round(entity.y),
      health: entity.ethereal
        ? 0
        : Number.isFinite(entity.health)
          ? Math.max(0, Math.round(entity.health))
          : 1,
      maxHealth:
        entity.kind === "field" || entity.ethereal
          ? 0
          : Number.isFinite(entity.maxHealth)
            ? Math.round(entity.maxHealth)
            : 1,
      ethereal: entity.ethereal || undefined,
      effectRadius: entity.field?.radius,
      undeadUnitId: entity.undeadUnitId,
      guardForm: entity.guardForm,
      taunting: this.isTauntingUndead(entity) || undefined,
      earthSpiritStar: entity.earthSpirit?.star,
      earthSpiritBehavior: entity.earthSpirit?.behavior,
    };
  }

  private toWireCityBuilding(building: SimCityBuilding): CityBuildingState {
    return {
      id: building.id,
      definitionId: building.definitionId,
      name: building.name,
      presentationId: building.presentationId,
      function: building.function,
      x: Math.round(building.x),
      y: Math.round(building.y),
      radius: building.radius,
      health: Math.max(0, Math.round(building.health)),
      maxHealth: Math.round(building.maxHealth),
      armor: building.armor,
      destroyed: building.destroyed,
      spawnModelId: building.spawnModelId,
      spawnIntervalMs: building.spawnIntervalMs,
      spawnBatchSize: building.spawnBatchSize,
      activeUnitCount: building.activeUnitCount,
      globalUnitCap: building.globalUnitCap,
    };
  }

  private toWireCityAlly(ally: SimCityAlly): CityAllyUnitState {
    return {
      id: ally.id,
      modelId: ally.modelId,
      presentationId: ally.presentationId,
      x: Math.round(ally.x),
      y: Math.round(ally.y),
      health: Math.max(0, Math.round(ally.health)),
      maxHealth: Math.round(ally.maxHealth),
    };
  }

  private getLaneStates(): LaneState[] {
    return LANE_CONFIGS.map((lane) => {
      const enemies = [...this.enemies.values()].filter(
        (enemy) => enemy.spaceId === "surface" && enemy.laneId === lane.id,
      );
      const eliteCount = enemies.filter(
        (enemy) => getEnemy(enemy.definitionId).rank !== "normal",
      ).length;
      const pressure = enemies.reduce((sum, enemy) => {
        const definition = getEnemy(enemy.definitionId);
        const rankWeight =
          definition.rank === "boss" ? 14 : definition.rank === "elite" ? 4 : 1;
        const distance = Math.hypot(enemy.x - this.core.x, enemy.y - this.core.y);
        const proximity = 1 + clamp(1 - distance / 1_050, 0, 1) * 1.6;
        return sum + rankWeight * proximity;
      }, 0);
      return {
        ...lane,
        pressure: Math.round(pressure),
        enemyCount: enemies.length,
        eliteCount,
      };
    });
  }

  private pickTarget(
    x: number,
    y: number,
    range: number,
    mode:
      | "random"
      | "closest"
      | "farthest"
      | "highest_health"
      | "lowest_health"
      | "closest_to_core"
      | "fastest"
      | "alternating_extremes"
      | "random_area"
      | "elite_only"
      | "boss_only",
    forcedRank?: "normal" | "elite" | "boss",
    spaceId?: WorldSpaceId,
  ): SimEnemy | undefined {
    return this.pickTargets(x, y, range, mode, 1, forcedRank, spaceId)[0];
  }

  private pickTargets(
    x: number,
    y: number,
    range: number,
    mode:
      | "random"
      | "closest"
      | "farthest"
      | "highest_health"
      | "lowest_health"
      | "closest_to_core"
      | "fastest"
      | "alternating_extremes"
      | "random_area"
      | "elite_only"
      | "boss_only",
    count: number,
    forcedRank?: "normal" | "elite" | "boss",
    spaceId?: WorldSpaceId,
  ): SimEnemy[] {
    let available = [...this.enemies.values()].filter(
      (enemy) =>
        (spaceId === undefined || enemy.spaceId === spaceId) &&
        Math.hypot(enemy.x - x, enemy.y - y) <= range,
    );
    if (forcedRank) {
      available = available.filter(
        (enemy) => getEnemy(enemy.definitionId).rank === forcedRank,
      );
    } else if (mode === "elite_only") {
      available = available.filter(
        (enemy) => getEnemy(enemy.definitionId).rank !== "normal",
      );
    } else if (mode === "boss_only") {
      available = available.filter(
        (enemy) => getEnemy(enemy.definitionId).rank === "boss",
      );
    }
    if (mode === "closest") {
      return available
        .sort(
          (a, b) =>
            distanceSquared(a.x, a.y, x, y) - distanceSquared(b.x, b.y, x, y),
        )
        .slice(0, count);
    }
    if (mode === "farthest") {
      return available
        .sort(
          (a, b) =>
            distanceSquared(b.x, b.y, x, y) - distanceSquared(a.x, a.y, x, y) ||
            a.id - b.id,
        )
        .slice(0, count);
    }
    if (mode === "highest_health") {
      return available
        .sort((a, b) => b.health - a.health || a.id - b.id)
        .slice(0, count);
    }
    if (mode === "lowest_health") {
      return available
        .sort((a, b) => a.health - b.health || a.id - b.id)
        .slice(0, count);
    }
    if (mode === "closest_to_core") {
      return available
        .sort(
          (a, b) =>
            distanceSquared(a.x, a.y, this.core.x, this.core.y) -
              distanceSquared(b.x, b.y, this.core.x, this.core.y) ||
            a.id - b.id,
        )
        .slice(0, count);
    }
    if (mode === "fastest") {
      return available
        .sort(
          (a, b) =>
            getEnemy(b.definitionId).speed - getEnemy(a.definitionId).speed ||
            a.id - b.id,
        )
        .slice(0, count);
    }
    const selected: SimEnemy[] = [];
    const remaining = [...available];
    while (selected.length < count && remaining.length > 0) {
      const picked = this.random.pick(remaining);
      selected.push(picked);
      remaining.splice(remaining.indexOf(picked), 1);
    }
    return selected;
  }

  private findClosestEnemy(
    x: number,
    y: number,
    range: number,
    spaceId?: WorldSpaceId,
  ): SimEnemy | undefined {
    return this.pickTarget(
      x,
      y,
      range,
      "closest",
      undefined,
      spaceId,
    );
  }

  private bestCombatAbility(player: SimPlayer): ModuleDefinition | undefined {
    return player.modules
      .filter((id): id is string => Boolean(id))
      .map((id) => getModule(id))
      .filter((ability) => ability.combat)
      .sort(
        (a, b) =>
          ((b.combat!.damage * (b.combat!.projectiles ?? 1)) /
            b.combat!.intervalMs) -
          ((a.combat!.damage * (a.combat!.projectiles ?? 1)) /
            a.combat!.intervalMs),
      )[0];
  }

  private getLichMechanic(
    player: SimPlayer,
  ):
    | Extract<AbilityMechanicDefinition, { kind: "lich_form" }>
    | undefined {
    const mechanics = player.modules.flatMap((abilityId) => {
      if (!abilityId) {
        return [];
      }
      return (getModule(abilityId).mechanics ?? []).filter(
        (
          mechanic,
        ): mechanic is Extract<
          AbilityMechanicDefinition,
          { kind: "lich_form" }
        > => mechanic.kind === "lich_form",
      );
    });
    return (
      mechanics.find((mechanic) => mechanic.mode === "permanent") ??
      mechanics[0]
    );
  }

  private isPermanentLich(player: SimPlayer): boolean {
    return this.getLichMechanic(player)?.mode === "permanent";
  }

  private getEffectiveModuleIds(
    player: SimPlayer,
  ): Array<string | null> {
    return player.modules.map((abilityId) => {
      if (!abilityId) {
        return null;
      }
      const ability = getModule(abilityId);
      return this.isAbilityEnabled(player, ability)
        ? abilityId
        : null;
    });
  }

  private isAbilityEnabled(
    player: SimPlayer,
    ability: ModuleDefinition,
  ): boolean {
    const suppressedModuleIds = this.getActiveSynergyMechanic(
      player,
      "divine_guard_upgrade",
    )?.suppressedModuleIds;
    const genericSuppressed = this.getActiveSynergyMechanic(
      player,
      "suppress_modules",
    )?.moduleIds;
    const wealthSuppressed = this.getActiveSynergyMechanic(
      player,
      "greed_and_wealth",
    )?.suppressedModuleIds;
    if (
      suppressedModuleIds?.includes(baseAbilityId(ability.id)) ||
      genericSuppressed?.includes(baseAbilityId(ability.id)) ||
      wealthSuppressed?.includes(baseAbilityId(ability.id))
    ) {
      return false;
    }
    if (player.status !== "lich") {
      return true;
    }
    if (ability.tags.includes("undead_summon")) {
      return true;
    }
    return (
      ability.id === "finality_decree" &&
      player.synergies.includes("undead_catastrophe")
    );
  }

  private getUndeadCatastropheMechanic(
    player: SimPlayer,
  ):
    | Extract<
        SynergyMechanicDefinition,
        { kind: "undead_catastrophe" }
      >
    | undefined {
    for (const synergyId of player.synergies) {
      const mechanic = getSynergy(synergyId).mechanics?.find(
        (entry) => entry.kind === "undead_catastrophe",
      );
      if (mechanic?.kind === "undead_catastrophe") {
        return mechanic;
      }
    }
    return undefined;
  }

  private isTauntingUndead(entity: SimAbilityEntity): boolean {
    return Boolean(
      entity.undeadUnitId &&
        hasUndeadAbility(getUndeadUnit(entity.undeadUnitId), "taunt"),
    );
  }

  private isTauntingAbilityEntity(entity: SimAbilityEntity): boolean {
    if (this.isTauntingUndead(entity)) return true;
    try {
      return Boolean(
        getModule(baseAbilityId(entity.abilityId)).companion?.taunt,
      );
    } catch {
      return false;
    }
  }

  private hasSpecial(
    player: SimPlayer,
    special: NonNullable<ModuleDefinition["special"]>,
  ): boolean {
    return player.modules.some((id) => id && getModule(id).special === special);
  }

  private hasSynergySpecial(
    player: SimPlayer,
    special: NonNullable<ModuleDefinition["special"]>,
  ): boolean {
    return evaluateSynergies(player.modules).some(
      (synergy) => synergy.special === special,
    );
  }

  private removeEntitiesForAbility(ownerId: string, abilityId: string | null): void {
    if (!abilityId) {
      return;
    }
    const owner = this.players.get(ownerId);
    if (owner?.modules.includes(abilityId)) {
      return;
    }
    for (const entity of [...this.abilityEntities.values()]) {
      if (
        entity.ownerId === ownerId &&
        entity.kind !== "field" &&
        !entity.undeadUnitId &&
        entity.abilityId.startsWith(abilityId)
      ) {
        this.cancelEarthAggregationForEntity(entity);
        this.abilityEntities.delete(entity.id);
      }
    }
  }

  private closestLaneId(x: number, y: number): LaneId {
    const normalizedX = (x - this.core.x) / (WORLD_WIDTH / 2);
    const normalizedY = (y - this.core.y) / (WORLD_HEIGHT / 2);
    if (Math.abs(normalizedX) >= Math.abs(normalizedY)) {
      return normalizedX >= 0 ? "east" : "west";
    }
    return normalizedY >= 0 ? "south" : "north";
  }

  private expireSignals(): void {
    for (let index = this.signals.length - 1; index >= 0; index -= 1) {
      if (this.signals[index].expiresAtMs <= this.elapsedMs) {
        this.signals.splice(index, 1);
      }
    }
  }

  private finish(outcome: "victory" | "draw" | "defeat", reason: string): void {
    this.phase = outcome;
    this.resultReason = reason;
    this.events.push({
      type: "result",
      outcome,
      victory: outcome === "victory",
      reason,
    });
  }
}

function nextLevelExperience(level: number): number {
  return 70 + level * 35;
}

function describeTalentEffect(talentId: TalentId, level: number): string {
  switch (talentId) {
    case "vital_essence":
      return `当前生命上限 +${level * 60}`;
    case "mana_well":
      return `当前能量上限 +${level * 30}`;
    case "life_font":
      return `当前生命恢复 +${(level * 1.5).toFixed(1)}/秒`;
    case "mana_tide":
      return `当前能量恢复 +${(level * 0.75).toFixed(2)}/秒`;
    case "war_essence":
      return `当前基础攻击力 +${level * 8}`;
    case "haste_essence":
      return `当前攻击间隔 ×${Math.pow(0.94, level).toFixed(2)}`;
    case "swift_essence":
      return `当前移动速度 +${level * 12}`;
    case "stone_skin":
      return `当前护甲 +${level * 8}`;
    case "survey":
      return "采矿勘测已封存";
    case "spirit_sight":
      return `当前反隐范围 ${[0, 240, 420, 600, 800, 1_050][level]}`;
    case "far_sight":
      return `当前侦查范围 +${level * 120}`;
  }
}

function damageFromTier(tier: ModuleTier): number {
  return [0, 20, 38, 70, 125, 220, 380][tier];
}

function mechanicCooldownKey(
  abilityId: string,
  index: number,
  sourceSlot?: number,
): string {
  return sourceSlot === undefined
    ? `${abilityId}:mechanic:${index}`
    : `${abilityId}:slot:${sourceSlot}:mechanic:${index}`;
}

function mechanicIntervalMs(
  mechanic: AbilityMechanicDefinition,
): number | undefined {
  switch (mechanic.kind) {
    case "periodic_target_damage":
    case "periodic_reroll":
    case "random_skill_cast":
    case "enemy_conversion":
    case "periodic_self_area_damage":
    case "periodic_normal_execute_reward":
    case "periodic_forced_aggro":
    case "periodic_health_extremes_damage":
    case "periodic_distance_extremes_damage":
    case "damage_lifesteal":
    case "cooldown_acceleration":
    case "synergy_substitution":
      return mechanic.intervalMs;
    case "gold_contract":
      return mechanic.drainIntervalMs;
    case "energy_reservoir":
      return 1_000;
    case "undead_summon":
      return mechanic.intervalMs;
    case "black_hole":
      return mechanic.spawnIntervalMs;
    case "death_guard":
    case "companion_aura":
    case "basic_attack_cannon":
    case "guard_periodic_area_damage":
    case "earth_spirit_summon":
    case "follower_periodic_damage":
    case "follower_periodic_area_damage":
    case "basic_attack_chain":
    case "movement_damage_field":
    case "guard_charge":
    case "ability_kill_growth":
    case "pasture_effect_amplifier":
    case "lich_form":
    case "basic_attack_health_cost":
    case "guard_periodic_strike":
    case "guard_basic_attack_splash":
    case "summon_damage_aura":
    case "every_nth_summon_commander":
    case "boss_attack_delay_on_hit":
    case "death_growth":
      return undefined;
  }
}

function isInstantlyCastable(ability: ModuleDefinition): boolean {
  return Boolean(
    ability.combat ||
      ability.healing ||
      ability.mechanics?.some(
        (mechanic) =>
          mechanic.kind === "periodic_target_damage" ||
          mechanic.kind === "enemy_conversion" ||
          mechanic.kind === "black_hole" ||
          mechanic.kind === "periodic_self_area_damage" ||
          mechanic.kind === "periodic_normal_execute_reward" ||
          mechanic.kind === "periodic_forced_aggro" ||
          mechanic.kind === "periodic_health_extremes_damage",
      ),
  );
}

function scaleHiddenRuntimeEffect(
  effect: HiddenRuntimeEffect,
  multiplier: number,
): HiddenRuntimeEffect {
  if (multiplier === 1) {
    return effect;
  }
  switch (effect.kind) {
    case "periodic_area_damage":
      return { ...effect, damage: effect.damage * multiplier };
    case "periodic_random_damage":
      return { ...effect, damage: effect.damage * multiplier };
    case "periodic_non_boss_push_damage":
      return { ...effect, damage: effect.damage * multiplier };
    case "ally_healing_aura":
      return {
        ...effect,
        healingPerSecond: effect.healingPerSecond * multiplier,
      };
    case "periodic_heavy_damage":
      return { ...effect, damage: effect.damage * multiplier };
    case "ally_armor_aura":
      return { ...effect, armor: effect.armor * multiplier };
    case "ally_speed_aura":
      return {
        ...effect,
        attackSpeedMultiplier: roundRuntimeMultiplier(
          1 + (effect.attackSpeedMultiplier - 1) * multiplier,
        ),
        moveSpeedMultiplier: roundRuntimeMultiplier(
          1 + (effect.moveSpeedMultiplier - 1) * multiplier,
        ),
      };
    case "periodic_normal_execute":
      return { ...effect, intervalMs: effect.intervalMs / multiplier };
    case "low_health_execute":
      return {
        ...effect,
        healthRatio: Math.min(1, effect.healthRatio * multiplier),
      };
    case "base_move_speed_multiplier":
      return {
        ...effect,
        multiplier: roundRuntimeMultiplier(
          1 + (effect.multiplier - 1) * multiplier,
        ),
      };
    case "enemy_slow_immunity":
    case "normal_enemy_taunt_aura":
    case "every_nth_cast_double":
      return effect;
  }
}

function roundRuntimeMultiplier(value: number): number {
  return Math.round(value * 1_000_000_000) / 1_000_000_000;
}

function baseAbilityId(abilityId: string): string {
  return abilityId.includes(":") ? abilityId.split(":")[0] : abilityId;
}

function sanitizeName(value: string): string {
  const name = value.trim().replace(/\s+/g, " ").slice(0, 16);
  return name || "无名史莱姆";
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function roundUpTo(value: number, step: number): number {
  return Math.ceil(value / step) * step;
}

function distanceSquared(
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

function distanceToSegment(
  point: Vec2,
  start: Vec2,
  end: Vec2,
): number {
  const segmentX = end.x - start.x;
  const segmentY = end.y - start.y;
  const lengthSquared =
    segmentX * segmentX + segmentY * segmentY;
  if (lengthSquared <= 0.000001) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }
  const ratio = clamp(
    ((point.x - start.x) * segmentX +
      (point.y - start.y) * segmentY) /
      lengthSquared,
    0,
    1,
  );
  const closestX = start.x + segmentX * ratio;
  const closestY = start.y + segmentY * ratio;
  return Math.hypot(point.x - closestX, point.y - closestY);
}

function knockEnemyOutOfPath(
  enemy: SimEnemy,
  start: Vec2,
  end: Vec2,
  pathWidth: number,
): void {
  const segmentX = end.x - start.x;
  const segmentY = end.y - start.y;
  const lengthSquared = segmentX * segmentX + segmentY * segmentY;
  const ratio =
    lengthSquared <= 0.000001
      ? 0
      : clamp(
          ((enemy.x - start.x) * segmentX +
            (enemy.y - start.y) * segmentY) /
            lengthSquared,
          0,
          1,
        );
  const closestX = start.x + segmentX * ratio;
  const closestY = start.y + segmentY * ratio;
  let directionX = enemy.x - closestX;
  let directionY = enemy.y - closestY;
  let length = Math.hypot(directionX, directionY);
  if (length <= 0.000001) {
    const segmentLength = Math.hypot(segmentX, segmentY);
    if (segmentLength <= 0.000001) {
      directionX = enemy.id % 2 === 0 ? 1 : -1;
      directionY = 0;
    } else {
      const side = enemy.id % 2 === 0 ? 1 : -1;
      directionX = (-segmentY / segmentLength) * side;
      directionY = (segmentX / segmentLength) * side;
    }
    length = 1;
  }
  const enemyRadius = getEnemy(enemy.definitionId).radius;
  const targetDistance = pathWidth / 2 + enemyRadius + 0.001;
  enemy.x = clamp(
    closestX + (directionX / length) * targetDistance,
    enemyRadius,
    WORLD_WIDTH - enemyRadius,
  );
  enemy.y = clamp(
    closestY + (directionY / length) * targetDistance,
    enemyRadius,
    WORLD_HEIGHT - enemyRadius,
  );
}

function knockEnemyOutOfCircle(
  enemy: SimEnemy,
  center: Vec2,
  radius: number,
  random: SeededRandom,
): void {
  let directionX = enemy.x - center.x;
  let directionY = enemy.y - center.y;
  let length = Math.hypot(directionX, directionY);
  if (length <= 0.000001) {
    const angle = random.next() * Math.PI * 2;
    directionX = Math.cos(angle);
    directionY = Math.sin(angle);
    length = 1;
  }
  const enemyRadius = getEnemy(enemy.definitionId).radius;
  const targetDistance = radius + enemyRadius + 0.001;
  enemy.x = clamp(
    center.x + (directionX / length) * targetDistance,
    enemyRadius,
    WORLD_WIDTH - enemyRadius,
  );
  enemy.y = clamp(
    center.y + (directionY / length) * targetDistance,
    enemyRadius,
    WORLD_HEIGHT - enemyRadius,
  );
}

function circleIntersectsRectangle(
  x: number,
  y: number,
  radius: number,
  rectangle: Pick<DefenseSegmentState, "x" | "y" | "width" | "height">,
): boolean {
  const closestX = clamp(
    x,
    rectangle.x - rectangle.width / 2,
    rectangle.x + rectangle.width / 2,
  );
  const closestY = clamp(
    y,
    rectangle.y - rectangle.height / 2,
    rectangle.y + rectangle.height / 2,
  );
  return distanceSquared(x, y, closestX, closestY) <= radius * radius;
}

function distanceToRectangle(
  x: number,
  y: number,
  rectangle: Pick<DefenseSegmentState, "x" | "y" | "width" | "height">,
): number {
  const closestX = clamp(
    x,
    rectangle.x - rectangle.width / 2,
    rectangle.x + rectangle.width / 2,
  );
  const closestY = clamp(
    y,
    rectangle.y - rectangle.height / 2,
    rectangle.y + rectangle.height / 2,
  );
  return Math.hypot(x - closestX, y - closestY);
}

function normalizedDirection(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  deadZone = 0,
): Vec2 {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const length = Math.hypot(dx, dy);
  if (length <= deadZone) {
    return { x: 0, y: 0 };
  }
  return { x: dx / length, y: dy / length };
}
